import React, { useEffect, useMemo, useRef, useState, useCallback, useReducer } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, InputAdornment, LinearProgress, Link, Menu, MenuItem, Paper, Skeleton,
  Slide, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CastIcon from '@mui/icons-material/Cast';
import CastConnectedIcon from '@mui/icons-material/CastConnected';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import PhotoSizeSelectActualIcon from '@mui/icons-material/PhotoSizeSelectActual';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import eventApi from './eventApi';
import { useLocalAutoSync } from '../../utils/localFolderSync/useLocalAutoSync';
import { useManualFolderPersistence } from '../../utils/localFolderSync/useManualFolderPersistence';
import { nativeFilePicker } from '../../utils/localFolderSync/nativeProvider';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import * as stagingProvider from '../../utils/backgroundUpload/stagingProvider';
import { nativeUploadService, makeUploadJobId } from '../../utils/backgroundUpload/nativeUploadService';
import { runUploadJob } from '../../utils/backgroundUpload/resumeUploadJob';
import { useNativeUploadObserver } from '../../utils/backgroundUpload/useNativeUploadObserver';
import ResumeUploadBanner from '../../utils/backgroundUpload/ResumeUploadBanner';
import StrandedAutoSyncStopButton from '../../components/common/StrandedAutoSyncStopButton';
import PhotoInfoPanel from '../../components/common/PhotoInfoPanel';
import {
  UPLOAD_IN_PROGRESS_WARNING, GROUPING_IN_PROGRESS_NOTICE, UPLOAD_BACKGROUNDABLE_NOTICE,
  shouldShowUploadWarning, shouldShowBackgroundableNotice,
} from '../../utils/backgroundUpload/uploadCloseWarning';
import profileApi from '../Profile/profileApi';
import networkApi from '../Network/services/networkApi';
import AddToCirclePrompt from '../../components/network/AddToCirclePrompt';
import MarkdownContent from './EventMarkdown';
import PhotoRequestFormDialog from './PhotoRequestFormDialog';
import ReferencePhotoRow from './ReferencePhotoRow';
import ReferenceSetsDialog from './ReferenceSetsDialog';
import SelfieCaptureDialog from './SelfieCaptureDialog';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { swalConfirm, swalConfirmDelete, swalPrompt, swalConsentCheckbox, swalChoice } from '../../utils/swal';
import { PRIVACY_POLICY_VERSION, DRIVE_BACKGROUND_MAX_FILES } from '../../config/appConfig';
import uploadConstants from '../../config/uploadConstants.json';
import api, { BACKEND_URL as LAMBDA_BACKEND_URL, CORE_BACKEND_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { compressEventImage, compressedImageName, uniqueCompressedImageName, isCompressibleEventImage, sanitizeUploadRelativePath, computeUcMd5Hash, extractCaptureMetadata } from '../../utils/compressEventImage';
import { getPref, setPref } from '../../utils/userPrefs';
import { downloadUcOriginal, downloadUrlAsBlob, UcDownloadProgressBar } from '../../utils/ucOriginalDownload';
import { sharePhoto } from '../../utils/sharePhoto';
import { useChromecastSender } from '../../utils/chromecast';
import './EventGallery.css';

const {
  UPLOAD_BATCH_SIZE, UPLOAD_HEARTBEAT_INTERVAL_MS, UPLOAD_PREWARM_IMMEDIATE_THRESHOLD,
  GROUPING_STATUS_POLL_INTERVAL_MS,
} = uploadConstants;

// Newest-first by real photo capture date (EXIF DateTimeOriginal, falling
// back to file mtime - see face_group/main.py's extract_image_metadata).
// Photos without a captured_at (pre-dating this feature) sink to the end,
// keeping their existing relative order.
function sortPhotosByCapturedAtDesc(photos) {
  const dated = [];
  const undated = [];
  for (const p of photos) (p?.captured_at ? dated : undated).push(p);
  dated.sort((a, b) => (a.captured_at < b.captured_at ? 1 : a.captured_at > b.captured_at ? -1 : 0));
  return [...dated, ...undated];
}

// ── Google Drive helpers ──────────────────────────────────────────────────────
const DEFAULT_FACE_GROUPING_MODEL = 'adaface';
const MAX_GROUPING_REQUEST_PHOTOS = 2000;
// Group view renders every "shared via" section at once (no scroll window),
// so each section is capped to this many photos with a "Show more" button
// rather than mounting hundreds/thousands of thumbnails up front.
const GROUP_VIEW_PAGE_SIZE = 60;
const GROUPING_LIMIT_MESSAGE = 'Select only 2000 photos, then run incremental grouping for the next 2000 photos.';
// Above this count, a browser/PWA upload (foreground-only - no background
// service the way the native app's WorkManager job has) is worth flagging so
// the organiser knows to keep the tab open, rather than being surprised if
// closing it partway through stops the upload. The native app has no such
// limitation (CmUploadWorker survives the app being closed), so this only
// applies to the web "Upload" button.
const LARGE_WEB_UPLOAD_WARNING_THRESHOLD = 100;
// Kept in sync with AUTO_MATCH_TRACK_LABEL in cm-backend/backend/services/auto_populate_service.py.
const AUTO_MATCH_TRACK_LABEL = 'Smart Auto Match';

// Persists the "uploaded this session, not yet told the server to group"
// submission_ids list (see AddPhotosSection/SmartSortSection's
// sessionSubmissionIds) to localStorage, mirroring UploadLinkPage.js's own
// pendingGroupingStorageKey pattern. Without this, the list only lives in a
// plain useState - on mobile, switching away from the "Event" tab and back
// genuinely unmounts SmartSortSection (and its embedded AddPhotosSection),
// which would otherwise silently reset the count to 0 even though the photos
// themselves are already safely sitting in the submissions pool. A web page
// refresh at the same point would lose it too - this closes both cases.
// One shared key per event: AddPhotosSection (add-mode) and SmartSortSection
// (initial-mode) are mutually exclusive for a given event (gated by
// hasExistingGallery), so there's never a real conflict between the two.
function sessionSubmissionIdsStorageKey(eventId) {
  return `cm:sessionSubmissionIds:${eventId}`;
}
function loadSessionSubmissionIds(eventId) {
  try {
    const raw = JSON.parse(localStorage.getItem(sessionSubmissionIdsStorageKey(eventId)) || 'null');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}
function saveSessionSubmissionIds(eventId, ids) {
  try {
    if (ids && ids.length) localStorage.setItem(sessionSubmissionIdsStorageKey(eventId), JSON.stringify(ids));
    else localStorage.removeItem(sessionSubmissionIdsStorageKey(eventId));
  } catch {
    // Storage unavailable/full - the accumulator still works for the rest of
    // this mount, it just won't survive a remount/refresh. Non-fatal.
  }
}

const FACE_GROUPING_CONSENT_HTML = `
  <div style="text-align:left;font-size:0.92rem;line-height:1.55;">
    <p>CircleMoments will process these event photos to detect faces, create face embeddings, generate thumbnails/search metadata, and group similar faces for this event.</p>
    <p>By continuing, you confirm you have the necessary rights and permissions to upload and process these photos, as described in our <a href="/terms.html#organizers" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
  </div>
`;
const FACE_GROUPING_CONSENT_CHECKBOX_LABEL = "I have read and agree to the Terms of Service and Privacy Policy.";

async function confirmFaceGroupingConsent(title = 'Face Grouping & Biometric Processing Consent') {
  return swalConsentCheckbox({
    title,
    html: FACE_GROUPING_CONSENT_HTML,
    checkboxLabel: FACE_GROUPING_CONSENT_CHECKBOX_LABEL,
  });
}

const UPLOAD_LINK_GROUPING_CONSENT_HTML = `
  <div style="text-align:left;font-size:0.92rem;line-height:1.55;">
    <p>Turning this on lets anyone who opens this link <strong>and signs in to a CircleMoments account</strong> trigger face-grouping for this event - billed to your account, using the same biometric face-detection/face-matching processing described in our <a href="/terms.html#organizers" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
    <p><strong>Share this link responsibly.</strong> It's meant for a small number of authorised people uploading on your behalf (e.g. a hired photographer) - not your whole guest list, and not anywhere public.</p>
  </div>
`;
const UPLOAD_LINK_GROUPING_CONSENT_CHECKBOX_LABEL = 'I understand, and will only share this link with people I trust to upload photos on my behalf.';

// Asked once, when the organiser turns grouping ON for an upload link (either
// creating a new link with it enabled, or flipping an existing link's switch
// on) - distinct wording from confirmFaceGroupingConsent because the risk
// being consented to is different: not just "these photos get biometrically
// processed", but "anyone holding this link can trigger that processing,
// billed to me". Turning grouping OFF never needs this - only enabling it.
async function confirmUploadLinkGroupingConsent() {
  return swalConsentCheckbox({
    title: 'Enable Grouping via Upload Link',
    html: UPLOAD_LINK_GROUPING_CONSENT_HTML,
    checkboxLabel: UPLOAD_LINK_GROUPING_CONSENT_CHECKBOX_LABEL,
  });
}

const AUTO_MATCH_CONSENT_HTML = `
  <div style="text-align:left;font-size:0.92rem;line-height:1.55;">
    <p>Turning this on automatically compares every photo request's reference selfie against newly grouped photos each time grouping runs - without you clicking "Auto populate" yourself - using the same biometric face-matching processing described in our <a href="/terms.html#organizers" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
    <p>Matches are saved as a track on each request automatically; you can still review and edit them at any time.</p>
  </div>
`;
const AUTO_MATCH_CONSENT_CHECKBOX_LABEL = "I have read and agree to the Terms of Service and Privacy Policy.";

// Asked once, only when the organiser turns Smart Auto-Match ON (never on
// OFF) - this is the background-job equivalent of the manual "Auto populate"
// button's consent_reference_face_match flag, but since the match runs
// unattended after every future grouping job (not from a live click), consent
// must be captured up front rather than per-run.
async function confirmAutoMatchConsent() {
  return swalConsentCheckbox({
    title: 'Enable Smart Auto-Match',
    html: AUTO_MATCH_CONSENT_HTML,
    checkboxLabel: AUTO_MATCH_CONSENT_CHECKBOX_LABEL,
  });
}

// Shared by both SmartSortSection's Advanced panel (pre-first-grouping) and
// AddPhotosSection (once a gallery exists) - same toggle either way, since the
// setting just needs to be reachable regardless of whether this event has been
// grouped yet. `onStatusChange` is the same patch-merging setter both parents
// already pass down for grouping_status updates (see EventPage's handleStatusChange).
function AutoMatchToggle({ event, onStatusChange }) {
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [referenceSetsOpen, setReferenceSetsOpen] = useState(false);

  const handleToggle = async (_evt, checked) => {
    if (checked) {
      const consentOk = await confirmAutoMatchConsent();
      if (!consentOk) return;
    }
    setSaving(true);
    try {
      await eventApi.updateAutoPopulateSetting(event.event_id, checked, { consent_reference_face_match: checked });
      onStatusChange({ auto_populate_requests_enabled: checked });
      setSnack({ open: true, msg: checked ? 'Smart Auto-Match turned on.' : 'Smart Auto-Match turned off.', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Could not update Smart Auto-Match. Please try again.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(event.auto_populate_requests_enabled)}
            onChange={handleToggle}
            disabled={saving}
            size="small"
          />
        }
        label={<Typography variant="body2">Smart Auto-Match</Typography>}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4.25, mt: -0.5 }}>
        Automatically match every photo request's selfie against new photos and save it as a track, every time grouping runs - no need to click "Auto populate" yourself.
      </Typography>
      <Button
        size="small"
        onClick={() => setReferenceSetsOpen(true)}
        sx={{ textTransform: 'none', ml: 4.25, mt: 0.5 }}
      >
        Manage reference sets
      </Button>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
      <ReferenceSetsDialog
        open={referenceSetsOpen}
        onClose={() => setReferenceSetsOpen(false)}
        eventId={event.event_id}
      />
    </Box>
  );
}

// Asked right after a folder is picked (permission granted) but before it's
// scanned - lets the engine know whether to treat any existing photos found
// in it as a first batch (include) or as a baseline to ignore, only reacting
// to photos added from this point on (skip). Asked unconditionally, since we
// don't yet know if the folder is empty. Returns true/false, or null if the
// user cancelled.
export async function promptAutoSyncIncludeExisting() {
  const choice = await swalChoice({
    title: 'Include existing photos?',
    html: 'If this folder already has photos in it, do you want auto-sync to include them, or only sync photos added from now on?',
    confirmText: 'Include existing photos',
    denyText: 'Only new photos from now on',
  });
  if (!choice) return null;
  return choice === 'confirm';
}

// Asked right after a folder is picked, only when another auto-sync is
// already armed elsewhere on this device (any event/link, organiser or
// upload-link - see autoSyncRegistry.js's findOtherArmedAutoSync). Only one
// auto-sync may run at a time device-wide, so this is the one place that
// choice is surfaced to the user, shared by every "Choose folder" entry point.
export async function promptStopOtherAutoSync(other) {
  return swalConfirm({
    title: 'Only one auto-sync at a time',
    html: `Auto-sync is already watching <strong>${other.folderName}</strong> ${other.isUploadLink ? 'from another upload link' : 'for another event'} on this device.<br/>Stop that one and start watching this folder instead?`,
    confirmText: 'Stop the other one and continue',
    cancelText: 'Cancel',
  });
}
const FACE_MODEL_LABELS = {
  adaface: 'AdaFace',
  'insightface/buffalo_l': 'InsightFace buffalo_l',
};

function normaliseFaceModel(value) {
  const model = String(value || '').trim();
  return model || DEFAULT_FACE_GROUPING_MODEL;
}

function faceModelLabel(value) {
  const model = normaliseFaceModel(value);
  return FACE_MODEL_LABELS[model] || model;
}

async function _loadGapiPicker() {
  if (window._gapiPickerReady) return;
  await new Promise((resolve, reject) => {
    if (window.gapi) { window.gapi.load('picker', () => { window._gapiPickerReady = true; resolve(); }); return; }
    const s = document.createElement('script');
    s.src = 'https://apis.google.com/js/api.js';
    s.onload = () => window.gapi.load('picker', () => { window._gapiPickerReady = true; resolve(); });
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function _loadGis() {
  if (window.google?.accounts?.oauth2) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

async function getDriveAccessToken(hint = '') {
  if (Capacitor.isNativePlatform()) {
    // GIS's popup-based token client (below) is blocked inside embedded
    // WebViews by Google's own policy - see Auth.js's getGoogleAccessToken
    // for the matching comment. Use the native Play Services sign-in instead,
    // re-initialized here with the Drive scope layered on top of the app's
    // default login scopes (capacitor.config.json only configures email+profile,
    // since login shouldn't prompt for Drive access every time).
    await GoogleAuth.initialize({ scopes: ['email', 'profile', DRIVE_FILE_SCOPE] });
    const result = await GoogleAuth.signIn();
    const accessToken = result?.authentication?.accessToken;
    if (!accessToken) throw new Error('Google sign-in did not return an access token.');
    return accessToken;
  }

  await _loadGis();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('REACT_APP_GOOGLE_CLIENT_ID not configured');

  const requestToken = (prompt) => new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: `${DRIVE_FILE_SCOPE} email`,
      hint: hint || undefined,
      callback: (r) => {
        if (r.error) reject(new Error(r.error_description || r.error));
        else resolve(r.access_token);
      },
    });
    client.requestAccessToken({ prompt });
  });

  try {
    return await requestToken('');
  } catch (err) {
    // Silent token requests may fail if the grant is not yet available.
    // Retry with an account chooser so the selected Drive account matches the picker selection.
    return await requestToken('select_account');
  }
}

const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder';
const DRIVE_PHOTO_MIME_QUERY = "(mimeType='image/jpeg' or mimeType='image/png' or mimeType='image/webp' " +
  "or mimeType='image/heic' or mimeType='image/heif' or mimeType='image/bmp' or mimeType='image/tiff')";

// Lists the top-level image files inside a Drive folder (no subfolder
// recursion). Relies on drive.file scope's folder-select grant, which
// extends to the folder's existing contents at pick time - see the picker
// setup's matching comment.
async function listDriveFolderImages(folderId, accessToken) {
  const files = [];
  let pageToken = '';
  do {
    const url = new URL('https://www.googleapis.com/drive/v3/files');
    url.searchParams.set('q', `'${folderId}' in parents and trashed=false and ${DRIVE_PHOTO_MIME_QUERY}`);
    url.searchParams.set('fields', 'nextPageToken,files(id,name,mimeType)');
    url.searchParams.set('pageSize', '1000');
    url.searchParams.set('supportsAllDrives', 'true');
    url.searchParams.set('includeItemsFromAllDrives', 'true');
    if (pageToken) url.searchParams.set('pageToken', pageToken);
    const resp = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!resp.ok) throw new Error(`Failed to read folder contents: ${resp.status}`);
    const data = await resp.json();
    files.push(...(data.files || []));
    pageToken = data.nextPageToken || '';
  } while (pageToken);
  return files;
}

// Expands any folders among the picker's returned docs into their contained
// image files (top-level only), leaving directly-picked files untouched.
async function expandDrivePickerDocs(docs, accessToken) {
  const resolved = [];
  for (const doc of docs) {
    if (doc.mimeType === DRIVE_FOLDER_MIME) {
      resolved.push(...await listDriveFolderImages(doc.id, accessToken));
    } else {
      resolved.push(doc);
    }
  }
  return resolved;
}

// ── "Match Originals from Drive" - metadata-only Drive reads ─────────────────
// Separate from expandDrivePickerDocs/listDriveFolderImages above (the normal
// import path) because this is the only flow that needs md5Checksum - no
// point adding it to every other Drive listing call that never uses it.
// `md5Checksum` is a plain field on the Drive v3 file resource (populated for
// any real binary/blob file - exactly what image mimeTypes are), returned by
// a metadata-only files.get call. No file content is ever downloaded, and no
// scope beyond drive.file is needed - same as reading name/mimeType already
// does today.

// The Picker's own returned doc objects, and the plain files.list enumeration
// expandDrivePickerDocs already does for a folder, don't include md5Checksum
// (it's not requested by either) - every file needs its own metadata-only
// files.get call for it. Done one at a time (rather than batched into the
// folder-listing call) deliberately, so the checksum-reading dialog can show
// real "N / M" progress and be cancelled mid-way, same as any other file in
// the selection - a folder pick isn't a special case here.
async function fetchDriveFileChecksum(fileId, accessToken) {
  const url = new URL(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}`);
  url.searchParams.set('fields', 'id,name,mimeType,md5Checksum');
  url.searchParams.set('supportsAllDrives', 'true');
  const resp = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!resp.ok) throw new Error(`Failed to read file metadata: ${resp.status}`);
  return resp.json();
}

// Resolves a Drive Picker selection (files and/or folders) into a flat list
// of {id, name, md5Checksum}, skipping anything Drive didn't return a
// checksum for (native Google Docs/Sheets/Slides, shortcuts - never matches
// a photo anyway). `onProgress({completed, total})` fires after the initial
// (checksum-less) enumeration establishes `total`, then again after every
// file's checksum is read. `isCancelled()` is polled between files so a
// large folder can be aborted mid-way without waiting for the rest.
async function resolveDriveChecksumsWithProgress(docs, accessToken, onProgress, isCancelled) {
  const flat = await expandDrivePickerDocs(docs, accessToken);
  onProgress({ completed: 0, total: flat.length });
  const resolved = [];
  for (let i = 0; i < flat.length; i += 1) {
    if (isCancelled()) return resolved;
    try {
      const meta = await fetchDriveFileChecksum(flat[i].id, accessToken);
      if (meta.md5Checksum) resolved.push(meta);
    } catch {
      // One file's metadata lookup failing shouldn't abort the whole batch -
      // it simply won't be a match candidate.
    }
    onProgress({ completed: i + 1, total: flat.length });
  }
  return resolved;
}

// Inject once: boost z-index so the Picker layers above any app modals.
// Do NOT override position/transform - Google Picker handles its own centering.
function _injectPickerStyles() {
  if (document.getElementById('cm-picker-fix')) return;
  const s = document.createElement('style');
  s.id = 'cm-picker-fix';
  s.textContent =
    '.picker-dialog-bg{z-index:9998!important;}' +
    '.picker-dialog{z-index:9999!important;}';
  document.head.appendChild(s);
}

async function openDrivePicker(accessToken) {
  await _loadGapiPicker();
  _injectPickerStyles();
  const apiKey = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;
  // The numeric prefix of the OAuth client ID is the GCP project number.
  // Picker requires setAppId(<project number>) for the drive.file scope -
  // without it, picked files are visible/selectable but the per-file grant
  // never registers, so subsequent alt=media fetches 404 with "File not found"
  // even for files the user owns.
  const appId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').split('-')[0];

  // Save scroll position - the Picker injects an iframe at the end of <body>
  // which causes the browser to auto-scroll. We lock both <html> and <body>,
  // then use a double-RAF to restore position after the injection fires.
  const savedScrollY = window.scrollY;
  const prevBodyOverflow = document.body.style.overflow;
  const prevHtmlOverflow = document.documentElement.style.overflow;
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';

  const restoreScroll = () => {
    document.body.style.overflow = prevBodyOverflow;
    document.documentElement.style.overflow = prevHtmlOverflow;
    window.scrollTo(0, savedScrollY);
  };

  return new Promise((resolve) => {
    let resolved = false;
    const done = (docs) => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onEscape);
      restoreScroll();
      resolve(docs);
    };

    // Escape key fallback - restores scroll and closes picker if the normal
    // callback doesn't fire (e.g. on an API error dialog with no close button)
    const onEscape = (e) => { if (e.key === 'Escape') done([]); };
    document.addEventListener('keydown', onEscape);

    // Single view: browsable folder tree that shows both subfolders (to keep
    // navigating) and images (for visual confirmation / direct selection),
    // with the current folder itself also selectable as a whole. Replaces
    // the earlier two-tab setup (a flat file-only view plus a separate
    // folder-only view) - one browsable view covers both cases, since a user
    // can either select individual images directly here or select a folder
    // to pull in everything inside it.
    //
    // Deliberately NOT `new DocsView(google.picker.ViewId.FOLDERS)` - that
    // special view id hardcodes its own listing to folders only and ignores
    // setMimeTypes entirely (confirmed on-device: photos still didn't show
    // inside a subfolder even after adding image mime types to it).
    //
    // Under drive.file scope, selecting a folder here also grants read
    // access to its existing contents - that's what makes
    // expandDrivePickerDocs's files.list call below work without a broader
    // Drive scope.
    const folderView = new window.google.picker.DocsView();
    folderView.setIncludeFolders(true);
    folderView.setSelectFolderEnabled(true);
    folderView.setMimeTypes(`${DRIVE_FOLDER_MIME},image/jpeg,image/png,image/webp,image/heic,image/heif,image/bmp,image/tiff`);

    const builder = new window.google.picker.PickerBuilder()
      .addView(folderView)
      .setOrigin(`${window.location.protocol}//${window.location.host}`)
      .setOAuthToken(accessToken)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) done(data.docs || []);
        else if (data.action === window.google.picker.Action.CANCEL) done([]);
      });
    if (apiKey) builder.setDeveloperKey(apiKey);
    if (appId) builder.setAppId(appId);
    builder.build().setVisible(true);
    // Two animation frames: first lets the browser process the DOM injection,
    // second overrides any auto-scroll the browser triggered from it.
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, savedScrollY)));
  });
}

// ── Choose Folder dropdown ────────────────────────────────────────────────────
function ChooseFolderDropdown({ driveConnected, onLocalFilesSelect, onLocalFolderSelect, onDriveSelect, onAutoSyncSelect, autoSyncSupported, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <Box ref={ref} sx={{ position: 'relative', flexShrink: 0 }}>
      <Button
        variant="outlined"
        startIcon={<FolderOpenIcon />}
        onClick={() => !disabled && setOpen(p => !p)}
        disabled={disabled}
        sx={{ whiteSpace: 'nowrap' }}
        endIcon={<span style={{ fontSize: '0.6em', lineHeight: 1 }}>▾</span>}
      >
        Upload Photos
      </Button>
      {open && (
        <Paper elevation={6} sx={{ position: 'absolute', top: '110%', left: 0, minWidth: 240, zIndex: 200, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ px: 2, pt: 1, pb: 0.25, display: 'block', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
              Local
            </Typography>
            <MenuItem onClick={() => { setOpen(false); onLocalFilesSelect?.(); }} sx={{ gap: 1.5 }}>
              <PhotoLibraryIcon fontSize="small" color="action" />
              <span>Choose local photos</span>
            </MenuItem>
            <MenuItem onClick={() => { setOpen(false); onLocalFolderSelect?.(); }} sx={{ gap: 1.5 }}>
              <FolderOpenIcon fontSize="small" color="action" />
              <span>Choose local folder</span>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary"
              sx={{ px: 2, pt: 0.5, pb: 0.25, display: 'block', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
              Auto-Sync
            </Typography>
            <Tooltip title={autoSyncSupported ? '' : 'Needs Chrome or Edge on desktop'} placement="right">
              <span>
                <MenuItem
                  onClick={() => { if (autoSyncSupported) { setOpen(false); onAutoSyncSelect?.(); } }}
                  disabled={!autoSyncSupported}
                  sx={{ gap: 1.5, opacity: autoSyncSupported ? 1 : 0.5 }}
                >
                  <SyncIcon fontSize="small" color="action" />
                  <span>Set up folder auto-sync</span>
                </MenuItem>
              </span>
            </Tooltip>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary"
              sx={{ px: 2, pt: 0.5, pb: 0.25, display: 'block', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
              Cloud
            </Typography>
            <MenuItem
              onClick={() => { if (driveConnected) { setOpen(false); onDriveSelect(); } }}
              disabled={!driveConnected}
              sx={{ gap: 1.5, opacity: driveConnected ? 1 : 0.5 }}
            >
              <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png" alt="Drive" sx={{ width: 18, height: 18, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2">Choose Google Drive photos</Typography>
                {!driveConnected && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    Connect in Profile → Account
                  </Typography>
                )}
              </Box>
            </MenuItem>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// Step 1 status card shown in place of the ChooseFolderDropdown/upload-count line once
// Shown while useLocalAutoSync is reading a folder that hasn't finished
// arming yet (the very first pick, before AutoSyncStatusCard exists to show
// its own copy of this same block) - a folder with thousands of photos can
// take a long time, so this gives real feedback plus a cancel button instead
// of ChooseFolderDropdown just silently sitting there for minutes.
function AutoSyncScanProgress({ autoSync }) {
  if (!autoSync.scanning) return null;
  const { phase, completed, total } = autoSync.scanProgress || {};
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LinearProgress
          variant={total ? 'determinate' : 'indeterminate'}
          value={total ? (completed / total) * 100 : undefined}
          sx={{ borderRadius: 1, height: 5 }}
        />
        <Typography variant="caption" color="text.secondary">
          {phase === 'scanning'
            ? `Scanning folder… ${completed || 0} found so far`
            : `Reading photos… ${completed || 0}/${total || 0}`}
        </Typography>
      </Box>
      <Tooltip title="Cancel this scan">
        <span>
          <IconButton size="small" onClick={autoSync.cancelScan} disabled={autoSync.scanCancelRequested}>
            {autoSync.scanCancelRequested ? <CircularProgress size={14} /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

// a local-folder auto-sync is armed. Shared verbatim by SmartSortSection (initial job)
// and AddPhotosSection (increment job) - both receive the same `autoSync` object from
// the single useLocalAutoSync instance that lives in SmartSortSection.
function AutoSyncStatusCard({ autoSync }) {
  const {
    folderName, pendingCount, batchSizeSetting, status, needsReconnect, lastError,
    syncNow, reconnect, stopSync, hardReset, setBatchSizeSetting, statusComment,
    scanning, scanProgress, scanCancelRequested, cancelScan,
  } = autoSync;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isSubmitting = status === 'submitting';

  if (needsReconnect) {
    return (
      <Alert severity="warning">
        <Stack spacing={0.75} alignItems="flex-start">
          <span>Auto-sync paused for "{folderName}" - folder access needs to be reconfirmed.</span>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" size="small" onClick={reconnect} sx={{ whiteSpace: 'nowrap' }}>Reconnect</Button>
            <Button
              color="inherit"
              size="small"
              sx={{ whiteSpace: 'nowrap', opacity: 0.7 }}
              onClick={async () => {
                const ok = await swalConfirmDelete({
                  title: 'Reset auto-sync data?',
                  text: "This clears the local sync state for every event on this device, not just this one - you'll need to re-choose any watched folders afterwards.",
                  confirmText: 'Reset',
                });
                if (ok) await hardReset();
              }}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FolderOpenIcon fontSize="small" color="primary" />
        <Typography variant="body2" fontWeight={600}>{folderName}</Typography>
        <CircularProgress size={13} thickness={5} color={isSubmitting ? 'warning' : 'primary'} />
        <Typography variant="caption" color="text.secondary">
          {isSubmitting ? (statusComment || 'Uploading batch…') : 'Watching for changes…'}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${pendingCount} / ${batchSizeSetting} new photos detected`}
          color={pendingCount >= batchSizeSetting ? 'success' : 'default'}
        />
        <Tooltip title="Upload detected photos now">
          <span>
            <IconButton size="small" onClick={syncNow} disabled={!pendingCount || isSubmitting}>
              <SyncIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} disabled={isSubmitting}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); stopSync(); }}>Stop auto-sync</MenuItem>
          <MenuItem
            onClick={async () => {
              setMenuAnchor(null);
              const ok = await swalConfirmDelete({
                title: 'Reset auto-sync data?',
                text: "This clears the local sync state for every event on this device, not just this one - you'll need to re-choose any watched folders afterwards.",
                confirmText: 'Reset',
              });
              if (ok) await hardReset();
            }}
          >
            Reset auto-sync data…
          </MenuItem>
        </Menu>
      </Stack>
      {/* A folder with thousands of photos can take a long time to re-scan on
          each poll cycle - show real progress + a cancel button instead of
          the pending count silently not updating for minutes. */}
      {scanning && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LinearProgress
              variant={scanProgress?.total ? 'determinate' : 'indeterminate'}
              value={scanProgress?.total ? (scanProgress.completed / scanProgress.total) * 100 : undefined}
              sx={{ borderRadius: 1, height: 5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {scanProgress?.phase === 'scanning'
                ? `Scanning folder… ${scanProgress.completed || 0} found so far`
                : `Reading photos… ${scanProgress?.completed || 0}/${scanProgress?.total || 0}`}
            </Typography>
          </Box>
          <Tooltip title="Cancel this scan">
            <span>
              <IconButton size="small" onClick={cancelScan} disabled={scanCancelRequested}>
                {scanCancelRequested ? <CircularProgress size={14} /> : <CloseIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">Batch size:</Typography>
        <TextField
          size="small"
          value={batchSizeSetting}
          onChange={(e) => setBatchSizeSetting(e.target.value.replace(/[^\d]/g, ''))}
          inputProps={{ inputMode: 'numeric', style: { padding: '2px 8px', width: 56, textAlign: 'center' } }}
          disabled={isSubmitting}
        />
      </Stack>
      {lastError && (
        <Typography variant="caption" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14 }} />{lastError}
        </Typography>
      )}
    </Box>
  );
}

// ── Status helpers ────────────────────────────────────────────────────────────
const STATUS_COLOR = { none: 'default', processing: 'warning', uploading: 'warning', complete: 'success', failed: 'error' };
const STATUS_LABEL = { none: 'Not grouped', processing: 'Grouping…', uploading: 'Uploading…', complete: 'Ready', failed: 'Failed' };

function formatCreditAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

function groupingBillingMessage(error) {
  const balance = formatCreditAmount(error?.data?.balance);
  const required = formatCreditAmount(error?.data?.required);
  if (balance !== null && required !== null) {
    return `You need ${required} credits to run face grouping. Your current balance is ${balance} credits.`;
  }
  return error?.message || 'You do not have enough credits to run face grouping.';
}

// ── Gallery helpers ───────────────────────────────────────────────────────────
function buildGroups(photos, groupsFromMetadata) {
  const computed = {}, byGroup = {};
  for (const photo of photos) {
    for (const face of photo.faces || []) {
      const g = face.group || 'Unknown';
      computed[g] = (computed[g] || 0) + 1;
      if (!byGroup[g]) byGroup[g] = { photo, face };
    }
  }
  const groups = Object.keys(groupsFromMetadata || {}).length ? groupsFromMetadata : computed;
  return { groups, sampleByGroup: byGroup };
}

function displayName(group, groupNames) {
  const a = groupNames[group];
  return a && a.trim() ? a.trim() : group;
}

// Lazily-loaded face group thumbnail: shows a shimmer skeleton until `src` is
// available, requesting it via `onEnsure` once the element scrolls into view.
const GroupThumbImg = React.memo(function GroupThumbImg({ group, src, alt, onEnsure, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    if (src || !onEnsure) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        onEnsure([group]);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [group, src, onEnsure]);

  if (!src) return <div ref={ref} className={`thumb-skeleton ${className}`} />;
  return <img ref={ref} src={src} alt={alt} loading="lazy" decoding="async" className={className} />;
});

// Keeps keystrokes local to this input instead of pushing every character into
// the parent gallery viewer's state, which would re-render the whole (very
// large) component tree on every keystroke.
const GroupSearchInput = React.memo(function GroupSearchInput({ value, onChange, placeholder }) {
  const [text, setText] = useState(value);

  useEffect(() => {
    const t = window.setTimeout(() => { if (text !== value) onChange(text); }, 200);
    return () => window.clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      type="text"
      className="group-search"
      value={text}
      onChange={e => setText(e.target.value)}
      placeholder={placeholder}
    />
  );
});

// Lets the "123 / 800" photo counter double as a jump-to-photo control: typing
// a number and pressing Enter or blurring loads that photo (and re-triggers the
// caller's thumbnail-window prefetch around it) instead of only stepping via
// the arrow buttons or clicking a thumbnail. Local text state mirrors the
// GroupSearchInput pattern above so keystrokes don't churn the parent viewer.
const PhotoJumpInput = React.memo(function PhotoJumpInput({ current, total, onJump, className }) {
  const [text, setText] = useState(String(current));

  useEffect(() => { setText(String(current)); }, [current]);

  if (!total) return <span className={className}>0 / 0</span>;

  const commit = (e) => {
    const n = parseInt(text, 10);
    if (Number.isFinite(n) && n >= 1 && n <= total) {
      if (n !== current) onJump(n);
      else setText(String(current));
    } else {
      setText(String(current));
    }
  };

  return (
    <span className={className}>
      <input
        type="text"
        inputMode="numeric"
        className="photo-counter-input"
        style={{ width: `${Math.max(2, String(total).length)}ch` }}
        value={text}
        onChange={e => setText(e.target.value.replace(/[^\d]/g, ''))}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        onBlur={commit}
        onFocus={e => e.target.select()}
        aria-label="Jump to photo number"
      />
      {` / ${total}`}
    </span>
  );
});

// One row in the attendee face-groups sidebar. Memoized so that unrelated
// state changes elsewhere in the (very large) gallery viewer - a hover, a
// keystroke, a thumbnail URL arriving - don't force every group row to
// re-render; only the row(s) whose own props actually changed re-render.
// This only works because the callback props below are stabilized with
// useCallback at the call site (see AttendeeGalleryViewer).
const GroupListItem = React.memo(function GroupListItem({
  eid, group, count, value, selected, thumbSrc, memoryFaces, memoryFaceThumbUrls, groupNames,
  groupThumbSrc, ensureGroupThumbnails, onToggleSelect, onJumpToSample,
  onSelectMemoryFace, onCreateNew, onClear, onOpenOptions,
}) {
  const groupLabel = String(group).includes('__') ? group : `${eid}__${group}`;
  return (
    <div className="group-item">
      <div className="group-row-top">
        <label className="group-label">{groupLabel}</label>
        <button type="button" className="group-thumb-btn" onClick={() => onJumpToSample(group)} title={`Jump to ${group}`}>
          <GroupThumbImg group={group} src={thumbSrc} alt={group} onEnsure={ensureGroupThumbnails} />
        </button>
        <label className="group-check-wrap" onMouseDown={e => e.preventDefault()}>
          <input type="checkbox" className="group-check" tabIndex="-1" checked={selected} onChange={() => onToggleSelect(group)} />
          <span className="group-check-ui" aria-hidden="true" />
        </label>
      </div>
      <FaceAssignmentInput
        group={group}
        value={value}
        memoryFaces={memoryFaces}
        memoryFaceThumbUrls={memoryFaceThumbUrls}
        groupNames={groupNames}
        groupThumbSrc={groupThumbSrc}
        ensureGroupThumbnails={ensureGroupThumbnails}
        onSelectMemoryFace={onSelectMemoryFace}
        onCreateNew={onCreateNew}
        onClear={onClear}
        onOpenOptions={onOpenOptions}
      />
      <span className="group-count">({count} faces)</span>
    </div>
  );
});

// Organiser variant of GroupListItem - adds the add-to-request/circle/clipboard
// and blur-group actions that only make sense for the organiser's view.
const OrgGroupListItem = React.memo(function OrgGroupListItem({
  eid, group, count, value, selected, thumbSrc, memoryFaces, memoryFaceThumbUrls, groupNames,
  groupThumbSrc, ensureGroupThumbnails, showAddToTrackActions, isOrganiser,
  onToggleSelect, onJumpToSample, onAddToRequest, onAddToAllRequests, onAddToClipboard,
  onAddToCircle, onAddToAllCircles, onBlurGroup, onSelectMemoryFace, onCreateNew, onClear, onOpenOptions,
}) {
  const groupLabel = String(group).includes('__') ? group : `${eid}__${group}`;
  return (
    <div className="group-item">
      <div className="group-row-top">
        <label className="group-label">{groupLabel}</label>
        <button type="button" className="group-thumb-btn" onClick={() => onJumpToSample(group)} title={`Jump to ${group}`}>
          <GroupThumbImg group={group} src={thumbSrc} alt={group} onEnsure={ensureGroupThumbnails} />
        </button>
        <label className="group-check-wrap" onMouseDown={e => e.preventDefault()}>
          <input type="checkbox" className="group-check" tabIndex="-1" checked={selected} onChange={() => onToggleSelect(group)} />
          <span className="group-check-ui" aria-hidden="true" />
        </label>
        {showAddToTrackActions && (
          <SplitButton label="Add to request" onPrimary={() => onAddToRequest(group)} menuItems={[
            { id: 'all', label: 'Add to all requests', onClick: () => onAddToAllRequests(group) },
            { id: 'clipboard', label: 'Add to clipboard', onClick: () => onAddToClipboard(group) },
            { id: 'circle', label: 'Add to circle', onClick: () => onAddToCircle(group) },
            { id: 'all-circles', label: 'Add to all circles', onClick: () => onAddToAllCircles(group) },
            ...(isOrganiser ? [
              { id: 'blur-group', label: 'Blur on all photos', onClick: () => onBlurGroup({ open: true, group, mode: 'blur' }) },
              { id: 'revert-blur-group', label: 'Revert blur on all photos', onClick: () => onBlurGroup({ open: true, group, mode: 'revert' }) },
            ] : []),
          ]} />
        )}
        {!showAddToTrackActions && isOrganiser && (
          <SplitButton label="Face actions" onPrimary={() => {}} menuItems={[
            { id: 'blur-group', label: 'Blur on all photos', onClick: () => onBlurGroup({ open: true, group, mode: 'blur' }) },
            { id: 'revert-blur-group', label: 'Revert blur on all photos', onClick: () => onBlurGroup({ open: true, group, mode: 'revert' }) },
          ]} />
        )}
      </div>
      <FaceAssignmentInput
        group={group}
        value={value}
        memoryFaces={memoryFaces}
        memoryFaceThumbUrls={memoryFaceThumbUrls}
        groupNames={groupNames}
        groupThumbSrc={groupThumbSrc}
        ensureGroupThumbnails={ensureGroupThumbnails}
        onSelectMemoryFace={onSelectMemoryFace}
        onCreateNew={onCreateNew}
        onClear={onClear}
        onOpenOptions={onOpenOptions}
      />
      <span className="group-count">({count} faces)</span>
    </div>
  );
});

function SearchModeIcon({ label, mode, onToggle }) {
  const isLegacy = mode === 'legacy';
  const Icon = isLegacy ? ManageSearchIcon : AutoAwesomeIcon;
  const modeLabel = isLegacy ? 'Legacy' : 'AI Planner';
  const title = label ? `Selected ${modeLabel}. ${label}` : `Selected ${modeLabel}`;

  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-label={title}
        sx={{
          width: 32,
          height: 32,
          px: 0,
          flex: '0 0 auto',
          overflow: 'hidden',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.75,
          bgcolor: label ? 'background.paper' : 'grey.100',
          border: '1px solid',
          borderColor: label ? 'primary.light' : 'divider',
          color: label ? 'primary.main' : 'text.disabled',
          borderRadius: 999,
          cursor: 'pointer',
          transition: 'width 180ms ease, background-color 180ms ease, color 180ms ease',
          '&:hover': { width: 128, bgcolor: label ? 'primary.50' : 'grey.100' },
        }}
      >
        <Box sx={{ width: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
          <Icon fontSize="small" />
        </Box>
        <Typography variant="caption" fontWeight={800} noWrap sx={{ lineHeight: 1 }}>
          {modeLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const GalleryAiSearchField = React.memo(function GalleryAiSearchField({ active, loading, onSubmit, onClear, searchModeLabel, selectedSearchMode, onToggleSearchMode, onInvalidateCache, invalidatingCache }) {
  const [draft, setDraft] = useState('');

  const submit = () => { onSubmit(draft.trim()); };
  const clear = () => { setDraft(''); onClear(); };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        fullWidth
        size="small"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); submit(); }
        }}
        placeholder="Search this event's photos - people, scenes, tags, notes..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={18} /> : <SearchIcon color={active ? 'primary' : 'action'} />}
            </InputAdornment>
          ),
          endAdornment: draft ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={submit} disabled={loading || !draft.trim()} aria-label="Search photos">
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={clear} edge="end" aria-label="Clear photo search">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : (
            <InputAdornment position="end">
              <Chip icon={<AutoAwesomeIcon />} label="AI search" size="small" color="primary" variant="outlined" />
            </InputAdornment>
          ),
        }}
      />
      <Stack alignItems="center" spacing={0.5}>
        <SearchModeIcon label={searchModeLabel} mode={selectedSearchMode} onToggle={onToggleSearchMode} />
        {onInvalidateCache && (
          <Tooltip title="Invalidate search cache" placement="left">
            <span>
              <IconButton
                size="small"
                onClick={onInvalidateCache}
                disabled={invalidatingCache}
                aria-label="Invalidate search cache"
                sx={{ width: 24, height: 24, p: 0 }}
              >
                {invalidatingCache
                  ? <CircularProgress size={14} />
                  : <RefreshIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
});

function requesterIdentityMeta(request) {
  const identity = request?.requester_identity || {};
  if (identity.username) {
    return {
      className: identity.kind === 'public' ? 'public' : 'private',
      main: `@${identity.username}`,
      sub: identity.kind === 'public' ? 'Public profile' : 'CM user',
    };
  }
  if (identity.kind === 'private' || request?.customer_id) {
    return { className: 'private', main: 'CM user', sub: 'Username not public' };
  }
  return { className: 'guest', main: 'Not a CM user', sub: '' };
}

function SplitButton({ label, onPrimary, menuItems = [], disabled = false, menuPlacement = 'down' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="split-btn" ref={ref}>
      <button type="button" className="save-btn split-btn-main" onClick={onPrimary} disabled={disabled}>{label}</button>
      <button type="button" className="save-btn split-btn-arrow" onClick={() => !disabled && setOpen(p => !p)} disabled={disabled} aria-label="More options">▾</button>
      {open && menuItems.length > 0 && !disabled && (
        <div className={`split-menu${menuPlacement === 'up' ? ' split-menu-up' : ''}`} role="menu">
          {menuItems.map(item => item.children?.length ? (
            <div key={item.id} className="split-menu-submenu">
              <button type="button" className="split-menu-item split-menu-parent" role="menuitem" onClick={e => e.preventDefault()}>
                <span>{item.label}</span>
                <span aria-hidden="true">▾</span>
              </button>
              <div className="split-submenu" role="menu">
                {item.children.map(child => (
                  <button key={child.id} type="button" className={`split-menu-item${child.disabled ? ' disabled' : ''}`} role="menuitem" disabled={child.disabled} onClick={() => { if (child.disabled) return; setOpen(false); child.onClick(); }}>{child.label}</button>
                ))}
              </div>
            </div>
          ) : (
            <button key={item.id} type="button" className={`split-menu-item${item.disabled ? ' disabled' : ''}`} role="menuitem" disabled={item.disabled} onClick={() => { if (item.disabled) return; setOpen(false); item.onClick(); }}>{item.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// "Add to Profile View" toggle - shows/hides a single photo on the current
// user's public profile grid (ProfileView.js). `added` / `loading` /
// `onToggle` are owned by the caller so prev/next navigation in the photo
// preview dialog can share one fetched showcase set instead of refetching.
function AddToProfileButton({ added, loading, onToggle, size = 'medium' }) {
  return (
    <Tooltip title={added ? 'Remove from your public profile grid' : 'Show this photo on your public profile'}>
      <span>
        <Button
          variant={added ? 'contained' : 'outlined'}
          color={added ? 'success' : 'primary'}
          size={size}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : (added ? <CheckCircleIcon /> : <AddAPhotoIcon />)}
          onClick={onToggle}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          {added ? 'On Profile' : 'Add to Profile View'}
        </Button>
      </span>
    </Tooltip>
  );
}

// Right-click context menu for gallery thumbnails. `items` may contain
// top-level entries with `children` rendered as a hover submenu, replicating
// the SplitButton dropdown structure above.
function ThumbnailContextMenu({ anchor, items, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!anchor) return;
    const close = (e) => { if (!ref.current || !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('contextmenu', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('contextmenu', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!anchor || !items.length) return null;
  return (
    <div className="thumb-context-menu split-menu" ref={ref} style={{ top: anchor.y, left: anchor.x }} role="menu">
      {items.map(item => item.children?.length ? (
        <div key={item.id} className="split-menu-submenu">
          <button type="button" className="split-menu-item split-menu-parent" role="menuitem" onClick={e => e.preventDefault()}>
            <span>{item.label}</span>
            <span aria-hidden="true">▾</span>
          </button>
          <div className="split-submenu" role="menu">
            {item.children.map(child => (
              <button key={child.id} type="button" className={`split-menu-item${child.disabled ? ' disabled' : ''}`} role="menuitem" disabled={child.disabled} onClick={() => { if (child.disabled) return; onClose(); child.onClick(); }}>{child.label}</button>
            ))}
          </div>
        </div>
      ) : (
        <button key={item.id} type="button" className={`split-menu-item${item.disabled ? ' disabled' : ''}`} role="menuitem" disabled={item.disabled} onClick={() => { if (item.disabled) return; onClose(); item.onClick(); }}>{item.label}</button>
      ))}
    </div>
  );
}

// Wraps a "request/circle detail" panel in a bottom-sheet style mobile dialog
// (slides up, leaves the app header and bottom nav visible) with a back
// button + title header. On desktop it renders children inline (no dialog).
function MobileDetailDialog({ open, onClose, title, chips, isMobile, children }) {
  if (!isMobile) return children;
  return (
    <Dialog open={open} onClose={onClose} className="request-detail-dialog" TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
      <Box className="request-detail-dialog-header">
        <IconButton edge="start" onClick={onClose} aria-label="Back" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: '#8f2949' }}>
            {title}
          </Typography>
          {chips}
        </Box>
      </Box>
      <Box className="request-detail-dialog-body">
        {children}
      </Box>
    </Dialog>
  );
}

// Two-step "Add to other requests/circles" picker: step 1 searches and picks a
// target request/circle (excluding the currently selected one), step 2 picks a
// track (new or existing) within that target. Renders as a slide-up dialog,
// full-screen on narrow viewports.
function AddToOtherTargetDialog({ open, onClose, meta, onPickTrack }) {
  const [search, setSearch] = useState('');
  const [step, setStep] = useState(1);
  const [targetId, setTargetId] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const isNarrow = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (!open) { setSearch(''); setStep(1); setTargetId(null); setLoadingTracks(false); }
  }, [open]);

  if (!open || !meta) return null;

  const allItems = meta.list.filter(item => item[meta.idKey]);
  const q = search.trim().toLowerCase();
  const filtered = q ? allItems.filter(item => meta.nameOf(item).toLowerCase().includes(q)) : allItems;
  const target = step === 2 ? meta.list.find(item => item[meta.idKey] === targetId) : null;
  const tracks = targetId ? (meta.tracksMap[targetId] || []) : [];

  async function selectTarget(item) {
    const id = item[meta.idKey];
    setTargetId(id);
    setStep(2);
    if (meta.tracksMap[id] === undefined) {
      setLoadingTracks(true);
      await meta.loadSelections(id);
      setLoadingTracks(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isNarrow} className="cross-picker-dialog" TransitionComponent={Slide} TransitionProps={{ direction: 'up' }} disableEnforceFocus>
      <Box className="cross-picker-header">
        {step === 2 && (
          <IconButton edge="start" onClick={() => { setStep(1); setTargetId(null); }} aria-label="Back" size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: '#8f2949', flex: 1 }}>
          {step === 1 ? `Add to ${meta.labelPlural}` : `Add to ${meta.nameOf(target || {})}`}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="cross-picker-body">
        {step === 1 ? (
          <>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={`Search ${meta.labelPlural}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="cross-picker-search"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <div className="cross-picker-list">
              {filtered.length === 0 ? (
                <div className="request-empty">No {meta.labelPlural} found.</div>
              ) : filtered.map(item => (
                <button key={item[meta.idKey]} type="button" className="request-menu-item cross-picker-item" onClick={() => selectTarget(item)}>
                  {meta.nameOf(item)}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="cross-picker-list">
            {loadingTracks ? (
              <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
            ) : (
              <>
                <button type="button" className="request-menu-item cross-picker-item" onClick={() => onPickTrack(targetId, { newTrack: true })}>
                  New track...
                </button>
                {tracks.map((t, i) => (
                  <button key={t.id || i} type="button" className="request-menu-item cross-picker-item" onClick={() => onPickTrack(targetId, { track: t })}>
                    {t.label || `Track ${i + 1}`}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </Box>
    </Dialog>
  );
}

function EditableTrackLabel({ label, count, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label || 'Track');

  useEffect(() => {
    if (!editing) setDraft(label || 'Track');
  }, [editing, label]);

  function commit() {
    const next = draft.trim() || 'Track';
    onRename(next);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="track-label-edit">
        <input
          className="track-label-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          autoFocus
        />
        <button type="button" className="track-label-save" onClick={commit}>Save</button>
        <button type="button" className="track-label-cancel" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    );
  }

  return (
    <div className="track-title-row">
      <span className="track-label">{label || 'Track'}</span>
      <span className="track-meta"> ({count})</span>
      <button type="button" className="track-rename-btn" onClick={() => setEditing(true)}>Rename</button>
    </div>
  );
}

function PhotoMetadataDialog({ open, photo, eventId, onClose, onSave }) {
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // AI caption generation state
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionJobId, setCaptionJobId] = useState(null);
  const [captionError, setCaptionError] = useState('');

  useEffect(() => {
    if (!open || !photo) return;
    setTags(Array.isArray(photo.tags) ? photo.tags : []);
    setNotes(photo.notes || '');
    setTagInput('');
    setError('');
    // Reset caption state when dialog opens for a new photo
    setCaptionLoading(false);
    setCaptionJobId(null);
    setCaptionError('');
  }, [open, photo]);

  // Poll for caption result while a job is pending
  useEffect(() => {
    if (!captionJobId) return;
    const token = localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token');
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${CORE_BACKEND_URL}/api/core/caption/result/${captionJobId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          setCaptionError('Caption job expired or not found.');
          setCaptionLoading(false);
          setCaptionJobId(null);
          return;
        }
        const data = await res.json();
        if (data.status === 'complete') {
          setNotes(prev => (prev ? prev + '\n\n' : '') + (data.result || ''));
          setCaptionLoading(false);
          setCaptionJobId(null);
        } else if (data.status === 'error') {
          setCaptionError(data.error || 'Caption generation failed.');
          setCaptionLoading(false);
          setCaptionJobId(null);
        }
      } catch {
        setCaptionError('Failed to check caption status.');
        setCaptionLoading(false);
        setCaptionJobId(null);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [captionJobId]);

  const handleGenerateCaption = async () => {
    if (!photo?.photo_id || !eventId) return;
    setCaptionError('');
    setCaptionLoading(true);
    const token = localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${CORE_BACKEND_URL}/api/core/caption/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ event_id: eventId, photo_id: photo.photo_id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCaptionError(data.error || 'Failed to start caption job.');
        setCaptionLoading(false);
        return;
      }
      setCaptionJobId(data.job_id);
    } catch {
      setCaptionError('Failed to start caption generation.');
      setCaptionLoading(false);
    }
  };

  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;
    if (tags.some(t => t.toLowerCase() === value.toLowerCase())) {
      setTagInput('');
      return;
    }
    setTags(prev => [...prev, value]);
    setTagInput('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave({ tags, notes });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save photo metadata');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add tags and notes</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            These details are saved alongside this event gallery metadata in S3.
          </Typography>
          <Box>
            <TextField
              label="Add tag"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a tag and press Enter"
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {tags.map(tag => (
                <Chip key={tag} label={tag} onDelete={() => setTags(prev => prev.filter(item => item !== tag))} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Notes</Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={captionLoading || saving}
                onClick={handleGenerateCaption}
                startIcon={captionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {captionLoading ? 'Generating…' : 'AI Caption'}
              </Button>
            </Stack>
            {captionError && <Alert severity="warning" sx={{ mb: 1 }}>{captionError}</Alert>}
            <TextField
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              placeholder="Write anything you want to remember about this image."
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Event header card ─────────────────────────────────────────────────────────
const EVENT_EDIT_TYPES = [
  'Wedding', 'Birthday', 'Reception', 'Baby Shower',
  'Corporate Meet', 'Conference', 'Cultural Celebration', 'Family Gathering',
];

function EditEventDialog({ open, onClose, event, onUpdated }) {
  const [form, setForm] = useState({ event_name: '', event_type: '', event_date: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && event) {
      setForm({
        event_name: event.event_name || '',
        event_type: event.event_type || '',
        event_date: event.event_date ? String(event.event_date).split('T')[0] : '',
        description: event.description || '',
      });
      setErr('');
    }
  }, [open, event]);

  const handleChange = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.event_name.trim()) { setErr('Event name is required'); return; }
    setLoading(true); setErr('');
    try {
      const updated = await eventApi.updateEventDetails(event.event_id, form);
      onUpdated(updated);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Typography color="error" variant="body2">{err}</Typography>}
          <TextField label="Event Name *" value={form.event_name} onChange={handleChange('event_name')} fullWidth />
          <TextField select label="Event Type" value={form.event_type} onChange={handleChange('event_type')} fullWidth>
            <MenuItem value="">- Select type -</MenuItem>
            {EVENT_EDIT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Date" type="date" value={form.event_date} onChange={handleChange('event_date')} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Update Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Prompts a non-organiser attendee to connect with a public event organiser
// they aren't already connected to (see the organiser_connection_status /
// organiser_is_public fields on the event payload, and the localStorage
// dismissal check in EventPage below). On "Yes, connect" it sends the
// connection request, then hands the new request_id back to the parent so
// it can chain into the shared AddToCirclePrompt (requirement: every
// connection request, however it's sent, offers to add the person to a
// circle).
function OrganiserConnectDialog({ open, onClose, event, onSent }) {
  const [sending, setSending] = useState(false);

  const handleConnect = async () => {
    setSending(true);
    try {
      const sent = await networkApi.sendConnectionRequest({ receiver_id: event.organiser_id });
      onSent(sent.request_id);
    } catch (err) {
      // Already connected/pending (race with another tab, etc.) - just
      // dismiss, there's nothing actionable for the attendee to do here.
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Connect with the organiser?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This event is organised by <strong>{event?.organiser_full_name}</strong>. Add them to your
          network so you can get future event updates via Circle Moments and faster photo sharing.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Not now</Button>
        <Button variant="contained" onClick={handleConnect} disabled={sending}>
          {sending ? <CircularProgress size={18} color="inherit" /> : 'Yes, connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Shared by EventHeader's own invite-code share icon and the standalone
// "Share Event" button in the organiser's gallery toolbar - there's no
// whole-album share link on the backend (only per-photo-request tokens, see
// eventApi.generateShareLink), so sharing the event invite link is what
// actually gets a recipient into the event.
async function shareEventInvite(event) {
  const inviteUrl = event.invite_code
    ? `https://www.circlemoments.com/join-event/${encodeURIComponent(event.invite_code)}`
    : '';
  if (!inviteUrl) return false;
  // navigator.share isn't implemented by Android's WebView (even though
  // it's Chrome-based), so the native app must go through Capacitor's
  // Share plugin - the browser's Web Share API is only reachable when
  // this page is loaded in an actual mobile browser tab.
  if (Capacitor.isNativePlatform()) {
    await Share.share({
      title: event.event_name || 'Circle Moments event',
      text: `Join ${event.event_name || 'this event'} on Circle Moments`,
      url: inviteUrl,
    });
  } else if (navigator.share) {
    await navigator.share({
      title: event.event_name || 'Circle Moments event',
      text: `Join ${event.event_name || 'this event'} on Circle Moments`,
      url: inviteUrl,
    });
  } else {
    await navigator.clipboard.writeText(inviteUrl);
  }
  return true;
}

function EventHeader({ event, onUpdated }) {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadLinksOpen, setUploadLinksOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const toast = useCallback((msg, sev = 'info') => setSnack({ open: true, msg: String(msg), sev }), []);

  // ── Organiser-connect prompt (attendees of a public, not-yet-connected
  // organiser) + the follow-on "add to circle" prompt shared with Network/
  // ProfileView. Declining is remembered per event so it never nags again.
  const [organiserPromptOpen, setOrganiserPromptOpen] = useState(false);
  const [addToCircleRequestId, setAddToCircleRequestId] = useState(null);
  const organiserPromptSeenKey = event?.event_id ? `cm_organiser_prompt_seen_${event.event_id}` : null;

  useEffect(() => {
    if (!event?.event_id || event.is_organiser) return;
    if (!event.organiser_is_public || event.organiser_connection_status !== 'none') return;
    if (localStorage.getItem(`cm_organiser_prompt_seen_${event.event_id}`)) return;
    // Wait a minute of the attendee actually being on the page before
    // interrupting them with the connect prompt, rather than firing it the
    // instant the event loads.
    const timer = setTimeout(() => setOrganiserPromptOpen(true), 60000);
    return () => clearTimeout(timer);
  }, [event?.event_id, event?.is_organiser, event?.organiser_is_public, event?.organiser_connection_status]);

  const dismissOrganiserPrompt = useCallback(() => {
    if (organiserPromptSeenKey) localStorage.setItem(organiserPromptSeenKey, '1');
    setOrganiserPromptOpen(false);
  }, [organiserPromptSeenKey]);

  const handleOrganiserRequestSent = useCallback((requestId) => {
    if (organiserPromptSeenKey) localStorage.setItem(organiserPromptSeenKey, '1');
    setOrganiserPromptOpen(false);
    setAddToCircleRequestId(requestId);
  }, [organiserPromptSeenKey]);
  const inviteUrl = event.invite_code
    ? `https://www.circlemoments.com/join-event/${encodeURIComponent(event.invite_code)}`
    : '';
  const qrCodeUrl = inviteUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=132x132&margin=8&data=${encodeURIComponent(inviteUrl)}`
    : '';
  const handleCopy = () => {
    navigator.clipboard.writeText(event.invite_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleShare = async () => {
    try {
      if (!(await shareEventInvite(event))) return;
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {
      // User cancelled the native share sheet.
    }
  };
  const EVENT_TYPES = { Wedding: '💍', Birthday: '🎂', Reception: '🥂', 'Baby Shower': '👶', 'Corporate Meet': '💼', Conference: '🎤', 'Cultural Celebration': '🎊', 'Family Gathering': '👨‍👩‍👧‍👦' };
  const emoji = EVENT_TYPES[event.event_type] || '📸';

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2, background: 'linear-gradient(135deg, #fff4f8 0%, #fff8ef 100%)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Box
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover .event-edit-btn': { opacity: 1 } }}
          >
            <Typography variant="h5" fontWeight={700}>{emoji} {event.event_name}</Typography>
            {event.is_organiser && (
              <Tooltip title="Edit event details">
                <IconButton
                  className="event-edit-btn"
                  size="small"
                  onClick={() => setEditOpen(true)}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', color: 'text.secondary' }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {event.organiser_full_name && (
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              Organised by: {event.organiser_full_name}
              {event.organiser_is_public && event.organiser_username && (
                <>
                  {' '}(
                  <Link component={RouterLink} to={`/profile/${event.organiser_username}`}>
                    @{event.organiser_username}
                  </Link>
                  )
                </>
              )}
            </Typography>
          )}
          <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
            {event.event_type && <Chip label={event.event_type} size="small" />}
            {event.event_date && <Chip label={new Date(event.event_date).toLocaleDateString()} size="small" variant="outlined" />}
            <Chip
              label={event.is_organiser ? 'Organiser' : event.role === 'circle_viewer' ? 'Circle Viewer' : 'Attendee'}
              size="small"
              color={event.is_organiser ? 'primary' : event.role === 'circle_viewer' ? 'secondary' : 'default'}
            />
          </Stack>
          {event.description && <Typography variant="body2" color="text.secondary" mt={0.5}>{event.description}</Typography>}
        </Box>
        {event.is_organiser && (
          <EditEventDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            event={event}
            onUpdated={(updated) => { onUpdated(updated); setEditOpen(false); }}
          />
        )}
        {event.invite_code && event.is_organiser && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: { xs: 0, sm: 310 }, width: { xs: '100%', sm: 'auto' }, overflow: 'hidden' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
              <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">Invite Code</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    fontWeight={800}
                    noWrap
                    sx={{
                      color: '#C73866',
                      letterSpacing: { xs: '0.02em', sm: '0.12em', md: '0.15em' },
                      fontSize: {
                        xs: 'clamp(0.68rem, calc((100vw - 178px) / 8), 1.5rem)',
                        sm: 'clamp(1rem, 4vw, 1.5rem)',
                      },
                      lineHeight: 1.25,
                      minWidth: 0,
                      flexShrink: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {event.invite_code}
                  </Typography>
                  <Tooltip title={copied ? 'Code copied!' : 'Copy invite code'}>
                    <IconButton size="small" onClick={handleCopy} sx={{ flexShrink: 0, p: { xs: 0.35, sm: 0.5 } }}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={shared ? 'Link shared!' : 'Share invite link'}>
                    <IconButton size="small" onClick={handleShare} sx={{ flexShrink: 0, p: { xs: 0.35, sm: 0.5 } }}><IosShareIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary">Share with attendees</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                <Box
                  component="img"
                  src={qrCodeUrl}
                  alt={`QR code for invite ${event.invite_code}`}
                  sx={{ width: 96, height: 96, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'white', p: 0.5 }}
                />
                <Typography
                  component="button"
                  type="button"
                  onClick={() => setUploadLinksOpen(true)}
                  variant="caption"
                  sx={{
                    display: 'block', width: '100%', mt: 0.5, p: 0, border: 0, background: 'none',
                    color: 'primary.main', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Manage upload links
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>
      {event.is_organiser && (
        <UploadLinksDialog open={uploadLinksOpen} onClose={() => setUploadLinksOpen(false)} eventId={event.event_id} toast={toast} />
      )}
      <OrganiserConnectDialog
        open={organiserPromptOpen}
        onClose={dismissOrganiserPrompt}
        event={event}
        onSent={handleOrganiserRequestSent}
      />
      <AddToCirclePrompt
        open={!!addToCircleRequestId}
        onClose={() => setAddToCircleRequestId(null)}
        requestId={addToCircleRequestId}
        personName={event.organiser_full_name}
      />
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}

// ── Smart Sort grouping section (organiser only) ───────────────────────────────
// Content-only agenda editor/viewer - rendered inside the shared "Event details"
// pill in EventAgendaAndToolsPanel, which owns the expand/collapse chrome.
function EventAgendaPanel({ event, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(event.event_agenda || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const hasAgenda = Boolean((event.event_agenda || '').trim());

  useEffect(() => {
    setDraft(event.event_agenda || '');
  }, [event.event_agenda]);

  async function saveAgenda() {
    setSaving(true);
    setError('');
    try {
      const updated = await eventApi.updateEventDetails(event.event_id, { event_agenda: draft });
      onSaved(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || 'Failed to save event details.');
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setDraft(event.event_agenda || '');
    setError('');
    setEditing(false);
  }

  return (
    event.is_organiser && editing ? (
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            Markdown is supported for headings, lists, links, bold, italic, and inline code.
          </Typography>
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Button variant="contained" onClick={saveAgenda} disabled={saving} sx={{ textTransform: 'none' }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
            <Button onClick={cancelEdit} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          </Stack>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            label="Agenda markdown"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            multiline
            minRows={7}
            placeholder={"## Event Agenda\n- 10:00 Welcome\n- 11:00 Ceremony\n- 12:30 Lunch"}
            fullWidth
            sx={{ flex: 1 }}
          />
          {draft.trim() && (
            <Box className="event-agenda-preview" sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Preview</Typography>
              <MarkdownContent markdown={draft} />
            </Box>
          )}
        </Stack>
      </Stack>
    ) : hasAgenda ? (
      <Stack spacing={1.5}>
        <MarkdownContent markdown={event.event_agenda} />
        {event.is_organiser && (
          <Box>
            <Button variant="outlined" size="small" onClick={() => setEditing(true)} sx={{ textTransform: 'none' }}>
              Edit
            </Button>
          </Box>
        )}
      </Stack>
    ) : (
      <Box
        sx={{
          textAlign: 'center', py: 2.5, px: 2, borderRadius: 2,
          bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300',
        }}
      >
        <ViewAgendaIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: event.is_organiser ? 1.5 : 0 }}>
          No agenda added yet{event.is_organiser ? ' - let guests know the schedule.' : '.'}
        </Typography>
        {event.is_organiser && (
          <Button variant="contained" size="small" onClick={() => setEditing(true)} sx={{ textTransform: 'none' }}>
            Add agenda
          </Button>
        )}
      </Box>
    )
  );
}

function SmartSortSection({
  event,
  onStatusChange,
  onGalleryRefresh,
  driveConnected,
  driveEmail,
  accountFaceGroupingModel,
  accountFaceGroupingModelLoaded,
}) {
  const navigate = useNavigate();
  const [sourceDir, setSourceDir] = useState('');
  const [serverFiles, setServerFiles] = useState([]);
  const [sourceMode, setSourceMode] = useState('local');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [photoLimit, setPhotoLimit] = useState(event.grouping_limit_override || '');
  const [starting, setStarting] = useState(false);
  const [rebuildingSearch, setRebuildingSearch] = useState(false);
  const [log, setLog] = useState('');
  const [progress, setProgress] = useState({ processed: 0, total: 0, percent: 0, current_image: '', phase: 'starting' });
  const [statusComment, setStatusComment] = useState('Starting...');
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [billingError, setBillingError] = useState('');
  // Reconciliation for an initial-grouping upload still running (native only)
  // when this page was last closed - see the matching effect below.
  const [resumeJob, setResumeJob] = useState(null);
  // Off by default: auto-synced batches upload into the submissions pool
  // without triggering grouping, same as the manual "Upload" step - the
  // organiser must separately click "Start grouping" (in AddPhotosSection)
  // to actually process them. Turning this on restores the old combined
  // upload-and-group-immediately behavior for auto-sync specifically.
  const [autoSyncAlsoGroup, setAutoSyncAlsoGroup] = useState(false);
  // Bridges submission_ids created by an upload-only auto-sync batch (handled
  // here, since the auto-sync engine lives in this component) into
  // AddPhotosSection's own sessionSubmissionIds accumulator (owned there,
  // since that's also where the manual Upload button's ids land) - consumed
  // and cleared by AddPhotosSection's effect once merged in.
  const [autoSyncUploadedSubmissionIds, setAutoSyncUploadedSubmissionIds] = useState([]);
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const directoryInputRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const uploadCancelledRef = useRef(false);
  // relativePath -> Drive fileId, captured at picker time so the brokered
  // "uncompressed original" download can resolve back to the source file in
  // the organiser's own Drive once grouping has finished.
  const driveFileIdByPathRef = useRef(null);
  // Metadata-only Drive selection awaiting a server-side import (see
  // drive_import_service.py) - [{driveFileId, name}], set by
  // handleDriveFolderSelect, consumed (and cleared) by handleUploadOnlyInitial
  // or the native fast path below. No file bytes are ever held on this
  // device for a Drive-sourced selection; the backend fetches them directly
  // from Drive once a job is scheduled.
  const driveImportPendingRef = useRef(null);
  // Set only when serverFiles came from manualFolder.pickFolder() on native (a
  // real SAF folder) - lets handleStart pass it through as startInitialGroupingUpload's
  // `treeUri`, so the native upload job resolves original bytes itself, lazily,
  // straight from SAF instead of needing real File/Blob objects staged in JS first
  // (see useManualFolderPersistence's toPlaceholderFile doc comment). Reset to null
  // by every other photo-selection path (plain files, Drive, the <input webkitdirectory>
  // fallback) so a stale treeUri from an earlier folder pick never gets attached to an
  // unrelated selection.
  const manualFolderTreeUriRef = useRef(null);
  // Same idea as manualFolderTreeUriRef, for the OTHER native-lazy source: a
  // flat multi-select via nativeFilePicker.pickFiles (individually-picked SAF
  // documents, not a whole folder tree). [{relativePath, uri}], or null.
  // Mutually exclusive with manualFolderTreeUriRef - only one photo-selection
  // path is ever active at a time.
  const manualFolderDocsRef = useRef(null);
  const status = event.grouping_status || 'none';
  const isAddJob = (event.grouping_job_type || 'initial') === 'add';

  // Native-only: observes whatever CmUploadWorker (WorkManager) is already
  // doing for this event's initial-grouping job - it never resumes or re-runs
  // anything here (native never stopped running it, closed app or not; this
  // purely reads state back for the banner/progress bar). Nudges the parent's
  // own grouping_status once so its existing pollStatus effect (below) picks
  // up the compute phase once CmUploadWorker's own groupingUploadComplete call
  // flips the server's status - no separate waitForGroupingCompletion call
  // needed here anymore.
  const nativeJob = useNativeUploadObserver(event.event_id, ['initial-grouping']);
  // Same idea, for the "Upload only" native job handleUploadOnlyInitial starts
  // (flowKind 'organiser-photo-submit') - without this, that job ran fully
  // headlessly pre-gallery with no progress bar at all, since nativeJob above
  // only ever watches 'initial-grouping'. See the id-reconciliation effect
  // further down (near sessionSubmissionIds) for how its results get back
  // into "Start grouping (N)" once it finishes.
  const nativePhotoSubmitJob = useNativeUploadObserver(event.event_id, ['organiser-photo-submit']);
  useEffect(() => { setResumeJob(nativeJob || nativePhotoSubmitJob || null); }, [nativeJob, nativePhotoSubmitJob]);
  const nudgedStatusRef = useRef(false);
  useEffect(() => {
    if (nativeJob && !nudgedStatusRef.current && (event.grouping_status || 'none') === 'none') {
      nudgedStatusRef.current = true;
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'initial' });
    }
  }, [nativeJob, event.grouping_status, onStatusChange]);

  const manualFolder = useManualFolderPersistence({
    eventId: event.event_id,
    onFilesReady: (files, folderName, treeUri) => {
      setServerFiles(files);
      setSourceDir(folderName);
      setSourceMode('local');
      manualFolderTreeUriRef.current = treeUri || null;
    },
  });

  const selectedServerImages = useMemo(() => {
    const images = serverFiles.filter(isCompressibleEventImage);
    const limitValue = photoLimit === '' ? 0 : Number(photoLimit);
    return limitValue > 0 ? images.slice(0, limitValue) : images;
  }, [photoLimit, serverFiles]);

  const handleServerDirectoryChange = (event) => {
    const files = Array.from(event.target.files || []);
    setBillingError('');
    setServerFiles(files);
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    event.target.value = '';
    const acceptedCount = files.filter(isCompressibleEventImage).length;
    const ignoredCount = files.length - acceptedCount;
    if (acceptedCount > MAX_GROUPING_REQUEST_PHOTOS) {
      setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' });
    }
    if (files.length && ignoredCount > 0) {
      setSnack({
        open: true,
        msg: `${acceptedCount} supported photos selected. Ignored ${ignoredCount} unsupported file${ignoredCount === 1 ? '' : 's'}.`,
        sev: 'info',
      });
    }
    const firstFile = files[0];
    const nativePath = firstFile?.path || firstFile?.mozFullPath || '';
    if (nativePath) {
      const normalized = nativePath.replace(/\\/g, '/');
      const relative = (firstFile.webkitRelativePath || firstFile.name || '').replace(/\\/g, '/');
      const relativeParts = relative.split('/').filter(Boolean);
      const removeParts = relativeParts.length > 1 ? relativeParts.length : 1;
      const baseParts = normalized.split('/').slice(0, -removeParts);
      setSourceDir(baseParts.join('/'));
      return;
    }

    const browserRelativePath = firstFile?.webkitRelativePath || firstFile?.name || '';
    const folder = firstFile?.webkitRelativePath
      ? browserRelativePath.split('/')[0]
      : files.length > 1 ? 'Selected photos' : browserRelativePath;
    setSourceDir(folder);
  };

  // Thin wrapper: real implementation lives in module-level runOrganiserUploadBatches
  // (below waitForGroupingCompletion) so it's callable outside this component tree
  // (e.g. the native headless upload-worker route).
  const _uploadBatches = (filesToUpload, payload, hooks = {}) => runOrganiserUploadBatches(event, filesToUpload, 'initial', payload, {
    hooks, uploadCancelledRef, uploadAbortRef, driveFileIdByPathRef,
  });

  const handleLocalFolderSelect = async () => {
    setBillingError('');
    setSourceMode('local');
    if (!manualFolder.supported) {
      manualFolderTreeUriRef.current = null;
      manualFolderDocsRef.current = null;
      directoryInputRef.current?.click();
      return;
    }
    const result = await manualFolder.pickFolder();
    if (result) {
      setServerFiles(result.files);
      setSourceDir(result.folderName);
      manualFolderTreeUriRef.current = result.treeUri || null;
      manualFolderDocsRef.current = null;
    }
  };

  const handleLocalFilesSelect = async () => {
    setBillingError('');
    setSourceMode('local');
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    // Native: bypasses <input type=file> entirely - see CmWebChromeClient's
    // doc comment for why that route silently caps at 100 selections, and
    // nativeFilePicker's own doc comment for why this stays lazy (bare
    // {uri, name} descriptors, no eager Blob materialisation) exactly like
    // manualFolder.pickFolder() does for a whole folder.
    if (nativeFilePicker.supported()) {
      const picked = await nativeFilePicker.pickFiles();
      if (!picked.length) return; // user cancelled the OS picker
      manualFolderDocsRef.current = picked.map((p) => ({ relativePath: p.name, uri: p.uri }));
      setServerFiles(picked.map((p) => ({ name: p.name, webkitRelativePath: p.name, size: p.size, lastModified: Date.now(), type: '' })));
      setSourceDir(picked.length > 1 ? 'Selected photos' : picked[0].name);
      return;
    }
    fileInputRef.current?.click();
  };

  // Native small-batch fast path: schedules a server-side Drive import (see
  // drive_import_service.py) in mode='group_now' and returns immediately -
  // the backend fetches, compresses, and uploads the photos itself, then
  // drives the same grouping-upload-progress/complete bookkeeping the
  // browser's own upload flow uses, so the existing grouping-status poller
  // below picks up progress exactly as it would for any other upload. No
  // download or upload of any kind happens on this device.
  const runDriveDownloadAndUpload = async (photoFiles, limitVal) => {
    const consentOk = await confirmFaceGroupingConsent();
    if (!consentOk) {
      setSnack({ open: true, msg: 'Face grouping was not started because consent was not granted.', sev: 'info' });
      return;
    }
    const creditHold = await eventApi.initiateGrouping(event.event_id, 'initial');
    eventApi.preWarmGroupingServer(event.event_id, 'initial').catch(() => {});
    try {
      await eventApi.scheduleDriveImport(event.event_id, {
        mode: 'group_now',
        drive_files: photoFiles.map((d) => ({ drive_file_id: d.id, name: d.name })),
        source_directory: 'Google Drive',
        limit: limitVal,
        credit_hold_id: creditHold.credit_hold_id,
      });
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'initial' });
      setSnack({ open: true, msg: `Importing ${photoFiles.length} photos from Drive in the background - you can close the app.`, sev: 'info' });
    } catch (err) {
      onStatusChange({ grouping_status: 'none', grouping_job_type: 'initial' });
      setSnack({ open: true, msg: err.message || 'Drive import failed.', sev: 'error' });
    }
  };

  const handleDriveFolderSelect = async () => {
    setBillingError('');
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    setSnack({ open: true, msg: 'Opening Google Drive file picker…', sev: 'info' });
    try {
      const token = await getDriveAccessToken(driveEmail);
      const pickedDocs = await openDrivePicker(token);
      if (!pickedDocs.length) { setSnack({ open: true, msg: 'No files selected.', sev: 'info' }); return; }
      if (pickedDocs.some(d => d.mimeType === DRIVE_FOLDER_MIME)) {
        setSnack({ open: true, msg: 'Reading folder contents…', sev: 'info' });
      }
      const docs = await expandDrivePickerDocs(pickedDocs, token);
      if (!docs.length) { setSnack({ open: true, msg: 'No image files found in the selected folder.', sev: 'warning' }); return; }
      const photoMimes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/bmp', 'image/tiff']);
      const photoFiles = docs.filter(d => photoMimes.has(d.mimeType));
      if (!photoFiles.length) { setSnack({ open: true, msg: 'No image files found in selection.', sev: 'warning' }); return; }
      if (photoFiles.length > MAX_GROUPING_REQUEST_PHOTOS) { setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' }); return; }

      const limitVal = photoLimit === '' ? 0 : Number(photoLimit);
      const sliced = limitVal > 0 ? photoFiles.slice(0, limitVal) : photoFiles;

      if (Capacitor.isNativePlatform() && sliced.length <= DRIVE_BACKGROUND_MAX_FILES) {
        await runDriveDownloadAndUpload(sliced, limitVal);
        return;
      }

      // Metadata-only selection - no photo bytes are ever downloaded to this
      // device. Clicking "Upload" (handleUploadOnlyInitial below) schedules
      // the actual server-side import (drive_import_service.py).
      driveImportPendingRef.current = sliced.map((d) => ({ driveFileId: d.id, name: d.name }));
      setServerFiles(sliced.map((d) => ({ name: d.name, webkitRelativePath: d.name, size: 0, lastModified: Date.now(), type: d.mimeType || '' })));
      setSourceDir(sliced.length > 1 ? 'Google Drive' : sliced[0].name);
      setSourceMode('cloud-google');
      setSnack({ open: true, msg: `${sliced.length} Drive photos selected.`, sev: 'success' });
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Drive access failed.', sev: 'error' });
    }
  };

  // Shared by the manual "Group Photos" button and the local-folder auto-sync
  // engine (which calls this directly with its own detected batch, bypassing
  // the manual-selection state below). `hooks` redirects progress/log/status
  // to the auto-sync engine's own state when called from there.
  const startInitialGroupingUpload = async (filesToUpload, sourceDirLabel, opts = {}) => {
    // `treeUri` (a whole SAF folder) and `docs` (individually-picked SAF
    // documents, from the plain "Choose local photos" picker on native - see
    // nativeFilePicker.pickFiles) are mutually exclusive resumeSource sources;
    // at most one is ever set by a given caller.
    const { limit, sourceModeOverride, hooks = {}, skipConsentCheck = false, treeUri, docs } = opts;
    if (!skipConsentCheck) {
      const consentOk = await confirmFaceGroupingConsent();
      if (!consentOk) {
        setSnack({ open: true, msg: 'Face grouping was not started because consent was not granted.', sev: 'info' });
        return;
      }
    }
    setStarting(true);
    setBillingError('');
    const emitProgress = hooks.setProgress || setProgress;
    const emitStatusComment = hooks.setStatusComment || setStatusComment;
    const emitLog = hooks.setLog || setLog;
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(event.event_id, 'initial');
    console.log(`[Upload] startInitialGroupingUpload: event=${event.event_id} jobId=${jobId} files=${filesToUpload.length} isNative=${isNative}`);
    try {
      const creditHold = await eventApi.initiateGrouping(event.event_id, 'initial');
      // Immediate pre-warm for small uploads now happens inside
      // runOrganiserUploadBatches (PWA) / CmUploadWorker.runOrganiserUpload
      // (native) uniformly - no longer duplicated here.
      const payload = {
        source_directory: (sourceDirLabel || '').trim() || 'browser-upload',
        limit,
        source_mode: sourceModeOverride || sourceMode,
        credit_hold_id: creditHold.credit_hold_id,
        organizer_terms_ack: true,
        policy_version: PRIVACY_POLICY_VERSION,
      };
      let kickoffResult;
      if (isNative) {
        // On native, every upload - whether the app stays open or not - hands
        // off to CmUploadWorker (WorkManager), which does the entire
        // compress/upload/kickoff/grouping-watch pipeline itself, independent
        // of this page/process from here on - there's no JS-side "run" or
        // "resume" step to keep in sync anymore; see useNativeUploadObserver
        // for how this page reads its progress back for display.
        console.log(`[Upload] startInitialGroupingUpload: event=${event.event_id} jobId=${jobId} handing off to native CmUploadWorker (see CmUploadWorker/CmApiClient logs for the rest of this job)`);
        await nativeUploadService.startUploadJob({
          jobId, flowKind: 'initial-grouping', eventId: event.event_id,
          flowMeta: payload,
          totalFiles: filesToUpload.length,
          entries: filesToUpload.map((e) => (
            e.resolveFile
              ? e
              : { relativePath: e.relativePath || e.webkitRelativePath || e.name || e.file?.name, file: e.file || e }
          )),
          resumeSource: treeUri ? { kind: 'saf', treeUri } : (docs ? { kind: 'saf-docs', docs } : null),
        });
      } else {
        kickoffResult = await _uploadBatches(filesToUpload, payload, hooks);
      }
      console.log(`[Upload] startInitialGroupingUpload: event=${event.event_id} jobId=${jobId} kickoff done, status=${kickoffResult?.status}`);
      emitProgress({ processed: 0, total: 0, percent: 0, current_image: '', phase: 'starting' });
      emitStatusComment(kickoffResult?.status === 'starting' ? 'Grouping server is starting…' : 'Starting...');
      emitLog('');
      setSnack({
        open: true,
        msg: 'Grouping started - this may take several minutes',
        sev: 'info',
      });
      onStatusChange({ grouping_status: kickoffResult?.status === 'starting' ? 'starting' : 'processing', grouping_job_type: 'initial' });
    } catch (e) {
      if (e.message === 'Upload cancelled.' || e.name === 'AbortError') {
        console.log(`[Upload] startInitialGroupingUpload: event=${event.event_id} jobId=${jobId} cancelled by user`);
        return;
      }
      console.error(`[Upload] startInitialGroupingUpload: event=${event.event_id} jobId=${jobId} FAILED:`, e?.message || e);
      onStatusChange({ grouping_status: 'none', grouping_job_type: 'initial' });
      if (e.status === 402) {
        const msg = groupingBillingMessage(e);
        setBillingError(msg);
        setStatusComment('Grouping needs credits before it can start.');
        setSnack({ open: true, msg, sev: 'warning' });
      } else {
        setSnack({ open: true, msg: e.message || 'Failed to start grouping', sev: 'error' });
      }
      throw e;
    } finally {
      setStarting(false);
    }
  };

  const pollStatus = useCallback(async () => {
    try {
      const res = await eventApi.getGroupingStatus(event.event_id);
      setLog(res.log || '');
      setProgress(res.progress || {
        processed: res.processed || 0,
        total: res.total || 0,
        percent: res.percent || 0,
        current_image: res.current_image || '',
        phase: res.status || 'starting',
      });
      setStatusComment(res.comment || (res.status === 'uploading' ? 'Finalising...' : res.status === 'starting' ? 'Grouping server is starting…' : 'Processing images...'));
      onStatusChange({ grouping_status: res.status, grouping_job_type: res.grouping_job_type || 'initial' });
      if (res.status !== 'processing' && res.status !== 'uploading' && res.status !== 'starting') {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        // Resolve any "Start grouping" run this component itself kicked off
        // (see handleStartGroupingInitial) - this is the single poller both
        // that flow and the older upload-and-group-immediately flow share,
        // so there's no second, uncoordinated poll loop racing this one.
        if (pendingIngestSubmissionIdsRef.current) {
          const mine = new Set(pendingIngestSubmissionIdsRef.current);
          pendingIngestSubmissionIdsRef.current = null;
          setGroupingIngestStatus(res.status === 'complete' ? 'complete' : 'failed');
          if (res.status === 'complete') {
            setSnack({ open: true, msg: 'Gallery created successfully.', sev: 'success' });
            setSessionSubmissionIds(prev => prev.filter(id => !mine.has(id)));
          } else {
            setSnack({ open: true, msg: 'Grouping failed. Check Smart Sort for details.', sev: 'error' });
          }
        }
      }
    } catch { /* ignore */ }
  }, [event.event_id, onStatusChange]);

  useEffect(() => {
    if (isAddJob || (status !== 'processing' && status !== 'uploading' && status !== 'starting')) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      return;
    }
    pollStatus();
    pollingRef.current = setInterval(pollStatus, GROUPING_STATUS_POLL_INTERVAL_MS);
    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [isAddJob, status, pollStatus]);

  // ── Decoupled "upload without grouping" for the very first (initial)
  // grouping run - AddPhotosSection only mounts once a gallery already
  // exists, so the pre-gallery case needs its own Upload/Start grouping pair
  // here. Mirrors AddPhotosSection's identical state/handlers.
  const [sessionSubmissionIds, setSessionSubmissionIds] = useState(() => loadSessionSubmissionIds(event.event_id));
  useEffect(() => {
    saveSessionSubmissionIds(event.event_id, sessionSubmissionIds);
  }, [event.event_id, sessionSubmissionIds]);
  // Reconciliation: a persisted id can go stale if it was already grouped
  // through some other path (or through a bug where this component's own
  // completion-handling never got a chance to clear it - see the
  // grouping_job_type mismatch this fixed) - without this check, a stale
  // "already grouped" id would sit in localStorage forever, surviving every
  // refresh, and clicking "Start grouping" would silently re-submit an
  // already-processed photo. Runs on mount, and again every time
  // event.grouping_status transitions into 'complete' - grouping kicked off
  // from the Photo Submissions tab's own "Process all unprocessed"/"Add to
  // gallery" buttons finishes through a totally separate poller that this
  // component never sees directly, so mount-only would leave the stale count
  // showing until the next full page refresh (see EventGalleryViewer's
  // onGroupingComplete, which is what makes event.grouping_status catch up
  // for that case). The boolean-dependency trick means this only re-fires on
  // the false->true/true->false edge, not on every intermediate
  // starting/processing tick.
  useEffect(() => {
    if (!sessionSubmissionIds.length) return undefined;
    let cancelled = false;
    eventApi.listPhotoSubmissions(event.event_id).then((res) => {
      if (cancelled) return;
      const allSubs = (res?.submitters || []).flatMap(s => s.submissions || []);
      const stillUngrouped = new Set(allSubs.filter(s => !s.grouped).map(s => s.submission_id));
      setSessionSubmissionIds(prev => prev.filter(id => stillUngrouped.has(id)));
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id, event.grouping_status === 'complete']);
  // Snapshot of this organiser's own ungrouped submission ids, refreshed on
  // mount and every time a native 'organiser-photo-submit' job (started by
  // handleUploadOnlyInitial below) finishes - the diff against this snapshot
  // is how that job's created ids get back into sessionSubmissionIds, since
  // (unlike the PWA path, which gets ids back synchronously) native never
  // returns them directly. Mirrors AddPhotosSection's identical
  // lastKnownOrganiserUngroupedIdsRef - that copy only ever runs once a
  // gallery exists, so this pre-gallery case needs its own.
  const lastKnownOrganiserUngroupedIdsRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    eventApi.listPhotoSubmissions(event.event_id).then((res) => {
      if (cancelled) return;
      lastKnownOrganiserUngroupedIdsRef.current = new Set(
        (res?.submitters || []).filter(s => s.is_organiser).flatMap(s => s.submissions.filter(x => !x.grouped).map(x => x.submission_id))
      );
    }).catch(() => { lastKnownOrganiserUngroupedIdsRef.current = new Set(); });
    return () => { cancelled = true; };
  }, [event.event_id]);
  const priorNativePhotoSubmitJobRef = useRef(null);
  useEffect(() => {
    if (!nativePhotoSubmitJob && priorNativePhotoSubmitJobRef.current) {
      const before = lastKnownOrganiserUngroupedIdsRef.current || new Set();
      eventApi.listPhotoSubmissions(event.event_id).then((res) => {
        const currentIds = (res?.submitters || [])
          .filter(s => s.is_organiser)
          .flatMap(s => s.submissions.filter(x => !x.grouped).map(x => x.submission_id));
        lastKnownOrganiserUngroupedIdsRef.current = new Set(currentIds);
        const newIds = currentIds.filter(id => !before.has(id));
        if (newIds.length) {
          setSessionSubmissionIds(prev => [...prev, ...newIds]);
          setSnack({ open: true, msg: `${newIds.length} photo${newIds.length === 1 ? '' : 's'} uploaded (see Photo Submissions tab).`, sev: 'success' });
        }
      }).catch(() => {});
    }
    priorNativePhotoSubmitJobRef.current = nativePhotoSubmitJob;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativePhotoSubmitJob]);
  const [uploadOnlyBusy, setUploadOnlyBusy] = useState(false);
  const [uploadOnlyProgress, setUploadOnlyProgress] = useState({ uploaded: 0, total: 0 });
  const [groupingIngestStatus, setGroupingIngestStatus] = useState('idle'); // idle | processing | complete | failed
  // Synchronous guard (set before the first await, unlike groupingIngestStatus
  // which only flips to 'processing' once the network call resolves) - closes
  // the window where a fast double-click on "Start grouping" could fire the
  // ingestPhotoSubmissions call twice before the button's disabled state catches up.
  const [startingGrouping, setStartingGrouping] = useState(false);
  // Snapshot of the submission_ids handed to ingestPhotoSubmissions for the
  // grouping run currently in flight - null when no such run is outstanding.
  // Resolved by the EXISTING pollStatus loop below, not a second poller of
  // our own. A separate poll loop here used to run alongside pollStatus (both
  // watching the exact same grouping-status endpoint, both calling
  // onStatusChange independently) - two uncoordinated pollers racing on the
  // same event.grouping_status could report completion out of order, and
  // since that same status also gates whether the Upload/Start-grouping UI
  // is shown, the UI could reappear (with the just-grouped ids not yet
  // cleared from sessionSubmissionIds) and let the organiser resubmit the
  // exact same photos for grouping a second time. Folding into the one
  // existing poller removes the race entirely.
  const pendingIngestSubmissionIdsRef = useRef(null);

  const handleUploadOnlyInitial = async () => {
    if (!selectedServerImages.length) { setSnack({ open: true, msg: 'Choose a photo folder to upload first', sev: 'warning' }); return; }
    if (selectedServerImages.length > MAX_GROUPING_REQUEST_PHOTOS) { setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' }); return; }
    const consentOk = await confirmFaceGroupingConsent();
    if (!consentOk) {
      setSnack({ open: true, msg: 'Upload was cancelled because consent was not granted.', sev: 'info' });
      return;
    }

    // Drive-sourced selection: no photo bytes are held on this device -
    // schedule a server-side import (mode='upload_only') and poll it instead
    // of the local-file compress/PUT loop below. See drive_import_service.py.
    if (sourceMode === 'cloud-google' && driveImportPendingRef.current?.length) {
      const driveFiles = driveImportPendingRef.current;
      setUploadOnlyBusy(true);
      setUploadOnlyProgress({ uploaded: 0, total: driveFiles.length });
      try {
        const { job_id: jobId } = await eventApi.scheduleDriveImport(event.event_id, {
          mode: 'upload_only',
          drive_files: driveFiles.map((d) => ({ drive_file_id: d.driveFileId, name: d.name })),
          source_directory: sourceDir || 'Google Drive',
        });
        for (;;) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const job = await eventApi.getDriveImportStatus(event.event_id, jobId);
          setUploadOnlyProgress({ uploaded: job.done_count || 0, total: job.total_count || driveFiles.length });
          if (job.status === 'complete') {
            const ids = job.submission_ids || [];
            setSessionSubmissionIds(prev => [...prev, ...ids]);
            setSnack({ open: true, msg: `${ids.length} photo${ids.length === 1 ? '' : 's'} imported from Drive (see Photo Submissions tab).`, sev: 'success' });
            break;
          }
          if (job.status === 'failed' || job.status === 'cancelled') {
            throw new Error(job.error || 'Drive import failed.');
          }
        }
        driveImportPendingRef.current = null;
        setServerFiles([]);
        setSourceDir('');
      } catch (err) {
        setSnack({ open: true, msg: err.message || 'Drive import failed', sev: 'error' });
      } finally {
        setUploadOnlyBusy(false);
        setUploadOnlyProgress({ uploaded: 0, total: 0 });
      }
      return;
    }

    setUploadOnlyBusy(true);
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(event.event_id, 'photo-submit-initial');
    const filesToUpload = selectedServerImages.map((f) => ({ file: f, relativePath: (f.webkitRelativePath || f.name) }));
    try {
      if (isNative) {
        await nativeUploadService.startUploadJob({
          jobId, flowKind: 'organiser-photo-submit', eventId: event.event_id,
          flowMeta: { canGroup: false },
          totalFiles: filesToUpload.length,
          entries: filesToUpload,
          resumeSource: manualFolderTreeUriRef.current
            ? { kind: 'saf', treeUri: manualFolderTreeUriRef.current }
            : manualFolderDocsRef.current
              ? { kind: 'saf-docs', docs: manualFolderDocsRef.current }
              : null,
        });
        setSnack({ open: true, msg: `Uploading ${filesToUpload.length} photo${filesToUpload.length === 1 ? '' : 's'} in the background - check back in Photo Submissions once it's done.`, sev: 'info' });
      } else {
        setUploadOnlyProgress({ uploaded: 0, total: selectedServerImages.length });
        const ids = await uploadPhotoSubmissionsInBatches(event.event_id, selectedServerImages, {
          onProgress: (p) => setUploadOnlyProgress(p),
        });
        setSessionSubmissionIds(prev => [...prev, ...ids]);
        setSnack({ open: true, msg: `${ids.length} photo${ids.length === 1 ? '' : 's'} selected and uploaded (see Photo Submissions tab).`, sev: 'success' });
      }
      setServerFiles([]);
      setSourceDir('');
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Upload failed', sev: 'error' });
    } finally {
      setUploadOnlyBusy(false);
      setUploadOnlyProgress({ uploaded: 0, total: 0 });
    }
  };

  const handleStartGroupingInitial = async () => {
    if (!sessionSubmissionIds.length || startingGrouping || groupingIngestStatus === 'processing') return;
    setStartingGrouping(true);
    // Snapshot now - sessionSubmissionIds can keep growing (another upload
    // can complete) while this run is in flight, and only these specific ids
    // should ever be cleared once pollStatus below sees this run finish,
    // never the whole list.
    const submittedIds = sessionSubmissionIds;
    try {
      await eventApi.ingestPhotoSubmissions(event.event_id, submittedIds, 'initial');
      pendingIngestSubmissionIdsRef.current = submittedIds;
      setGroupingIngestStatus('processing');
      setSnack({ open: true, msg: `Grouping ${submittedIds.length} photo${submittedIds.length === 1 ? '' : 's'}…`, sev: 'info' });
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'initial' });
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Failed to start grouping', sev: 'error' });
    } finally {
      setStartingGrouping(false);
    }
  };

  const handleRebuildSearch = async () => {
    setRebuildingSearch(true);
    try {
      await eventApi.rebuildSearchIndex(event.event_id);
      setSnack({ open: true, msg: 'Search index rebuild started', sev: 'info' });
      onStatusChange('processing');
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Failed to rebuild search index', sev: 'error' });
    } finally {
      setRebuildingSearch(false);
    }
  };

  // When an add-photos job is running, keep the main grouping panel in its
  // complete-gallery mode while the embedded add controls show progress.
  const addJobActive = isAddJob && (status === 'processing' || status === 'uploading' || status === 'starting');
  const effectiveStatus = addJobActive ? 'complete' : status;
  const isActive = !isAddJob && (status === 'processing' || status === 'uploading' || status === 'starting' || starting);
  // On native, CmUploadWorker (not this component) drives the actual upload -
  // `progress`/`statusComment` state only ever gets fed by the PWA's own
  // `_uploadBatches` hooks, so for a native run this derives the display from
  // the observed job instead (same pattern AddPhotosSection already uses for
  // its own resumeJob display).
  const nativeUploadPhase = resumeJob?.phase;
  const displayProgress = (Capacitor.isNativePlatform() && resumeJob)
    ? {
      processed: resumeJob.completedFiles,
      total: resumeJob.totalFiles,
      percent: resumeJob.totalFiles > 0 ? Math.round((resumeJob.completedFiles / resumeJob.totalFiles) * 100) : 0,
      current_image: '',
      phase: nativeUploadPhase,
    }
    : progress;
  const displayStatusComment = (Capacitor.isNativePlatform() && resumeJob)
    ? ({ compressing: 'Compressing photos…', uploading: 'Uploading photos…', processing: 'Finalising upload…' }[nativeUploadPhase] || 'Starting…')
    : statusComment;
  const progressPercent = Number.isFinite(Number(displayProgress.percent)) ? Number(displayProgress.percent) : 0;
  const hasProgressTotal = Number(displayProgress.total) > 0;
  const hasExistingGallery = Boolean(event.s3_output_prefix);
  // Auto-sync's upload-only batches land in autoSyncUploadedSubmissionIds
  // regardless of mode (see handleAutoSyncBatch) - only claim them into this
  // component's own accumulator while there's no gallery yet; once a gallery
  // exists, AddPhotosSection (the only thing rendered then) claims them instead.
  useEffect(() => {
    if (!hasExistingGallery && autoSyncUploadedSubmissionIds.length) {
      setSessionSubmissionIds(prev => [...prev, ...autoSyncUploadedSubmissionIds]);
      setAutoSyncUploadedSubmissionIds([]);
    }
  }, [autoSyncUploadedSubmissionIds, hasExistingGallery]);
  // A failed job with an existing gallery is most often an add/incremental
  // failure; keep the safe add retry as the default and leave full rebuild as
  // an explicit Re-run grouping action.
  const showInitialGroupingControls = effectiveStatus === 'none' || (effectiveStatus === 'failed' && !hasExistingGallery);

  const isPrimaryAction = showInitialGroupingControls;

  // Mirrors exactly the condition that decides whether <AddPhotosSection> is embedded
  // below (`(status === 'complete' || isAddJob || status === 'failed') && hasExistingGallery`).
  // `hasExistingGallery` alone is NOT enough: an event can have a leftover s3_output_prefix
  // from a previous run while grouping_status is 'none' (e.g. after a cancel reverted it) -
  // in that state the rest of the UI treats it as "start fresh" (status==='none' takes
  // priority in showInitialGroupingControls above), so auto-sync must make the identical
  // call or it'll try 'add' mode against a server that correctly rejects it with 409
  // ("Existing grouping must be complete before adding photos").
  const useAddModeForAutoSync = hasExistingGallery && (status === 'complete' || isAddJob || status === 'failed');

  // Local-folder auto-sync: lives here (not in AddPhotosSection) because this component
  // never unmounts for the life of the event page, so the watch/poll loop survives the
  // "no gallery yet" -> "gallery exists" transition. `handleAutoSyncBatch` decides at call
  // time (via the closed-over `useAddModeForAutoSync`) whether a detected batch should start
  // a brand-new job or increment the existing one - both share the exact same upload code
  // the manual buttons use.
  const handleAutoSyncBatch = async (entries, treeUri) => {
    console.log(`[Upload] handleAutoSyncBatch: event=${event.event_id} entries=${entries.length} mode=${useAddModeForAutoSync ? 'add' : 'initial'} alsoGroup=${autoSyncAlsoGroup} isNative=${Capacitor.isNativePlatform()}`);
    if (!autoSyncAlsoGroup) {
      // Upload only - lands photos in the submissions pool
      // (eventphotosubmissions), never triggers grouping. The organiser
      // starts grouping explicitly later via AddPhotosSection's "Start
      // grouping" button, fed by autoSyncUploadedSubmissionIds below.
      const isNative = Capacitor.isNativePlatform();
      if (isNative) {
        const jobId = makeUploadJobId(event.event_id, 'photo-submit-autosync');
        console.log(`[Upload] handleAutoSyncBatch: event=${event.event_id} jobId=${jobId} handing off to native CmUploadWorker (organiser-photo-submit, upload only)`);
        const startResult = await nativeUploadService.startUploadJob({
          jobId, flowKind: 'organiser-photo-submit', eventId: event.event_id,
          flowMeta: { canGroup: false },
          totalFiles: entries.length,
          entries,
          resumeSource: treeUri ? { kind: 'saf', treeUri } : null,
        });
        if (startResult?.alreadyActive) throw new Error('Another upload is already in progress for this event.');
        if (startResult?.failed) throw new Error('Upload failed to start.');
        // Deliberately awaited to completion (not fire-and-forget) - the caller
        // (useLocalAutoSync's resolveAndSubmit) treats this promise resolving as
        // "batch delivered, safe to mark seen and resume watching for new files",
        // and treats it throwing as "requeue this batch, keep the folder-scan
        // paused" (busyRef/scanningRef both stay true for as long as this is
        // pending - see doPoll's own guard). Previously this resolved the
        // instant startUploadJob's hand-off call returned - i.e. the moment the
        // job was merely enqueued, not once it actually finished. That made
        // useLocalAutoSync treat the batch as delivered (clearing pendingCount
        // back to 0/N and letting the very next 25s poll tick resume scanning
        // the folder) while CmUploadWorker was still compressing/uploading it in
        // the background, and - worse - meant a real failure in that background
        // job was never seen here at all: the batch had already been marked
        // "success" and dropped from pendingQueue/seenKeys reconciliation, so a
        // failed upload just silently vanished instead of being retried.
        // AddPhotosSection's own job-diffing effect (watching the same
        // 'organiser-photo-submit' flowKind) still separately picks up whatever
        // submission ids this job creates once it completes - that part is
        // unaffected by waiting here too.
        const job = await nativeUploadService.waitForJobCompletion(jobId);
        if (job?.outcome === 'failed') throw new Error(job.lastError || 'Upload failed.');
      } else {
        const ids = await uploadPhotoSubmissionsInBatches(event.event_id, entries);
        if (ids.length) setAutoSyncUploadedSubmissionIds(prev => [...prev, ...ids]);
      }
      return;
    }
    if (useAddModeForAutoSync) {
      const eventFaceModel = normaliseFaceModel(event.grouping_model);
      const accountFaceModel = normaliseFaceModel(accountFaceGroupingModel);
      if (accountFaceGroupingModelLoaded && eventFaceModel !== accountFaceModel) {
        throw new Error(`Switch your account face model back to ${faceModelLabel(eventFaceModel)} to auto-sync photos into this event.`);
      }
      const creditHold = await eventApi.initiateGrouping(event.event_id, 'add');
      const autoSyncHooks = { setLog: autoSync.setLog, setProgress: autoSync.setProgress, setStatusComment: autoSync.setStatusComment };
      const addOpts = {
        sourceDirLabel: autoSync.folderName,
        sourceModeOverride: 'local',
        faceModel: eventFaceModel,
        creditHoldId: creditHold.credit_hold_id,
      };
      if (Capacitor.isNativePlatform()) {
        // Hands off to CmUploadWorker (WorkManager), same flowKind AddPhotosSection's
        // "auto-group after auto-sync" branch uses - see EventPage.js's startInitialGroupingUpload for
        // the fuller rationale. TODO(Phase 8): this JS-side trigger (auto-sync
        // deciding *when* a batch uploads) is itself superseded once
        // AutoSyncCheckWorker exists - at that point native decides when to
        // upload autonomously, even with the app fully closed, and this
        // branch goes away entirely rather than just changing its target.
        const jobId = makeUploadJobId(event.event_id, 'add-autosync');
        console.log(`[Upload] handleAutoSyncBatch: event=${event.event_id} jobId=${jobId} handing off to native CmUploadWorker (add-photos)`);
        await nativeUploadService.startUploadJob({
          jobId, flowKind: 'add-photos', eventId: event.event_id,
          flowMeta: { ...addOpts, source_directory: addOpts.sourceDirLabel, source_mode: addOpts.sourceModeOverride, credit_hold_id: addOpts.creditHoldId, model: addOpts.faceModel },
          totalFiles: entries.length,
          entries,
          resumeSource: treeUri ? { kind: 'saf', treeUri } : null,
        });
      } else {
        const payload = {
          source_directory: addOpts.sourceDirLabel, source_mode: addOpts.sourceModeOverride, limit: 0,
          model: addOpts.faceModel, credit_hold_id: addOpts.creditHoldId,
          organizer_terms_ack: true, policy_version: PRIVACY_POLICY_VERSION,
        };
        await runOrganiserUploadBatches(event, entries, 'add', payload, { hooks: autoSyncHooks, uploadCancelledRef, uploadAbortRef });
      }
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'add' });
      // Upload finishing only means the photos reached S3 - the batch isn't "delivered"
      // (safe to drop from the auto-sync retry queue) until grouping itself succeeds.
      // If this throws (grouping failed), useLocalAutoSync keeps the batch queued so the
      // next threshold-hit or manual "Sync now" retries it instead of silently losing it.
      await waitForGroupingCompletion(event.event_id, { hooks: autoSyncHooks, onStatusChange, jobType: 'add' });
      onGalleryRefresh?.();
    } else {
      // No `hooks` override here - this component (SmartSortSection) renders its own
      // Step1/2 + progress UI whenever !hasExistingGallery, so the default setProgress/
      // setStatusComment/setLog (this component's own state) already drive that UI
      // whether the upload was triggered manually or by auto-sync.
      await startInitialGroupingUpload(entries, autoSync.folderName, {
        sourceModeOverride: 'local',
        skipConsentCheck: true,
        treeUri,
      });
      // Same reasoning as the add-branch: don't let useLocalAutoSync mark this batch
      // delivered until grouping actually finishes, not just once upload finishes.
      // SmartSortSection's own pollStatus effect is also polling status for display
      // purposes; this loop is solely about the auto-sync engine's success/retry gate.
      await waitForGroupingCompletion(event.event_id, { onStatusChange, jobType: 'initial' });
    }
  };

  // Called once on mount only when useLocalAutoSync finds a batch that was still
  // in flight when this event page was last closed/refreshed (see useLocalAutoSync's
  // onReattach doc comment). Checks the event's *current* server-side status/job_type
  // (this is the freshly-fetched `event` prop, not a stale local guess) to figure out
  // whether that old submission is still running, and if so waits for its real
  // outcome instead of blindly resubmitting the same files (which would collide with
  // the still-running job) or blindly discarding them (which could silently drop photos).
  const reattachIfJobInFlight = async () => {
    const expectedJobType = useAddModeForAutoSync ? 'add' : 'initial';
    const currentStatus = event.grouping_status || 'none';
    const currentJobType = event.grouping_job_type || 'initial';
    const nonTerminal = currentStatus === 'starting' || currentStatus === 'processing' || currentStatus === 'uploading';
    console.log(`[Upload] reattachIfJobInFlight: event=${event.event_id} expectedJobType=${expectedJobType} currentJobType=${currentJobType} currentStatus=${currentStatus} nonTerminal=${nonTerminal}`);
    if (currentJobType !== expectedJobType || !nonTerminal) {
      // Nothing recognisable still running for our expected job type. We can't prove
      // the interrupted batch was delivered, so only skip the retry if status already
      // shows a successful completion; otherwise err on the side of retrying.
      if (currentStatus === 'complete') {
        console.log(`[Upload] reattachIfJobInFlight: event=${event.event_id} already complete, treating interrupted batch as delivered`);
        return;
      }
      console.log(`[Upload] reattachIfJobInFlight: event=${event.event_id} no matching job in flight - forcing requeue`);
      throw new Error('Previous sync was interrupted before finishing - retrying.');
    }
    console.log(`[Upload] reattachIfJobInFlight: event=${event.event_id} matching job still running - waiting for it to finish instead of resending`);
    const hooks = useAddModeForAutoSync
      ? { setLog: autoSync.setLog, setProgress: autoSync.setProgress, setStatusComment: autoSync.setStatusComment }
      : {};
    await waitForGroupingCompletion(event.event_id, { hooks, onStatusChange, jobType: expectedJobType });
    if (useAddModeForAutoSync) onGalleryRefresh?.();
  };
  const autoSync = useLocalAutoSync({ eventId: event.event_id, onBatchReady: handleAutoSyncBatch, onReattach: reattachIfJobInFlight });

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5, borderRadius: 2, mb: 2,
        ...(isPrimaryAction && { borderColor: 'primary.main', borderWidth: 2, boxShadow: '0 0 0 3px rgba(199, 56, 102, 0.06)' }),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Smart Sort</Typography>
        <Chip label={STATUS_LABEL[effectiveStatus] || effectiveStatus} color={STATUS_COLOR[effectiveStatus] || 'default'} size="small" />
      </Stack>

      {status === 'failed' && hasExistingGallery && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={rebuildingSearch ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              disabled={rebuildingSearch}
              onClick={handleRebuildSearch}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Rebuild Search
            </Button>
          }
        >
          The last background task failed, but the existing grouped gallery is still available.
        </Alert>
      )}

      {showInitialGroupingControls && (
        <>
          {status === 'failed' && <Alert severity="error" sx={{ mb: 1.5 }}>Previous grouping failed. You can retry.</Alert>}
          {billingError && (
            <Alert
              severity="warning"
              sx={{ mb: 1.5 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/profile')} sx={{ whiteSpace: 'nowrap' }}>
                  Add credits
                </Button>
              }
            >
              {billingError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Pick the folder with this event's photos - we'll group them by face so they're easy to organise and find. Nothing is sent to guests automatically; you stay in control of what gets shared.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Run on CircleMoments Server <Box component="span" sx={{ color: 'text.disabled' }}>(local compute run coming soon)</Box>
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif"
            onChange={handleServerDirectoryChange}
            style={{ display: 'none' }}
          />
          <input
            ref={directoryInputRef}
            type="file"
            multiple
            webkitdirectory="true"
            directory=""
            accept=".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif"
            onChange={handleServerDirectoryChange}
            style={{ display: 'none' }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 1 &middot; Select photos
              </Typography>
              {autoSync.armed ? (
                <>
                  <AutoSyncStatusCard autoSync={autoSync} />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={autoSyncAlsoGroup ? 'group' : 'upload'}
                    onChange={(e, value) => { if (value) setAutoSyncAlsoGroup(value === 'group'); }}
                    sx={{ mt: 0.75 }}
                  >
                    <ToggleButton value="upload" sx={{ px: 1.5, py: 0.25, lineHeight: 1.2 }}>
                      <Typography variant="caption">Upload only</Typography>
                    </ToggleButton>
                    <ToggleButton value="group" sx={{ px: 1.5, py: 0.25, lineHeight: 1.2 }}>
                      <Typography variant="caption">Upload + Group</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </>
              ) : (
                <>
                  <ChooseFolderDropdown
                    driveConnected={Boolean(driveConnected)}
                    onLocalFilesSelect={handleLocalFilesSelect}
                    onLocalFolderSelect={handleLocalFolderSelect}
                    onDriveSelect={handleDriveFolderSelect}
                    onAutoSyncSelect={async () => {
                      const consentOk = await confirmFaceGroupingConsent();
                      if (!consentOk) return;
                      autoSync.selectFolder(promptAutoSyncIncludeExisting, promptStopOtherAutoSync);
                    }}
                    autoSyncSupported={autoSync.supported}
                    disabled={starting || autoSync.scanning || manualFolder.scanning}
                  />
                  <AutoSyncScanProgress autoSync={autoSync} />
                  {/* Manual (non-auto-sync) "Choose local folder" scan - reuses the same
                      progress/cancel UI as auto-sync's own folder scan (see
                      useManualFolderPersistence's scanFolder), so a big folder gives
                      real feedback here too instead of "No photos selected yet" just
                      sitting there for tens of seconds. */}
                  <AutoSyncScanProgress autoSync={manualFolder} />
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                    {sourceMode === 'cloud-google'
                      ? <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png" alt="Drive" sx={{ width: 14, height: 14, flexShrink: 0 }} />
                      : <FolderOpenIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />}
                    <Typography variant="caption" color={selectedServerImages.length ? 'success.main' : 'text.secondary'}>
                      {selectedServerImages.length
                        ? `${selectedServerImages.length} ${sourceMode === 'cloud-google' ? 'Drive' : 'local'} photos selected${sourceDir && sourceDir !== 'Selected photos' ? ` from "${sourceDir}"` : ''}.`
                        : 'No photos selected yet.'}
                    </Typography>
                  </Stack>
                  {manualFolder.reconnect && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
                      <Stack spacing={0.75} alignItems="flex-start">
                        <span>Continue with "{manualFolder.reconnect.folderName}"?</span>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={manualFolder.reconnectNow}>Reconnect</Button>
                          <Button
                            size="small"
                            sx={{ opacity: 0.7 }}
                            onClick={async () => {
                              const ok = await swalConfirmDelete({
                                title: 'Reset this folder?',
                                text: "This forgets the picked folder for this event - you'll need to choose it (or a different one) again.",
                                confirmText: 'Reset',
                              });
                              if (ok) await manualFolder.reset();
                            }}
                          >
                            Reset
                          </Button>
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                </>
              )}
            </Box>

            <ArrowForwardIcon sx={{ color: 'grey.300', mt: 4, display: { xs: 'none', sm: 'block' } }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 2 &middot; Upload
              </Typography>
              <Button
                variant="contained"
                startIcon={uploadOnlyBusy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleUploadOnlyInitial}
                disabled={uploadOnlyBusy || !selectedServerImages.length || Boolean(resumeJob)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {uploadOnlyBusy
                  ? (uploadOnlyProgress.total > 0 ? `Uploading ${uploadOnlyProgress.uploaded}/${uploadOnlyProgress.total}…` : 'Uploading…')
                  : 'Upload'}
              </Button>
              {!Capacitor.isNativePlatform() && selectedServerImages.length > LARGE_WEB_UPLOAD_WARNING_THRESHOLD && !uploadOnlyBusy && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedServerImages.length} photos is a big upload for a browser tab - keep this tab open until it finishes. The mobile app can do this in the background instead.
                </Typography>
              )}
              {sessionSubmissionIds.length > 0 && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                  {sessionSubmissionIds.length} photo{sessionSubmissionIds.length === 1 ? '' : 's'} selected and uploaded (see Photo Submissions tab).
                </Typography>
              )}
            </Box>

            <ArrowForwardIcon sx={{ color: 'grey.300', mt: 4, display: { xs: 'none', sm: 'block' } }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 3 &middot; Start grouping
              </Typography>
              <Tooltip title={!sessionSubmissionIds.length ? 'Upload photos first' : `Group the ${sessionSubmissionIds.length} photo(s) uploaded in this window - not any other pending submissions`}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={(startingGrouping || groupingIngestStatus === 'processing') ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleStartGroupingInitial}
                    disabled={!sessionSubmissionIds.length || startingGrouping || groupingIngestStatus === 'processing' || Boolean(resumeJob)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {startingGrouping ? 'Starting…' : groupingIngestStatus === 'processing' ? `Grouping… ${progress.percent || 0}%` : (status === 'failed' ? 'Retry' : `Start grouping${sessionSubmissionIds.length ? ` (${sessionSubmissionIds.length})` : ''}`)}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Stack>

          <ResumeUploadBanner
            job={resumeJob}
            sx={{ mt: 1, py: 0.5 }}
            onCancel={async () => {
              // A resumed job has no live component to hold uploadCancelledRef/
              // uploadAbortRef for (see resumeUploadJob.js's own doc comment on
              // why) - cancelJob is the only thing that can actually stop it.
              await nativeUploadService.cancelJob({ jobId: resumeJob.jobId }).catch(() => {});
              try {
                await eventApi.cancelGrouping(event.event_id);
              } catch {
                // Local state still clears below; backend cleanup may already be complete.
              }
              autoSync?.cancelInFlightBatch?.();
              setResumeJob(null);
              onStatusChange({ grouping_status: 'none', grouping_job_type: 'initial' });
            }}
          />

          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => setAdvancedOpen(v => !v)}
              sx={{ textTransform: 'none', color: 'text.secondary', px: 0, minWidth: 0 }}
            >
              Advanced{advancedOpen ? ' ▴' : ' ▾'}
            </Button>
            <Collapse in={advancedOpen}>
              <TextField
                label="Photo limit"
                value={photoLimit}
                onChange={e => setPhotoLimit(e.target.value.replace(/[^\d]/g, ''))}
                size="small"
                placeholder="Default"
                helperText="Only process the first N photos in the folder (leave blank to process all)."
                sx={{ width: { xs: '100%', sm: 200 }, mt: 1 }}
                inputProps={{ inputMode: 'numeric', min: 0 }}
              />
              <AutoMatchToggle event={event} onStatusChange={onStatusChange} />
            </Collapse>
          </Box>
        </>
      )}

      {isActive && (
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">{STATUS_LABEL[status]}  This can take 5-30 min depending on photo count.</Typography>
            </Stack>
            <Button
              size="small" color="error" variant="outlined"
              onClick={async () => {
                try {
                  uploadCancelledRef.current = true;
                  uploadAbortRef.current?.abort();
                  // isActive covers a native job too (starting is part of its
                  // condition) - without this, cancelGrouping only resets the
                  // server's status and CmUploadWorker keeps running until it
                  // happens to notice on its own ~8s grouping-status poll.
                  if (Capacitor.isNativePlatform() && resumeJob) {
                    await nativeUploadService.cancelJob({ jobId: resumeJob.jobId }).catch(() => {});
                    setResumeJob(null);
                  }
                  await eventApi.cancelGrouping(event.event_id);
                  clearInterval(pollingRef.current);
                  onStatusChange({ grouping_status: 'none', grouping_job_type: 'initial' });
                  setLog('');
                  setProgress({ processed: 0, total: 0, percent: 0, current_image: '', phase: 'starting' });
                  setStatusComment('Starting...');
                  setSnack({ open: true, msg: 'Grouping cancelled.', sev: 'info' });
                } catch (e) {
                  setSnack({ open: true, msg: e.message || 'Failed to cancel', sev: 'error' });
                }
              }}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Cancel
            </Button>
          </Stack>
          <Box>
            <Typography variant="body2" fontWeight={700}>{displayStatusComment || STATUS_LABEL[status]}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {hasProgressTotal
                ? `${displayProgress.processed || 0} of ${displayProgress.total} images${displayProgress.current_image ? ` - ${displayProgress.current_image}` : ''}`
                : 'Preparing image list...'}
            </Typography>
            <LinearProgress
              variant={hasProgressTotal ? 'determinate' : 'indeterminate'}
              value={Math.max(0, Math.min(progressPercent, 100))}
              sx={{ height: 8, borderRadius: 999 }}
            />
            {hasProgressTotal && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {Math.max(0, Math.min(progressPercent, 100))}% complete
              </Typography>
            )}
          </Box>
          {(starting || status === 'starting') ? (
            shouldShowUploadWarning() ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{UPLOAD_IN_PROGRESS_WARNING}</Alert>
            ) : shouldShowBackgroundableNotice() ? (
              <Alert severity="info" sx={{ py: 0.5 }}>{UPLOAD_BACKGROUNDABLE_NOTICE}</Alert>
            ) : null
          ) : status === 'processing' ? (
            <Alert severity="success" sx={{ py: 0.5 }}>{GROUPING_IN_PROGRESS_NOTICE}</Alert>
          ) : null}
          {/* Processing log hidden from frontend display (backend logging unaffected)
          {log && (
            <Box component="details" sx={{ mt: 0.5 }}>
              <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary', fontSize: '0.875rem' }}>
                Processing log
              </Box>
              <Box sx={{ bgcolor: '#111', color: '#cfc', borderRadius: 1, p: 1.5, mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {log}
              </Box>
            </Box>
          )}
          */}
        </Stack>
      )}

      {((status === 'complete' || isAddJob || status === 'failed') && hasExistingGallery) && (
        <Stack spacing={1.5}>
          {status === 'failed' && (
            <Alert severity="warning">
              The last grouping task failed, but the existing gallery is still available. You can retry adding photos below or explicitly re-run grouping from scratch.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {status === 'failed' ? 'Existing grouped photos are still ready.' : 'Grouping complete - photos are ready.'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => onStatusChange('none')}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Re-run grouping
            </Button>
          </Stack>
          <Divider />
          <AddPhotosSection
            event={event}
            onStatusChange={(newStatus) => {
              onStatusChange(newStatus);
              if (newStatus === 'complete') {
                onGalleryRefresh?.();
              }
            }}
            onGalleryRefresh={onGalleryRefresh}
            driveConnected={driveConnected}
            driveEmail={driveEmail}
            accountFaceGroupingModel={accountFaceGroupingModel}
            accountFaceGroupingModelLoaded={accountFaceGroupingModelLoaded}
            autoSync={autoSync}
            autoSyncUploadedSubmissionIds={autoSyncUploadedSubmissionIds}
            onAutoSyncSubmissionIdsConsumed={() => setAutoSyncUploadedSubmissionIds([])}
            autoSyncAlsoGroup={autoSyncAlsoGroup}
            onAutoSyncAlsoGroupChange={setAutoSyncAlsoGroup}
            embedded
          />
        </Stack>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}

// ── Attendee: their photo request details ─────────────────────────────────────
function MyRequestSection({ event, onSubmissionsChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [addRequestOpen, setAddRequestOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [requests, setRequests] = useState(undefined);
  const requestsScrollRef = useRef(null);
  const scrollRequests = (dir) => {
    requestsScrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };
  const [referenceUrls, setReferenceUrls] = useState({});
  const [editingId, setEditingId] = useState('');
  const [savingId, setSavingId] = useState('');
  const [editForm, setEditForm] = useState({ name: '', email_address: '', phone_number: '', notes: '' });
  const [newReferencePhoto, setNewReferencePhoto] = useState(null);
  const [editError, setEditError] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [selfieCamOpen, setSelfieCamOpen] = useState(false);
  const [editingSecondaryPhotos, setEditingSecondaryPhotos] = useState([]);

  async function loadRequests() {
    try {
      const data = await eventApi.getMyRequests(event.event_id);
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch {
      setRequests([]);
    }
  }

  // Load only on first expand
  useEffect(() => {
    if (!expanded || loaded) return;
    setLoaded(true);
    setRequests(undefined);
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (!Array.isArray(requests) || !requests.length) return undefined;
    let active = true;
    const objectUrls = [];
    Promise.all(requests.map(async req => {
      try {
        const url = await eventApi.getRequestPhotoBlobUrl(event.event_id, req.request_id);
        objectUrls.push(url);
        return [req.request_id, url];
      } catch {
        return [req.request_id, ''];
      }
    })).then(entries => {
      if (!active) return;
      setReferenceUrls(Object.fromEntries(entries));
    });
    return () => {
      active = false;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [event.event_id, requests]);

  const requestCount = Array.isArray(requests) ? requests.length : null;

  function beginEdit(req) {
    setEditingId(req.request_id);
    setEditMessage('');
    setEditError('');
    setNewReferencePhoto(null);
    setEditForm({
      name: req.name || '',
      email_address: req.email_address || '',
      phone_number: req.phone_number || '',
      notes: req.notes || '',
    });
    setEditingSecondaryPhotos([]);
    if (req.has_selfie) {
      eventApi.listRequestSecondaryPhotos(event.event_id, req.request_id)
        .then(res => setEditingSecondaryPhotos(res.photos || []))
        .catch(() => setEditingSecondaryPhotos([]));
    }
  }

  async function addEditingSecondaryPhoto(file) {
    const result = await eventApi.addRequestSecondaryPhoto(event.event_id, editingId, file);
    setEditingSecondaryPhotos(prev => [...prev, { photo_key: result.photo_key, url: URL.createObjectURL(file) }]);
  }

  async function removeEditingSecondaryPhoto(photoKey) {
    await eventApi.deleteRequestSecondaryPhoto(event.event_id, editingId, photoKey);
    setEditingSecondaryPhotos(prev => prev.filter(p => p.photo_key !== photoKey));
  }

  function onEditFileSelected(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setEditError('Please upload an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setEditError('Image must be less than 10 MB.'); return; }
    setNewReferencePhoto(file);
    setEditError('');
  }

  async function resubmitRequest(requestId) {
    const req = (requests || []).find(item => item.request_id === requestId);
    if (!editForm.name.trim() || (!editForm.email_address.trim() && !editForm.phone_number.trim())) {
      setEditError('Please fill in your name and either an email address or phone number.');
      return;
    }
    const addingSelfie = newReferencePhoto && !req?.has_selfie;
    if (addingSelfie) {
      const agreed = await swalConfirm({
        title: 'Selfie Consent',
        text: 'CircleMoments will store this reference selfie and compare it with event face groups to find your photos for this event. Do you agree?',
        confirmText: 'I Agree',
        cancelText: 'Cancel',
      });
      if (!agreed) return;
    }
    setSavingId(requestId);
    setEditError('');
    setEditMessage('');
    try {
      const formData = new FormData();
      formData.append('name', editForm.name.trim());
      formData.append('email_address', editForm.email_address.trim());
      formData.append('phone_number', editForm.phone_number.trim());
      formData.append('notes', editForm.notes.trim());
      if (newReferencePhoto) {
        formData.append('reference_photo', newReferencePhoto);
        if (addingSelfie) formData.append('consent_reference_face_match', 'true');
      }
      const updated = await eventApi.updateMySpecificRequest(event.event_id, requestId, formData);
      setRequests(prev => prev.map(item => item.request_id === requestId ? updated : item));
      setEditingId('');
      setNewReferencePhoto(null);
      setEditMessage('Submission updated.');
      onSubmissionsChanged?.();
    } catch (e) {
      setEditError(e.message || 'Failed to update submission.');
    } finally {
      setSavingId('');
    }
  }

  async function deleteRequest(requestId) {
    const ok = await swalConfirmDelete({
      title: 'Delete submission?',
      text: 'Any photos shared only through this request will be removed from your photos.',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await eventApi.deleteMyRequest(event.event_id, requestId);
      setRequests(prev => prev.filter(item => item.request_id !== requestId));
      setEditMessage('Submission deleted.');
      onSubmissionsChanged?.();
    } catch (e) {
      setEditError(e.message || 'Failed to delete submission.');
    }
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      {/* Collapsible header */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setExpanded(p => !p)}
        sx={{ px: 2.5, py: 1.5, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Your Requests{requestCount !== null ? ` (${requestCount})` : ''}
        </Typography>
        <IconButton size="small" onClick={e => { e.stopPropagation(); setExpanded(p => !p); }} aria-label={expanded ? 'Collapse' : 'Expand'}>
          <ExpandMoreIcon sx={{ transition: 'transform 200ms', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </IconButton>
      </Stack>

      {/* Body - only rendered when expanded */}
      {expanded && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          {requests === undefined && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
          {Array.isArray(requests) && !requests.length && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              You haven't submitted a photo request for this event yet.
            </Alert>
          )}
          {editMessage && <Alert severity="success" sx={{ mb: 1.5 }}>{editMessage}</Alert>}
          {editError && <Alert severity="error" sx={{ mb: 1.5 }}>{editError}</Alert>}
          {Array.isArray(requests) && requests.length > 0 && (
            <Box sx={{ position: 'relative', mt: 1 }}>
              {requests.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => scrollRequests(1)}
                  aria-label="Scroll to see more requests"
                  sx={{
                    position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                    zIndex: 1, bgcolor: 'background.paper', boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              )}
              <Stack ref={requestsScrollRef} direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
              {requests.map((req, index) => {
                const editing = editingId === req.request_id;
                return (
                  <Paper
                    key={req.request_id}
                    variant="outlined"
                    sx={{ minWidth: 300, maxWidth: 360, p: 1.75, borderRadius: 2, bgcolor: index % 2 === 0 ? '#f8fbff' : '#fff8f1', borderColor: index % 2 === 0 ? '#bfdbfe' : '#fed7aa' }}
                  >
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip label={req.request_id} size="small" variant="outlined" />
                        {!editing && (
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" onClick={() => beginEdit(req)} sx={{ textTransform: 'none' }}>Edit</Button>
                            <Button size="small" color="error" onClick={() => deleteRequest(req.request_id)} sx={{ textTransform: 'none' }}>Delete</Button>
                          </Stack>
                        )}
                      </Stack>
                      {referenceUrls[req.request_id] ? (
                        <Box component="img" src={referenceUrls[req.request_id]} alt="Reference selfie" sx={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 1.5 }} />
                      ) : !req.has_selfie && !editing && (
                        <Box
                          onClick={() => beginEdit(req)}
                          sx={{
                            width: '100%', height: 130, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2">+ Add a selfie</Typography>
                        </Box>
                      )}
                      {editing ? (
                        <Stack spacing={1}>
                          <TextField label="Full name" size="small" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} fullWidth />
                          <TextField label="Email address" type="email" size="small" value={editForm.email_address} onChange={e => setEditForm(f => ({ ...f, email_address: e.target.value }))} fullWidth />
                          <TextField label="Phone number" type="tel" size="small" value={editForm.phone_number} onChange={e => setEditForm(f => ({ ...f, phone_number: e.target.value }))} fullWidth />
                          <TextField label="Notes" size="small" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline minRows={2} inputProps={{ maxLength: 2000 }} />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddPhotoAlternateIcon />}
                              onClick={() => setSelfieCamOpen(true)}
                              sx={{ textTransform: 'none' }}
                            >
                              {newReferencePhoto ? newReferencePhoto.name : req.has_selfie ? 'Replace selfie' : 'Add a selfie'}
                            </Button>
                            <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none', minWidth: 0, px: 1 }} title="Upload from gallery">
                              <FileUploadOutlinedIcon fontSize="small" />
                              <input hidden type="file" accept="image/*" onChange={e => onEditFileSelected(e.target.files?.[0])} />
                            </Button>
                          </Stack>
                          {req.has_selfie && !newReferencePhoto && (
                            <ReferencePhotoRow
                              photos={editingSecondaryPhotos}
                              onAdd={addEditingSecondaryPhoto}
                              onRemove={removeEditingSecondaryPhoto}
                              label="Additional reference photos"
                            />
                          )}
                          <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" onClick={() => resubmitRequest(req.request_id)} disabled={savingId === req.request_id}>
                              {savingId === req.request_id ? 'Saving...' : 'Save'}
                            </Button>
                            <Button size="small" onClick={() => setEditingId('')} disabled={savingId === req.request_id}>Cancel</Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack spacing={0.4}>
                          <Typography fontWeight={700}>{req.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{req.email_address}</Typography>
                          <Typography variant="body2" color="text.secondary">{req.phone_number}</Typography>
                          {req.notes && <Typography variant="body2" color="text.secondary">Notes: {req.notes}</Typography>}
                          <Typography variant="caption" color="text.disabled">
                            Submitted {req.created_at ? new Date(req.created_at).toLocaleDateString() : '-'}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
              </Stack>
            </Box>
          )}
          {Array.isArray(requests) && (
            <Button size="small" variant="outlined" onClick={() => setAddRequestOpen(true)} sx={{ mt: 1.5, textTransform: 'none' }}>
              Add Request
            </Button>
          )}
        </Box>
      )}
      <PhotoRequestFormDialog
        open={addRequestOpen}
        onClose={() => setAddRequestOpen(false)}
        eventId={event.event_id}
        inviteCode={event.invite_code}
        mode="photos"
        onSuccess={() => {
          setAddRequestOpen(false);
          loadRequests();
          onSubmissionsChanged?.();
        }}
      />
      <SelfieCaptureDialog
        open={selfieCamOpen}
        onClose={() => setSelfieCamOpen(false)}
        onCapture={file => { onEditFileSelected(file); setSelfieCamOpen(false); }}
      />
    </Paper>
  );
}

// ── Attendee: photos received placeholder ────────────────────────────────────
function MyPhotosSection({ event, refreshKey = 0 }) {
  const [payload, setPayload] = useState(undefined);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [originalLoading, setOriginalLoading] = useState(false);
  const [ucByPhotoId, setUcByPhotoId] = useState({});
  const [photoInfoByPhotoId, setPhotoInfoByPhotoId] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [ucDownloading, setUcDownloading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const toast = useCallback((msg, sev = 'info') => setSnack({ open: true, msg: String(msg), sev }), []);

  useEffect(() => {
    let active = true;
    setPayload(undefined);
    eventApi.getMyPhotos(event.event_id)
      .then(data => { if (active) setPayload(data || { tracks: [] }); })
      .catch(() => { if (active) setPayload({ tracks: [] }); });
    // Gallery metadata carries uc_org_url (resolved live for cloud-google
    // events) - reused here purely to know which of "my photos" genuinely
    // have a distinct uncompressed original, so the download menu can grey
    // out the option instead of silently re-downloading the compressed copy.
    // Also reused (photoInfoByPhotoId) to back the photo-info panel, since
    // gallery-metadata photos already carry captured_at/exif/submitted_by_*
    // (see resolve_uc_file_metadata_batch) - no separate fetch needed.
    eventApi.getGalleryMetadata(event.event_id)
      .then(meta => {
        if (!active) return;
        const data = meta?.data ?? meta;
        const photos = Array.isArray(data) ? data : Array.isArray(data?.photos) ? data.photos : Array.isArray(meta?.photos) ? meta.photos : [];
        const map = {};
        const infoMap = {};
        photos.forEach(p => {
          if (!p?.photo_id) return;
          map[String(p.photo_id)] = Boolean(String(p.uc_org_url || '').trim());
          infoMap[String(p.photo_id)] = p;
        });
        setUcByPhotoId(map);
        setPhotoInfoByPhotoId(infoMap);
      })
      .catch(() => { if (active) setUcByPhotoId({}); });
    return () => { active = false; };
  }, [event.event_id, refreshKey]);

  useEffect(() => { setDownloadMenuAnchor(null); setInfoOpen(false); }, [selectedPhoto]);

  function downloadPhoto(photoId) {
    if (!photoId) return;
    downloadUrlAsBlob(eventApi.originalUrl(event.event_id, photoId), `circlemoments-${event.event_id}-${photoId}.jpg`)
      .catch((err) => toast(err.message || 'Download failed.', 'error'));
  }

  function downloadMany(photoIds) {
    const uniqueIds = Array.from(new Set((photoIds || []).filter(Boolean)));
    uniqueIds.forEach((photoId, index) => {
      window.setTimeout(() => downloadPhoto(photoId), index * 250);
    });
  }

  function downloadUcOriginalPhoto(photoId) {
    if (!photoId || ucDownloading) return;
    downloadUcOriginal(eventApi.requestUcOriginalDownload, event.event_id, photoId, {
      filename: `circlemoments-${event.event_id}-${photoId}-uncompressed.jpg`,
      onActiveChange: setUcDownloading,
    }).catch((err) => toast(err.message || 'Download failed.', 'error'));
  }

  function sharePhotoNow(photoId) {
    if (!photoId) return;
    sharePhoto(eventApi.originalUrl(event.event_id, photoId), `circlemoments-${event.event_id}-${photoId}.jpg`, {
      title: event.event_name,
      text: `Photo from ${event.event_name || 'this event'} on Circle Moments`,
    }).catch((err) => toast(err.message || 'Share failed.', 'error'));
  }

  if (payload === undefined) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary" mt={1}>Loading your photos...</Typography>
      </Paper>
    );
  }

  const tracks = Array.isArray(payload.tracks) ? payload.tracks : [];
  if (!tracks.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <PhotoLibraryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>Your Photos</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Photos assigned to you by the organiser will appear here.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <UcDownloadProgressBar active={ucDownloading} />
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PhotoLibraryIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Your Photos</Typography>
            <Chip label={`${payload.photo_ids?.length || 0} photos`} size="small" variant="outlined" />
          </Stack>
          <Button size="small" variant="outlined" onClick={() => downloadMany(payload.photo_ids || [])} sx={{ textTransform: 'none' }}>
            Download all shared
          </Button>
        </Stack>
        <Stack spacing={2}>
          {tracks.map((track, index) => {
            const ids = Array.isArray(track.photo_ids) ? track.photo_ids : [];
            return (
              <Box key={`${track.source || 'track'}-${track.id || index}`}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={800}>{track.label || 'Shared photos'}</Typography>
                    <Chip
                      label={track.source === 'circle' ? 'Circle shared' : 'Requested'}
                      size="small"
                      color={track.source === 'circle' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                  <Button size="small" variant="text" onClick={() => downloadMany(ids)} sx={{ textTransform: 'none' }}>
                    Download track
                  </Button>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)', md: 'repeat(8, 1fr)' }, gap: 1 }}>
                  {ids.map(photoId => (
                    <Box
                      key={`${track.id}-${photoId}`}
                      component="button"
                      type="button"
                      onClick={() => { setOriginalLoading(true); setSelectedPhoto({ photoId, track }); }}
                      sx={{ border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer', borderRadius: 1.25, overflow: 'hidden' }}
                    >
                      <Box
                        component="img"
                        src={eventApi.thumbUrl(event.event_id, photoId)}
                        alt=""
                        loading="lazy"
                        sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block', bgcolor: 'grey.100' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      <Dialog open={Boolean(selectedPhoto)} onClose={() => setSelectedPhoto(null)} maxWidth="md" fullWidth>
        {selectedPhoto && (
          <>
            <DialogContent sx={{ p: 0, bgcolor: 'grey.950' }}>
              <Box sx={{ position: 'relative', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {originalLoading && (
                  <CircularProgress size={28} sx={{ position: 'absolute', color: 'common.white' }} />
                )}
                <Tooltip title={infoOpen ? 'Hide photo info' : 'Photo info'}>
                  <IconButton
                    onClick={() => setInfoOpen((v) => !v)}
                    sx={{
                      position: 'absolute', top: 8, left: 8, zIndex: 1, color: 'common.white',
                      bgcolor: infoOpen ? 'primary.main' : 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: infoOpen ? 'primary.dark' : 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Box
                  component="img"
                  src={eventApi.originalUrl(event.event_id, selectedPhoto.photoId)}
                  alt=""
                  onLoad={() => setOriginalLoading(false)}
                  onError={() => setOriginalLoading(false)}
                  sx={{ width: '100%', maxHeight: '78vh', objectFit: 'contain', display: 'block', bgcolor: '#111' }}
                />
              </Box>
            </DialogContent>
            <Collapse in={infoOpen}>
              <PhotoInfoPanel photo={photoInfoByPhotoId[String(selectedPhoto.photoId)]} sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />
            </Collapse>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} noWrap>{selectedPhoto.track?.label || 'Shared photo'}</Typography>
                <Typography variant="caption" color="text.secondary">{selectedPhoto.photoId}</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Share photo">
                  <IconButton onClick={() => sharePhotoNow(selectedPhoto.photoId)}>
                    <IosShareIcon />
                  </IconButton>
                </Tooltip>
                <Stack direction="row" spacing={0}>
                  <Button variant="outlined" onClick={() => downloadPhoto(selectedPhoto.photoId)} sx={{ textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                    Download photo
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none', minWidth: 0, px: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                    aria-label="More download options"
                  >
                    ▾
                  </Button>
                </Stack>
                <Menu anchorEl={downloadMenuAnchor} open={Boolean(downloadMenuAnchor)} onClose={() => setDownloadMenuAnchor(null)}>
                  <MenuItem
                    disabled={!ucByPhotoId[String(selectedPhoto.photoId)] || ucDownloading}
                    onClick={() => { setDownloadMenuAnchor(null); downloadUcOriginalPhoto(selectedPhoto.photoId); }}
                  >
                    Download uncompressed original
                  </MenuItem>
                </Menu>
                <Button variant="contained" onClick={() => setSelectedPhoto(null)} sx={{ textTransform: 'none' }}>
                  Close
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}

// ── Attendee: submit photos for organiser review ──────────────────────────────
function AttendeePhotoUploadSection({ event }) {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [mySubmissions, setMySubmissions] = useState(null); // null = not yet loaded
  const fileInputRef = useRef(null);

  // Reconciliation for a submission still running (native only) when this
  // page was last closed - see SmartSortSection's/AddPhotosSection's/
  // UploadLinkPage.js's matching effects and resumeUploadJob.js's doc
  // comment for why this must actually resume the job, not just display it.
  const [resumeJob, setResumeJob] = useState(null);
  const resumedJobIdsRef = useRef(new Set());
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let intervalId = null;
    const check = async () => {
      const { jobs } = await nativeUploadService.listIncompleteJobs();
      if (cancelled) return;
      const mine = (jobs || []).find((j) => String(j.eventId) === String(event.event_id) && j.flowKind === 'attendee-submit');
      setResumeJob(mine || null);
      if (mine && !resumedJobIdsRef.current.has(mine.jobId)) {
        resumedJobIdsRef.current.add(mine.jobId);
        runUploadJob(mine, {}).then(() => loadMySubmissions()).catch(() => {});
      }
      if (mine && !intervalId) intervalId = setInterval(check, 10000);
      if (!mine && intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    check();
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id]);

  // Presigned thumbnail URLs, batch-fetched (never one request per photo - see
  // getPhotoSubmissionPresignedUrls's comment) and cached by submission_id.
  const [thumbUrls, setThumbUrls] = useState({});
  const fetchedSubmissionIdsRef = useRef(new Set());
  const ensureThumbUrls = useCallback((ids) => {
    const need = ids.filter(id => id && !fetchedSubmissionIdsRef.current.has(id));
    if (!need.length) return;
    need.forEach(id => fetchedSubmissionIdsRef.current.add(id));
    eventApi.getMyPhotoSubmissionPresignedUrls(event.event_id, need).then(res => {
      setThumbUrls(prev => ({ ...prev, ...(res.urls || {}) }));
    }).catch(() => { need.forEach(id => fetchedSubmissionIdsRef.current.delete(id)); });
  }, [event.event_id]);

  const loadMySubmissions = useCallback(async () => {
    try {
      const data = await eventApi.getMyPhotoSubmissions(event.event_id);
      const submissions = Array.isArray(data?.submissions) ? data.submissions : [];
      setMySubmissions(submissions);
      ensureThumbUrls(submissions.map(s => s.submission_id));
    } catch {
      setMySubmissions([]);
    }
  }, [event.event_id, ensureThumbUrls]);

  // Lazy-load on first expand
  useEffect(() => {
    if (!expanded || loaded) return;
    setLoaded(true);
    loadMySubmissions();
  }, [expanded, loaded, loadMySubmissions]);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []).filter(isCompressibleEventImage);
    e.target.value = '';
    if (!files.length) {
      setSnack({ open: true, msg: 'No compatible images selected.', sev: 'warning' });
      return;
    }
    const consentOk = window.confirm(
      'Photo upload consent required: these photos will be stored for this event and may be visible to the organiser according to event sharing settings. Continue?'
    );
    if (!consentOk) {
      setSnack({ open: true, msg: 'Upload cancelled because consent was not granted.', sev: 'info' });
      return;
    }
    setUploading(true);
    setProgress({ current: 0, total: files.length });
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(event.event_id, 'submit');
    try {
      const compressedEntries = [];
      for (let i = 0; i < files.length; i += 1) {
        setProgress({ current: i + 1, total: files.length });
        const compressed = await compressEventImage(files[i]);
        compressedEntries.push({ file: compressed, relativePath: compressedImageName(files[i].name) });
      }
      if (isNative) {
        // Same background-job machinery, run unconditionally on native
        // regardless of whether the app stays open - see
        // startInitialGroupingUpload's matching comment in SmartSortSection.
        await stagingProvider.createJob({ jobId, flowKind: 'attendee-submit', eventId: String(event.event_id), flowMeta: {}, totalFiles: files.length });
        await nativeUploadService.startForegroundUpload({
          jobId, flowKind: 'attendee-submit', eventId: String(event.event_id), totalFiles: files.length,
          label: `Submitting photos to ${event.event_name || 'event'}`,
        });
        await runUploadJob({ jobId, flowKind: 'attendee-submit' }, { freshFiles: compressedEntries });
      } else {
        const form = new FormData();
        compressedEntries.forEach(({ file, relativePath }) => form.append('files', file, relativePath));
        await eventApi.uploadPhotoSubmissions(event.event_id, form);
      }
      setSnack({ open: true, msg: `${files.length} photo${files.length > 1 ? 's' : ''} submitted successfully!`, sev: 'success' });
      loadMySubmissions();
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Upload failed', sev: 'error' });
    } finally {
      setUploading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const submissionCount = Array.isArray(mySubmissions) ? mySubmissions.length : null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      {/* Collapsible header */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setExpanded(p => !p)}
        sx={{ px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AddPhotoAlternateIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Submit Photos{submissionCount !== null ? ` (${submissionCount})` : ''}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={e => { e.stopPropagation(); setExpanded(p => !p); }} aria-label={expanded ? 'Collapse' : 'Expand'}>
          <ExpandMoreIcon sx={{ transition: 'transform 200ms', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </IconButton>
      </Stack>

      {/* Body - only rendered when expanded */}
      {expanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Upload your photos from this event for the organiser to review and add to the gallery
          </Typography>

          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <AddPhotoAlternateIcon />}
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading || Boolean(resumeJob)}
            sx={{ whiteSpace: 'nowrap', textTransform: 'none', mb: uploading ? 1 : 1.5 }}
          >
            {uploading ? `Uploading ${progress.current}/${progress.total}…` : 'Choose photos'}
          </Button>

          {uploading && (
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.current / progress.total) * 100 : 0}
              sx={{ mb: 1.5, borderRadius: 1 }}
            />
          )}
          {uploading && (
            shouldShowUploadWarning() ? (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>{UPLOAD_IN_PROGRESS_WARNING}</Alert>
            ) : shouldShowBackgroundableNotice() ? (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>{UPLOAD_BACKGROUNDABLE_NOTICE}</Alert>
            ) : null
          )}
          <ResumeUploadBanner job={resumeJob} label="a submission" />

          {/* Thumbnails of submitted photos */}
          {mySubmissions === null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading your submissions…</Typography>
            </Box>
          )}
          {submissionCount !== null && submissionCount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {submissionCount} photo{submissionCount !== 1 ? 's' : ''} submitted - the organiser will review these
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {mySubmissions.map(sub => (
                  thumbUrls[sub.submission_id] ? (
                    <Box
                      key={sub.submission_id}
                      component="img"
                      src={thumbUrls[sub.submission_id]}
                      alt={sub.original_filename}
                      loading="lazy"
                      sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, bgcolor: 'grey.100' }}
                    />
                  ) : (
                    <Box key={sub.submission_id} className="thumb-skeleton" sx={{ width: 80, height: 80, borderRadius: 1 }} />
                  )
                ))}
              </Box>
            </Box>
          )}
          {submissionCount === 0 && !uploading && (
            <Typography variant="caption" color="text.secondary">No photos submitted yet.</Typography>
          )}
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFilesSelected}
      />
      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}

// Shows only the photos shared with this attendee, with group naming saved to
// the attendee's own face memory (not the event's organiser-controlled names).
function AttendeeNameFaceDialog({ open, onClose, face, eventId, groupNames, groupThumbSrc, ensureGroupThumbnails, onNameSaved }) {
  const [name, setName] = useState('');
  const [selectedMemoryFaceId, setSelectedMemoryFaceId] = useState('');
  const [createNewSelected, setCreateNewSelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [memoryFaces, setMemoryFaces] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);

  const groupId = face?.group || '';
  const thumbUrl = groupId ? groupThumbSrc(groupId) : '';

  useEffect(() => {
    if (open && groupId) ensureGroupThumbnails?.([groupId]);
  }, [open, groupId, ensureGroupThumbnails]);

  useEffect(() => {
    if (!open) return;
    setName(groupId ? (groupNames[groupId] || '') : '');
    setSelectedMemoryFaceId('');
    setCreateNewSelected(false);
    setError('');
    setMemoryLoading(true);
    eventApi.getNamedFaces()
      .then(data => setMemoryFaces(data.faces || []))
      .catch(() => {})
      .finally(() => setMemoryLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !face) return null;

  const handleSave = async () => {
    if (!selectedMemoryFaceId && !createNewSelected && !name.trim()) {
      setError('Please enter a name or pick a memory face.');
      return;
    }
    if (createNewSelected && !name.trim()) {
      setError('Please enter a name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = selectedMemoryFaceId
        ? { group_id: groupId, memory_group_id: selectedMemoryFaceId }
        : { group_id: groupId, display_name: name.trim() };
      const res = await eventApi.saveMyGroupNames(eventId, body);
      if (res.ok) {
        const displayNameForGroup = name.trim()
          || memoryFaces.find(f => f.group_id === selectedMemoryFaceId)?.display_name
          || res.display_name
          || '';
        onNameSaved(groupId, displayNameForGroup, !selectedMemoryFaceId);
        onClose();
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Name this person</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1} alignItems="center">
          {thumbUrl && (
            <Box component="img" src={thumbUrl} alt="face"
              sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid', borderColor: 'divider' }} />
          )}
          {error && <Typography color="error" variant="body2" alignSelf="stretch">{error}</Typography>}
          <TextField
            label="Name" value={name} onChange={e => { setName(e.target.value); setSelectedMemoryFaceId(''); setCreateNewSelected(false); }}
            fullWidth autoFocus placeholder="Enter a name…"
          />
          {name.trim() && !selectedMemoryFaceId && (
            <Paper
              onClick={() => setCreateNewSelected(true)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCreateNewSelected(true);
                }
              }}
              elevation={0}
              sx={{
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: createNewSelected ? 'primary.main' : 'divider',
                bgcolor: createNewSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: createNewSelected ? 'action.selected' : 'action.hover',
                },
              }}
            >
              {createNewSelected
                ? <CheckCircleIcon color="primary" fontSize="small" />
                : <AddCircleOutlineIcon color="action" fontSize="small" />}
              <Box minWidth={0} flex={1}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  Create new memory face
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {name.trim()}
                </Typography>
              </Box>
              {createNewSelected && (
                <Chip size="small" color="primary" label="Selected" />
              )}
            </Paper>
          )}
          {!memoryLoading && memoryFaces.length > 0 && (
            <Box alignSelf="stretch">
              <Typography variant="caption" color="text.secondary">Or pick from your memory</Typography>
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                {memoryFaces.map(mf => (
                  <Chip key={mf.group_id} label={mf.display_name} size="small"
                    title={`${mf.display_name} (${mf.group_id})`}
                    onClick={() => { setName(mf.display_name); setSelectedMemoryFaceId(mf.group_id); setCreateNewSelected(false); }}
                    variant={selectedMemoryFaceId === mf.group_id ? 'filled' : 'outlined'}
                    color={selectedMemoryFaceId === mf.group_id ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer', mb: 0.5 }} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LearnFaceDialog({ open, onClose, group, name, memoryGroupId, accountModel, eventId, groupThumbSrc, ensureGroupThumbnails, onLearned }) {
  const [learning, setLearning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && group) ensureGroupThumbnails?.([group]);
  }, [open, group, ensureGroupThumbnails]);

  const modelToUse = accountModel || DEFAULT_FACE_GROUPING_MODEL;

  async function handleLearn() {
    setLearning(true);
    setError('');
    try {
      const initial = await eventApi.learnFaceGroup(eventId, { group_id: group, memory_group_id: memoryGroupId, model: modelToUse });
      const jobId = initial.job_id;
      if (!jobId) throw new Error('No job ID returned from server');

      const POLL_INTERVAL = 5000;
      const MAX_WAIT = 300000;
      const deadline = Date.now() + MAX_WAIT;
      let res = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        const poll = await eventApi.getLearnFaceGroupStatus(eventId, jobId);
        if (poll.status === 'done' || poll.status === 'error') { res = poll; break; }
      }
      if (!res) throw new Error('Learning timed out. Please try again.');
      if (res.status === 'error') throw new Error(res.error || 'Failed to learn face.');

      onLearned(res);
      onClose();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to learn face.';
      setError(msg);
    } finally {
      setLearning(false);
    }
  }

  const thumbUrl = group && groupThumbSrc ? groupThumbSrc(group) : '';

  return (
    <Dialog open={open} onClose={learning ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Learn this face?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems="center" mt={1}>
          {thumbUrl && (
            <Box component="img" src={thumbUrl} alt="face"
              sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid', borderColor: 'divider' }} />
          )}
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>{name}</strong> has been named but this face hasn't been learned into your memory yet.
            Learning lets face recognition identify this person in future events automatically.
          </Typography>
          <Tooltip title="Face model from your account settings (Profile → Face grouping model).">
            <Chip size="small" variant="outlined" label={`Model: ${modelToUse}`} sx={{ alignSelf: 'flex-start' }} />
          </Tooltip>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Without learning, you can search for <strong>{name}</strong> in your Moments but this face won't be auto-recognised in upcoming events.
          </Typography>
          {error && <Alert severity="error" sx={{ alignSelf: 'stretch' }}>{error}</Alert>}
          <Stack spacing={1} alignSelf="stretch">
            <Button variant="contained" onClick={handleLearn} disabled={learning} fullWidth>
              {learning && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
              {learning ? 'Learning…' : 'Learn this face (1 credit)'}
            </Button>
            <Button variant="outlined" onClick={onClose} disabled={learning} fullWidth>
              Continue without learning
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

const PHOTO_TRANSITIONS = ['fade', 'slide-left', 'slide-right', 'zoom-in', 'zoom-out'];

// ── Pinch-to-zoom / pan for the fullscreen photo viewer (mobile touch) ────────
function usePinchZoomPan(resetKey) {
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const containerRef = useRef(null);
  const gestureRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

  useEffect(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
    gestureRef.current = null;
  }, [resetKey]);

  const clampTranslate = useCallback((scale, x, y) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const maxX = Math.max(0, (rect.width * (scale - 1)) / 2);
    const maxY = Math.max(0, (rect.height * (scale - 1)) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
  }, []);

  const onTouchStart = useCallback((e) => {
    const touches = e.touches;
    if (touches.length === 2) {
      const [a, b] = touches;
      gestureRef.current = {
        mode: 'pinch',
        startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        startScale: transform.scale,
        startTranslate: { x: transform.x, y: transform.y },
      };
    } else if (touches.length === 1 && transform.scale > 1.01) {
      gestureRef.current = {
        mode: 'pan',
        startTouch: { x: touches[0].clientX, y: touches[0].clientY },
        startTranslate: { x: transform.x, y: transform.y },
      };
    } else {
      gestureRef.current = null;
    }
  }, [transform]);

  const onTouchMove = useCallback((e) => {
    const touches = e.touches;
    let g = gestureRef.current;
    if (touches.length === 2) {
      e.preventDefault();
      const [a, b] = touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (!g || g.mode !== 'pinch') {
        g = { mode: 'pinch', startDist: dist, startScale: transform.scale, startTranslate: { x: transform.x, y: transform.y } };
        gestureRef.current = g;
        return;
      }
      const newScale = Math.min(4, Math.max(1, g.startScale * (dist / g.startDist)));
      const { x, y } = clampTranslate(newScale, g.startTranslate.x, g.startTranslate.y);
      setTransform({ scale: newScale, x, y });
      return;
    }
    if (touches.length === 1 && g && g.mode === 'pan') {
      e.preventDefault();
      const dx = touches[0].clientX - g.startTouch.x;
      const dy = touches[0].clientY - g.startTouch.y;
      const { x, y } = clampTranslate(transform.scale, g.startTranslate.x + dx, g.startTranslate.y + dy);
      setTransform(t => ({ ...t, x, y }));
    }
  }, [transform, clampTranslate]);

  const onTouchEnd = useCallback((e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (transform.scale < 1.02) {
      setTransform({ scale: 1, x: 0, y: 0 });
    }
    if (e.touches.length === 0 && e.changedTouches?.length === 1 && !g) {
      const touch = e.changedTouches[0];
      const now = performance.now();
      const last = lastTapRef.current;
      const dt = now - last.time;
      const dist = Math.hypot(touch.clientX - last.x, touch.clientY - last.y);
      if (dt < 300 && dist < 30) {
        setTransform(transform.scale > 1.01 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 });
        lastTapRef.current = { time: 0, x: 0, y: 0 };
        return;
      }
      lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };
    }
  }, [transform]);

  return {
    containerRef,
    transform,
    isZoomed: transform.scale > 1.01,
    isGesturing: gestureRef.current != null,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}

function AttendeeGalleryViewer({ event, accountFaceGroupingModel, mobileTab = null, onPhotoCountChange }) {
  const navigate = useNavigate();
  const isMobileTabs = Boolean(mobileTab);
  const showPhotosTab = !isMobileTabs || mobileTab === 'photos';
  const [facesSheetOpen, setFacesSheetOpen] = useState(false);
  const isMobilePreview = useMediaQuery('(max-width:600px)');
  const eid = event.event_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);           // only the attendee's shared photos
  const [groups, setGroups] = useState({});
  const [sampleByGroup, setSampleByGroup] = useState({});
  const [groupNames, setGroupNames] = useState({});
  const [memoryFaces, setMemoryFaces] = useState([]);
  const [memoryFaceThumbUrls, setMemoryFaceThumbUrls] = useState({});
  // Held in a ref (mutated in place) rather than React state so that merging in
  // a new batch of URLs is O(new keys), not O(everything fetched so far in the
  // session). `presignedUrlsVersion` is the only piece of this that's real
  // state - bumping it is what tells React a re-render is needed.
  const presignedUrlsRef = useRef({ thumbnails: {}, previews: {}, group_thumbnails: {} });
  const [, bumpPresignedUrls] = useReducer(c => c + 1, 0);
  const mergePresignedUrls = useCallback((patch) => {
    const cur = presignedUrlsRef.current;
    if (patch.thumbnails) Object.assign(cur.thumbnails, patch.thumbnails);
    if (patch.previews) Object.assign(cur.previews, patch.previews);
    if (patch.group_thumbnails) Object.assign(cur.group_thumbnails, patch.group_thumbnails);
    bumpPresignedUrls();
  }, []);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgTransform, setImgTransform] = useState({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 });
  const [showOriginal, setShowOriginal] = useState(false);
  const [originalBlobUrl, setOriginalBlobUrl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchPhotoIds, setAiSearchPhotoIds] = useState([]);
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState('');
  const [selectedAiSearchMode, setSelectedAiSearchMode] = useState('plan');
  const [aiSearchModeLabel, setAiSearchModeLabel] = useState('');
  const [aiSearchModeNote, setAiSearchModeNote] = useState('');
  const trackPhotoFilterIds = null;
  const [showAssigned, setShowAssigned] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [matchAll, setMatchAll] = useState(false);
  const [thumbHeight, setThumbHeight] = useState(100);
  const [groupPanelOpen, setGroupPanelOpen] = useState(() => window.innerWidth > 980);
  const [selectedThumbs, setSelectedThumbs] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [reloadKey, setReloadKey] = useState(0);
  const [galleryViewMode, setGalleryViewMode] = useState('list'); // 'list' | 'group'
  const [myPhotoTracks, setMyPhotoTracks] = useState({ requestTracks: [], circleTracks: [] });
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  // Group view has no scroll-position-based window (every "shared via" section
  // is shown at once), so each section is paginated independently instead -
  // otherwise an event with hundreds of shared photos would mount them all.
  const [groupViewVisibleCounts, setGroupViewVisibleCounts] = useState({});
  const [showFaceBoxes, setShowFaceBoxes] = useState(() => getPref('showFaceBoxes', true));
  const [invalidatingCache, setInvalidatingCache] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [nameFaceModal, setNameFaceModal] = useState({ open: false, face: null });
  const [ucDownloading, setUcDownloading] = useState(false);

  function downloadUcOriginalPhoto(photoId) {
    if (!photoId || ucDownloading) return;
    downloadUcOriginal(eventApi.requestUcOriginalDownload, eid, photoId, {
      filename: `circlemoments-${eid}-${photoId}-uncompressed.jpg`,
      onActiveChange: setUcDownloading,
    }).catch((err) => toast(err.message || 'Download failed.', 'error'));
  }
  const [learnFaceModal, setLearnFaceModal] = useState({ open: false, group: null, name: null, memoryGroupId: null });
  const [previewSwipeActive, setPreviewSwipeActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [thumbPreviewOpen, setThumbPreviewOpen] = useState(false);
  const [previewMoreAnchor, setPreviewMoreAnchor] = useState(null);
  const [previewInfoOpen, setPreviewInfoOpen] = useState(false);
  const [autoTransition, setAutoTransition] = useState(false);
  const [photoTransition, setPhotoTransition] = useState('');
  const fsZoom = usePinchZoomPan(currentIndex);
  const [fsNavHighlight, setFsNavHighlight] = useState(false);
  const fsNavHighlightTimeoutRef = useRef(null);
  const [showcasedPhotoIds, setShowcasedPhotoIds] = useState(new Set());
  const [showcaseTogglingId, setShowcaseTogglingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    profileApi.getMyShowcasePhotos()
      .then(d => {
        if (cancelled) return;
        const ids = (d?.photos || []).filter(p => p.event_id === eid).map(p => p.photo_id);
        setShowcasedPhotoIds(new Set(ids));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eid]);

  async function toggleShowcasePhoto(photoId) {
    if (!photoId || showcaseTogglingId) return;
    setShowcaseTogglingId(photoId);
    try {
      const alreadyOn = showcasedPhotoIds.has(photoId);
      if (alreadyOn) {
        await profileApi.removeShowcasePhoto(eid, photoId);
      } else {
        await profileApi.addShowcasePhoto(eid, photoId);
      }
      setShowcasedPhotoIds(prev => {
        const next = new Set(prev);
        if (alreadyOn) next.delete(photoId); else next.add(photoId);
        return next;
      });
    } catch {
      // non-fatal - button stays at its prior state
    } finally {
      setShowcaseTogglingId(null);
    }
  }

  const thumbRef = useRef(null);
  const viewerSectionRef = useRef(null);
  const previewSwipeRef = useRef({ startX: 0, startY: 0, lastX: 0, startTime: 0, startIndex: 0, moved: false });
  const previewSwipeSettleRef = useRef(null);
  const fetchedPhotoIdsRef = useRef(new Set());
  const fetchedGroupIdsRef = useRef(new Set());
  const pendingGroupIdsRef = useRef(new Set());
  const groupFetchTimerRef = useRef(null);
  const allGroupIdsRef = useRef([]);
  const autoTransitionRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const filteredPhotosLengthRef = useRef(0);

  const toast = useCallback((message, severity = 'info') => setSnackbar({ open: true, message: String(message), severity }), []);
  const thumbWindow = useMemo(() => Math.max(12, Math.floor(thumbHeight / 60) * 12), [thumbHeight]);

  // Keep an ordered list of all group IDs (by face count) for padding lazy-load batches
  useEffect(() => {
    allGroupIdsRef.current = Object.keys(groups)
      .sort((a, b) => (Number(groups[b]) || 0) - (Number(groups[a]) || 0));
  }, [groups]);

  const GROUP_THUMB_BATCH_SIZE = 30;

  // Batch-fetch presigned URLs for face-group thumbnails as they scroll into view.
  // Each request is padded up to GROUP_THUMB_BATCH_SIZE ids so scrolling doesn't
  // trigger a flurry of tiny one-off requests.
  const ensureGroupThumbnails = useCallback((ids) => {
    const toFetch = (ids || []).filter(g => g && !fetchedGroupIdsRef.current.has(g));
    if (!toFetch.length) return;
    toFetch.forEach(g => { fetchedGroupIdsRef.current.add(g); pendingGroupIdsRef.current.add(g); });
    if (groupFetchTimerRef.current) return;
    groupFetchTimerRef.current = setTimeout(() => {
      const batch = Array.from(pendingGroupIdsRef.current);
      pendingGroupIdsRef.current.clear();
      groupFetchTimerRef.current = null;
      // Pad the batch with the next not-yet-fetched groups (in count order) so
      // each request fetches at least GROUP_THUMB_BATCH_SIZE thumbnails.
      for (const gid of allGroupIdsRef.current) {
        if (batch.length >= GROUP_THUMB_BATCH_SIZE) break;
        if (!fetchedGroupIdsRef.current.has(gid)) {
          fetchedGroupIdsRef.current.add(gid);
          batch.push(gid);
        }
      }
      eventApi.getPresignedUrls(eid, [], false, batch).then(urls => {
        mergePresignedUrls({ group_thumbnails: urls.group_thumbnails || {} });
      }).catch(() => { batch.forEach(g => fetchedGroupIdsRef.current.delete(g)); });
    }, 100);
  }, [eid, mergePresignedUrls]);

  const thumbSrc = useCallback((pid) => presignedUrlsRef.current.thumbnails[pid] || eventApi.thumbUrl(eid, pid), [eid]);
  const previewSrc = useCallback((pid) => presignedUrlsRef.current.previews[pid] || eventApi.previewUrl(eid, pid), [eid]);
  const groupThumbSrc = useCallback((g) => presignedUrlsRef.current.group_thumbnails[g] || '', []);

  const refreshMemoryFaces = useCallback(async () => {
    try {
      const data = await eventApi.getNamedFaces();
      const loadedMemoryFaces = Array.isArray(data?.faces) ? data.faces : [];
      setMemoryFaces(loadedMemoryFaces);
      const thumbKeys = [...new Set(loadedMemoryFaces.map(face => face.thumbnail_key).filter(Boolean))];
      if (thumbKeys.length) {
        const thumbData = await eventApi.getMemoryFaceThumbnailUrls(thumbKeys);
        setMemoryFaceThumbUrls(thumbData.urls || {});
      } else {
        setMemoryFaceThumbUrls({});
      }
    } catch {
      // Keep the last loaded list; the save path will still validate server-side.
    }
  }, []);

  useEffect(() => {
    fetchedPhotoIdsRef.current = new Set();
    async function load() {
      try {
        const [meta, myPhotos, namedFacesPayload, savedGroupNames] = await Promise.all([
          eventApi.getGalleryMetadata(eid).catch(() => ({ photos: [], groups: {} })),
          eventApi.getMyPhotos(eid).catch(() => ({ photo_ids: [] })),
          eventApi.getNamedFaces().catch(() => ({ faces: [] })),
          eventApi.getMyGroupNames(eid).catch(() => ({})),
        ]);
        const payload = meta?.data ?? meta;
        const allPhotos = Array.isArray(payload) ? payload : Array.isArray(payload?.photos) ? payload.photos : Array.isArray(meta?.photos) ? meta.photos : [];
        // Filter to only photos shared with this attendee. A 'sub:<id>' entry
        // is an unprocessed photo distributed to one of this attendee's own
        // requests/circles - it has no metadata.json entry (gallery_metadata
        // never includes unprocessed photos for non-organisers), so it's
        // synthesized here instead of coming from `allPhotos`. Its thumbnail
        // resolves through the same lazy presigned-URL fetch as everything
        // else - the backend's gallery-presigned-urls route already accepts
        // 'sub:' IDs and checks this attendee's own entitlement for them.
        const sharedIdList = (Array.isArray(myPhotos?.photo_ids) ? myPhotos.photo_ids : []).map(String);
        const gallerySharedIds = new Set(sharedIdList.filter(id => !isUnprocessedPhotoId(id)));
        const unprocessedShared = sharedIdList.filter(isUnprocessedPhotoId).map(id => ({ photo_id: id, unprocessed: true, faces: [] }));
        const myPhotoList = [
          ...(gallerySharedIds.size > 0 ? allPhotos.filter(p => gallerySharedIds.has(String(p.photo_id))) : []),
          ...unprocessedShared,
        ];
        setMyPhotoTracks({
          requestTracks: Array.isArray(myPhotos?.request_tracks) ? myPhotos.request_tracks : [],  
          circleTracks: Array.isArray(myPhotos?.circle_tracks) ? myPhotos.circle_tracks : [],
        });
        // Do NOT use groupsFromMeta here - it contains all event groups regardless of sharing.
        // Attendee groups must be computed purely from their own shared photos.
        const { groups: nextGroups, sampleByGroup: nextSamples } = buildGroups(myPhotoList, {});
        const groupNamesMap = savedGroupNames?.group_names ?? (typeof savedGroupNames === 'object' ? savedGroupNames : {});
        const initialIds = myPhotoList.slice(0, 24).map(p => p.photo_id).filter(Boolean);
        initialIds.forEach(id => fetchedPhotoIdsRef.current.add(String(id)));
        const initialGroupIds = Object.keys(nextGroups)
          .sort((a, b) => (Number(nextGroups[b]) || 0) - (Number(nextGroups[a]) || 0))
          .slice(0, 30);
        fetchedGroupIdsRef.current = new Set(initialGroupIds);
        const urls = await eventApi.getPresignedUrls(eid, initialIds, false, initialGroupIds).catch(() => ({ thumbnails: {}, previews: {}, group_thumbnails: {} }));
        setPhotos(sortPhotosByCapturedAtDesc(myPhotoList));
        setGroups(nextGroups);
        setSampleByGroup(nextSamples);
        setGroupNames(groupNamesMap && typeof groupNamesMap === 'object' ? groupNamesMap : {});
        mergePresignedUrls(urls);
        const loadedMemoryFaces = Array.isArray(namedFacesPayload?.faces) ? namedFacesPayload.faces : [];
        setMemoryFaces(loadedMemoryFaces);
        const thumbKeys = [...new Set(loadedMemoryFaces.map(face => face.thumbnail_key).filter(Boolean))];
        if (thumbKeys.length) {
          eventApi.getMemoryFaceThumbnailUrls(thumbKeys)
            .then(data => setMemoryFaceThumbUrls(data.urls || {}))
            .catch(() => setMemoryFaceThumbUrls({}));
        } else {
          setMemoryFaceThumbUrls({});
        }
      } catch (e) {
        setError(e.message || 'Failed to load your gallery');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eid, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const processedGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const all = Object.keys(groups).map(group => {
      const an = (groupNames[group] || '').trim();
      return { group, count: Number(groups[group]) || 0, assignedName: an, isAssigned: Boolean(an) };
    }).sort((a, b) => b.count - a.count || a.group.localeCompare(b.group));
    if (!q) return all;
    return all.filter(({ group, assignedName }) => {
      const dn = displayName(group, groupNames).toLowerCase();
      return group.toLowerCase().includes(q) || assignedName.toLowerCase().includes(q) || dn.includes(q);
    });
  }, [groups, groupNames, searchQuery]);

  const assignedGroups = useMemo(() => processedGroups.filter(i => i.isAssigned), [processedGroups]);
  const unassignedGroups = useMemo(() => processedGroups.filter(i => !i.isAssigned), [processedGroups]);

  const photoById = useMemo(() => { const m = new Map(); for (const p of photos) if (p?.photo_id) m.set(String(p.photo_id), p); return m; }, [photos]);

  const aiOrderedPhotos = useMemo(() => {
    if (!aiSearchActive) return photos;
    const seen = new Set();
    return aiSearchPhotoIds
      .map(pid => String(pid))
      .filter(pid => { if (!pid || seen.has(pid)) return false; seen.add(pid); return true; })
      .map(pid => photoById.get(pid))
      .filter(Boolean);
  }, [aiSearchActive, aiSearchPhotoIds, photoById, photos]);

  // Mirrors the order organisers arranged tracks in: requests first (in track
  // order, then photo order within each track), then circles, then any photo
  // not covered by a track keeps its original relative position.
  const trackOrderedPhotos = useMemo(() => {
    const rank = new Map();
    let next = 0;
    for (const track of [...myPhotoTracks.requestTracks, ...myPhotoTracks.circleTracks]) {
      for (const pid of (track.photo_ids || track.photoIds || [])) {
        const key = String(pid);
        if (!rank.has(key)) rank.set(key, next++);
      }
    }
    if (!rank.size) return photos;
    return photos
      .map((photo, idx) => ({ photo, idx, rank: rank.has(String(photo.photo_id)) ? rank.get(String(photo.photo_id)) : Infinity }))
      .sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx))
      .map(x => x.photo);
  }, [photos, myPhotoTracks]);

  const filteredPhotos = useMemo(() => {
    const sourcePhotos = aiSearchActive ? aiOrderedPhotos : trackOrderedPhotos;
    const groupFiltered = !selectedGroups.size ? sourcePhotos : sourcePhotos.filter(photo => {
      const fg = new Set((photo.faces || []).map(f => f.group || 'Unknown'));
      if (matchAll) { if (fg.size !== selectedGroups.size) return false; return Array.from(selectedGroups).every(g => fg.has(g)); }
      return Array.from(selectedGroups).some(g => fg.has(g));
    });
    if (!trackPhotoFilterIds?.size) return groupFiltered;
    return groupFiltered.filter(photo => trackPhotoFilterIds.has(String(photo.photo_id)));
  }, [trackOrderedPhotos, aiOrderedPhotos, aiSearchActive, selectedGroups, matchAll, trackPhotoFilterIds]);

  // Group view: split filteredPhotos into "Shared via <source>" sections - one
  // per photo request and one per circle owner. A photo may appear in more
  // than one section (e.g. shared via a request AND a circle), but duplicate
  // appearances within a single section's own tracks are collapsed.
  const groupedPhotoSections = useMemo(() => {
    if (galleryViewMode !== 'group') return [];
    const photoMap = new Map(filteredPhotos.map(p => [String(p.photo_id), p]));
    const sections = [];
    const sharedIds = new Set();

    const requestGroups = new Map(); // request_id -> Set of photo ids
    for (const track of myPhotoTracks.requestTracks) {
      const rid = track.request_id || 'request';
      if (!requestGroups.has(rid)) requestGroups.set(rid, new Set());
      const ids = requestGroups.get(rid);
      for (const pid of (track.photo_ids || track.photoIds || [])) ids.add(String(pid));
    }
    for (const [rid, ids] of requestGroups) {
      const sectionPhotos = [...ids].map(id => photoMap.get(id)).filter(Boolean);
      if (!sectionPhotos.length) continue;
      sectionPhotos.forEach(p => sharedIds.add(String(p.photo_id)));
      sections.push({ key: `request-${rid}`, label: `Photo Request (${rid})`, photos: sectionPhotos });
    }

    const circleGroups = new Map(); // shared_by -> Set of photo ids
    for (const track of myPhotoTracks.circleTracks) {
      const owner = track.shared_by || 'Circle';
      if (!circleGroups.has(owner)) circleGroups.set(owner, new Set());
      const ids = circleGroups.get(owner);
      for (const pid of (track.photo_ids || track.photoIds || [])) ids.add(String(pid));
    }
    for (const [owner, ids] of circleGroups) {
      const sectionPhotos = [...ids].map(id => photoMap.get(id)).filter(Boolean);
      if (!sectionPhotos.length) continue;
      sectionPhotos.forEach(p => sharedIds.add(String(p.photo_id)));
      sections.push({ key: `circle-${owner}`, label: `Circles (${owner})`, photos: sectionPhotos });
    }

    const directPhotos = filteredPhotos.filter(p => !sharedIds.has(String(p.photo_id)));
    if (directPhotos.length) {
      sections.unshift({ key: 'direct', label: 'Not yet shared', photos: directPhotos, noPrefix: true });
    }

    return sections;
  }, [galleryViewMode, filteredPhotos, myPhotoTracks]);

  const toggleSectionCollapsed = useCallback((key) => {
    setCollapsedSections(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }, []);

  filteredPhotosLengthRef.current = filteredPhotos.length;

  useEffect(() => {
    if (loading) return;
    onPhotoCountChange?.(filteredPhotos.length);
  }, [loading, filteredPhotos.length, onPhotoCountChange]);

  const filteredPhotoOrderKey = useMemo(
    () => filteredPhotos.map(p => String(p.photo_id || p.path || '')).join('|'),
    [filteredPhotos],
  );

  useEffect(() => {
    let active = true;
    const q = aiSearchQuery.trim();
    setAiSearchError('');
    setSelectedThumbs(new Set());
    setCurrentIndex(0);
    if (!q) {
      setAiSearchPhotoIds([]);
      setAiSearchActive(false);
      setAiSearchLoading(false);
      setAiSearchModeLabel('');
      setAiSearchModeNote('');
      return () => { active = false; };
    }
    setAiSearchLoading(true);
    eventApi.searchEventPhotos(eid, q, selectedAiSearchMode)
      .then(payload => {
        if (!active) return;
        // Intersect AI results with attendee's own photos only
        const mySet = new Set(photos.map(p => String(p.photo_id)));
        const ids = (Array.isArray(payload.photos) ? payload.photos : [])
          .map(item => item.photo_id)
          .filter(pid => pid && mySet.has(String(pid)));
        setAiSearchPhotoIds(ids);
        setAiSearchActive(true);
        setAiSearchModeLabel(payload.search_mode_label || '');
        setAiSearchModeNote(payload.search_mode_note || '');
      })
      .catch(err => {
        if (!active) return;
        setAiSearchPhotoIds([]);
        setAiSearchActive(false);
        setAiSearchError(err.message || 'Search failed');
        setAiSearchModeLabel('');
        setAiSearchModeNote('');
      })
      .finally(() => { if (active) setAiSearchLoading(false); });
    return () => { active = false; };
  }, [eid, aiSearchQuery, selectedAiSearchMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAiSearchMode = useCallback(() => setSelectedAiSearchMode(m => m === 'legacy' ? 'plan' : 'legacy'), []);
  const submitAiSearch = useCallback((query) => setAiSearchQuery(query), []);
  const clearAiSearch = useCallback(() => {
    setAiSearchQuery(''); setAiSearchPhotoIds([]); setAiSearchActive(false);
    setAiSearchLoading(false); setAiSearchError(''); setAiSearchModeLabel(''); setAiSearchModeNote('');
    setSelectedThumbs(new Set()); setCurrentIndex(0);
  }, []);

  useEffect(() => { if (currentIndex >= filteredPhotos.length) setCurrentIndex(0); }, [currentIndex, filteredPhotos.length]);
  useEffect(() => { setCurrentIndex(0); setSelectedThumbs(new Set()); setGroupViewVisibleCounts({}); }, [filteredPhotoOrderKey]);
  useEffect(() => {
    if (!previewSwipeActive) return undefined;
    const timer = window.setTimeout(() => setPreviewSwipeActive(false), 450);
    return () => window.clearTimeout(timer);
  }, [previewSwipeActive, currentIndex]);
  useEffect(() => { setShowOriginal(false); setOriginalBlobUrl(null); }, [currentIndex, filteredPhotos]);

  // Lazy-fetch presigned URLs for the visible thumbnail window as the user navigates
  useEffect(() => {
    if (!filteredPhotos.length) return;
    const half = Math.floor(thumbWindow / 2);
    let start = Math.max(0, currentIndex - half);
    let end = Math.min(filteredPhotos.length, start + thumbWindow);
    if (end - start < thumbWindow) start = Math.max(0, end - thumbWindow);
    const prefetchEnd = Math.min(filteredPhotos.length, end + thumbWindow);
    const ids = filteredPhotos.slice(start, prefetchEnd)
      .map(p => String(p.photo_id || ''))
      .filter(pid => pid && !fetchedPhotoIdsRef.current.has(pid));
    if (!ids.length) return;
    ids.forEach(id => fetchedPhotoIdsRef.current.add(id));
    eventApi.getPresignedUrls(eid, ids).then(urls => {
      mergePresignedUrls({ thumbnails: urls.thumbnails, previews: urls.previews, group_thumbnails: urls.group_thumbnails });
    }).catch(() => { ids.forEach(id => fetchedPhotoIdsRef.current.delete(id)); });
  }, [eid, currentIndex, filteredPhotos, thumbWindow, mergePresignedUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group view paginates each section independently (see groupViewVisibleCounts),
  // so only prefetch thumbnails for the photos actually visible right now rather
  // than every photo across every "shared via" section.
  const GROUP_VIEW_PRESIGN_BATCH_SIZE = 200;
  useEffect(() => {
    if (galleryViewMode !== 'group') return;
    const ids = Array.from(new Set(
      groupedPhotoSections
        .flatMap(section => section.photos.slice(0, groupViewVisibleCounts[section.key] || GROUP_VIEW_PAGE_SIZE))
        .map(p => String(p.photo_id || ''))
        .filter(pid => pid && !fetchedPhotoIdsRef.current.has(pid))
    ));
    if (!ids.length) return;
    ids.forEach(id => fetchedPhotoIdsRef.current.add(id));
    for (let i = 0; i < ids.length; i += GROUP_VIEW_PRESIGN_BATCH_SIZE) {
      const batch = ids.slice(i, i + GROUP_VIEW_PRESIGN_BATCH_SIZE);
      eventApi.getPresignedUrls(eid, batch).then(urls => {
        mergePresignedUrls({ thumbnails: urls.thumbnails, previews: urls.previews, group_thumbnails: urls.group_thumbnails });
      }).catch(() => { batch.forEach(id => fetchedPhotoIdsRef.current.delete(id)); });
    }
  }, [eid, galleryViewMode, groupedPhotoSections, groupViewVisibleCounts, mergePresignedUrls]);

  const currentPhoto = filteredPhotos[currentIndex] || null;

  const cast = useChromecastSender();
  useEffect(() => {
    if (cast.connected && currentPhoto?.photo_id) {
      cast.loadImage(previewSrc(currentPhoto.photo_id), currentPhoto.path);
    }
  }, [cast.connected, cast.loadImage, currentPhoto, previewSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let revoke;
    if (showOriginal && currentPhoto?.photo_id) {
      const token = localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token');
      fetch(`${LAMBDA_BACKEND_URL}/api/events/${eid}/photo-original?photo_id=${encodeURIComponent(currentPhoto.photo_id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.ok ? r.blob() : Promise.reject())
        .then(blob => { const url = URL.createObjectURL(blob); revoke = url; setOriginalBlobUrl(url); })
        .catch(() => setOriginalBlobUrl(null));
    } else {
      setOriginalBlobUrl(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [showOriginal, currentPhoto?.photo_id, eid]); // eslint-disable-line

  async function handleInvalidateCache() {
    setInvalidatingCache(true);
    try {
      await eventApi.invalidateSearchCache(eid);
      toast('Search cache cleared.', 'success');
    } catch (e) {
      toast(e.message || 'Failed to invalidate cache.', 'error');
    } finally {
      setInvalidatingCache(false);
    }
  }

  const handleSelectMemoryFace = useCallback(async (group, face) => {
    try {
      const res = await eventApi.saveMyGroupNames(eid, { group_id: group, memory_group_id: face.group_id });
      if (res.ok) {
        setGroupNames(prev => ({ ...prev, [group]: face.display_name || res.display_name || '' }));
        toast(`Linked to "${face.display_name || res.display_name}".`, 'success');
        refreshMemoryFaces();
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to assign face.', 'error');
    }
  }, [eid, toast, refreshMemoryFaces]);

  const handleCreateNewFace = useCallback(async (group, name) => {
    const cleanName = String(name || '').trim();
    if (!cleanName) return;
    try {
      const res = await eventApi.saveMyGroupNames(eid, { group_id: group, display_name: cleanName });
      if (res.ok) {
        setGroupNames(prev => ({ ...prev, [group]: cleanName }));
        refreshMemoryFaces();
        const photosWithFace = photos.filter(p => (p.faces || []).some(f => f.group === group));
        if (photosWithFace.length > 0) {
          setLearnFaceModal({ open: true, group, name: cleanName, memoryGroupId: res.memory_group_id });
        } else {
          toast(`Saved "${cleanName}" to your memory.`, 'success');
        }
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to create memory face.', 'error');
    }
  }, [eid, toast, refreshMemoryFaces, photos]);

  const handleClearFaceAssignment = useCallback(async (group) => {
    try {
      const res = await eventApi.saveMyGroupNames(eid, { group_id: group, clear: true });
      if (res.ok) {
        setGroupNames(prev => { const n = { ...prev }; delete n[group]; return n; });
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to clear assignment.', 'error');
    }
  }, [eid, toast]);

  async function autoPopulateFromMemory() {
    setAutoPopulating(true);
    try {
      const res = await eventApi.autoPopulateMyGroupNames(eid);
      if (res.applied_count > 0) {
        setGroupNames(prev => {
          const next = { ...prev };
          for (const [gid, name] of Object.entries(res.names || {})) {
            if (name) next[gid] = name;
          }
          return next;
        });
        toast(`Auto-populated ${res.applied_count} name(s) from your memory.`, 'success');
      } else {
        toast('No matches found in your memory.', 'info');
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Auto-populate failed.', 'error');
    } finally {
      setAutoPopulating(false);
    }
  }

  function nav(d) {
    if (!filteredPhotos.length) return;
    setCurrentIndex(prev => { const n = prev + d; if (n < 0) return filteredPhotos.length - 1; if (n >= filteredPhotos.length) return 0; return n; });
  }

  // oneBasedIndex comes from the editable "123 / 800" counter - setting
  // currentIndex here reuses the existing thumbnail-window prefetch effect,
  // so no separate fetch logic is needed for a jump vs. a step.
  function goToIndex(oneBasedIndex) {
    if (!filteredPhotos.length) return;
    const n = Math.min(Math.max(oneBasedIndex - 1, 0), filteredPhotos.length - 1);
    setCurrentIndex(n);
  }

  function navWithTransition(d) {
    if (!filteredPhotos.length) return;
    const t = PHOTO_TRANSITIONS[Math.floor(Math.random() * PHOTO_TRANSITIONS.length)];
    setPhotoTransition(t);
    nav(d);
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = window.setTimeout(() => setPhotoTransition(''), 650);
  }

  const fsHistoryPushedRef = useRef(false);

  function enterFullscreen() {
    window.history.pushState({ fsOverlay: true }, '');
    fsHistoryPushedRef.current = true;
    setIsFullscreen(true);
  }

  function exitFullscreen() {
    if (fsHistoryPushedRef.current) {
      fsHistoryPushedRef.current = false;
      window.history.back();
    }
    setIsFullscreen(false);
    setAutoTransition(false);
  }

  useEffect(() => {
    if (!autoTransition) {
      if (autoTransitionRef.current) clearInterval(autoTransitionRef.current);
      return;
    }
    autoTransitionRef.current = window.setInterval(() => {
      if (filteredPhotosLengthRef.current <= 1) return;
      const t = PHOTO_TRANSITIONS[Math.floor(Math.random() * PHOTO_TRANSITIONS.length)];
      setPhotoTransition(t);
      setCurrentIndex(prev => (prev + 1 >= filteredPhotosLengthRef.current ? 0 : prev + 1));
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = window.setTimeout(() => setPhotoTransition(''), 650);
    }, 5000);
    return () => { if (autoTransitionRef.current) clearInterval(autoTransitionRef.current); };
  }, [autoTransition]); // eslint-disable-line

  useEffect(() => {
    if (!isFullscreen) return;
    function handlePopState() {
      fsHistoryPushedRef.current = false;
      setIsFullscreen(false);
      setAutoTransition(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') exitFullscreen();
      if (e.key === 'ArrowLeft') navWithTransition(-1);
      if (e.key === 'ArrowRight') navWithTransition(1);
    }
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isFullscreen]); // eslint-disable-line

  function clampPhotoIndex(index) {
    if (!filteredPhotos.length) return 0;
    return Math.max(0, Math.min(filteredPhotos.length - 1, index));
  }

  function settlePreviewSwipe() {
    if (previewSwipeSettleRef.current) window.clearTimeout(previewSwipeSettleRef.current);
    previewSwipeSettleRef.current = window.setTimeout(() => setPreviewSwipeActive(false), 120);
  }

  function handlePreviewTouchStart(e) {
    if (filteredPhotos.length <= 1) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    if (previewSwipeSettleRef.current) window.clearTimeout(previewSwipeSettleRef.current);
    previewSwipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      startTime: performance.now(),
      startIndex: currentIndex,
      moved: false,
    };
  }

  function handlePreviewTouchMove(e) {
    if (filteredPhotos.length <= 1) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const state = previewSwipeRef.current;
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;
    if (Math.abs(dx) < 10 || Math.abs(dx) < Math.abs(dy)) return;
    e.preventDefault();
    state.moved = true;
    state.lastX = touch.clientX;
    setPreviewSwipeActive(true);
    const stepWidth = Math.max(52, Math.min(140, window.innerWidth * 0.18));
    const steps = Math.trunc(-dx / stepWidth);
    setCurrentIndex(clampPhotoIndex(state.startIndex + steps));
  }

  function handlePreviewTouchEnd() {
    const state = previewSwipeRef.current;
    if (!state.moved) return;
    const elapsed = Math.max(1, performance.now() - state.startTime);
    const dx = state.lastX - state.startX;
    const velocity = Math.abs(dx) / elapsed;
    if (velocity > 0.65) {
      const direction = dx < 0 ? 1 : -1;
      const momentum = Math.min(6, Math.max(1, Math.round(velocity * 3)));
      setCurrentIndex(prev => clampPhotoIndex(prev + direction * momentum));
    }
    settlePreviewSwipe();
  }

  function handleFsTouchStart(e) {
    fsZoom.handlers.onTouchStart(e);
    if (fsZoom.transform.scale <= 1.01 && e.touches.length === 1) handlePreviewTouchStart(e);
  }
  function handleFsTouchMove(e) {
    if (fsZoom.transform.scale > 1.01 || e.touches.length === 2) fsZoom.handlers.onTouchMove(e);
    else handlePreviewTouchMove(e);
  }
  function handleFsTouchEnd(e) {
    const wasZoomed = fsZoom.transform.scale > 1.01;
    const tapped = !wasZoomed && !previewSwipeRef.current.moved && e.touches.length === 0;
    fsZoom.handlers.onTouchEnd(e);
    if (!wasZoomed) handlePreviewTouchEnd(e);
    if (tapped) {
      setFsNavHighlight(true);
      if (fsNavHighlightTimeoutRef.current) window.clearTimeout(fsNavHighlightTimeoutRef.current);
      fsNavHighlightTimeoutRef.current = window.setTimeout(() => setFsNavHighlight(false), 2000);
    }
  }

  function onImageLoad(ev) {
    const img = ev.currentTarget;
    if (!img.naturalWidth) return;
    const cW = img.clientWidth, cH = img.clientHeight, ia = img.naturalWidth / img.naturalHeight, ca = cW / cH;
    let rW = cW, rH = cH;
    if (ca > ia) { rH = cH; rW = rH * ia; } else { rW = cW; rH = rW / ia; }
    setImgTransform({ scaleX: rW / img.naturalWidth, scaleY: rH / img.naturalHeight, offsetX: (cW - rW) / 2, offsetY: (cH - rH) / 2 });
  }

  const jumpToGroupSample = useCallback((g) => {
    const sample = sampleByGroup[g];
    if (!sample) return;
    const i = filteredPhotos.findIndex(p => p.path === sample.photo.path);
    if (i >= 0) setCurrentIndex(i);
    if (isMobileTabs) {
      setFacesSheetOpen(false);
      viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [sampleByGroup, filteredPhotos, isMobileTabs]);

  const toggleGroupSelected = useCallback((group) => {
    setSelectedGroups(prev => { const n = new Set(prev); if (n.has(group)) n.delete(group); else n.add(group); return n; });
  }, []);

  // On mobile, applying a face filter closes the face-groups sheet and scrolls
  // the merged Photos tab down to the viewer so the filtered result is
  // immediately visible - desktop leaves the panel open inline, unchanged.
  const handleToggleGroupFiltered = useCallback((group) => {
    toggleGroupSelected(group);
    if (isMobileTabs) {
      setFacesSheetOpen(false);
      viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [toggleGroupSelected, isMobileTabs]);

  const half = Math.floor(thumbWindow / 2);
  let thumbStart = Math.max(0, currentIndex - half);
  let thumbEnd = Math.min(filteredPhotos.length, thumbStart + thumbWindow);
  if (thumbEnd - thumbStart < thumbWindow) thumbStart = Math.max(0, thumbEnd - thumbWindow);
  const visibleThumbs = filteredPhotos.slice(thumbStart, thumbEnd);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /><Typography mt={1} color="text.secondary">Loading your gallery…</Typography></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!photos.length) {
    return (
      <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
        <PhotoLibraryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          No photos have been shared with you yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mb: 2 }}>
          Request your photos by submitting your selfie to help the organiser easily find and share your photos with you.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => navigate(`/join-event/${event.invite_code}`)}
          sx={{ textTransform: 'none' }}
        >
          Add Request
        </Button>
      </Box>
    );
  }

  const groupPanelExpanded = isMobileTabs || groupPanelOpen;
  const groupControlsPanel = (
    <aside className={`panel-section${isMobileTabs ? ' panel-section--standalone' : ''}`}>
      <div className="group-panel-header">
        <h2 style={{ margin: 0 }}>Face Groups</h2>
        {!isMobileTabs && (
          <button type="button" className="group-panel-toggle" onClick={() => setGroupPanelOpen(p => !p)} aria-label={groupPanelOpen ? 'Collapse face groups' : 'Expand face groups'}>
            {groupPanelOpen ? '▾' : '▸'}
          </button>
        )}
        {isMobileTabs && (
          <IconButton size="small" onClick={() => setFacesSheetOpen(false)} aria-label="Close face groups">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {groupPanelExpanded && (
        <>
          <Stack direction="row" spacing={1} mb={1} mt={1}>
            <span style={{ color: '#5f6368', fontSize: '0.9rem' }}>Photos: <strong>{filteredPhotos.length}</strong></span>
            <span style={{ color: '#5f6368', fontSize: '0.9rem' }}>Groups: <strong>{Object.keys(groups).length}</strong></span>
          </Stack>
          <GroupSearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by group or name…" />
          <div className="group-list">
            <details className="group-expander" open={showAssigned}>
              <summary onClick={e => e.preventDefault()}><button type="button" className="expander-toggle" onClick={() => setShowAssigned(p => !p)}>{showAssigned ? '▾' : '▸'} Named ({assignedGroups.length})</button></summary>
              {showAssigned && assignedGroups.length === 0 && <div className="group-empty">None match search.</div>}
              {showAssigned && assignedGroups.map(({ group, count }) => (
                <GroupListItem
                  key={group}
                  eid={eid}
                  group={group}
                  count={count}
                  value={groupNames[group] || ''}
                  selected={selectedGroups.has(group)}
                  thumbSrc={groupThumbSrc(group)}
                  memoryFaces={memoryFaces}
                  memoryFaceThumbUrls={memoryFaceThumbUrls}
                  groupNames={groupNames}
                  groupThumbSrc={groupThumbSrc}
                  ensureGroupThumbnails={ensureGroupThumbnails}
                  onToggleSelect={handleToggleGroupFiltered}
                  onJumpToSample={jumpToGroupSample}
                  onSelectMemoryFace={handleSelectMemoryFace}
                  onCreateNew={handleCreateNewFace}
                  onClear={handleClearFaceAssignment}
                  onOpenOptions={refreshMemoryFaces}
                />
              ))}
            </details>
            <details className="group-expander" open={showUnassigned}>
              <summary onClick={e => e.preventDefault()}><button type="button" className="expander-toggle" onClick={() => setShowUnassigned(p => !p)}>{showUnassigned ? '▾' : '▸'} Unnamed ({unassignedGroups.length})</button></summary>
              {showUnassigned && unassignedGroups.length === 0 && <div className="group-empty">None match search.</div>}
              {showUnassigned && unassignedGroups.map(({ group, count }) => (
                <GroupListItem
                  key={group}
                  eid={eid}
                  group={group}
                  count={count}
                  value={groupNames[group] || ''}
                  selected={selectedGroups.has(group)}
                  thumbSrc={groupThumbSrc(group)}
                  memoryFaces={memoryFaces}
                  memoryFaceThumbUrls={memoryFaceThumbUrls}
                  groupNames={groupNames}
                  groupThumbSrc={groupThumbSrc}
                  ensureGroupThumbnails={ensureGroupThumbnails}
                  onToggleSelect={handleToggleGroupFiltered}
                  onJumpToSample={jumpToGroupSample}
                  onSelectMemoryFace={handleSelectMemoryFace}
                  onCreateNew={handleCreateNewFace}
                  onClear={handleClearFaceAssignment}
                  onOpenOptions={refreshMemoryFaces}
                />
              ))}
            </details>
          </div>
          {!isMobileTabs && (
            <label className="match-mode-toggle">
              <input type="checkbox" checked={matchAll} onChange={e => setMatchAll(e.target.checked)} />
              <span className="match-mode-ui" aria-hidden="true" />
              Match all selected groups
            </label>
          )}
          <div className={`panel-actions${isMobileTabs ? ' panel-actions--floating' : ''}`}>
            {isMobileTabs && (
              <label className="match-mode-toggle match-mode-toggle--compact">
                <input
                  type="checkbox"
                  checked={matchAll}
                  onChange={e => {
                    setMatchAll(e.target.checked);
                    setFacesSheetOpen(false);
                    viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
                <span className="match-mode-ui" aria-hidden="true" />
                Match all selected groups
              </label>
            )}
            <button type="button" className="save-btn" onClick={autoPopulateFromMemory} disabled={autoPopulating}>
              {autoPopulating && <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} />}
              {autoPopulating ? 'Matching…' : 'Auto-populate from Memory'}
            </button>
            {selectedGroups.size > 0 && (
              <button type="button" className="clear-btn" onClick={() => { setSelectedGroups(new Set()); setMatchAll(false); }}>Clear Filters</button>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return (
    <>
      <UcDownloadProgressBar active={ucDownloading} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>

      <LearnFaceDialog
        open={learnFaceModal.open}
        onClose={() => setLearnFaceModal({ open: false, group: null, name: null, memoryGroupId: null })}
        group={learnFaceModal.group}
        name={learnFaceModal.name}
        memoryGroupId={learnFaceModal.memoryGroupId}
        accountModel={accountFaceGroupingModel || DEFAULT_FACE_GROUPING_MODEL}
        eventId={eid}
        groupThumbSrc={groupThumbSrc}
        ensureGroupThumbnails={ensureGroupThumbnails}
        onLearned={(res) => {
          toast(`"${learnFaceModal.name}" learned from ${res.embeddings_computed} photo${res.embeddings_computed === 1 ? '' : 's'}.`, 'success');
        }}
      />

      <AttendeeNameFaceDialog
        open={nameFaceModal.open}
        onClose={() => setNameFaceModal({ open: false, face: null })}
        face={nameFaceModal.face}
        eventId={eid}
        groupNames={groupNames}
        groupThumbSrc={groupThumbSrc}
        ensureGroupThumbnails={ensureGroupThumbnails}
        onNameSaved={(gid, displayName, isNewFace) => {
          setGroupNames(prev => ({ ...prev, [gid]: displayName }));
          refreshMemoryFaces();
          if (isNewFace) {
            const photosWithFace = photos.filter(p => (p.faces || []).some(f => f.group === gid));
            if (photosWithFace.length > 0) {
              const memoryGroupId = gid.includes('__') ? gid : `${eid}__${gid}`;
              setLearnFaceModal({ open: true, group: gid, name: displayName, memoryGroupId });
              return;
            }
          }
          toast(`Saved "${displayName}".`, 'success');
        }}
      />

      {!isMobileTabs && (
        <Typography variant="h6" fontWeight={700} mb={1.5}>Your Photo Gallery</Typography>
      )}
      {showPhotosTab && (
      <Box className="page">
        <div className="viewer-container">
          <section className="slider-section" ref={viewerSectionRef}>
            <div className="slider-wrapper">
            <div
              className={`main-photo-wrapper${previewSwipeActive ? ' preview-swiping' : ''}`}
              onTouchStart={handlePreviewTouchStart}
              onTouchMove={handlePreviewTouchMove}
              onTouchEnd={handlePreviewTouchEnd}
              onTouchCancel={handlePreviewTouchEnd}
            >
              {currentPhoto ? (
                <>
                  <img key={currentPhoto.path} className="main-photo"
                    src={showOriginal ? (originalBlobUrl || previewSrc(currentPhoto.photo_id)) : previewSrc(currentPhoto.photo_id)}
                    alt={currentPhoto.path} onLoad={onImageLoad} onClick={enterFullscreen} />
                  <button type="button" className="preview-swipe-btn preview-swipe-btn-left" onClick={() => nav(-1)} aria-label="Previous photo">
                    &#8249;
                  </button>
                  <button type="button" className="preview-swipe-btn preview-swipe-btn-right" onClick={() => nav(1)} aria-label="Next photo">
                    &#8250;
                  </button>
                  {showFaceBoxes && (
                    <div className="main-overlay">
                        {(currentPhoto.faces || []).map((face, idx) => (
                          <div key={`${currentPhoto.path}-${idx}`}
                            className="face-overlay face-overlay-clickable"
                            style={{ left: `${imgTransform.offsetX + face.bbox[0] * imgTransform.scaleX}px`, top: `${imgTransform.offsetY + face.bbox[1] * imgTransform.scaleY}px`, width: `${face.size[0] * imgTransform.scaleX}px`, height: `${face.size[1] * imgTransform.scaleY}px` }}
                            onClick={(e) => { e.stopPropagation(); setNameFaceModal({ open: true, face }); }}>
                            <div className="face-label">{displayName(face.group || 'Unknown', groupNames)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : <div className="status">No photos match the selected groups.</div>}
              </div>
              <div className="slider-nav">
                <button type="button" className="nav-btn" onClick={() => nav(-1)}>←</button>
                <PhotoJumpInput className="photo-counter" current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
                <button type="button" className="nav-btn" onClick={() => nav(1)}>→</button>
                {currentPhoto && <button type="button" className="nav-btn fullscreen-btn" onClick={enterFullscreen} title="Fullscreen view" aria-label="Enter fullscreen">⛶</button>}
              </div>
              {currentPhoto && (
                <div className="viewer-actions">
                  {isUnprocessedPhotoId(currentPhoto?.photo_id) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
                      <UnprocessedPhotoBadge />
                      <Typography variant="caption" color="text.secondary">Not yet processed</Typography>
                    </Box>
                  )}
                  <SplitButton
                    label="Download original"
                    onPrimary={() => {
                      if (!currentPhoto?.photo_id) return;
                      const url = isUnprocessedPhotoId(currentPhoto.photo_id)
                        ? previewSrc(currentPhoto.photo_id)
                        : eventApi.originalUrl(eid, currentPhoto.photo_id);
                      downloadUrlAsBlob(url, `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`).catch((err) => toast(err.message || 'Download failed.', 'error'));
                    }}
                    menuItems={[
                      { id: 'uc-original', label: 'Download uncompressed original', disabled: !currentPhoto?.uc_org_url || ucDownloading || !!(currentPhoto?.blurred_group_ids?.length), onClick: () => currentPhoto?.photo_id && downloadUcOriginalPhoto(currentPhoto.photo_id) },
                    ]}
                  />
                  {!isUnprocessedPhotoId(currentPhoto?.photo_id) && (
                    <button type="button" className={`clear-btn viewer-action-btn${showFaceBoxes ? ' active' : ''}`} onClick={() => { const v = !showFaceBoxes; setShowFaceBoxes(v); setPref('showFaceBoxes', v); }}>
                      {showFaceBoxes ? 'Hide Facebox' : 'Show Facebox'}
                    </button>
                  )}
                  {cast.available && (
                    <button type="button" className={`clear-btn viewer-action-btn${cast.connected ? ' cast-active' : ''}`} onClick={cast.toggleCast} title="Cast to a TV on the same wifi">
                      {cast.connected ? <CastConnectedIcon fontSize="small" /> : <CastIcon fontSize="small" />}
                      {cast.connected ? 'Casting' : 'Cast to device'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          {!isMobileTabs && groupControlsPanel}
        </div>

        <section className="gallery-ai-search" aria-label="Search your shared photos">
          <GalleryAiSearchField
            active={aiSearchActive}
            loading={aiSearchLoading}
            onSubmit={submitAiSearch}
            onClear={clearAiSearch}
            searchModeLabel={aiSearchModeLabel}
            selectedSearchMode={selectedAiSearchMode}
            onToggleSearchMode={toggleAiSearchMode}
            onInvalidateCache={handleInvalidateCache}
            invalidatingCache={invalidatingCache}
          />
          <div className="gallery-ai-search-meta">
            {aiSearchError ? (
              <span className="gallery-ai-search-error">{aiSearchError}</span>
            ) : aiSearchActive && aiSearchQuery ? (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <span>Showing {filteredPhotos.length} ranked match{filteredPhotos.length === 1 ? '' : 'es'} for <strong>{aiSearchQuery}</strong></span>
                  {aiSearchModeLabel && <Chip label={aiSearchModeLabel} size="small" color="primary" variant="outlined" />}
                </Stack>
                {aiSearchModeNote && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                    {aiSearchModeNote}
                  </Typography>
                )}
              </Box>
            ) : (
              <span>{photos.length} photos shared with you</span>
            )}
          </div>
        </section>

        <section className="thumb-section" ref={thumbRef} style={{ height: thumbHeight, position: 'relative', overflowY: 'auto', overflowX: 'hidden', alignItems: 'flex-start', display: galleryViewMode === 'group' ? 'block' : 'flex' }}>
          <div className="thumb-view-toggle" style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, display: 'flex', gap: 2, background: 'rgba(255,255,255,0.85)', borderRadius: 6 }}>
            <IconButton size="small" onClick={() => setGalleryViewMode('list')} color={galleryViewMode === 'list' ? 'primary' : 'default'} title="List view" aria-label="List view">
              <ViewListIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setGalleryViewMode('group')} color={galleryViewMode === 'group' ? 'primary' : 'default'} title="Group view (by share source)" aria-label="Group view">
              <ViewAgendaIcon fontSize="small" />
            </IconButton>
          </div>
          {galleryViewMode === 'group' ? (
            <div className="thumb-group-view" style={{ paddingTop: 36 }}>
              {groupedPhotoSections.length === 0 && <div className="status" style={{ padding: '8px 12px' }}>No photos to show.</div>}
              {groupedPhotoSections.map(section => {
                const collapsed = collapsedSections.has(section.key);
                const visibleCount = groupViewVisibleCounts[section.key] || GROUP_VIEW_PAGE_SIZE;
                const visibleSectionPhotos = section.photos.slice(0, visibleCount);
                const remaining = section.photos.length - visibleSectionPhotos.length;
                return (
                  <div key={section.key} className="thumb-group-section">
                    <button type="button" className="thumb-group-divider" onClick={() => toggleSectionCollapsed(section.key)} aria-expanded={!collapsed}>
                      <span>{`${collapsed ? '▸' : '▾'} ${section.noPrefix ? section.label : `Shared via ${section.label}`} (${section.photos.length})`}</span>
                    </button>
                    {!collapsed && (
                      <div className="thumb-group-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {visibleSectionPhotos.map(photo => {
                          const pi = filteredPhotos.indexOf(photo);
                          const isSelected = selectedThumbs.has(photo.photo_id);
                          const src = presignedUrlsRef.current.thumbnails[photo.photo_id];
                          return (
                            <button type="button" key={`${section.key}-${photo.photo_id || photo.path}`} className={`thumbnail ${pi === currentIndex ? 'active' : ''} ${isSelected ? 'selected' : ''}`} style={{ position: 'relative' }} onClick={() => { setCurrentIndex(pi); setThumbPreviewOpen(true); viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} title={photo.path}>
                              {src ? <img src={src} alt={photo.path} loading="lazy" decoding="async" /> : <div className="thumb-skeleton" />}
                              {photo.unprocessed && (
                                <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                                  <UnprocessedPhotoBadge />
                                </Box>
                              )}
                            </button>
                          );
                        })}
                        {remaining > 0 && (
                          <button
                            type="button"
                            className="thumb-jump thumb-group-more"
                            style={{ position: 'static', transform: 'none' }}
                            onClick={() => setGroupViewVisibleCounts(prev => ({ ...prev, [section.key]: visibleCount + GROUP_VIEW_PAGE_SIZE }))}
                          >
                            {remaining}+ more
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {thumbStart > 0 && <button type="button" className="thumb-jump" style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} onClick={() => setCurrentIndex(Math.max(0, thumbStart - thumbWindow))}>+{thumbStart} earlier</button>}
              <div className="thumb-container" style={{ minHeight: '100%', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: '4px', paddingLeft: thumbStart > 0 ? '60px' : 0, paddingRight: thumbEnd < filteredPhotos.length ? '60px' : 0, overflow: 'visible' }}>
                {visibleThumbs.map((photo, idx) => {
                  const pi = thumbStart + idx;
                  const isSelected = selectedThumbs.has(photo.photo_id);
                  const src = presignedUrlsRef.current.thumbnails[photo.photo_id];
                  return (
                    <button type="button" key={`${photo.photo_id || photo.path}-${pi}`} className={`thumbnail ${pi === currentIndex ? 'active' : ''} ${isSelected ? 'selected' : ''}`} style={{ position: 'relative' }} onClick={() => { setCurrentIndex(pi); setThumbPreviewOpen(true); viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} title={photo.path}>
                      {src ? <img src={src} alt={photo.path} loading="lazy" decoding="async" /> : <div className="thumb-skeleton" />}
                      {photo.unprocessed && (
                        <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                          <UnprocessedPhotoBadge />
                        </Box>
                      )}
                    </button>
                  );
                })}
              </div>
              {thumbEnd < filteredPhotos.length && <button type="button" className="thumb-jump" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} onClick={() => setCurrentIndex(thumbEnd)}>{filteredPhotos.length - thumbEnd}+ more</button>}
            </>
          )}
        </section>
        <div className="resize-handle" style={{ height: '12px', background: '#ccc', cursor: 'ns-resize', userSelect: 'none', touchAction: 'none' }}
          onMouseDown={(e) => {
            const startY = e.clientY;
            const startHeight = thumbHeight;
            let rafId = null;
            const onMouseMove = (ev) => {
              const next = Math.max(60, startHeight + ev.clientY - startY);
              if (rafId) cancelAnimationFrame(rafId);
              rafId = requestAnimationFrame(() => setThumbHeight(next));
            };
            const onMouseUp = () => { if (rafId) cancelAnimationFrame(rafId); document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            const startY = e.touches[0].clientY;
            const startHeight = thumbHeight;
            let rafId = null;
            const onTouchMove = (ev) => { ev.preventDefault(); const next = Math.max(60, startHeight + ev.touches[0].clientY - startY); if (rafId) cancelAnimationFrame(rafId); rafId = requestAnimationFrame(() => setThumbHeight(next)); };
            const onTouchEnd = () => { if (rafId) cancelAnimationFrame(rafId); document.removeEventListener('touchmove', onTouchMove); document.removeEventListener('touchend', onTouchEnd); };
            document.addEventListener('touchmove', onTouchMove, { passive: false });
            document.addEventListener('touchend', onTouchEnd);
          }}
        />
        {isMobileTabs && (
          <button type="button" className="faces-trigger-btn" onClick={() => setFacesSheetOpen(true)}>
            <FaceRetouchingNaturalIcon fontSize="small" />
            <span className="faces-trigger-count">{Object.keys(groups).length}</span>
          </button>
        )}
      </Box>
      )}

      {isMobileTabs && (
        <Dialog
          open={facesSheetOpen}
          onClose={() => setFacesSheetOpen(false)}
          className="request-detail-dialog faces-sheet-dialog"
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          <Box className="request-detail-dialog-body">
            {groupControlsPanel}
          </Box>
        </Dialog>
      )}

      <Dialog open={thumbPreviewOpen && Boolean(currentPhoto)} onClose={() => { setThumbPreviewOpen(false); setPreviewInfoOpen(false); }} maxWidth="lg" fullWidth>
        {currentPhoto && (
          <>
            <DialogContent sx={{ p: 0, bgcolor: '#111', position: 'relative' }}>
              <IconButton
                size={isMobilePreview ? 'small' : 'medium'}
                onClick={() => { setThumbPreviewOpen(false); setPreviewInfoOpen(false); }}
                aria-label="Close preview"
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
              >
                <CloseIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
              </IconButton>
              <Tooltip title={previewInfoOpen ? 'Hide photo info' : 'Photo info'}>
                <IconButton
                  size={isMobilePreview ? 'small' : 'medium'}
                  onClick={() => setPreviewInfoOpen((v) => !v)}
                  aria-label="Toggle photo info"
                  sx={{
                    position: 'absolute', top: 8, left: 8, zIndex: 1, color: 'common.white',
                    bgcolor: previewInfoOpen ? 'primary.main' : 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: previewInfoOpen ? 'primary.dark' : 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <InfoOutlinedIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
              <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={previewSrc(currentPhoto.photo_id)}
                  alt={currentPhoto.path}
                  sx={{ width: '100%', maxHeight: '82vh', objectFit: 'contain', display: 'block' }}
                />
                {filteredPhotos.length > 1 && (
                  <>
                    <IconButton
                      size={isMobilePreview ? 'small' : 'medium'}
                      onClick={() => nav(-1)}
                      aria-label="Previous photo"
                      sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateBeforeIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                    </IconButton>
                    <IconButton
                      size={isMobilePreview ? 'small' : 'medium'}
                      onClick={() => nav(1)}
                      aria-label="Next photo"
                      sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateNextIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                    </IconButton>
                    <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', px: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', fontSize: 12 }}>
                      <PhotoJumpInput current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <Collapse in={previewInfoOpen}>
              <Box sx={{ bgcolor: '#111', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <PhotoInfoPanel photo={currentPhoto} dark />
              </Box>
            </Collapse>
            <DialogActions
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between', px: 2, py: 1.5, gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{currentPhoto.path}</Typography>
                {currentPhoto.submitted_by_name && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                    onClick={currentPhoto.submitted_by_username ? () => navigate(`/profile/${currentPhoto.submitted_by_username}`) : undefined}
                    sx={{
                      cursor: currentPhoto.submitted_by_username ? 'pointer' : 'default',
                      '&:hover .submitted-by-name': currentPhoto.submitted_by_username ? { textDecoration: 'underline' } : undefined,
                    }}
                  >
                    <Avatar src={currentPhoto.submitted_by_avatar || undefined} sx={{ width: 16, height: 16, fontSize: 10 }}>
                      {currentPhoto.submitted_by_name.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Typography variant="caption" noWrap className="submitted-by-name" sx={{ color: 'grey.400' }}>
                      Submitted by {currentPhoto.submitted_by_name}
                    </Typography>
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                <Tooltip title="Share photo">
                  <IconButton
                    size={isMobilePreview ? 'small' : 'medium'}
                    onClick={() => sharePhoto(eventApi.originalUrl(eid, currentPhoto.photo_id), `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`, {
                      title: event.event_name,
                      text: `Photo from ${event.event_name || 'this event'} on Circle Moments`,
                    }).catch((err) => toast(err.message || 'Share failed.', 'error'))}
                  >
                    <IosShareIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                  </IconButton>
                </Tooltip>
                <SplitButton
                  label="Download original"
                  menuPlacement="up"
                  onPrimary={() => downloadUrlAsBlob(eventApi.originalUrl(eid, currentPhoto.photo_id), `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`).catch((err) => toast(err.message || 'Download failed.', 'error'))}
                  menuItems={[
                    { id: 'uc-original', label: 'Download uncompressed original', disabled: !currentPhoto.uc_org_url || ucDownloading || !!(currentPhoto.blurred_group_ids?.length), onClick: () => downloadUcOriginalPhoto(currentPhoto.photo_id) },
                  ]}
                />
                {isMobilePreview ? (
                  <>
                    <Tooltip title="More options">
                      <IconButton size="small" onClick={(e) => setPreviewMoreAnchor(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={previewMoreAnchor} open={!!previewMoreAnchor} onClose={() => setPreviewMoreAnchor(null)}>
                      <MenuItem onClick={() => { setPreviewMoreAnchor(null); setThumbPreviewOpen(false); enterFullscreen(); }}>
                        <FullscreenIcon fontSize="small" style={{ marginRight: 8 }} /> Fullscreen view
                      </MenuItem>
                      <MenuItem onClick={() => { setPreviewMoreAnchor(null); toggleShowcasePhoto(currentPhoto.photo_id); }}>
                        <AddAPhotoIcon fontSize="small" style={{ marginRight: 8 }} />
                        {showcasedPhotoIds.has(currentPhoto.photo_id) ? 'Remove from profile' : 'Add to Profile View'}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Tooltip title="Fullscreen view">
                      <IconButton aria-label="Fullscreen view" onClick={() => { setThumbPreviewOpen(false); enterFullscreen(); }}>
                        <FullscreenIcon />
                      </IconButton>
                    </Tooltip>
                    <AddToProfileButton
                      size="small"
                      added={showcasedPhotoIds.has(currentPhoto.photo_id)}
                      loading={showcaseTogglingId === currentPhoto.photo_id}
                      onToggle={() => toggleShowcasePhoto(currentPhoto.photo_id)}
                    />
                    <Button variant="outlined" onClick={() => setThumbPreviewOpen(false)} sx={{ textTransform: 'none' }}>
                      Close
                    </Button>
                  </>
                )}
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {isFullscreen && currentPhoto && (
        <div
          className={`fs-overlay${fsZoom.isZoomed ? ' fs-zoomed' : ''}`}
          onTouchStart={handleFsTouchStart}
          onTouchMove={handleFsTouchMove}
          onTouchEnd={handleFsTouchEnd}
          onTouchCancel={handleFsTouchEnd}
        >
          <button type="button" className="fs-close-btn" onClick={exitFullscreen} aria-label="Exit fullscreen">✕</button>
          <button
            type="button"
            className="fs-uc-btn"
            onClick={() => downloadUcOriginalPhoto(currentPhoto.photo_id)}
            disabled={!currentPhoto.uc_org_url || ucDownloading || !!(currentPhoto.blurred_group_ids?.length)}
            title={currentPhoto.uc_org_url ? 'Download uncompressed original' : 'Uncompressed original not available'}
            aria-label="Download uncompressed original"
          >
            <DownloadIcon fontSize="small" />
          </button>
          <div className="fs-photo-area" ref={fsZoom.containerRef}>
            <img
              key={currentPhoto.path + '-fs'}
              className={`main-photo${photoTransition ? ` photo-anim-${photoTransition}` : ''}`}
              src={showOriginal ? (originalBlobUrl || previewSrc(currentPhoto.photo_id)) : previewSrc(currentPhoto.photo_id)}
              alt={currentPhoto.path}
              draggable={false}
              style={{
                transform: `translate(${fsZoom.transform.x}px, ${fsZoom.transform.y}px) scale(${fsZoom.transform.scale})`,
                transition: fsZoom.isGesturing ? 'none' : 'transform 0.2s ease-out',
              }}
            />
            {!fsZoom.isZoomed && (
              <>
                <button type="button" className={`preview-swipe-btn preview-swipe-btn-left fs-nav-arrow${fsNavHighlight ? ' fs-nav-highlight' : ''}`} onClick={() => navWithTransition(-1)} aria-label="Previous photo">&#8249;</button>
                <button type="button" className={`preview-swipe-btn preview-swipe-btn-right fs-nav-arrow${fsNavHighlight ? ' fs-nav-highlight' : ''}`} onClick={() => navWithTransition(1)} aria-label="Next photo">&#8250;</button>
              </>
            )}
          </div>
          {!fsZoom.isZoomed && (
            <>
              <PhotoJumpInput className="fs-counter" current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
              <button type="button" className={`fs-auto-btn${autoTransition ? ' active' : ''}`} onClick={() => setAutoTransition(p => !p)} title="Auto-transition">
                <span className="fs-auto-icon">{autoTransition ? '⏸' : '▶'}</span>
                <span className="fs-auto-label">Auto-transition</span>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ── Event feedback components ─────────────────────────────────────────────────

function FeedbackViewer({ event, canDelete }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventApi.getFeedback(event.event_id)
      .then(r => setFeedbacks(r.feedbacks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [event.event_id]);

  const handleDelete = async (feedbackId) => {
    const ok = await swalConfirmDelete({ title: 'Delete this feedback?' });
    if (!ok) return;
    try {
      await eventApi.deleteFeedback(event.event_id, feedbackId);
      setFeedbacks(prev => prev.filter(f => f.feedback_id !== feedbackId));
    } catch {
      // silent
    }
  };

  if (loading) return <CircularProgress size={14} sx={{ mt: 1.5, display: 'block' }} />;

  if (!feedbacks.length) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block', fontStyle: 'italic' }}>
        No feedback yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {feedbacks.map((f) => (
        <Box
          key={f.feedback_id}
          sx={{
            flex: '1 1 200px',
            maxWidth: 300,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} mb={0.75}>
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap>{f.name}</Typography>
              <Typography variant="caption" color="text.disabled">
                {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}
              </Typography>
            </Box>
            {canDelete && (
              <IconButton
                size="small"
                onClick={() => handleDelete(f.feedback_id)}
                sx={{ color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Stack>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: 120,
              pr: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>
              {f.message}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function FeedbackForm({ event, userName }) {
  const [name, setName] = useState(userName || '');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) { setError('Please fill in your name and message.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await eventApi.submitFeedback(event.event_id, { name: name.trim(), message: message.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Typography variant="body2" color="success.main" sx={{ mt: 1.5, fontStyle: 'italic' }}>
        Thanks for your feedback!
      </Typography>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1.5, width: '100%' }}>
      <Stack spacing={1.25}>
        <TextField
          variant="standard"
          size="small"
          fullWidth
          placeholder="Your name"
          value={name}
          onChange={e => setName(e.target.value)}
          inputProps={{ maxLength: 255 }}
          InputProps={{ disableUnderline: false }}
          sx={{ '& input': { fontSize: '0.85rem', p: 0 } }}
        />
        <TextField
          variant="standard"
          size="small"
          fullWidth
          placeholder="Share your thoughts about the event…"
          value={message}
          onChange={e => setMessage(e.target.value)}
          multiline
          rows={3}
          inputProps={{ maxLength: 5000 }}
          sx={{ '& textarea': { fontSize: '0.85rem', p: 0 } }}
        />
        {error && <Typography variant="caption" color="error">{error}</Typography>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            size="small"
            disabled={submitting}
            sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
          >
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function EventFeedbackSection({ event, isOrgContext, orgRole, userName }) {
  const [expanded, setExpanded] = useState(false);
  const isOrganiser = event.is_organiser;
  const canDelete = !isOrgContext || orgRole === 'admin';

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded(p => !p)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.4,
          background: 'none', border: 'none', cursor: 'pointer', p: 0,
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary' },
          transition: 'color 0.15s',
        }}
      >
        <Typography variant="caption" sx={{ color: 'inherit', fontStyle: 'italic' }}>
          {isOrganiser ? 'View attendee feedback' : 'Event feedback'}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: '0.95rem',
            color: 'inherit',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>
      {expanded && (
        <Box sx={{ width: '100%' }}>
          {isOrganiser
            ? <FeedbackViewer event={event} canDelete={canDelete} />
            : <FeedbackForm event={event} userName={userName} />
          }
        </Box>
      )}
    </Box>
  );
}

// ── Event tools: order standee / stickers ─────────────────────────────────────
function MerchOrderDialog({ open, onClose, event, kind }) {
  const { user } = useAuth();
  const orderLabel = kind === 'stickers' ? 'Event stickers' : 'Event standee';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState('');
  const [addingAddress, setAddingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ name: '', type: 'Home', address1: '', state: '', pincode: '', phone_number: '' });
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const loadAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const res = await profileApi.listAddresses();
      const list = res.addresses || [];
      setAddresses(list);
      setAddressId(cur => {
        if (cur && list.some(a => String(a.id) === String(cur))) return cur;
        const defaultAddr = list.find(a => a.default);
        return defaultAddr ? String(defaultAddr.id) : (list[0] ? String(list[0].id) : '');
      });
    } catch {
      // No addresses yet, or request failed - user can still add one.
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setName('');
    setEmail('');
    setPhone('');
    setAddressId('');
    setAddingAddress(false);
    setAddressForm({ name: '', type: 'Home', address1: '', state: '', pincode: '', phone_number: '' });
    setError('');
    setSuccess(false);
    loadAddresses();
  }, [open, loadAddresses]);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    setName(cur => cur || user.customer_name || '');
    setEmail(cur => cur || user.email || '');
    profileApi.getProfile()
      .then(profile => {
        if (cancelled || !profile) return;
        setName(cur => cur || profile.customer_name || '');
        setEmail(cur => cur || profile.email || '');
        setPhone(cur => cur || profile.phone || profile.extended?.alternate_phone || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, user]);

  async function saveNewAddress() {
    if (!addressForm.address1.trim()) { setError('Please enter a street address.'); return; }
    setSavingAddress(true);
    setError('');
    try {
      await profileApi.addAddress(addressForm);
      const res = await profileApi.listAddresses();
      const list = res.addresses || [];
      setAddresses(list);
      const added = list.find(a => a.address1 === addressForm.address1 && a.pincode === addressForm.pincode) || list[list.length - 1];
      setAddressId(added ? String(added.id) : '');
      setAddingAddress(false);
    } catch (e) {
      setError(e.message || 'Failed to save address.');
    } finally {
      setSavingAddress(false);
    }
  }

  async function submit() {
    if (!name.trim() || !(email.trim() || phone.trim())) {
      setError('Please add your name and an email or phone number.');
      return;
    }
    if (!addressId) {
      setError('Please choose or add a delivery address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await eventApi.orderEventMerch(event.event_id, {
        kind,
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address_id: Number(addressId),
      });
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{success ? 'Request received' : `Order ${orderLabel.toLowerCase()}`}</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            We've got your details - our team will reach out within the next 48 hours.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {user && (
              <Typography variant="caption" color="text.secondary">
                Pre-filled from your account where available - update if needed.
              </Typography>
            )}
            <TextField label="Full name" value={name} onChange={e => setName(e.target.value)} fullWidth required />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} fullWidth />
              <TextField label="Phone number" value={phone} onChange={e => setPhone(e.target.value)} fullWidth />
            </Stack>
            <Typography variant="caption" color="text.secondary">Provide at least one of email or phone.</Typography>

            <Divider />

            <Typography variant="subtitle2">Delivery address</Typography>
            {loadingAddresses ? (
              <CircularProgress size={20} />
            ) : addingAddress ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5}>
                  <TextField label="Label" value={addressForm.name} onChange={e => setAddressForm(f => ({ ...f, name: e.target.value }))} fullWidth />
                  <TextField select label="Type" value={addressForm.type} onChange={e => setAddressForm(f => ({ ...f, type: e.target.value }))} fullWidth>
                    {['Home', 'Office', 'Event Venue', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField label="Street address" value={addressForm.address1} onChange={e => setAddressForm(f => ({ ...f, address1: e.target.value }))} fullWidth multiline rows={2} />
                <Stack direction="row" spacing={1.5}>
                  <TextField label="State / Region" value={addressForm.state} onChange={e => setAddressForm(f => ({ ...f, state: e.target.value }))} fullWidth />
                  <TextField label="Postcode / PIN" value={addressForm.pincode} onChange={e => setAddressForm(f => ({ ...f, pincode: e.target.value }))} fullWidth />
                </Stack>
                <TextField label="Contact number" value={addressForm.phone_number} onChange={e => setAddressForm(f => ({ ...f, phone_number: e.target.value }))} fullWidth />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={saveNewAddress} disabled={savingAddress} sx={{ textTransform: 'none' }}>
                    {savingAddress ? <CircularProgress size={18} color="inherit" /> : 'Save address'}
                  </Button>
                  <Button onClick={() => setAddingAddress(false)} disabled={savingAddress} sx={{ textTransform: 'none' }}>Cancel</Button>
                </Stack>
              </Stack>
            ) : addresses.length ? (
              <Stack spacing={1.5}>
                <TextField select label="Choose address" value={addressId} onChange={e => setAddressId(e.target.value)} fullWidth>
                  {addresses.map(addr => (
                    <MenuItem key={addr.id} value={String(addr.id)}>
                      {(addr.name || addr.type)} - {addr.address1}{addr.pincode ? `, ${addr.pincode}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                <Button startIcon={<AddCircleOutlineIcon />} onClick={() => setAddingAddress(true)} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                  Add a new address
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">No saved addresses yet - add one to continue.</Typography>
                <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={() => setAddingAddress(true)} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                  Add address
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={submitting || addingAddress} sx={{ textTransform: 'none' }}>
              {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit request'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function EventAgendaAndToolsPanel({ event, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [orderKind, setOrderKind] = useState(null);
  const hasAgenda = Boolean((event.event_agenda || '').trim());
  const pillLabel = expanded
    ? 'Collapse event details'
    : hasAgenda ? 'Event details & agenda' : event.is_organiser ? 'Add agenda, order prints' : 'View event details';

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: -1.5, position: 'relative', zIndex: 1 }}>
        <Box
          onClick={() => setExpanded(v => !v)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.25,
            border: '1px solid', borderColor: 'divider', borderTop: 'none',
            borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
            bgcolor: 'background.paper', cursor: 'pointer', userSelect: 'none', color: 'text.secondary',
            '&:hover': { color: 'text.primary', borderColor: 'text.secondary' },
          }}
          title={pillLabel}
        >
          <Typography variant="caption">{pillLabel}</Typography>
          <ExpandMoreIcon
            fontSize="small"
            sx={{ transition: 'transform 0.2s ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </Box>
      </Box>

      {expanded && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mt: 0.5, borderColor: 'grey.200' }}>
          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
                <ViewAgendaIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>Agenda</Typography>
              </Stack>
              <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                <EventAgendaPanel event={event} onSaved={onSaved} />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <StorefrontIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>Event Tools</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Don't chase guests for photos after the event - let them request prints right here, while the moment's still fresh.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Paper
                  variant="outlined"
                  onClick={() => setOrderKind('standee')}
                  sx={{
                    flex: 1, maxWidth: 160, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 0.5, cursor: 'pointer', p: 1.75, borderRadius: 2, textAlign: 'center', borderColor: 'grey.200',
                    transition: 'box-shadow 120ms ease, border-color 120ms ease',
                    '&:hover': { boxShadow: 2, borderColor: 'primary.main' },
                  }}
                >
                  <PhotoSizeSelectActualIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                  <Typography variant="caption" fontWeight={600}>Order event standee</Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  onClick={() => setOrderKind('stickers')}
                  sx={{
                    flex: 1, maxWidth: 160, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 0.5, cursor: 'pointer', p: 1.75, borderRadius: 2, textAlign: 'center', borderColor: 'grey.200',
                    transition: 'box-shadow 120ms ease, border-color 120ms ease',
                    '&:hover': { boxShadow: 2, borderColor: 'primary.main' },
                  }}
                >
                  <LocalOfferIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                  <Typography variant="caption" fontWeight={600}>Order event stickers</Typography>
                </Paper>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}
      <MerchOrderDialog open={Boolean(orderKind)} onClose={() => setOrderKind(null)} event={event} kind={orderKind} />
    </Box>
  );
}

// ── Clipboard panel ───────────────────────────────────────────────────────────
function ClipboardPanel({ clipboard, selectedTargetId, targets, targetLabel, onPasteTo, onPasteToAll, onDelete }) {
  const [open, setOpen] = useState(false);
  const count = clipboard.length;
  return (
    <div className={`clipboard-panel${open ? ' open' : ''}`}>
      <button type="button" className="clipboard-header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>Clipboard{count > 0 ? ` (${count})` : ''}</span>
        <span className="clipboard-chevron" aria-hidden="true">{open ? '▾' : '▴'}</span>
      </button>
      {open && (
        <div className="clipboard-body">
          {count === 0
            ? <div className="clipboard-empty">Clipboard is empty.</div>
            : clipboard.map(item => (
              <div key={item.id} className="clipboard-item">
                <div className="clipboard-item-info">
                  <span className="clipboard-item-label" title={item.label}>{item.label}</span>
                  <span className="clipboard-item-count">{item.photoIds.length} photo{item.photoIds.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="clipboard-item-actions">
                  <button type="button" className="clipboard-paste-btn"
                    onClick={() => onPasteTo(item.id, selectedTargetId)}
                    disabled={!selectedTargetId}
                    title={selectedTargetId ? `Paste to selected ${targetLabel}` : `Select a ${targetLabel} first`}>
                    Paste here
                  </button>
                  <button type="button" className="clipboard-paste-all-btn"
                    onClick={() => onPasteToAll(item.id)}
                    disabled={!targets.length}
                    title={`Paste to all ${targetLabel}s`}>
                    All
                  </button>
                  <button type="button" className="clipboard-delete-btn" onClick={() => onDelete(item.id)} title="Remove">✕</button>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── Reusable mini group card (same CSS classes as the main panel card) ────────
function GroupCardMini({ group, count, groupNames, groupThumbSrc, ensureGroupThumbnails, onNameChange, onSaveName, nameSaving, onCountClick, countExpanded = false }) {
  const editable = typeof onNameChange === 'function';
  return (
    <div className="group-item">
      <div className="group-row-top">
        <label className="group-label">{group}</label>
        <button type="button" className="group-thumb-btn" title={`Group ${group}`}>
          <GroupThumbImg group={group} src={groupThumbSrc(group)} alt={group} onEnsure={ensureGroupThumbnails} />
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {editable ? (
          <input
            type="text"
            className="group-input"
            value={groupNames[group] || ''}
            onChange={e => onNameChange(group, e.target.value)}
            placeholder="Enter name…"
          />
        ) : (
          <div className={`group-name-text${groupNames[group] ? '' : ' empty'}`}>
            {groupNames[group] || 'Unnamed'}
          </div>
        )}
        {onSaveName && (
          <button
            type="button"
            className="save-btn"
            style={{ whiteSpace: 'nowrap', padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => onSaveName(group)}
            disabled={nameSaving}
          >
            {nameSaving ? 'Saving…' : 'Save Name'}
          </button>
        )}
      </div>
      {onCountClick ? (
        <button
          type="button"
          className={`group-count group-count-toggle${countExpanded ? ' expanded' : ''}`}
          onClick={onCountClick}
          aria-expanded={countExpanded}
        >
          ({count} faces)
        </button>
      ) : (
        <span className="group-count">({count} faces)</span>
      )}
    </div>
  );
}

function MemoryFaceThumb({ src, name }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="memory-face-no-thumb">&#128100;</span>;
  return <img src={src} alt={name} onError={() => setFailed(true)} />;
}

function FaceCropCanvas({ src, face, label }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !src || !face?.bbox) return undefined;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const bbox = face.bbox || [];
      const x1 = Number(bbox[0]) || 0;
      const y1 = Number(bbox[1]) || 0;
      const x2 = Number(bbox[2]) || x1 + Number(face.size?.[0] || 1);
      const y2 = Number(bbox[3]) || y1 + Number(face.size?.[1] || 1);
      const width = Math.max(1, x2 - x1);
      const height = Math.max(1, y2 - y1);
      const pad = Math.max(width, height) * 0.28;
      const sx = Math.max(0, x1 - pad);
      const sy = Math.max(0, y1 - pad);
      const sw = Math.min(img.naturalWidth - sx, width + pad * 2);
      const sh = Math.min(img.naturalHeight - sy, height + pad * 2);
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src, face]);

  return <canvas ref={canvasRef} width={72} height={72} aria-label={label} />;
}

function FacePhotoPreview({ item, previewSrc }) {
  const [transform, setTransform] = useState({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 });
  if (!item) return null;
  const src = previewSrc(item.photo.photo_id);
  const bbox = item.face?.bbox || [0, 0, 0, 0];
  const width = Number(item.face?.size?.[0]) || Math.max(1, Number(bbox[2]) - Number(bbox[0]));
  const height = Number(item.face?.size?.[1]) || Math.max(1, Number(bbox[3]) - Number(bbox[1]));

  function onLoad(ev) {
    const img = ev.currentTarget;
    const cW = img.clientWidth;
    const cH = img.clientHeight;
    if (!img.naturalWidth || !img.naturalHeight || !cW || !cH) return;
    const ia = img.naturalWidth / img.naturalHeight;
    const ca = cW / cH;
    let rW = cW;
    let rH = cH;
    if (ca > ia) { rH = cH; rW = rH * ia; } else { rW = cW; rH = rW / ia; }
    setTransform({
      scaleX: rW / img.naturalWidth,
      scaleY: rH / img.naturalHeight,
      offsetX: (cW - rW) / 2,
      offsetY: (cH - rH) / 2,
    });
  }

  return (
    <div className="group-face-photo-preview">
      <div className="group-face-photo-frame">
        <img src={src} alt={item.photo.path || item.photo.photo_id} onLoad={onLoad} />
        <span
          className="group-face-photo-box"
          style={{
            left: `${transform.offsetX + Number(bbox[0] || 0) * transform.scaleX}px`,
            top: `${transform.offsetY + Number(bbox[1] || 0) * transform.scaleY}px`,
            width: `${width * transform.scaleX}px`,
            height: `${height * transform.scaleY}px`,
          }}
        />
      </div>
      <div className="group-face-photo-caption">{item.photo.path || item.photo.photo_id}</div>
    </div>
  );
}

function GroupFacePreviewPanel({ items, selectedId, onSelect, previewSrc }) {
  if (!items.length) return <div className="group-face-empty">No matching faces found in loaded photos.</div>;
  const selected = items.find(item => item.id === selectedId) || items[0];
  return (
    <div className="group-face-preview-panel">
      <div className="group-face-strip">
        {items.map((item, index) => (
          <button
            type="button"
            key={item.id}
            className={`group-face-crop${selected.id === item.id ? ' selected' : ''}`}
            onClick={() => onSelect(item.id)}
            title={item.photo.path || item.photo.photo_id}
          >
            <FaceCropCanvas src={previewSrc(item.photo.photo_id)} face={item.face} label={`Face ${index + 1}`} />
            <span>F{index + 1}</span>
          </button>
        ))}
      </div>
      <FacePhotoPreview item={selected} previewSrc={previewSrc} />
    </div>
  );
}

function FaceAssignmentInput({ group, value, memoryFaces, memoryFaceThumbUrls, groupNames = {}, groupThumbSrc, ensureGroupThumbnails, onSelectMemoryFace, onCreateNew, onClear, onOpenOptions }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const [pendingNewName, setPendingNewName] = useState('');
  const query = draft.trim().toLowerCase();
  const options = useMemo(() => {
    if (!focused) return [];
    const source = memoryFaces || [];
    const filtered = query
      ? source.filter(face => (
          String(face.display_name || '').toLowerCase().includes(query)
          || String(face.group_id || '').toLowerCase().includes(query)
        ))
      : source;
    return filtered.slice(0, 20);
  }, [focused, memoryFaces, query]);
  const nameToEventGroup = useMemo(() => {
    const map = {};
    Object.entries(groupNames || {}).forEach(([gId, name]) => {
      const key = String(name || '').trim().toLowerCase();
      if (key) map[key] = gId;
    });
    return map;
  }, [groupNames]);
  const faceThumbSrc = useCallback((face) => {
    const eventGroup = nameToEventGroup[String(face.display_name || '').trim().toLowerCase()];
    if (eventGroup && groupThumbSrc) return groupThumbSrc(eventGroup) || '';
    return memoryFaceThumbUrls[face.thumbnail_key] || '';
  }, [groupThumbSrc, memoryFaceThumbUrls, nameToEventGroup]);
  const hasDraft = Boolean(draft.trim());

  useEffect(() => {
    if (!focused) setDraft('');
  }, [focused, value]);

  useEffect(() => {
    if (!focused || !ensureGroupThumbnails) return;
    const eventGroups = options
      .map(face => nameToEventGroup[String(face.display_name || '').trim().toLowerCase()])
      .filter(Boolean);
    if (eventGroups.length) ensureGroupThumbnails(eventGroups);
  }, [focused, options, nameToEventGroup, ensureGroupThumbnails]);

  useEffect(() => {
    setPendingNewName('');
  }, [group, value]);

  function commitMemoryFace(face) {
    if (typeof onSelectMemoryFace !== 'function') return;
    setPendingNewName('');
    onSelectMemoryFace(group, face);
    setFocused(false);
  }

  function chooseNewGroup() {
    const name = draft.trim();
    if (!name) return;
    setPendingNewName(name);
    setFocused(false);
  }

  function savePendingNewGroup() {
    const name = pendingNewName.trim();
    if (!name || typeof onCreateNew !== 'function') return;
    onCreateNew(group, name);
    setPendingNewName('');
  }

  const inputValue = focused ? draft : (pendingNewName || value || '');

  return (
    <div className="face-assignment-input">
      <input
        type="text"
        className="group-input"
        value={inputValue}
        onChange={e => {
          setPendingNewName('');
          setDraft(e.target.value);
        }}
        onFocus={() => { setDraft(pendingNewName || ''); setFocused(true); onOpenOptions?.(); }}
        onBlur={() => setTimeout(() => setFocused(false), 160)}
        placeholder={value ? 'Type to change assignment...' : 'Type to choose or create...'}
      />
      <button
        type="button"
        className="assignment-menu-btn"
        onMouseDown={e => e.preventDefault()}
        onClick={() => { setDraft(''); setFocused(true); onOpenOptions?.(); }}
        title="Show assignment options"
      >
        v
      </button>
      {value && (
        <button type="button" className="assignment-clear-btn" onMouseDown={e => e.preventDefault()} onClick={() => onClear(group)} title="Clear assignment">
          x
        </button>
      )}
      {focused && (hasDraft || options.length > 0) && (
        <div className="face-assignment-menu">
          {options.map(face => (
            <button
              key={face.group_id}
              type="button"
              className="face-assignment-option"
              onMouseDown={e => {
                e.preventDefault();
                commitMemoryFace(face);
              }}
              title={`${face.display_name} (${face.group_id})`}
            >
              <MemoryFaceThumb src={faceThumbSrc(face)} name={face.display_name} />
              <span>{face.display_name}</span>
              <small>{face.group_id}</small>
            </button>
          ))}
          {hasDraft && (
            <button
              type="button"
              className="face-assignment-option new"
              onMouseDown={e => {
                e.preventDefault();
                chooseNewGroup();
              }}
              title={`Select "${draft.trim()}" as a new memory face`}
            >
              <span className="new-group-plus">+</span>
              <span>Use as new group</span>
              <small>{draft.trim()}</small>
            </button>
          )}
        </div>
      )}
      {pendingNewName && (
        <div className="assignment-pending-new">
          <span className="assignment-pending-chip">
            <span className="new-group-plus small">+</span>
            {pendingNewName}
          </span>
          <button type="button" className="assignment-save-new" onClick={savePendingNewGroup}>
            Save to Memory
          </button>
          <button type="button" className="assignment-cancel-new" onClick={() => setPendingNewName('')} title="Cancel new group">
            x
          </button>
        </div>
      )}
    </div>
  );
}

// ── Face re-group modal ───────────────────────────────────────────────────────
function FaceRegroupModal({ open, onClose, photo, faceIndex, face, eventId, groups, groupNames, groupThumbSrc, ensureGroupThumbnails, memoryFaceThumbUrls, photos = [], previewSrc, onRegrouped, onBlurFace }) {
  const [selection, setSelection] = useState({ mode: '', face: null, name: '' });
  const [query, setQuery] = useState('');
  const [applyToAll, setApplyToAll] = useState(false);
  const [showGroupFaces, setShowGroupFaces] = useState(false);
  const [selectedGroupFaceId, setSelectedGroupFaceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [reclustering, setReclustering] = useState(false);
  const [reclusterMessage, setReclusterMessage] = useState('');
  const [error, setError] = useState('');
  const [memoryFaces, setMemoryFaces] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryFetchError, setMemoryFetchError] = useState('');
  const reclusterInFlightRef = useRef(false);

  // Reverse map: normalised display_name → raw event group id
  const nameToEventGroup = useMemo(() => {
    const map = {};
    Object.entries(groupNames).forEach(([gId, name]) => {
      if (name && name.trim()) map[name.trim().toLowerCase()] = gId;
    });
    return map;
  }, [groupNames]);

  useEffect(() => {
    if (!open || !face || !ensureGroupThumbnails) return;
    const cleanQ = query.trim().toLowerCase();
    const matched = memoryFaces
      .filter(f => !cleanQ || cleanQ === 'unnamed'
        || String(f.display_name || '').toLowerCase().includes(cleanQ)
        || String(f.group_id || '').toLowerCase().includes(cleanQ))
      .slice(0, 20);
    const ids = [face.group || 'Unknown', ...matched.map(f => nameToEventGroup[String(f.display_name || '').toLowerCase()])].filter(Boolean);
    ensureGroupThumbnails(ids);
  }, [open, face, query, memoryFaces, nameToEventGroup, ensureGroupThumbnails]);

  async function loadMemoryFaces() {
    setMemoryLoading(true);
    setMemoryFetchError('');
    try {
      const data = await eventApi.getNamedFaces();
      setMemoryFaces(data.faces || []);
    } catch (e) {
      setMemoryFetchError('Failed to load faces from memory.');
    } finally {
      setMemoryLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      setSelection({ mode: '', face: null, name: '' });
      setQuery('');
      setApplyToAll(false);
      setShowGroupFaces(false);
      setSelectedGroupFaceId('');
      setReclusterMessage('');
      setError('');
      loadMemoryFaces();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !face) return null;

  const currentGroup = face.group || 'Unknown';
  const currentDisplayName = (groupNames[currentGroup] || '').trim();
  const groupFaceItems = (photos || []).flatMap((p) => (
    (p.faces || [])
      .map((f, idx) => ({ photo: p, face: f, faceIndex: idx }))
      .filter(item => String(item.face?.group || 'Unknown') === currentGroup)
      .map(item => ({
        ...item,
        id: `${item.photo.photo_id || item.photo.path || 'photo'}-${item.faceIndex}`,
      }))
  ));
  const resolvedPreviewSrc = previewSrc || (() => '');

  let targetGroup = '';
  if (selection.mode === 'memory' && selection.face) {
    targetGroup = nameToEventGroup[String(selection.face.display_name || '').toLowerCase()] || '';
  }

  const cleanQuery = query.trim();
  const filteredMemoryFaces = memoryFaces
    .filter(f => {
      const q = cleanQuery.toLowerCase();
      if (!q || q === 'unnamed') return true;
      return (
        String(f.display_name || '').toLowerCase().includes(q)
        || String(f.group_id || '').toLowerCase().includes(q)
      );
    })
    .slice(0, 20);
  const exactMemoryName = memoryFaces.some(f => String(f.display_name || '').trim().toLowerCase() === cleanQuery.toLowerCase());
  const canCreate = cleanQuery && cleanQuery.toLowerCase() !== 'unnamed' && !exactMemoryName;

  const busy = saving || reclustering;

  function handleBlurFace() {
    if (!photo || !face) return;
    onClose();
    onBlurFace?.({ photo, face });
  }

  async function handleRegroup() {
    const cleanNewName = selection.mode === 'new' ? String(selection.name || query || '').trim() : '';
    if (!selection.mode || (selection.mode === 'memory' && !selection.face?.group_id) || (selection.mode === 'new' && !cleanNewName)) {
      setError('Select Unnamed, choose a face from memory, or create a new memory face.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await eventApi.regroupFace(eventId, {
        photo_id: photo.photo_id,
        face_index: faceIndex,
        current_group: currentGroup,
        new_group: targetGroup,
        target_memory_group_id: selection.mode === 'memory' ? selection.face.group_id : undefined,
        display_name: selection.mode === 'new' ? cleanNewName : undefined,
        clear_assignment: selection.mode === 'unnamed' || undefined,
        apply_to_all: applyToAll,
      });

      const label = selection.mode === 'unnamed'
        ? 'Unnamed'
        : (res.display_name || selection.face?.display_name || cleanNewName || res.new_group_id || 'selected face');
      const groupRename = res.old_group_id && res.new_group_id ? {
        oldGroupId: res.old_group_id,
        newGroupId: res.new_group_id,
        displayName: res.display_name || '',
        groups: res.groups,
      } : null;
      onRegrouped({
        message: `Updated ${res.changed} face${res.changed !== 1 ? 's' : ''} - group "${currentGroup}" → "${label}".`,
        severity: 'success',
        groupRename,
      });
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to reassign face.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRecluster() {
    if (reclusterInFlightRef.current) return;
    reclusterInFlightRef.current = true;
    setReclustering(true);
    setReclusterMessage('Starting re-cluster...');
    setError('');
    try {
      const initial = await eventApi.reclusterGroup(eventId, currentGroup);
      // Synchronous early-exit (e.g. only one face in group)
      if (initial.status !== 'pending' && initial.status !== 'running') {
        if (initial.split) {
          onRegrouped({ message: `Group "${currentGroup}" split into ${initial.n_clusters} clusters: ${initial.new_groups.join(', ')}.`, severity: 'success' });
          onClose();
        } else {
          setError(initial.message || 'Could not find distinct sub-clusters.');
        }
        return;
      }
      // Poll until done
      const jobId = initial.job_id;
      const POLL_INTERVAL = 2500;
      const MAX_WAIT = 900000;
      const deadline = Date.now() + MAX_WAIT;
      let res = null;
      let attempts = 0;
      setReclusterMessage('Analysing faces. This can take a minute while the face model warms up...');
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        const poll = await eventApi.reclusterGroupStatus(eventId, jobId);
        attempts += 1;
        if (poll.status === 'pending') setReclusterMessage('Waiting for re-cluster job to start...');
        else if (poll.status === 'running') setReclusterMessage(`Analysing faces${attempts > 3 ? '...' : ''}`);
        if (poll.status === 'done' || poll.status === 'error') { res = poll; break; }
      }
      if (!res) { setError('Re-cluster timed out - please try again.'); return; }
      if (res.status === 'error') { setError(res.error || 'Re-cluster failed.'); return; }
      if (res.split) {
        onRegrouped({ message: `Group "${currentGroup}" split into ${res.n_clusters} clusters: ${res.new_groups.join(', ')}.`, severity: 'success' });
        onClose();
      } else {
        setError(res.message || 'Could not find distinct sub-clusters.');
      }
    } catch (e) {
      setError(e.message || 'Re-cluster failed.');
    } finally {
      setReclustering(false);
      setReclusterMessage('');
      reclusterInFlightRef.current = false;
    }
  }

  return (
    <div className="regroup-modal-overlay" onClick={onClose}>
      <div className="regroup-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        <h3 className="modal-title">Reassign Face</h3>

        <p className="modal-section-title">Current group</p>
        <GroupCardMini
          group={currentGroup}
          count={groups[currentGroup] || 0}
          groupNames={groupNames}
          groupThumbSrc={groupThumbSrc}
          ensureGroupThumbnails={ensureGroupThumbnails}
          onCountClick={() => {
            setShowGroupFaces(prev => {
              const next = !prev;
              if (next && !selectedGroupFaceId && groupFaceItems[0]) {
                setSelectedGroupFaceId(groupFaceItems[0].id);
              }
              return next;
            });
          }}
          countExpanded={showGroupFaces}
        />
        {showGroupFaces && (
          <GroupFacePreviewPanel
            items={groupFaceItems}
            selectedId={selectedGroupFaceId}
            onSelect={setSelectedGroupFaceId}
            previewSrc={resolvedPreviewSrc}
          />
        )}
        {currentDisplayName && <p className="modal-current-name">Currently assigned to {currentDisplayName}</p>}

        <div className="modal-memory-header">
          <p className="modal-section-title" style={{ margin: 0 }}>Set face as</p>
          <button type="button" className="reload-memory-btn" onClick={loadMemoryFaces} disabled={memoryLoading}>
            {memoryLoading ? 'Loading…' : 'Reload from memory'}
          </button>
        </div>

        {memoryFetchError && <div className="modal-error" style={{ marginTop: 4 }}>{memoryFetchError}</div>}

        <input
          type="text"
          className="group-input"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setSelection({ mode: '', face: null, name: '' });
          }}
          placeholder="Type to filter memory faces or create a new one..."
          disabled={busy}
          style={{ margin: '8px 0' }}
        />

        {memoryLoading ? (
          <p style={{ color: '#888', fontSize: '0.85rem', margin: '8px 0' }}>Loading faces from memory...</p>
        ) : (
              <div className="modal-group-picker-list">
                <button
                  type="button"
                  className={`modal-group-option${selection.mode === 'unnamed' ? ' selected' : ''}`}
                  onClick={() => { setSelection({ mode: 'unnamed', face: null, name: '' }); setQuery('Unnamed'); }}
                >
                  <span className="memory-face-no-thumb">?</span>
                  <span>Unnamed</span>
                  <small>Clear assignment</small>
                </button>
                {filteredMemoryFaces.map(f => {
                  const eventGroup = nameToEventGroup[f.display_name.toLowerCase()];
                  const thumbUrl = (eventGroup ? groupThumbSrc(eventGroup) : '') || (memoryFaceThumbUrls || {})[f.thumbnail_key] || '';
                  return (
                    <button key={f.group_id} type="button"
                      className={`modal-group-option${selection.mode === 'memory' && selection.face?.group_id === f.group_id ? ' selected' : ''}`}
                      onClick={() => {
                        setSelection({ mode: 'memory', face: f, name: f.display_name || '' });
                        setQuery(f.display_name || f.group_id || '');
                      }}>
                      <MemoryFaceThumb src={thumbUrl} name={f.display_name} />
                      <span>{f.display_name}</span>
                      <small>{f.group_id}</small>
                    </button>
                  );
                })}
                {canCreate && (
                  <button
                    type="button"
                    className={`modal-group-option new${selection.mode === 'new' ? ' selected' : ''}`}
                    onClick={() => setSelection({ mode: 'new', face: null, name: cleanQuery })}
                  >
                    <span className="new-group-plus">+</span>
                    <span>Create new face</span>
                    <small>{cleanQuery}</small>
                  </button>
                )}
              </div>
        )}

        <label className="modal-apply-all">
          <input type="checkbox" checked={applyToAll} onChange={e => setApplyToAll(e.target.checked)} />
          Apply to all {groups[currentGroup] || 0} faces in "{currentGroup}"
        </label>

        {error && <div className="modal-error">{error}</div>}
        {reclusterMessage && <div className="modal-info">{reclusterMessage}</div>}

        <div className="modal-actions">
          <button type="button" className="save-btn" onClick={handleRegroup} disabled={busy || !selection.mode}>
            {saving ? 'Saving...' : applyToAll ? 'Apply to group' : 'Apply to face'}
          </button>
          <button type="button" className="clear-btn" onClick={handleRecluster} disabled={busy}
            title="Ask the face engine to automatically split this group into sub-groups">
            {reclustering ? 'Analysing...' : 'Re-cluster group'}
          </button>
          {onBlurFace && (
            <button type="button" className="blur-face-btn" onClick={handleBlurFace} disabled={busy}
              title="Apply Gaussian blur to this face in the photo">
              Blur face
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Blur face preview modal ───────────────────────────────────────────────────
function BlurFacePreviewModal({ open, onClose, photo, face, eventId, onAccepted }) {
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [blurResult, setBlurResult] = useState(null); // { newStem, previewUrl }

  useEffect(() => {
    if (!open || !photo || !face) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setBlurResult(null);
    const payload = {
      photo_id: photo.photo_id,
      current_stem: photo.blurred_stem || photo.photo_id,
      bbox: face.bbox,
      face_group_id: face.group,
    };
    eventApi.blurFace(eventId, payload)
      .then(res => { if (!cancelled) setBlurResult(res); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || e.message || 'Blur failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, photo, face, eventId]);

  async function handleAccept() {
    if (!blurResult) return;
    setAccepting(true);
    setError('');
    try {
      await eventApi.acceptBlur(eventId, {
        photo_id: photo.photo_id,
        new_stem: blurResult.new_stem,
        face_group_id: face.group,
      });
      onAccepted?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Accept failed');
    } finally {
      setAccepting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="regroup-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="regroup-modal blur-preview-modal">
        <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        <h3 className="modal-title">Blur face preview</h3>

        {loading && (
          <div className="blur-preview-loading">
            <div className="spinner" />
            <p>Applying blur…</p>
          </div>
        )}

        {error && <div className="modal-error">{error}</div>}

        {blurResult && !loading && (
          <>
            <p className="blur-preview-hint">Review the blurred preview below. Accept to save permanently.</p>
            <div className="blur-preview-img-wrap">
              <img
                src={blurResult.preview_url}
                alt="Blurred face preview"
                className="blur-preview-img"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="save-btn" onClick={handleAccept} disabled={accepting}>
                {accepting ? 'Saving…' : 'Accept blur'}
              </button>
              <button type="button" className="clear-btn" onClick={onClose} disabled={accepting}>
                Discard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Bulk blur / revert progress modal ────────────────────────────────────────
function BlurGroupProgressModal({ open, eventId, group, mode, onClose, onDone }) {
  const [phase, setPhase] = useState('starting'); // starting | running | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open || !group) return;
    setPhase('starting');
    setProgress({ done: 0, total: 0 });
    setErrorMsg('');

    const startFn = mode === 'blur'
      ? eventApi.blurFaceGroup(eventId, group)
      : eventApi.revertBlurGroup(eventId, group);

    startFn.then(({ job_id }) => {
      const pollFn = mode === 'blur'
        ? (jid) => eventApi.blurFaceGroupStatus(eventId, jid)
        : (jid) => eventApi.revertBlurGroupStatus(eventId, jid);

      pollRef.current = setInterval(async () => {
        try {
          const s = await pollFn(job_id);
          const r = s.result || {};
          setProgress({ done: r.done || 0, total: r.total || 0 });
          if (s.status === 'done') {
            clearInterval(pollRef.current);
            setPhase('done');
            onDone?.();
          } else if (s.status === 'error') {
            clearInterval(pollRef.current);
            setPhase('error');
            setErrorMsg(s.error || 'Job failed');
          } else {
            setPhase('running');
          }
        } catch { /* keep polling */ }
      }, 2000);
    }).catch(e => {
      setPhase('error');
      setErrorMsg(e?.response?.data?.error || e.message || 'Failed to start job');
    });

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, group, mode, eventId]);

  if (!open) return null;

  const title = mode === 'blur' ? 'Blurring faces on all photos…' : 'Reverting blur on all photos…';
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="regroup-modal-overlay" onClick={phase === 'done' || phase === 'error' ? onClose : undefined}>
      <div className="regroup-modal blur-group-modal" onClick={e => e.stopPropagation()}>
        {(phase === 'done' || phase === 'error') && (
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        )}
        <h3 className="modal-title">{title}</h3>
        {(phase === 'starting' || (phase === 'running' && progress.total === 0)) && (
          <div className="blur-preview-loading">
            <div className="spinner" />
            <p>Starting…</p>
          </div>
        )}
        {phase === 'running' && progress.total > 0 && (
          <div className="blur-group-progress">
            <div className="blur-group-bar-wrap">
              <div className="blur-group-bar" style={{ width: `${pct}%` }} />
            </div>
            <p className="blur-group-count">{progress.done} / {progress.total} photos ({pct}%)</p>
          </div>
        )}
        {phase === 'done' && (
          <div className="blur-group-done">
            <p>{progress.total} photo{progress.total !== 1 ? 's' : ''} processed successfully.</p>
            <button type="button" className="save-btn" onClick={onClose}>Close</button>
          </div>
        )}
        {phase === 'error' && (
          <div className="blur-group-error">
            <p className="blur-group-error-msg">{errorMsg}</p>
            <button type="button" className="clear-btn" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Upload links dialog (organiser tab) ───────────────────────────────────────
// Lets the organiser create/list/toggle/revoke shareable, permission-gated
// event-level links (see cm-backend/lambda/routes/event_gallery.py's
// create_upload_link/list_upload_links/update_upload_link/revoke_upload_link).
// Anyone with a link can submit photos with no login; links with
// can_trigger_grouping can also trigger grouping, billed to this organiser.
function UploadLinksDialog({ open, onClose, eventId, toast }) {
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCanGroup, setNewCanGroup] = useState(false);
  const [newAutoShare, setNewAutoShare] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await eventApi.listUploadLinks(eventId);
      setLinks(Array.isArray(data?.links) ? data.links : []);
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleCreate = async () => {
    if (newCanGroup) {
      const consentOk = await confirmUploadLinkGroupingConsent();
      if (!consentOk) return;
    }
    setCreating(true);
    try {
      await eventApi.createUploadLink(eventId, { label: newLabel.trim(), can_trigger_grouping: newCanGroup, auto_share_new_submissions: newAutoShare });
      setNewLabel('');
      setNewCanGroup(false);
      setNewAutoShare(false);
      await load();
      toast('Upload link created.', 'success');
    } catch (err) {
      toast(err.message || 'Failed to create upload link', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleGrouping = async (link) => {
    const enabling = !link.can_trigger_grouping;
    if (enabling) {
      const consentOk = await confirmUploadLinkGroupingConsent();
      if (!consentOk) return;
    }
    try {
      await eventApi.updateUploadLink(eventId, link.id, { can_trigger_grouping: enabling });
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, can_trigger_grouping: enabling } : l));
    } catch (err) {
      toast(err.message || 'Failed to update upload link', 'error');
    }
  };

  const handleToggleAutoShare = async (link) => {
    const enabling = !link.auto_share_new_submissions;
    try {
      await eventApi.updateUploadLink(eventId, link.id, { auto_share_new_submissions: enabling });
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, auto_share_new_submissions: enabling } : l));
    } catch (err) {
      toast(err.message || 'Failed to update upload link', 'error');
    }
  };

  const handleRevoke = async (link) => {
    const ok = await swalConfirmDelete({
      title: 'Revoke this upload link?',
      text: 'Anyone still using it will no longer be able to submit photos.',
      confirmText: 'Revoke',
    });
    if (!ok) return;
    try {
      await eventApi.revokeUploadLink(eventId, link.id);
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, revoked: true } : l));
      toast('Upload link revoked.', 'success');
    } catch (err) {
      toast(err.message || 'Failed to revoke upload link', 'error');
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied to clipboard.', 'success');
    } catch {
      toast('Could not copy link.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Upload Links</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anyone with an upload link can submit photos - no login required. Links
          that allow grouping will charge your credits when used, and only one
          grouping job can run for this event at a time.
        </Typography>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            label="Label (optional)"
            size="small"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Family uploads"
          />
          <FormControlLabel
            control={<Switch checked={newCanGroup} onChange={(e) => setNewCanGroup(e.target.checked)} />}
            label="Allow this link to trigger grouping (charges your credits)"
          />
          <FormControlLabel
            control={<Switch checked={newAutoShare} onChange={(e) => setNewAutoShare(e.target.checked)} />}
            label="Auto-share this link's submissions to all requests, without processing"
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            {creating ? <CircularProgress size={18} color="inherit" /> : 'Create link'}
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
        ) : !links.length ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No upload links yet.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {links.map((link) => (
              <Paper key={link.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, opacity: link.revoked ? 0.5 : 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>{link.label || 'Untitled link'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {link.upload_url}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
                  {link.revoked ? (
                    <Chip size="small" label="Revoked" />
                  ) : (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap>
                      <FormControlLabel
                        sx={{ ml: -0.5 }}
                        control={<Switch size="small" checked={link.can_trigger_grouping} onChange={() => handleToggleGrouping(link)} />}
                        label={<Typography variant="caption">Can trigger grouping</Typography>}
                      />
                      <Tooltip title="Any photo submitted through this link is added to every photo request immediately, without waiting for processing.">
                        <FormControlLabel
                          control={<Switch size="small" checked={Boolean(link.auto_share_new_submissions)} onChange={() => handleToggleAutoShare(link)} />}
                          label={<Typography variant="caption">Auto-share to all requests</Typography>}
                        />
                      </Tooltip>
                    </Stack>
                  )}
                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                    {!link.revoked && (
                      <Tooltip title="Open link to upload photos yourself">
                        <IconButton size="small" onClick={() => window.open(link.upload_url, '_blank', 'noopener,noreferrer')}><OpenInNewIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {!link.revoked && (
                      <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => copyLink(link.upload_url)}><ContentCopyIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {!link.revoked && (
                      <Tooltip title="Revoke link">
                        <IconButton size="small" onClick={() => handleRevoke(link)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Lightweight lightbox opened by clicking a thumbnail in PhotoSubmissionsPanel,
// mirroring how the main gallery's own thumbnails open a full preview on
// click. Deliberately self-contained (own prev/next/keyboard handling) rather
// than reusing EventGalleryViewer's swipe-based viewer, which is tightly
// coupled to grouped-photo/face data structures that don't apply to plain,
// ungrouped photo submissions.
// Small status badge shown on a submission thumbnail (and in the preview
// dialog) - whether this submitted photo has actually made it into the
// grouped gallery yet, or is still just sitting in Photo Submissions. Backed
// by eventphotosubmissions.grouped_at (see schema.sql) - set once a
// photo-submissions-sourced grouping run that included this photo completes
// successfully, cleared/never-set otherwise.
function SubmissionGroupedBadge({ grouped }) {
  return (
    <Tooltip title={grouped ? 'In gallery' : 'Not yet in gallery'}>
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          bgcolor: grouped ? 'success.main' : 'warning.main', color: '#fff',
        }}
      >
        {grouped ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <HourglassEmptyIcon sx={{ fontSize: 12 }} />}
      </Box>
    </Tooltip>
  );
}

// A photo whose photo_id is 'sub:<submission_id>' is an unprocessed submission
// distributed to a request/circle before grouping - it has no face groups, no
// embeddings, and won't appear in moments search, but is still viewable/
// downloadable. Shared everywhere such a photo can surface: request/circle
// track strips, the main organiser gallery grid (Include unprocessed photos),
// and the attendee's own request/circle results view.
const SUBMISSION_ID_PREFIX = 'sub:';
function isUnprocessedPhotoId(pid) {
  return typeof pid === 'string' && pid.startsWith(SUBMISSION_ID_PREFIX);
}

function UnprocessedPhotoBadge() {
  return (
    <Tooltip title="The organiser hasn't processed this photo yet - it has no face groups and won't show up in moments search, but it's still visible and downloadable.">
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          bgcolor: 'warning.main', color: '#fff',
        }}
      >
        <HourglassEmptyIcon sx={{ fontSize: 12 }} />
      </Box>
    </Tooltip>
  );
}

function SubmissionPreviewDialog({ submissions, index, thumbUrls, ensureThumbUrls, onClose, onNavigate, onDelete, uploaderName, uploaderUsername, isOrganiserUploader }) {
  const open = index !== null && index >= 0 && index < submissions.length;
  const sub = open ? submissions[index] : null;
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') onNavigate((index - 1 + submissions.length) % submissions.length);
      else if (e.key === 'ArrowRight') onNavigate((index + 1) % submissions.length);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, index, submissions.length, onNavigate, onClose]);

  // The grid only pre-fetches the currently-paginated page's worth of thumbnails
  // (see PhotoSubmissionsPanel's own effect) - navigating prev/next in here can
  // reach a submission_id beyond that page, so ensure it's fetched on demand too.
  // Shares the same cache/in-flight tracking, so this never duplicates a request
  // the grid has already made or is already making.
  useEffect(() => {
    if (sub?.submission_id) ensureThumbUrls([sub.submission_id]);
  }, [sub, ensureThumbUrls]);

  if (!open) return null;

  const infoPhoto = {
    original_filename: sub.original_filename,
    created_at: sub.created_at,
    submitted_by_name: uploaderName ? `${uploaderName}${isOrganiserUploader ? ' (Organiser)' : ''}` : null,
    submitted_by_username: uploaderUsername || null,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#111' } }}>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
        >
          <CloseIcon />
        </IconButton>
        <Tooltip title={infoOpen ? 'Hide photo info' : 'Photo info'}>
          <IconButton
            onClick={() => setInfoOpen((v) => !v)}
            sx={{
              position: 'absolute', top: 8, left: 8, color: '#fff',
              bgcolor: infoOpen ? 'primary.main' : 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: infoOpen ? 'primary.dark' : 'rgba(0,0,0,0.6)' },
            }}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
        {submissions.length > 1 && (
          <>
            <IconButton
              onClick={() => onNavigate((index - 1 + submissions.length) % submissions.length)}
              sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={() => onNavigate((index + 1) % submissions.length)}
              sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </>
        )}
        {thumbUrls[sub.submission_id] ? (
          <Box
            component="img"
            src={thumbUrls[sub.submission_id]}
            alt={sub.original_filename}
            sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        ) : (
          <CircularProgress sx={{ color: '#fff' }} />
        )}
      </Box>
      <Collapse in={infoOpen}>
        <Box sx={{ bgcolor: '#111', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <PhotoInfoPanel photo={infoPhoto} dark />
        </Box>
      </Collapse>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#1a1a1a', gap: 1.5 }}>
        <SubmissionGroupedBadge grouped={Boolean(sub.grouped)} />
        <Typography variant="body2" color="#fff" noWrap sx={{ flex: 1, mr: 2 }}>
          {sub.original_filename}
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', ml: 1 }}>
            ({index + 1}/{submissions.length})
          </Box>
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(sub.submission_id)}
          sx={{ textTransform: 'none' }}
        >
          Delete
        </Button>
      </Box>
    </Dialog>
  );
}

// ── Photo Submissions panel (organiser tab) ───────────────────────────────────
// Matches UploadLinkPage.js's own submissions-gallery pagination (same page
// size, same "Load N more (M remaining)" pattern) - mounting hundreds/
// thousands of <img> thumbnails at once for a heavy submitter is a real
// rendering cost, even with loading="lazy".
const SUBMISSIONS_PAGE_SIZE = 20;

function PhotoSubmissionsPanel({ event, isMobile = false, onReload, toast, refreshKey, pendingDroppedFiles, clearPendingDroppedFiles, onAddSubmissionsToTrack, onSubmissionsDeleted, onGroupingComplete }) {
  const eid = event.event_id;
  const [loading, setLoading] = useState(true);
  const [submitters, setSubmitters] = useState([]);
  const [selectedSubmitterIdx, setSelectedSubmitterIdx] = useState(0);
  const [submissionDialogOpen, setSubmissionDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(SUBMISSIONS_PAGE_SIZE);
  const [previewIndex, setPreviewIndex] = useState(null); // index into filteredSubmissions, or null when closed
  const [ungroupedOnly, setUngroupedOnly] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [ingestStatus, setIngestStatus] = useState('idle'); // idle | processing | complete | failed
  const [ingestProgress, setIngestProgress] = useState({ percent: 0, comment: '' });
  // Covers just the initial ingestPhotoSubmissions round-trip, before the
  // progress bar has anything to show - ingestStatus only flips to
  // 'processing' once that call resolves, so without this the button stays
  // clickable (and looks idle) for the duration of the request itself.
  const [isStarting, setIsStarting] = useState(false);
  const [uploadLinksOpen, setUploadLinksOpen] = useState(false);
  const ingestPollRef = useRef(null);

  // Per-thumbnail "⋮" overflow menu (Add to Request/Circle, Delete) - only
  // one open at a time, tracked by submission_id rather than a DOM anchor
  // stored per-thumbnail so re-renders during pagination don't stash stale refs.
  const [thumbMenu, setThumbMenu] = useState({ anchorEl: null, submissionId: null });

  // Organiser's own direct upload into this same submissions pool - deliberately
  // never calls ingestPhotoSubmissions, so it never starts a grouping job on its
  // own; it just lands the photos here for the organiser (or anyone) to add later.
  const [organiserUploading, setOrganiserUploading] = useState(false);
  const [organiserUploadProgress, setOrganiserUploadProgress] = useState({ current: 0, total: 0 });

  // Event-wide "auto-share" toggle: persisted on events.auto_share_new_submissions.
  // When on, every future submission (this session's or anyone else's) is added
  // to every request the moment it lands, with no processing/grouping needed -
  // see share_new_submissions_with_requests() on the backend.
  const [autoShareAll, setAutoShareAll] = useState(() => Boolean(event.auto_share_new_submissions));
  const [autoShareSaving, setAutoShareSaving] = useState(false);
  async function handleAutoShareAllToggle(_e, checked) {
    setAutoShareSaving(true);
    try {
      await eventApi.updateAutoShareNewSubmissions(eid, checked);
      setAutoShareAll(checked);
    } catch {
      toast?.('Could not update auto-share setting.', 'error');
    } finally {
      setAutoShareSaving(false);
    }
  }
  const organiserFileInputRef = useRef(null);

  // Reconciliation for a still-running native background upload (this panel
  // reopened after the app was killed/backgrounded mid-upload) - mirrors
  // AttendeePhotoUploadSection's matching effect exactly, just scoped to the
  // 'organiser-submit' flowKind instead of 'attendee-submit'.
  const [resumeJob, setResumeJob] = useState(null);
  const resumedJobIdsRef = useRef(new Set());
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let cancelled = false;
    let intervalId = null;
    const check = async () => {
      const { jobs } = await nativeUploadService.listIncompleteJobs();
      if (cancelled) return;
      const mine = (jobs || []).find((j) => String(j.eventId) === String(eid) && j.flowKind === 'organiser-submit');
      setResumeJob(mine || null);
      if (mine && !resumedJobIdsRef.current.has(mine.jobId)) {
        resumedJobIdsRef.current.add(mine.jobId);
        runUploadJob(mine, {}).then(() => load()).catch(() => {});
      }
      if (mine && !intervalId) intervalId = setInterval(check, 10000);
      if (!mine && intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    check();
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  // Durable WorkManager counterpart to the legacy resume-reconciliation effect
  // above - 'organiser-photo-submit' jobs are always already running (or
  // already finished) natively by the time this mounts, so this only ever
  // reads state back (see useNativeUploadObserver's own doc comment), it never
  // resumes/re-runs anything the way the legacy 'organiser-submit' path above
  // still needs to. Both effects write the same resumeJob/ResumeUploadBanner
  // state - harmless, since in practice only one flowKind is ever actually
  // in flight for a given upload.
  const nativePhotoSubmitJob = useNativeUploadObserver(eid, ['organiser-photo-submit']);
  const priorNativePhotoSubmitJobRef = useRef(null);
  useEffect(() => {
    setResumeJob(nativePhotoSubmitJob || null);
    if (!nativePhotoSubmitJob && priorNativePhotoSubmitJobRef.current) load();
    priorNativePhotoSubmitJobRef.current = nativePhotoSubmitJob;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativePhotoSubmitJob]);

  // Presigned thumbnail/preview URLs, batch-fetched (never one request per photo -
  // see getPhotoSubmissionPresignedUrls's comment) and cached by submission_id for
  // the life of this panel. fetchedSubmissionIdsRef tracks in-flight/already-fetched
  // IDs so re-renders (pagination, submitter switch) don't re-request the same ones;
  // an ID is removed again on failure so it's eligible for retry.
  const [thumbUrls, setThumbUrls] = useState({});
  const fetchedSubmissionIdsRef = useRef(new Set());
  const ensureThumbUrls = useCallback((ids) => {
    const need = ids.filter(id => id && !fetchedSubmissionIdsRef.current.has(id));
    if (!need.length) return;
    need.forEach(id => fetchedSubmissionIdsRef.current.add(id));
    eventApi.getPhotoSubmissionPresignedUrls(eid, need).then(res => {
      setThumbUrls(prev => ({ ...prev, ...(res.urls || {}) }));
    }).catch(() => { need.forEach(id => fetchedSubmissionIdsRef.current.delete(id)); });
  }, [eid]);

  // Pagination, any open preview, the ungrouped-only filter, and any active
  // selection are all scoped to one submitter's photo list - reset all of
  // them when switching submitters instead of carrying over state that no
  // longer refers to the newly-selected list.
  useEffect(() => {
    setVisibleCount(SUBMISSIONS_PAGE_SIZE);
    setPreviewIndex(null);
    setUngroupedOnly(false);
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, [selectedSubmitterIdx]);

  const load = useCallback(async () => {
    try {
      const data = await eventApi.listPhotoSubmissions(eid);
      setSubmitters(Array.isArray(data?.submitters) ? data.submitters : []);
    } catch {
      setSubmitters([]);
    } finally {
      setLoading(false);
    }
  }, [eid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirrors AttendeePhotoUploadSection's handleFilesSelected - same
  // background-job machinery on native (flowKind 'organiser-submit', so it
  // keeps running unconditionally regardless of whether the app stays open;
  // see that function's matching comment), foreground FormData post on web.
  // Deliberately skips the attendee flow's upload-consent confirm prompt -
  // this is the organiser managing their own event from this panel.
  const handleOrganiserFilesUpload = useCallback(async (rawFiles) => {
    const files = Array.from(rawFiles || []).filter(isCompressibleEventImage);
    if (!files.length) {
      toast('No compatible images selected.', 'warning');
      return;
    }
    setOrganiserUploading(true);
    setOrganiserUploadProgress({ current: 0, total: files.length });
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(eid, 'photo-submit');
    try {
      if (isNative) {
        // Durable WorkManager job ('organiser-photo-submit' flowKind) - the
        // native worker compresses each original itself, same as the
        // initial-grouping/add-photos flowKinds already do, so raw (not
        // pre-compressed) files are handed off here. Replaces the old
        // 'organiser-submit' legacy path (stagingProvider + startForegroundUpload
        // + runUploadJob), which was foreground-only and didn't survive the
        // app being killed mid-upload - see resumeUploadJob.js's own doc
        // comment on why that path existed and is being phased out.
        await nativeUploadService.startUploadJob({
          jobId, flowKind: 'organiser-photo-submit', eventId: String(eid),
          flowMeta: { canGroup: false },
          totalFiles: files.length,
          entries: files.map((f) => ({ file: f, relativePath: f.webkitRelativePath || f.name })),
          resumeSource: null,
        });
        toast(`Uploading ${files.length} photo${files.length > 1 ? 's' : ''} in the background - check back here once it's done.`, 'info');
      } else {
        const compressedEntries = [];
        for (let i = 0; i < files.length; i += 1) {
          setOrganiserUploadProgress({ current: i + 1, total: files.length });
          const compressed = await compressEventImage(files[i]);
          compressedEntries.push({ file: compressed, relativePath: compressedImageName(files[i].name) });
        }
        await uploadPhotoSubmissionsInBatches(eid, compressedEntries, {
          onProgress: ({ uploaded, total }) => setOrganiserUploadProgress({ current: uploaded, total }),
        });
        toast(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded.`, 'success');
      }
      load();
    } catch (err) {
      toast(err.message || 'Upload failed', 'error');
    } finally {
      setOrganiserUploading(false);
      setOrganiserUploadProgress({ current: 0, total: 0 });
    }
  }, [eid, load, toast]);

  const handleOrganiserFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    handleOrganiserFilesUpload(files);
  };

  // Files dropped anywhere on the event page while this panel wasn't even
  // mounted yet (see EventGalleryViewer's window-level drop handler) - once
  // the drop switches the tab open and this effect sees them, upload them the
  // same way a manual "Upload photos" pick would, then hand the empty slot
  // back so a later drop can refill it.
  useEffect(() => {
    if (!pendingDroppedFiles || !pendingDroppedFiles.length) return;
    handleOrganiserFilesUpload(pendingDroppedFiles);
    clearPendingDroppedFiles?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDroppedFiles]);

  const selectedSubmitter = submitters[selectedSubmitterIdx] || null;
  // Applied before pagination/preview so both only ever see what the filter
  // allows - the ungrouped-only toggle and "select all" both key off this,
  // not the raw submitter.submissions list.
  const filteredSubmissions = selectedSubmitter
    ? selectedSubmitter.submissions.filter(s => !ungroupedOnly || !s.grouped)
    : [];
  // Independent of the ungroupedOnly display toggle above - this is what
  // "Add unprocessed to gallery" actually acts on by default (see
  // handleAddToGallery), so its count/disabled state stay accurate even when
  // the toggle itself is off and grouped photos are still visible on screen.
  const ungroupedCount = selectedSubmitter
    ? selectedSubmitter.submissions.filter(s => !s.grouped).length
    : 0;
  // Backs "Process all unprocessed" below - every ungrouped submission across
  // every submitter, not just the one currently selected in the sidebar.
  const allUnprocessedIds = submitters.flatMap(s => s.submissions.filter(x => !x.grouped).map(x => x.submission_id));
  // Mirrors EventPage's own hasExistingGallery/mode logic (and
  // groupSubmittedPhotosViaLink on the upload-link page): an event with no
  // gallery yet needs its very first ('initial') grouping run kicked off from
  // submitted photos too, not just an incremental 'add' into an existing one.
  const hasExistingGallery = Boolean(event.s3_output_prefix);
  const groupingModeHint = hasExistingGallery ? 'add' : 'initial';
  // A job already running elsewhere (e.g. the main gallery panel's own
  // initial/add upload) - block starting a second one for the same event,
  // regardless of grouping_status='none'/'complete'/'failed' being otherwise
  // eligible to start.
  const groupingBusy = ['starting', 'processing', 'uploading'].includes(event.grouping_status);

  // Batch-fetch presigned URLs for exactly the page of thumbnails currently
  // visible (not the whole submitter's list) - grows as "Load N more" advances
  // visibleCount.
  useEffect(() => {
    ensureThumbUrls(filteredSubmissions.slice(0, visibleCount).map(s => s.submission_id));
  }, [filteredSubmissions, visibleCount, ensureThumbUrls]);

  const toggleSelected = (submissionId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(submissionId)) next.delete(submissionId);
      else next.add(submissionId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    const ok = await swalConfirmDelete({
      title: `Delete ${selectedIds.size} photo${selectedIds.size === 1 ? '' : 's'}?`,
      text: 'This permanently deletes the selected submissions and their photos.',
      confirmText: 'Delete',
    });
    if (!ok) return;
    const ids = Array.from(selectedIds);
    try {
      await eventApi.bulkDeletePhotoSubmissions(eid, ids);
      const idSet = new Set(ids);
      setSubmitters(prev => prev.map((s, i) => i !== selectedSubmitterIdx ? s : {
        ...s,
        submissions: s.submissions.filter(sub => !idSet.has(sub.submission_id)),
      }).filter(s => s.submissions.length > 0));
      if (selectedSubmitter && ids.length >= selectedSubmitter.submissions.length) {
        setSelectedSubmitterIdx(Math.max(0, selectedSubmitterIdx - 1));
      }
      setPreviewIndex(null);
      setSelectionMode(false);
      setSelectedIds(new Set());
      onSubmissionsDeleted?.(ids);
      toast(`${ids.length} photo${ids.length === 1 ? '' : 's'} deleted.`, 'success');
    } catch (err) {
      toast(err.message || 'Failed to delete photos', 'error');
    }
  };

  const handleDelete = async (submissionId) => {
    try {
      await eventApi.deletePhotoSubmission(eid, submissionId);
      setSubmitters(prev => prev.map((s, i) => i !== selectedSubmitterIdx ? s : {
        ...s,
        submissions: s.submissions.filter(sub => sub.submission_id !== submissionId),
      }).filter(s => s.submissions.length > 0));
      if (selectedSubmitter && selectedSubmitter.submissions.length === 1) {
        setSelectedSubmitterIdx(Math.max(0, selectedSubmitterIdx - 1));
      }
      // Close the preview rather than trying to re-target it at whatever now
      // occupies the deleted index - simpler and avoids ever showing a stale
      // or out-of-bounds photo after a delete.
      setPreviewIndex(null);
      onSubmissionsDeleted?.([submissionId]);
      toast('Photo deleted.', 'success');
    } catch (err) {
      toast(err.message || 'Failed to delete photo', 'error');
    }
  };

  const pollIngestStatus = useCallback(async () => {
    try {
      const res = await eventApi.getGroupingStatus(eid);
      setIngestProgress({
        percent: res.progress?.percent ?? res.percent ?? 0,
        comment: res.comment || 'Processing…',
      });
      if (res.status !== 'processing' && res.status !== 'uploading' && res.status !== 'starting') {
        clearInterval(ingestPollRef.current);
        ingestPollRef.current = null;
        setIngestStatus(res.status === 'complete' ? 'complete' : 'failed');
        if (res.status === 'complete') {
          toast('Photos added to gallery successfully.', 'success');
          onReload();
          load();
          // Tells the top-level event.grouping_status to catch up - the Smart
          // Sort/Add Photos panels' own "still says N photos selected" reconciliation
          // is keyed off that status flipping to 'complete', and this run started
          // from this tab, so neither of those panels' own pollers ever saw it.
          onGroupingComplete?.();
        } else {
          toast('Grouping failed. Check Smart Sort for details.', 'error');
        }
      }
    } catch { /* keep polling */ }
  }, [eid, onReload, load, toast, onGroupingComplete]);

  useEffect(() => {
    if (ingestStatus !== 'processing') return undefined;
    ingestPollRef.current = setInterval(pollIngestStatus, 3000);
    return () => { clearInterval(ingestPollRef.current); ingestPollRef.current = null; };
  }, [ingestStatus, pollIngestStatus]);

  const handleAddToGallery = async () => {
    if (!selectedSubmitter) return;
    // While in selection mode with something picked, only those photos are
    // ingested (an incremental grouping run scoped to just the selection),
    // deliberately including already-grouped ones if explicitly selected -
    // that's the one intentional way to re-run a photo through grouping.
    // Otherwise (the default, no selection), only photos not yet grouped are
    // sent - a bare "Add" should never silently reprocess photos already in
    // the gallery.
    const ids = selectionMode && selectedIds.size
      ? Array.from(selectedIds)
      : selectedSubmitter.submissions.filter(s => !s.grouped).map(s => s.submission_id);
    setIsStarting(true);
    try {
      await eventApi.ingestPhotoSubmissions(eid, ids, groupingModeHint);
      setIngestStatus('processing');
      setIngestProgress({ percent: 0, comment: 'Starting…' });
      toast(`Adding ${ids.length} photo${ids.length > 1 ? 's' : ''} to gallery…`, 'info');
      if (selectionMode) { setSelectionMode(false); setSelectedIds(new Set()); }
    } catch (err) {
      toast(err.message || 'Failed to start ingest', 'error');
    } finally {
      setIsStarting(false);
    }
  };

  // Same ingest/poll machinery as handleAddToGallery above, but scoped to
  // every submitter's ungrouped photos at once rather than just the one
  // selected in the sidebar - the one-click "clear the whole backlog" action.
  const handleProcessAllUnprocessed = async () => {
    if (!allUnprocessedIds.length) return;
    setIsStarting(true);
    try {
      await eventApi.ingestPhotoSubmissions(eid, allUnprocessedIds, groupingModeHint);
      setIngestStatus('processing');
      setIngestProgress({ percent: 0, comment: 'Starting…' });
      toast(`Adding ${allUnprocessedIds.length} photo${allUnprocessedIds.length > 1 ? 's' : ''} to gallery…`, 'info');
    } catch (err) {
      toast(err.message || 'Failed to start ingest', 'error');
    } finally {
      setIsStarting(false);
    }
  };

  function uploaderLabel(s) {
    if (!s) return '';
    const base = s.uploader_username ? `${s.uploader_name} (@${s.uploader_username})` : s.uploader_name;
    return s.is_organiser ? `${base} (Organiser)` : base;
  }

  const organiserFileInput = (
    <input
      ref={organiserFileInputRef}
      type="file"
      accept="image/*"
      multiple
      style={{ display: 'none' }}
      onChange={handleOrganiserFileInputChange}
    />
  );

  const autoShareToggle = (
    <Tooltip title="Any new submission - including from someone who's already submitted before - gets added to every request automatically, without processing.">
      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Switch
            size="small"
            checked={autoShareAll}
            onChange={handleAutoShareAllToggle}
            disabled={autoShareSaving}
          />
        }
        label={<Typography variant="body2">Auto-share all new submissions to all requests</Typography>}
      />
    </Tooltip>
  );

  if (loading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  if (!submitters.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No photo submissions yet.</Typography>
        <Typography variant="caption" color="text.secondary">Attendees can submit photos from their event view, or via an upload link.</Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={organiserUploading ? <CircularProgress size={14} color="inherit" /> : <FileUploadOutlinedIcon />}
            onClick={() => organiserFileInputRef.current?.click()}
            disabled={organiserUploading || Boolean(resumeJob)}
            sx={{ textTransform: 'none' }}
          >
            {organiserUploading ? `Uploading ${organiserUploadProgress.current}/${organiserUploadProgress.total}…` : 'Upload photos'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<LinkIcon />} onClick={() => setUploadLinksOpen(true)} sx={{ textTransform: 'none' }}>
            Manage Upload Links
          </Button>
          {autoShareToggle}
        </Stack>
        <ResumeUploadBanner job={resumeJob} label="an upload" />
        {organiserFileInput}
        <UploadLinksDialog open={uploadLinksOpen} onClose={() => setUploadLinksOpen(false)} eventId={eid} toast={toast} />
      </Box>
    );
  }

  return (
    <Box>
      <UploadLinksDialog open={uploadLinksOpen} onClose={() => setUploadLinksOpen(false)} eventId={eid} toast={toast} />
      {ingestStatus === 'processing' && (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress variant={ingestProgress.percent ? 'determinate' : 'indeterminate'} value={ingestProgress.percent} />
          <Typography variant="caption" color="text.secondary">{ingestProgress.comment}</Typography>
        </Box>
      )}
      {ingestStatus === 'complete' && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setIngestStatus('idle')}>
          Photos successfully added to the gallery.
        </Alert>
      )}
      <div className="map-requests-layout">
        <aside className="request-menu">
          {submitters.map((s, i) => (
            <button
              key={s.customer_id ?? s.uploader_name}
              type="button"
              className={`request-menu-item ${i === selectedSubmitterIdx ? 'active' : ''}`}
              onClick={() => { setSelectedSubmitterIdx(i); setSubmissionDialogOpen(true); }}
            >
              <span style={{ display: 'block', fontWeight: 600, lineHeight: 1.3 }}>{s.uploader_name}{s.is_organiser ? ' (Organiser)' : ''}</span>
              {s.uploader_username && (
                <span style={{ display: 'block', fontSize: '0.72rem', opacity: 0.7 }}>@{s.uploader_username}</span>
              )}
              <span style={{ display: 'block', fontSize: '0.72rem', opacity: 0.6 }}>
                {s.submissions.length} photo{s.submissions.length !== 1 ? 's' : ''}
              </span>
            </button>
          ))}
        </aside>
        <MobileDetailDialog
          isMobile={isMobile}
          open={submissionDialogOpen}
          onClose={() => setSubmissionDialogOpen(false)}
          title={selectedSubmitter ? uploaderLabel(selectedSubmitter) : 'Submitter'}
          chips={selectedSubmitter && (
            <Chip label={`${selectedSubmitter.submissions.length} photo${selectedSubmitter.submissions.length !== 1 ? 's' : ''}`} size="small" sx={{ fontSize: 11, height: 20 }} />
          )}
        >
        <div className="request-details">
          {selectedSubmitter ? (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap">
                {!isMobile && (
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={700}>{uploaderLabel(selectedSubmitter)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedSubmitter.submissions.length} photo{selectedSubmitter.submissions.length !== 1 ? 's' : ''} submitted
                    </Typography>
                  </Box>
                )}
                <Button
                  size="small"
                  variant={selectionMode ? 'contained' : 'outlined'}
                  color={selectionMode ? 'inherit' : 'primary'}
                  onClick={() => { setSelectionMode(m => !m); setSelectedIds(new Set()); }}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', ...(isMobile ? { flex: 1 } : {}) }}
                >
                  {selectionMode ? 'Cancel select' : 'Select'}
                </Button>
                <Tooltip title={
                  isStarting
                    ? 'Starting…'
                    : groupingBusy
                    ? "Please wait for the gallery to finish organizing before adding more photos"
                    : ingestStatus === 'processing'
                    ? 'Adding photos to the gallery…'
                    : (selectionMode && !selectedIds.size)
                    ? 'Select at least one photo to add'
                    : selectionMode
                    ? `Add the ${selectedIds.size} selected photo${selectedIds.size === 1 ? '' : 's'} to the gallery - including any already added, since you picked them yourself`
                    : !ungroupedCount
                    ? 'Every photo from this submitter is already in the gallery - use Select to re-add specific ones'
                    : 'Add the photos not yet in the gallery - already-added ones are skipped. Use Select to re-add specific photos.'
                }>
                  <span style={isMobile ? { flex: 1 } : undefined}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth={isMobile}
                      startIcon={isStarting ? <CircularProgress size={14} color="inherit" /> : <AddPhotoAlternateIcon />}
                      onClick={handleAddToGallery}
                      disabled={
                        isStarting ||
                        ingestStatus === 'processing' ||
                        groupingBusy ||
                        (selectionMode ? !selectedIds.size : !ungroupedCount)
                      }
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      {isStarting
                        ? 'Starting…'
                        : selectionMode ? `Add ${selectedIds.size} photo${selectedIds.size === 1 ? '' : 's'}` : 'Add unprocessed to gallery'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={<Switch size="small" checked={ungroupedOnly} onChange={(e) => setUngroupedOnly(e.target.checked)} />}
                  label={<Typography variant="body2">Show ungrouped only</Typography>}
                />
                {selectionMode && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds(new Set(filteredSubmissions.map(s => s.submission_id)))}
                      sx={{ textTransform: 'none' }}
                    >
                      Select all{filteredSubmissions.length ? ` (${filteredSubmissions.length})` : ''}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds(new Set())}
                      disabled={!selectedIds.size}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear
                    </Button>
                    {onAddSubmissionsToTrack && (
                      <>
                        <Button
                          size="small"
                          onClick={() => onAddSubmissionsToTrack('request', Array.from(selectedIds))}
                          disabled={!selectedIds.size}
                          sx={{ textTransform: 'none' }}
                        >
                          Add to Request
                        </Button>
                        <Button
                          size="small"
                          onClick={() => onAddSubmissionsToTrack('circle', Array.from(selectedIds))}
                          disabled={!selectedIds.size}
                          sx={{ textTransform: 'none' }}
                        >
                          Add to Circle
                        </Button>
                      </>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      onClick={handleBulkDelete}
                      disabled={!selectedIds.size}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete selected{selectedIds.size ? ` (${selectedIds.size})` : ''}
                    </Button>
                  </>
                )}
              </Stack>
              {!filteredSubmissions.length ? (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No {ungroupedOnly ? 'ungrouped ' : ''}photos to show.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {filteredSubmissions.slice(0, visibleCount).map((sub, i) => (
                    <Box key={sub.submission_id} sx={{ position: 'relative', width: 120, flexShrink: 0 }}>
                      {thumbUrls[sub.submission_id] ? (
                        <Box
                          component="img"
                          src={thumbUrls[sub.submission_id]}
                          alt={sub.original_filename}
                          loading="lazy"
                          onClick={() => selectionMode ? toggleSelected(sub.submission_id) : setPreviewIndex(i)}
                          sx={{
                            width: 120, height: 120, objectFit: 'cover', borderRadius: 1, display: 'block',
                            bgcolor: 'grey.100', cursor: 'pointer',
                            outline: selectionMode && selectedIds.has(sub.submission_id) ? '3px solid' : 'none',
                            outlineColor: 'primary.main',
                          }}
                        />
                      ) : (
                        <Box
                          className="thumb-skeleton"
                          onClick={() => selectionMode ? toggleSelected(sub.submission_id) : setPreviewIndex(i)}
                          sx={{ width: 120, height: 120, borderRadius: 1, cursor: 'pointer' }}
                        />
                      )}
                      {selectionMode ? (
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(sub.submission_id)}
                          onChange={() => toggleSelected(sub.submission_id)}
                          sx={{
                            position: 'absolute', top: 0, left: 0, p: '3px',
                            color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '0 0 4px 0',
                            '&.Mui-checked': { color: '#fff' },
                          }}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => setThumbMenu({ anchorEl: e.currentTarget, submissionId: sub.submission_id })}
                          sx={{
                            position: 'absolute', top: 2, right: 2,
                            bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            p: '3px',
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                        <SubmissionGroupedBadge grouped={Boolean(sub.grouped)} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 120, mt: 0.25 }}>
                        {sub.original_filename}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {visibleCount < filteredSubmissions.length && (
                <Button
                  size="small"
                  onClick={() => setVisibleCount(c => c + SUBMISSIONS_PAGE_SIZE)}
                  sx={{ textTransform: 'none', mt: 1.5 }}
                >
                  Load {Math.min(SUBMISSIONS_PAGE_SIZE, filteredSubmissions.length - visibleCount)} more
                  ({filteredSubmissions.length - visibleCount} remaining)
                </Button>
              )}
              <SubmissionPreviewDialog
                submissions={filteredSubmissions}
                index={previewIndex}
                thumbUrls={thumbUrls}
                ensureThumbUrls={ensureThumbUrls}
                onClose={() => setPreviewIndex(null)}
                onNavigate={setPreviewIndex}
                onDelete={handleDelete}
                uploaderName={selectedSubmitter?.uploader_name}
                uploaderUsername={selectedSubmitter?.uploader_username}
                isOrganiserUploader={selectedSubmitter?.is_organiser}
              />
              <Menu
                anchorEl={thumbMenu.anchorEl}
                open={Boolean(thumbMenu.anchorEl)}
                onClose={() => setThumbMenu({ anchorEl: null, submissionId: null })}
              >
                {onAddSubmissionsToTrack && (
                  <MenuItem
                    onClick={() => { onAddSubmissionsToTrack('request', [thumbMenu.submissionId]); setThumbMenu({ anchorEl: null, submissionId: null }); }}
                  >
                    Add to Request
                  </MenuItem>
                )}
                {onAddSubmissionsToTrack && (
                  <MenuItem
                    onClick={() => { onAddSubmissionsToTrack('circle', [thumbMenu.submissionId]); setThumbMenu({ anchorEl: null, submissionId: null }); }}
                  >
                    Add to Circle
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => { const sid = thumbMenu.submissionId; setThumbMenu({ anchorEl: null, submissionId: null }); handleDelete(sid); }}
                  sx={{ color: 'error.main' }}
                >
                  Delete this photo
                </MenuItem>
              </Menu>
            </>
          ) : (
            <div className="request-empty">Select a submitter.</div>
          )}
        </div>
        </MobileDetailDialog>
      </div>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.5}
        justifyContent="flex-end"
        alignItems={isMobile ? 'stretch' : 'center'}
        flexWrap="wrap"
        useFlexGap
        className="submissions-toolbar"
        sx={{ mt: 1.5 }}
      >
        <Button
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          startIcon={organiserUploading ? <CircularProgress size={14} color="inherit" /> : <FileUploadOutlinedIcon />}
          onClick={() => organiserFileInputRef.current?.click()}
          disabled={organiserUploading || Boolean(resumeJob)}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          {organiserUploading ? `Uploading ${organiserUploadProgress.current}/${organiserUploadProgress.total}…` : 'Upload photos'}
        </Button>
        {autoShareToggle}
        {Boolean(allUnprocessedIds.length) && (
          <Tooltip title={
            groupingBusy
              ? 'Please wait for the gallery to finish organizing before adding more photos'
              : `Add all ${allUnprocessedIds.length} unprocessed photo${allUnprocessedIds.length === 1 ? '' : 's'} from every submitter to the gallery`
          }>
            <span style={isMobile ? { width: '100%' } : undefined}>
              <Button
                variant="outlined"
                size="small"
                fullWidth={isMobile}
                startIcon={isStarting ? <CircularProgress size={14} color="inherit" /> : <DoneAllIcon />}
                onClick={handleProcessAllUnprocessed}
                disabled={isStarting || ingestStatus === 'processing' || groupingBusy}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Process all unprocessed ({allUnprocessedIds.length})
              </Button>
            </span>
          </Tooltip>
        )}
        <Button variant="outlined" size="small" fullWidth={isMobile} startIcon={<LinkIcon />} onClick={() => setUploadLinksOpen(true)} sx={{ textTransform: 'none' }}>
          Manage Upload Links
        </Button>
      </Stack>
      <ResumeUploadBanner job={resumeJob} label="an upload" />
      {organiserFileInput}
    </Box>
  );
}

// ── Gallery viewer (organiser - event-scoped with presigned URLs) ──────────────
function EventGalleryViewer({ event, isReady = true, emailIntegrationEnabled, mobileTab = null, onPhotoCountChange, onOpenSubmissionsTab, onGroupingComplete }) {
  const navigate = useNavigate();
  const isMobileTabs = Boolean(mobileTab);
  const showPhotosTab = !isMobileTabs || mobileTab === 'photos';
  const [facesSheetOpen, setFacesSheetOpen] = useState(false);
  const showRequestsTab = !isMobileTabs || mobileTab === 'requests';
  const notReadyMobile = isMobileTabs && !isReady;
  const isMobilePreview = useMediaQuery('(max-width:600px)');
  const eid = event.event_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [groups, setGroups] = useState({});
  const [sampleByGroup, setSampleByGroup] = useState({});
  const [groupNames, setGroupNames] = useState({});
  const [memoryFaces, setMemoryFaces] = useState([]);
  // Merges never-yet-grouped photo-submissions into the main gallery grid
  // (as synthetic photo_id='sub:<submission_id>' entries with no faces) so
  // the organiser can browse/distribute them before grouping completes.
  const [includeUnprocessed, setIncludeUnprocessed] = useState(false);
  const [memoryFaceThumbUrls, setMemoryFaceThumbUrls] = useState({});
  // Held in a ref (mutated in place) rather than React state so that merging in
  // a new batch of URLs is O(new keys), not O(everything fetched so far in the
  // session). `bumpPresignedUrls` is what tells React a re-render is needed.
  const presignedUrlsRef = useRef({ thumbnails: {}, previews: {}, group_thumbnails: {} });
  const [, bumpPresignedUrls] = useReducer(c => c + 1, 0);
  const mergePresignedUrls = useCallback((patch) => {
    const cur = presignedUrlsRef.current;
    if (patch.thumbnails) Object.assign(cur.thumbnails, patch.thumbnails);
    if (patch.previews) Object.assign(cur.previews, patch.previews);
    if (patch.group_thumbnails) Object.assign(cur.group_thumbnails, patch.group_thumbnails);
    bumpPresignedUrls();
  }, []);
  const [galleryImagesLoading, setGalleryImagesLoading] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgTransform, setImgTransform] = useState({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 });
  const [showOriginal, setShowOriginal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchPhotoIds, setAiSearchPhotoIds] = useState([]);
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState('');
  const [selectedAiSearchMode, setSelectedAiSearchMode] = useState('plan');
  const [aiSearchModeLabel, setAiSearchModeLabel] = useState('');
  const [aiSearchModeNote, setAiSearchModeNote] = useState('');
  const [showAssigned, setShowAssigned] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [matchAll, setMatchAll] = useState(false);
  const [mapTab, setMapTab] = useState('requests');
  // Files dragged onto anywhere on the page (see the window-level drag/drop
  // effect below) - handed to PhotoSubmissionsPanel once mapTab flips to
  // 'submissions' so it can upload them the same way a manual pick would.
  const [dragActive, setDragActive] = useState(false);
  const [pendingDroppedFiles, setPendingDroppedFiles] = useState([]);
  const dragDepthRef = useRef(0);
  const [requestsFilter, setRequestsFilter] = useState('all'); // 'all' | 'with_photo' | 'accepted'
  const [photoRequests, setPhotoRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [tracksByRequest, setTracksByRequest] = useState({});
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [selectedDeletionRequestId, setSelectedDeletionRequestId] = useState('');
  const [tracksByDeletionRequest, setTracksByDeletionRequest] = useState({});
  const [trackPhotoFilterIds, setTrackPhotoFilterIds] = useState(null);
  const [photoCircles, setPhotoCircles] = useState([]);
  const [selectedCircleId, setSelectedCircleId] = useState('');
  const [tracksByCircle, setTracksByCircle] = useState({});
  // Processed/unprocessed/all filter for a request's or circle's own already-
  // distributed photo list - local render-time filter only, not persisted,
  // reset whenever the selected request/circle changes.
  const [requestTrackFilter, setRequestTrackFilter] = useState('all'); // 'all' | 'processed' | 'unprocessed'
  const [circleTrackFilter, setCircleTrackFilter] = useState('all');
  useEffect(() => { setRequestTrackFilter('all'); }, [selectedRequestId]);
  useEffect(() => { setCircleTrackFilter('all'); }, [selectedCircleId]);
  // Controls the mobile full-screen detail dialog independently of selectedXxxId,
  // which is auto-populated on load and would otherwise pop the dialog open immediately.
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [circleDialogOpen, setCircleDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [originalBlobUrl, setOriginalBlobUrl] = useState(null);
  const [selectedRequestPhotoUrl, setSelectedRequestPhotoUrl] = useState('');
  const [selectedRequestPhotoLoading, setSelectedRequestPhotoLoading] = useState(false);
  const [selectedRequestPhotoError, setSelectedRequestPhotoError] = useState(false);
  const [selectedRequestSecondaryPhotos, setSelectedRequestSecondaryPhotos] = useState([]);
  const [replacingRequestPhoto, setReplacingRequestPhoto] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [regroupModal, setRegroupModal] = useState({ open: false, photo: null, faceIndex: null, face: null });
  const [blurPreview, setBlurPreview] = useState({ open: false, photo: null, face: null });
  const [revertingBlur, setRevertingBlur] = useState(false);
  const [blurGroupJob, setBlurGroupJob] = useState({ open: false, group: null, mode: null });
  const [clipboard, setClipboard] = useState([]);
  const [crossPicker, setCrossPicker] = useState({ open: false, kind: null, scope: null, photoIds: null });
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [autoPopulateAllProgress, setAutoPopulateAllProgress] = useState({ active: false, completed: 0, total: 0, failed: 0, current: '', failedRequests: [] });
  const [autoPopulateSingleLoading, setAutoPopulateSingleLoading] = useState(false);
  const [requestEdit, setRequestEdit] = useState({ requestId: '', email_address: '', phone_number: '', notes: '' });
  const [requestEditSaving, setRequestEditSaving] = useState(false);
  const [requestNameSavingId, setRequestNameSavingId] = useState('');
  const [trackPreviewPhoto, setTrackPreviewPhoto] = useState(null);
  const [trackPreviewTransform, setTrackPreviewTransform] = useState({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 });
  const [thumbHeight, setThumbHeight] = useState(100);
  const [groupPanelOpen, setGroupPanelOpen] = useState(() => window.innerWidth > 980);
  const [selectedThumbs, setSelectedThumbs] = useState(new Set());
  const [thumbContextMenu, setThumbContextMenu] = useState(null);
  const [deletingPhotos, setDeletingPhotos] = useState(false);
  const [photoMetadataDialog, setPhotoMetadataDialog] = useState({ open: false, photo: null });
  const [addRequestOpen, setAddRequestOpen] = useState(false);
  const [addRequestMode, setAddRequestMode] = useState('photos');
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [rebuildingEmbeddings, setRebuildingEmbeddings] = useState(false);
  const [shareUrls, setShareUrls] = useState({}); // { requestId: shareUrl }
  const [sendingEmailId, setSendingEmailId] = useState('');
  const [showFaceBoxes, setShowFaceBoxes] = useState(() => getPref('showFaceBoxes', true));
  const [invalidatingCache, setInvalidatingCache] = useState(false);
  const [previewSwipeActive, setPreviewSwipeActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [thumbPreviewOpen, setThumbPreviewOpen] = useState(false);
  const [previewMoreAnchor, setPreviewMoreAnchor] = useState(null);
  const [previewInfoOpen, setPreviewInfoOpen] = useState(false);
  const [autoTransition, setAutoTransition] = useState(false);
  const [photoTransition, setPhotoTransition] = useState('');
  const fsZoom = usePinchZoomPan(currentIndex);
  const [fsNavHighlight, setFsNavHighlight] = useState(false);
  const fsNavHighlightTimeoutRef = useRef(null);
  const [showcasedPhotoIds, setShowcasedPhotoIds] = useState(new Set());
  const [showcaseTogglingId, setShowcaseTogglingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    profileApi.getMyShowcasePhotos()
      .then(d => {
        if (cancelled) return;
        const ids = (d?.photos || []).filter(p => p.event_id === eid).map(p => p.photo_id);
        setShowcasedPhotoIds(new Set(ids));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eid]);

  async function toggleShowcasePhoto(photoId) {
    if (!photoId || showcaseTogglingId) return;
    setShowcaseTogglingId(photoId);
    try {
      const alreadyOn = showcasedPhotoIds.has(photoId);
      if (alreadyOn) {
        await profileApi.removeShowcasePhoto(eid, photoId);
      } else {
        await profileApi.addShowcasePhoto(eid, photoId);
      }
      setShowcasedPhotoIds(prev => {
        const next = new Set(prev);
        if (alreadyOn) next.delete(photoId); else next.add(photoId);
        return next;
      });
    } catch {
      // non-fatal - button stays at its prior state
    } finally {
      setShowcaseTogglingId(null);
    }
  }
  const [ucDownloading, setUcDownloading] = useState(false);

  function downloadUcOriginalPhoto(photoId) {
    if (!photoId || ucDownloading) return;
    downloadUcOriginal(eventApi.requestUcOriginalDownload, eid, photoId, {
      filename: `circlemoments-${eid}-${photoId}-uncompressed.jpg`,
      onActiveChange: setUcDownloading,
    }).catch((err) => toast(err.message || 'Download failed.', 'error'));
  }

  // Refs so interval callbacks always see the latest values without stale closures.
  const tracksByRequestRef = useRef({});
  const tracksByDeletionRequestRef = useRef({});
  const tracksByCircleRef = useRef({});
  const photoByIdRef = useRef(new Map());
  const lastSavedRef = useRef({}); // { requestId: JSON.stringify(tracks) } at last save
  const lastSavedDeletionRef = useRef({});
  const lastSavedCircleRef = useRef({});
  const thumbRef = useRef(null);
  const viewerSectionRef = useRef(null);
  const previewSwipeRef = useRef({ startX: 0, startY: 0, lastX: 0, startTime: 0, startIndex: 0, moved: false });
  const previewSwipeSettleRef = useRef(null);
  const fetchedPhotoIdsRef = useRef(new Set());
  const fetchedGroupIdsRef = useRef(new Set());
  const pendingGroupIdsRef = useRef(new Set());
  const groupFetchTimerRef = useRef(null);
  const allGroupIdsRef = useRef([]);
  const autoTransitionRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const filteredPhotosLengthRef = useRef(0);

  const toast = useCallback((message, severity = 'info') => setSnackbar({ open: true, message: String(message), severity }), []);

  // Lets the organiser start a photo-submission upload by dropping files
  // anywhere on the event page - not just while already on the Submissions
  // tab - by watching for a file drag at the window level for as long as this
  // component is mounted (i.e. the whole time the organiser is on the event
  // page - see EventPage.js's matching comment on why it's always mounted).
  // dragDepthRef counts nested dragenter/dragleave pairs (fired as the drag
  // crosses child element boundaries) so the overlay doesn't flicker off
  // until the drag has actually left the window, not just one child of it.
  useEffect(() => {
    if (!event.is_organiser) return undefined;
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setDragActive(true);
    };
    const onDragOver = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragActive(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files || []).filter(isCompressibleEventImage);
      if (!files.length) {
        toast('No compatible images in the dropped files.', 'warning');
        return;
      }
      setMapTab('submissions');
      onOpenSubmissionsTab?.();
      setPendingDroppedFiles(files);
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [event.is_organiser, onOpenSubmissionsTab, toast]);

  const thumbWindow = useMemo(() => Math.max(12, Math.floor(thumbHeight / 60) * 12), [thumbHeight]);

  // Keep an ordered list of all group IDs (by face count) for padding lazy-load batches
  useEffect(() => {
    allGroupIdsRef.current = Object.keys(groups)
      .sort((a, b) => (Number(groups[b]) || 0) - (Number(groups[a]) || 0));
  }, [groups]);

  const GROUP_THUMB_BATCH_SIZE = 30;

  // Batch-fetch presigned URLs for face-group thumbnails as they scroll into view.
  // Each request is padded up to GROUP_THUMB_BATCH_SIZE ids so scrolling doesn't
  // trigger a flurry of tiny one-off requests.
  const ensureGroupThumbnails = useCallback((ids) => {
    const toFetch = (ids || []).filter(g => g && !fetchedGroupIdsRef.current.has(g));
    if (!toFetch.length) return;
    toFetch.forEach(g => { fetchedGroupIdsRef.current.add(g); pendingGroupIdsRef.current.add(g); });
    if (groupFetchTimerRef.current) return;
    groupFetchTimerRef.current = setTimeout(() => {
      const batch = Array.from(pendingGroupIdsRef.current);
      pendingGroupIdsRef.current.clear();
      groupFetchTimerRef.current = null;
      // Pad the batch with the next not-yet-fetched groups (in count order) so
      // each request fetches at least GROUP_THUMB_BATCH_SIZE thumbnails.
      for (const gid of allGroupIdsRef.current) {
        if (batch.length >= GROUP_THUMB_BATCH_SIZE) break;
        if (!fetchedGroupIdsRef.current.has(gid)) {
          fetchedGroupIdsRef.current.add(gid);
          batch.push(gid);
        }
      }
      eventApi.getPresignedUrls(eid, [], false, batch).then(urls => {
        mergePresignedUrls({ group_thumbnails: urls.group_thumbnails || {} });
      }).catch(() => { batch.forEach(g => fetchedGroupIdsRef.current.delete(g)); });
    }, 100);
  }, [eid, mergePresignedUrls]);

  // URL helpers - presigned S3 URLs only; empty until fetched (no EC2 proxy fallback)
  const thumbSrc = useCallback((pid) => presignedUrlsRef.current.thumbnails[pid] || '', []);
  const previewSrc = useCallback((pid) => presignedUrlsRef.current.previews[pid] || eventApi.previewUrl(eid, pid), [eid]);
  const groupThumbSrc = useCallback((g) => presignedUrlsRef.current.group_thumbnails[g] || '', []);

  // Render-time only - filters which of a track's already-stored photoIds are
  // displayed; never mutates the stored track data itself.
  function filterTrackPhotoIds(photoIds, filter) {
    if (filter === 'unprocessed') return (photoIds || []).filter(isUnprocessedPhotoId);
    if (filter === 'processed') return (photoIds || []).filter(pid => !isUnprocessedPhotoId(pid));
    return photoIds || [];
  }

  const refreshMemoryFaces = useCallback(async () => {
    try {
      const data = await eventApi.getNamedFaces();
      const loadedMemoryFaces = Array.isArray(data?.faces) ? data.faces : [];
      setMemoryFaces(loadedMemoryFaces);
      const thumbKeys = [...new Set(loadedMemoryFaces.map(face => face.thumbnail_key).filter(Boolean))];
      if (thumbKeys.length) {
        const thumbData = await eventApi.getMemoryFaceThumbnailUrls(thumbKeys);
        setMemoryFaceThumbUrls(thumbData.urls || {});
      } else {
        setMemoryFaceThumbUrls({});
      }
    } catch {
      // Keep the last loaded list; the save path will still validate server-side.
    }
  }, []);

  useEffect(() => {
    fetchedPhotoIdsRef.current = new Set();
    setGalleryImagesLoading(true);
    async function load() {
      try {
        const [meta, assignmentsPayload, reqs, deletionReqs, circles, namedFacesPayload] = await Promise.all([
          eventApi.getGalleryMetadata(eid, { includeUnprocessed: event.is_organiser && includeUnprocessed }).catch(() => ({ photos: [], groups: {} })),
          eventApi.getGroupAssignments(eid).catch(() => ({ assignments: {} })),
          eventApi.listPhotoRequests(eid),
          eventApi.listDeletionRequests(eid).catch(() => ({ requests: [] })),
          eventApi.listPhotoCircles(eid).catch(() => ({ circles: [] })),
          eventApi.getNamedFaces().catch(() => ({ faces: [] })),
        ]);
        const payload = meta?.data ?? meta;
        const loadedPhotos = Array.isArray(payload) ? payload : Array.isArray(payload?.photos) ? payload.photos : Array.isArray(meta?.photos) ? meta.photos : [];
        const groupsFromMeta = (payload && !Array.isArray(payload) && typeof payload.groups === 'object' && payload.groups) || (meta && typeof meta.groups === 'object' && meta.groups) || {};
        const { groups: nextGroups, sampleByGroup: nextSamples } = buildGroups(loadedPhotos, groupsFromMeta);
        const rawAssignments = assignmentsPayload?.assignments && typeof assignmentsPayload.assignments === 'object' ? assignmentsPayload.assignments : {};
        const loadedNames = Object.fromEntries(
          Object.entries(rawAssignments)
            .filter(([, a]) => String(a?.display_name || '').trim())
            .map(([gid, a]) => [gid, String(a.display_name || '').trim()])
        );
        const initialIds = loadedPhotos.slice(0, 24).map(p => p.photo_id).filter(Boolean);
        initialIds.forEach(id => fetchedPhotoIdsRef.current.add(String(id)));
        const initialGroupIds = Object.keys(nextGroups)
          .sort((a, b) => (Number(nextGroups[b]) || 0) - (Number(nextGroups[a]) || 0))
          .slice(0, 30);
        fetchedGroupIdsRef.current = new Set(initialGroupIds);
        setPhotos(sortPhotosByCapturedAtDesc(loadedPhotos));
        setGroups(nextGroups);
        setSampleByGroup(nextSamples);
        setGroupNames(loadedNames);
        const loadedMemoryFaces = Array.isArray(namedFacesPayload?.faces) ? namedFacesPayload.faces : [];
        setMemoryFaces(loadedMemoryFaces);
        const thumbKeys = [...new Set(loadedMemoryFaces.map(face => face.thumbnail_key).filter(Boolean))];
        if (thumbKeys.length) {
          eventApi.getMemoryFaceThumbnailUrls(thumbKeys)
            .then(data => setMemoryFaceThumbUrls(data.urls || {}))
            .catch(() => setMemoryFaceThumbUrls({}));
        } else {
          setMemoryFaceThumbUrls({});
        }
        setPhotoRequests(Array.isArray(reqs?.requests) ? reqs.requests : []);
        setDeletionRequests(Array.isArray(deletionReqs?.requests) ? deletionReqs.requests : []);
        setPhotoCircles(Array.isArray(circles?.circles) ? circles.circles : []);
        // Presigned URLs are the slowest call (S3 signing); resolve them in the
        // background so requests/circles/submissions tabs don't wait on them.
        eventApi.getPresignedUrls(eid, initialIds, false, initialGroupIds)
          .catch(() => ({ thumbnails: {}, previews: {}, group_thumbnails: {} }))
          .then(urls => mergePresignedUrls(urls))
          .finally(() => setGalleryImagesLoading(false));
      } catch (e) {
        setError(e.message || 'Failed to load gallery');
        setGalleryImagesLoading(false);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eid, reloadKey, includeUnprocessed]); // eslint-disable-line react-hooks/exhaustive-deps

  const [refreshingTab, setRefreshingTab] = useState(false);
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);
  const refreshTabData = useCallback(async () => {
    setRefreshingTab(true);
    try {
      if (mapTab === 'requests') {
        const reqs = await eventApi.listPhotoRequests(eid);
        setPhotoRequests(Array.isArray(reqs?.requests) ? reqs.requests : []);
      } else if (mapTab === 'deletion') {
        const deletionReqs = await eventApi.listDeletionRequests(eid).catch(() => ({ requests: [] }));
        setDeletionRequests(Array.isArray(deletionReqs?.requests) ? deletionReqs.requests : []);
      } else if (mapTab === 'circles') {
        const circles = await eventApi.listPhotoCircles(eid).catch(() => ({ circles: [] }));
        setPhotoCircles(Array.isArray(circles?.circles) ? circles.circles : []);
      } else if (mapTab === 'submissions') {
        setSubmissionsRefreshKey(k => k + 1);
      }
    } finally {
      setRefreshingTab(false);
    }
  }, [eid, mapTab]);

  useEffect(() => {
    if (selectedRequestId && !photoRequests.some(r => r.request_id === selectedRequestId)) {
      setSelectedRequestId('');
    }
  }, [photoRequests, selectedRequestId]);

  useEffect(() => {
    if (selectedCircleId && !photoCircles.some(c => c.circle_id === selectedCircleId)) {
      setSelectedCircleId('');
    }
  }, [photoCircles, selectedCircleId]);

  useEffect(() => {
    if (!deletionRequests.length) { setSelectedDeletionRequestId(''); return; }
    if (!selectedDeletionRequestId || !deletionRequests.some(r => r.request_id === selectedDeletionRequestId)) {
      setSelectedDeletionRequestId(deletionRequests[0]?.request_id || '');
    }
  }, [deletionRequests, selectedDeletionRequestId]);

  const processedGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const all = Object.keys(groups).map(group => {
      const an = (groupNames[group] || '').trim();
      return { group, count: Number(groups[group]) || 0, assignedName: an, isAssigned: Boolean(an) };
    }).sort((a, b) => b.count - a.count || a.group.localeCompare(b.group));
    if (!q) return all;
    return all.filter(({ group, assignedName }) => {
      const dn = displayName(group, groupNames).toLowerCase();
      return group.toLowerCase().includes(q) || assignedName.toLowerCase().includes(q) || dn.includes(q);
    });
  }, [groups, groupNames, searchQuery]);

  const assignedGroups = useMemo(() => processedGroups.filter(i => i.isAssigned), [processedGroups]);
  const unassignedGroups = useMemo(() => processedGroups.filter(i => !i.isAssigned), [processedGroups]);

  const photoById = useMemo(() => { const m = new Map(); for (const p of photos) if (p?.photo_id) m.set(String(p.photo_id), p); return m; }, [photos]);

  const aiOrderedPhotos = useMemo(() => {
    if (!aiSearchActive) return photos;
    const seen = new Set();
    return aiSearchPhotoIds
      .map(pid => String(pid))
      .filter(pid => {
        if (!pid || seen.has(pid)) return false;
        seen.add(pid);
        return true;
      })
      .map(pid => photoById.get(pid))
      .filter(Boolean);
  }, [aiSearchActive, aiSearchPhotoIds, photoById, photos]);

  const filteredPhotos = useMemo(() => {
    const sourcePhotos = aiSearchActive ? aiOrderedPhotos : photos;
    const groupFiltered = !selectedGroups.size ? sourcePhotos : sourcePhotos.filter(photo => {
      const fg = new Set((photo.faces || []).map(f => f.group || 'Unknown'));
      if (matchAll) { if (fg.size !== selectedGroups.size) return false; return Array.from(selectedGroups).every(g => fg.has(g)); }
      return Array.from(selectedGroups).some(g => fg.has(g));
    });
    if (!trackPhotoFilterIds?.size) return groupFiltered;
    return groupFiltered.filter(photo => trackPhotoFilterIds.has(String(photo.photo_id)));
  }, [photos, aiOrderedPhotos, aiSearchActive, selectedGroups, matchAll, trackPhotoFilterIds]);

  filteredPhotosLengthRef.current = filteredPhotos.length;

  useEffect(() => {
    if (loading) return;
    onPhotoCountChange?.(filteredPhotos.length);
  }, [loading, filteredPhotos.length, onPhotoCountChange]);

  const filteredPhotoOrderKey = useMemo(
    () => filteredPhotos.map(p => String(p.photo_id || p.path || '')).join('|'),
    [filteredPhotos],
  );

  useEffect(() => {
    let active = true;
    const q = aiSearchQuery.trim();
    setAiSearchError('');
    setSelectedThumbs(new Set());
    setCurrentIndex(0);
    if (!q) {
      setAiSearchPhotoIds([]);
      setAiSearchActive(false);
      setAiSearchLoading(false);
      setAiSearchModeLabel('');
      setAiSearchModeNote('');
      return () => { active = false; };
    }
    setAiSearchLoading(true);
    eventApi.searchEventPhotos(eid, q, selectedAiSearchMode)
      .then(payload => {
        if (!active) return;
        const ids = (Array.isArray(payload.photos) ? payload.photos : [])
          .map(item => item.photo_id)
          .filter(Boolean);
        setAiSearchPhotoIds(ids);
        setAiSearchActive(true);
        setAiSearchModeLabel(payload.search_mode_label || '');
        setAiSearchModeNote(payload.search_mode_note || '');
      })
      .catch(err => {
        if (!active) return;
        setAiSearchPhotoIds([]);
        setAiSearchActive(false);
        setAiSearchError(err.message || 'Search failed');
        setAiSearchModeLabel('');
        setAiSearchModeNote('');
      })
      .finally(() => { if (active) setAiSearchLoading(false); });
    return () => { active = false; };
  }, [eid, aiSearchQuery, selectedAiSearchMode]);

  const toggleAiSearchMode = useCallback(() => {
    setSelectedAiSearchMode((current) => (current === 'legacy' ? 'plan' : 'legacy'));
  }, []);

  const submitAiSearch = useCallback((query) => {
    setAiSearchQuery(query);
  }, []);

  const clearAiSearch = useCallback(() => {
    setAiSearchQuery('');
    setAiSearchPhotoIds([]);
    setAiSearchActive(false);
    setAiSearchLoading(false);
    setAiSearchError('');
    setAiSearchModeLabel('');
    setAiSearchModeNote('');
    setSelectedThumbs(new Set());
    setCurrentIndex(0);
  }, []);

  useEffect(() => { if (currentIndex >= filteredPhotos.length) setCurrentIndex(0); }, [currentIndex, filteredPhotos.length]);
  useEffect(() => {
    setCurrentIndex(0);
    setSelectedThumbs(new Set());
  }, [filteredPhotoOrderKey]);
  useEffect(() => {
    if (!previewSwipeActive) return undefined;
    const timer = window.setTimeout(() => setPreviewSwipeActive(false), 450);
    return () => window.clearTimeout(timer);
  }, [previewSwipeActive, currentIndex]);
  useEffect(() => { setShowOriginal(false); setOriginalBlobUrl(null); }, [currentIndex, filteredPhotos]);

  // Lazy-fetch presigned URLs for the visible thumbnail window as the user navigates
  useEffect(() => {
    if (!filteredPhotos.length) return;
    const half = Math.floor(thumbWindow / 2);
    let start = Math.max(0, currentIndex - half);
    let end = Math.min(filteredPhotos.length, start + thumbWindow);
    if (end - start < thumbWindow) start = Math.max(0, end - thumbWindow);
    const prefetchEnd = Math.min(filteredPhotos.length, end + thumbWindow);
    const ids = filteredPhotos.slice(start, prefetchEnd)
      .map(p => String(p.photo_id || ''))
      .filter(pid => pid && !fetchedPhotoIdsRef.current.has(pid));
    if (!ids.length) return;
    ids.forEach(id => fetchedPhotoIdsRef.current.add(id));
    eventApi.getPresignedUrls(eid, ids).then(urls => {
      mergePresignedUrls({ thumbnails: urls.thumbnails, previews: urls.previews, group_thumbnails: urls.group_thumbnails });
    }).catch(() => { ids.forEach(id => fetchedPhotoIdsRef.current.delete(id)); });
  }, [eid, currentIndex, filteredPhotos, thumbWindow, mergePresignedUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentPhoto = filteredPhotos[currentIndex] || null;

  const cast = useChromecastSender();
  useEffect(() => {
    if (cast.connected && currentPhoto?.photo_id) {
      cast.loadImage(previewSrc(currentPhoto.photo_id), currentPhoto.path);
    }
  }, [cast.connected, cast.loadImage, currentPhoto, previewSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch original photo via JS (auth header can't be sent from <img src>)
  useEffect(() => {
    let revoke;
    if (showOriginal && currentPhoto?.photo_id) {
      const token = localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token');
      fetch(`${LAMBDA_BACKEND_URL}/api/events/${eid}/photo-original?photo_id=${encodeURIComponent(currentPhoto.photo_id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.ok ? r.blob() : Promise.reject())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          revoke = url;
          setOriginalBlobUrl(url);
        })
        .catch(() => setOriginalBlobUrl(null));
    } else {
      setOriginalBlobUrl(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [showOriginal, currentPhoto?.photo_id, eid]); // eslint-disable-line

  async function savePhotoMetadata({ tags, notes }) {
    const photo = photoMetadataDialog.photo;
    if (!photo?.photo_id) return;
    const res = await eventApi.updatePhotoMetadata(eid, photo.photo_id, { tags, notes });
    setPhotos(prev => prev.map(item => (
      item.photo_id === photo.photo_id
        ? { ...item, tags: res.tags || [], notes: res.notes || '', metadata_updated_at: res.metadata_updated_at }
        : item
    )));
    toast('Photo tags and notes saved.', 'success');
  }

  const filteredRequests = useMemo(() => {
    if (requestsFilter === 'with_photo') return photoRequests.filter(r => r.has_selfie !== false);
    if (requestsFilter === 'accepted') return photoRequests.filter(r => r.invitation_id || r.has_selfie !== false);
    return photoRequests;
  }, [photoRequests, requestsFilter]);

  const selectedRequest = photoRequests.find(r => r.request_id === selectedRequestId) || null;
  const selectedRequesterIdentity = requesterIdentityMeta(selectedRequest);
  const requestTracks = useMemo(() => selectedRequestId ? (tracksByRequest[selectedRequestId] || []) : [], [selectedRequestId, tracksByRequest]);
  const selectedRequestMatchedGroups = useMemo(() => {
    const seen = new Set();
    return requestTracks
      .map(track => trackMatchedGroup(track))
      .filter(group => {
        if (!group || seen.has(group)) return false;
        seen.add(group);
        return true;
      });
  }, [requestTracks]);
  const selectedDeletionRequest = deletionRequests.find(r => r.request_id === selectedDeletionRequestId) || null;
  const deletionRequestTracks = useMemo(() => selectedDeletionRequestId ? (tracksByDeletionRequest[selectedDeletionRequestId] || []) : [], [selectedDeletionRequestId, tracksByDeletionRequest]);
  const selectedCircle = photoCircles.find(c => c.circle_id === selectedCircleId) || null;
  const circleTracks = useMemo(() => selectedCircleId ? (tracksByCircle[selectedCircleId] || []) : [], [selectedCircleId, tracksByCircle]);
  const activeTracks = mapTab === 'circles' ? circleTracks : mapTab === 'deletion' ? deletionRequestTracks : requestTracks;
  // Keep refs up-to-date so the auto-save interval always sees the latest values.
  useEffect(() => { tracksByRequestRef.current = tracksByRequest; }, [tracksByRequest]);
  useEffect(() => { tracksByDeletionRequestRef.current = tracksByDeletionRequest; }, [tracksByDeletionRequest]);
  useEffect(() => { tracksByCircleRef.current = tracksByCircle; }, [tracksByCircle]);
  useEffect(() => { photoByIdRef.current = photoById; }, [photoById]);

  // Fetch presigned thumbnails for photos shown in the active request/deletion/circle
  // track strips - these photos may not be in the main gallery's lazy-fetch window.
  // The backend caps each batch at 200 ids, so long tracks are split into chunks
  // and fetched independently rather than relying on scroll position.
  const PRESIGN_BATCH_SIZE = 200;
  useEffect(() => {
    const ids = Array.from(new Set(
      activeTracks
        .flatMap(track => Array.isArray(track.photoIds) ? track.photoIds : [])
        .map(pid => String(pid || ''))
        .filter(pid => pid && !fetchedPhotoIdsRef.current.has(pid))
    ));
    if (!ids.length) return;
    ids.forEach(id => fetchedPhotoIdsRef.current.add(id));
    for (let i = 0; i < ids.length; i += PRESIGN_BATCH_SIZE) {
      const batch = ids.slice(i, i + PRESIGN_BATCH_SIZE);
      eventApi.getPresignedUrls(eid, batch).then(urls => {
        mergePresignedUrls({ thumbnails: urls.thumbnails, previews: urls.previews, group_thumbnails: urls.group_thumbnails });
      }).catch(() => { batch.forEach(id => fetchedPhotoIdsRef.current.delete(id)); });
    }
  }, [eid, activeTracks, mergePresignedUrls]);

  const toggleGroup = useCallback((g) => {
    setSelectedGroups(prev => { const n = new Set(prev); if (n.has(g)) n.delete(g); else n.add(g); return n; });
    // Don't reset index here - the existing guard effect handles out-of-range indices,
    // and avoiding setCurrentIndex(0) prevents the browser from scrolling to the
    // focused (but hidden) checkbox element after the re-render.
  }, []);

  // On mobile, applying a face filter closes the face-groups sheet and scrolls
  // the merged Photos tab down to the viewer so the filtered result is
  // immediately visible - desktop leaves the panel open inline, unchanged.
  const handleToggleGroupFiltered = useCallback((g) => {
    toggleGroup(g);
    if (isMobileTabs) {
      setFacesSheetOpen(false);
      viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [toggleGroup, isMobileTabs]);

  const addGroupToRequest = useCallback((g) => {
    if (mapTab === 'deletion') {
      if (!selectedDeletionRequestId) { toast('Select a deletion request first.'); return; }
      const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
      if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
      setTracksByDeletionRequest(prev => ({ ...prev, [selectedDeletionRequestId]: [...(prev[selectedDeletionRequestId] || []), { id: `group-${g}-${Date.now()}`, label: `Group: ${g}`, source: 'group', group: g, photoIds: ids }] }));
      toast(`Added ${ids.length} photos from ${g}.`, 'success');
      return;
    }
    const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
    if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
    setCrossPicker({ open: true, kind: 'request', scope: 'group', photoIds: ids });
  }, [mapTab, selectedDeletionRequestId, photos, toast]);

  const addGroupToAll = useCallback((g) => {
    if (mapTab === 'deletion') {
      if (!deletionRequests.length) return;
      const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
      if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
      setTracksByDeletionRequest(prev => {
        const n = { ...prev };
        for (const r of deletionRequests) { if (!r.request_id) continue; n[r.request_id] = [...(n[r.request_id] || []), { id: `group-${g}-${Date.now()}-${r.request_id}`, label: `Group: ${g}`, source: 'group', group: g, photoIds: ids }]; }
        return n;
      });
      toast(`Added group ${g} to all deletion requests.`, 'success');
      return;
    }
    if (!photoRequests.length) return;
    const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
    if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
    setTracksByRequest(prev => {
      const n = { ...prev };
      for (const r of photoRequests) { if (!r.request_id) continue; n[r.request_id] = [...(n[r.request_id] || []), { id: `group-${g}-${Date.now()}-${r.request_id}`, label: `Group: ${g}`, source: 'group', group: g, photoIds: ids }]; }
      return n;
    });
    toast(`Added group ${g} to all requests.`, 'success');
  }, [mapTab, deletionRequests, photoRequests, photos, toast]);

  const addGroupToCircle = useCallback((g) => {
    const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
    if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
    setCrossPicker({ open: true, kind: 'circle', scope: 'group', photoIds: ids });
  }, [photos, toast]);

  const addGroupToAllCircles = useCallback((g) => {
    if (!photoCircles.length) return;
    const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
    if (!ids.length) { toast(`No photos found for group ${g}.`, 'error'); return; }
    setTracksByCircle(prev => {
      const n = { ...prev };
      for (const c of photoCircles) { if (!c.circle_id) continue; n[c.circle_id] = [...(n[c.circle_id] || []), { id: `group-${g}-${Date.now()}-${c.circle_id}`, label: `Group: ${g}`, source: 'group', group: g, photoIds: ids }]; }
      return n;
    });
    toast(`Added group ${g} to all circles.`, 'success');
  }, [photoCircles, photos, toast]);

  const addGroupToClipboard = useCallback((g) => {
    const ids = photos.filter(p => (p.faces || []).some(f => (f.group || 'Unknown') === g)).map(p => p.photo_id).filter(Boolean);
    if (!ids.length) { toast(`No photos in group ${g}.`, 'error'); return; }
    setClipboard(prev => [...prev, { id: `clip-${Date.now()}`, label: `Group: ${displayName(g, groupNames)}`, photoIds: ids, source: 'group' }]);
    toast(`Group ${g} added to clipboard (${ids.length} photos).`, 'success');
  }, [photos, groupNames, toast]);

  function copyTrackToClipboard(track) {
    if (!(track?.photoIds?.length)) return;
    setClipboard(prev => [...prev, { id: `clip-${Date.now()}`, label: `Track: ${track.label || 'Track'}`, photoIds: [...track.photoIds], source: 'track' }]);
    toast('Track added to clipboard.', 'success');
  }

  function pasteClipboardItem(clipId, requestId) {
    if (!requestId) return;
    const item = clipboard.find(c => c.id === clipId);
    if (!item) return;
    if (mapTab === 'circles') {
      setTracksByCircle(prev => ({ ...prev, [requestId]: [...(prev[requestId] || []), { id: `paste-${Date.now()}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }] }));
      toast('Pasted to circle.', 'success');
      return;
    }
    if (mapTab === 'deletion') {
      setTracksByDeletionRequest(prev => ({ ...prev, [requestId]: [...(prev[requestId] || []), { id: `paste-${Date.now()}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }] }));
      toast('Pasted to deletion request.', 'success');
      return;
    }
    setTracksByRequest(prev => ({ ...prev, [requestId]: [...(prev[requestId] || []), { id: `paste-${Date.now()}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }] }));
    toast('Pasted to request.', 'success');
  }

  function pasteClipboardItemToAll(clipId) {
    const item = clipboard.find(c => c.id === clipId);
    if (!item) return;
    if (mapTab === 'circles') {
      setTracksByCircle(prev => {
        const n = { ...prev };
        for (const c of photoCircles) {
          if (!c.circle_id) continue;
          n[c.circle_id] = [...(n[c.circle_id] || []), { id: `paste-${Date.now()}-${c.circle_id}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }];
        }
        return n;
      });
      toast(`Pasted to all ${photoCircles.length} circles.`, 'success');
      return;
    }
    if (mapTab === 'deletion') {
      setTracksByDeletionRequest(prev => {
        const n = { ...prev };
        for (const r of deletionRequests) {
          if (!r.request_id) continue;
          n[r.request_id] = [...(n[r.request_id] || []), { id: `paste-${Date.now()}-${r.request_id}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }];
        }
        return n;
      });
      toast(`Pasted to all ${deletionRequests.length} deletion requests.`, 'success');
      return;
    }
    setTracksByRequest(prev => {
      const n = { ...prev };
      for (const r of photoRequests) {
        if (!r.request_id) continue;
        n[r.request_id] = [...(n[r.request_id] || []), { id: `paste-${Date.now()}-${r.request_id}`, label: item.label, source: 'paste', photoIds: [...item.photoIds] }];
      }
      return n;
    });
    toast(`Pasted to all ${photoRequests.length} requests.`, 'success');
  }

  function removeFromClipboard(clipId) {
    setClipboard(prev => prev.filter(c => c.id !== clipId));
  }

  function nav(d) {
    if (!filteredPhotos.length) return;
    setCurrentIndex(prev => { const n = prev + d; if (n < 0) return filteredPhotos.length - 1; if (n >= filteredPhotos.length) return 0; return n; });
  }

  // oneBasedIndex comes from the editable "123 / 800" counter - setting
  // currentIndex here reuses the existing thumbnail-window prefetch effect,
  // so no separate fetch logic is needed for a jump vs. a step.
  function goToIndex(oneBasedIndex) {
    if (!filteredPhotos.length) return;
    const n = Math.min(Math.max(oneBasedIndex - 1, 0), filteredPhotos.length - 1);
    setCurrentIndex(n);
  }

  function navWithTransition(d) {
    if (!filteredPhotos.length) return;
    const t = PHOTO_TRANSITIONS[Math.floor(Math.random() * PHOTO_TRANSITIONS.length)];
    setPhotoTransition(t);
    nav(d);
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = window.setTimeout(() => setPhotoTransition(''), 650);
  }

  const fsHistoryPushedRef = useRef(false);

  function enterFullscreen() {
    window.history.pushState({ fsOverlay: true }, '');
    fsHistoryPushedRef.current = true;
    setIsFullscreen(true);
  }

  function exitFullscreen() {
    if (fsHistoryPushedRef.current) {
      fsHistoryPushedRef.current = false;
      window.history.back();
    }
    setIsFullscreen(false);
    setAutoTransition(false);
  }

  useEffect(() => {
    if (!autoTransition) {
      if (autoTransitionRef.current) clearInterval(autoTransitionRef.current);
      return;
    }
    autoTransitionRef.current = window.setInterval(() => {
      if (filteredPhotosLengthRef.current <= 1) return;
      const t = PHOTO_TRANSITIONS[Math.floor(Math.random() * PHOTO_TRANSITIONS.length)];
      setPhotoTransition(t);
      setCurrentIndex(prev => (prev + 1 >= filteredPhotosLengthRef.current ? 0 : prev + 1));
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = window.setTimeout(() => setPhotoTransition(''), 650);
    }, 5000);
    return () => { if (autoTransitionRef.current) clearInterval(autoTransitionRef.current); };
  }, [autoTransition]); // eslint-disable-line

  useEffect(() => {
    if (!isFullscreen) return;
    function handlePopState() {
      fsHistoryPushedRef.current = false;
      setIsFullscreen(false);
      setAutoTransition(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') exitFullscreen();
      if (e.key === 'ArrowLeft') navWithTransition(-1);
      if (e.key === 'ArrowRight') navWithTransition(1);
    }
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isFullscreen]); // eslint-disable-line

  function clampPhotoIndex(index) {
    if (!filteredPhotos.length) return 0;
    return Math.max(0, Math.min(filteredPhotos.length - 1, index));
  }

  function settlePreviewSwipe() {
    if (previewSwipeSettleRef.current) window.clearTimeout(previewSwipeSettleRef.current);
    previewSwipeSettleRef.current = window.setTimeout(() => setPreviewSwipeActive(false), 120);
  }

  function handlePreviewTouchStart(e) {
    if (filteredPhotos.length <= 1) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    if (previewSwipeSettleRef.current) window.clearTimeout(previewSwipeSettleRef.current);
    previewSwipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      startTime: performance.now(),
      startIndex: currentIndex,
      moved: false,
    };
  }

  function handlePreviewTouchMove(e) {
    if (filteredPhotos.length <= 1) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const state = previewSwipeRef.current;
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;
    if (Math.abs(dx) < 10 || Math.abs(dx) < Math.abs(dy)) return;
    e.preventDefault();
    state.moved = true;
    state.lastX = touch.clientX;
    setPreviewSwipeActive(true);
    const stepWidth = Math.max(52, Math.min(140, window.innerWidth * 0.18));
    const steps = Math.trunc(-dx / stepWidth);
    setCurrentIndex(clampPhotoIndex(state.startIndex + steps));
  }

  function handlePreviewTouchEnd() {
    const state = previewSwipeRef.current;
    if (!state.moved) return;
    const elapsed = Math.max(1, performance.now() - state.startTime);
    const dx = state.lastX - state.startX;
    const velocity = Math.abs(dx) / elapsed;
    if (velocity > 0.65) {
      const direction = dx < 0 ? 1 : -1;
      const momentum = Math.min(6, Math.max(1, Math.round(velocity * 3)));
      setCurrentIndex(prev => clampPhotoIndex(prev + direction * momentum));
    }
    settlePreviewSwipe();
  }

  function handleFsTouchStart(e) {
    fsZoom.handlers.onTouchStart(e);
    if (fsZoom.transform.scale <= 1.01 && e.touches.length === 1) handlePreviewTouchStart(e);
  }
  function handleFsTouchMove(e) {
    if (fsZoom.transform.scale > 1.01 || e.touches.length === 2) fsZoom.handlers.onTouchMove(e);
    else handlePreviewTouchMove(e);
  }
  function handleFsTouchEnd(e) {
    const wasZoomed = fsZoom.transform.scale > 1.01;
    const tapped = !wasZoomed && !previewSwipeRef.current.moved && e.touches.length === 0;
    fsZoom.handlers.onTouchEnd(e);
    if (!wasZoomed) handlePreviewTouchEnd(e);
    if (tapped) {
      setFsNavHighlight(true);
      if (fsNavHighlightTimeoutRef.current) window.clearTimeout(fsNavHighlightTimeoutRef.current);
      fsNavHighlightTimeoutRef.current = window.setTimeout(() => setFsNavHighlight(false), 2000);
    }
  }

  function openTrackPhotoPreview(photoId, track) {
    if (!photoId) return;
    setTrackPreviewPhoto({ photoId, track });
  }

  function trackMatchedGroup(track) {
    const direct = String(track?.matchedGroup || track?.matched_group || track?.group || '').trim();
    if (direct) return direct;
    const label = String(track?.label || '');
    const match = label.match(/^Auto:\s*(.+)$/i);
    return match ? match[1].trim() : '';
  }

  // Every auto-matched photo (whether from a manual "Auto populate" click or
  // the background Smart Auto-Match job) lands in the same single, stable
  // track per request instead of a new track each time - kept in sync with
  // the backend's _apply_match()/AUTO_MATCH_TRACK_LABEL in
  // services/auto_populate_service.py, which must agree on the id/label
  // convention so both sides merge into the same track rather than each
  // creating their own.
  function mergeAutoMatchTrack(tracks, requestId, matchedGroup, newPhotoIds) {
    const newGroups = Array.isArray(matchedGroup) ? matchedGroup.filter(Boolean) : (matchedGroup ? [matchedGroup] : []);
    const trackId = `smart-auto-match-${requestId}`;
    const idx = tracks.findIndex(t => t.id === trackId || t.source === 'auto');
    if (idx === -1) {
      return [...tracks, {
        id: trackId, label: AUTO_MATCH_TRACK_LABEL, source: 'auto',
        matchedGroup: newGroups[0] || null, matchedGroups: newGroups, allPhotos: false,
        photoIds: [...newPhotoIds],
      }];
    }
    const existing = tracks[idx];
    const existingGroups = existing.matchedGroups || (existing.matchedGroup ? [existing.matchedGroup] : []);
    const mergedGroups = Array.from(new Set([...existingGroups, ...newGroups]));
    const mergedIds = Array.from(new Set([...(existing.photoIds || []), ...newPhotoIds]));
    const next = [...tracks];
    next[idx] = {
      ...existing, id: trackId, label: AUTO_MATCH_TRACK_LABEL, source: 'auto',
      matchedGroup: mergedGroups[0] || newGroups[0] || null, matchedGroups: mergedGroups,
      allPhotos: Boolean(existing.allPhotos), photoIds: mergedIds,
    };
    return next;
  }

  function matchedGroupDisplay(group) {
    const name = (groupNames[group] || '').trim();
    return name ? `${group} (${name})` : group;
  }

  function onTrackPreviewImageLoad(ev) {
    const img = ev.currentTarget;
    if (!img.naturalWidth) return;
    const cW = img.clientWidth;
    const cH = img.clientHeight;
    const ia = img.naturalWidth / img.naturalHeight;
    const ca = cW / cH;
    let rW = cW;
    let rH = cH;
    if (ca > ia) {
      rH = cH;
      rW = rH * ia;
    } else {
      rW = cW;
      rH = rW / ia;
    }
    setTrackPreviewTransform({
      scaleX: rW / img.naturalWidth,
      scaleY: rH / img.naturalHeight,
      offsetX: (cW - rW) / 2,
      offsetY: (cH - rH) / 2,
    });
  }

  async function handleInvalidateCache() {
    setInvalidatingCache(true);
    try {
      await eventApi.invalidateSearchCache(eid);
      toast('Search cache cleared.', 'success');
    } catch (e) {
      toast(e.message || 'Failed to invalidate cache.', 'error');
    } finally {
      setInvalidatingCache(false);
    }
  }

  const _applyGroupRename = useCallback((oldGroupId, newGroupId, displayName, newGroupsDict) => {
    if (newGroupsDict) setGroups(newGroupsDict);
    setPhotos(prev => prev.map(photo => ({
      ...photo,
      faces: (photo.faces || []).map(f =>
        f.group === oldGroupId ? { ...f, group: newGroupId } : f
      ),
    })));
    setGroupNames(prev => {
      const next = { ...prev };
      delete next[oldGroupId];
      if (displayName) next[newGroupId] = displayName;
      return next;
    });
    fetchedGroupIdsRef.current.delete(oldGroupId);
    ensureGroupThumbnails([newGroupId]);
  }, [ensureGroupThumbnails]);

  const selectMemoryFaceForGroup = useCallback(async (group, face) => {
    if (!face?.group_id) return;
    try {
      const res = await eventApi.saveGroupAssignment(eid, {
        group_id: group,
        memory_group_id: face.group_id,
      });
      if (res.ok) {
        _applyGroupRename(group, res.new_group_id, res.display_name || face.display_name || '', res.groups);
        toast(`Assigned group ${group} to ${res.display_name || face.display_name}.`, 'success');
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to assign face.', 'error');
    }
  }, [eid, toast, _applyGroupRename]);

  const createMemoryFaceForGroup = useCallback(async (group, displayNameValue) => {
    const cleanName = String(displayNameValue || '').trim();
    if (!cleanName) return;
    try {
      const res = await eventApi.saveGroupAssignment(eid, {
        group_id: group,
        display_name: cleanName,
      });
      if (res.ok) {
        _applyGroupRename(group, res.new_group_id, cleanName, res.groups);
        toast(`Created "${cleanName}" in memory.`, 'success');
        eventApi.getNamedFaces()
          .then(data => setMemoryFaces(Array.isArray(data.faces) ? data.faces : []))
          .catch(() => {});
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to create memory face.', 'error');
    }
  }, [eid, toast, _applyGroupRename]);

  const clearGroupAssignment = useCallback(async (group) => {
    try {
      const res = await eventApi.saveGroupAssignment(eid, { group_id: group, clear: true });
      if (res.ok) {
        _applyGroupRename(group, res.new_group_id, '', res.groups);
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to clear assignment.', 'error');
    }
  }, [eid, toast, _applyGroupRename]);

  async function autoPopulateFromMemory() {
    setAutoPopulating(true);
    try {
      const initial = await eventApi.autoPopulateGroupNames(eid);
      const jobId = initial.job_id;
      if (!jobId) throw new Error('No job ID returned from server');

      const POLL_INTERVAL = 5000;
      const MAX_WAIT = 300000;
      const deadline = Date.now() + MAX_WAIT;
      let res = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        const poll = await eventApi.getAutoPopulateGroupNamesStatus(eid, jobId);
        if (poll.status === 'done' || poll.status === 'error') { res = poll; break; }
      }
      if (!res) throw new Error('Auto-populate timed out. Please try again.');
      if (res.status === 'error') throw new Error(res.error || 'Auto-populate failed.');

      if (res.applied_count > 0) {
        // Reload full gallery state since multiple groups may have been merged
        const freshMeta = await eventApi.getGalleryMetadata(eid);
        const payload = freshMeta?.data ?? freshMeta;
        const loadedPhotos = Array.isArray(payload) ? payload : Array.isArray(payload?.photos) ? payload.photos : [];
        const groupsFromMeta = (payload && typeof payload.groups === 'object' && payload.groups) || {};
        const { groups: nextGroups, sampleByGroup: nextSamples } = buildGroups(loadedPhotos, groupsFromMeta);
        fetchedPhotoIdsRef.current = new Set();
        const freshInitialIds = loadedPhotos.slice(0, 24).map(p => p.photo_id).filter(Boolean);
        freshInitialIds.forEach(id => fetchedPhotoIdsRef.current.add(String(id)));
        const freshGroupIds = Object.keys(nextGroups)
          .sort((a, b) => (Number(nextGroups[b]) || 0) - (Number(nextGroups[a]) || 0))
          .slice(0, 30);
        fetchedGroupIdsRef.current = new Set(freshGroupIds);
        const freshUrls = await eventApi.getPresignedUrls(eid, freshInitialIds, false, freshGroupIds).catch(() => ({}));
        setPhotos(sortPhotosByCapturedAtDesc(loadedPhotos));
        setGroups(nextGroups);
        setSampleByGroup(nextSamples);
        mergePresignedUrls(freshUrls);
        setGroupNames(prev => {
          const next = { ...prev };
          for (const [gid, name] of Object.entries(res.names || {})) {
            if (name) next[gid] = name;
          }
          return next;
        });
        toast(`Auto-populated ${res.applied_count} name(s) from memory.`, 'success');
      } else {
        toast('No matches found in your memory.', 'info');
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Auto-populate failed.', 'error');
    } finally {
      setAutoPopulating(false);
    }
  }

  async function rebuildGroupEmbeddings() {
    setRebuildingEmbeddings(true);
    try {
      const initial = await eventApi.rebuildGroupEmbeddings(eid);
      const jobId = initial.job_id;
      if (!jobId) throw new Error('No job ID returned from server');

      const POLL_INTERVAL = 5000;
      const MAX_WAIT = 300000;
      const deadline = Date.now() + MAX_WAIT;
      let res = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
        const poll = await eventApi.getRebuildGroupEmbeddingsStatus(eid, jobId);
        if (poll.status === 'done' || poll.status === 'error') { res = poll; break; }
      }
      if (!res) throw new Error('Rebuild timed out. Please try again.');
      if (res.status === 'error') throw new Error(res.error || 'Rebuild failed.');

      toast(`Rebuilt face embeddings for ${res.groups?.length ?? 0} group(s).`, 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Rebuild failed.', 'error');
    } finally {
      setRebuildingEmbeddings(false);
    }
  }

  async function handleRegrouped({ message, severity, groupRename }) {
    toast(message || 'Face reassigned.', severity || 'success');
    if (groupRename) {
      _applyGroupRename(groupRename.oldGroupId, groupRename.newGroupId, groupRename.displayName || '', groupRename.groups);
    }
  }

  async function autoPopulate() {
    if (mapTab === 'deletion') {
      if (!selectedDeletionRequestId) return;
      setAutoPopulateSingleLoading(true);
      try {
        const res = await eventApi.autoPopulateDeletionRequest(eid, selectedDeletionRequestId);
        if (!res.photo_ids?.length) { toast(res.matched_group ? `Matched ${res.matched_group} but no photos.` : 'No match above threshold.'); return; }
        setTracksByDeletionRequest(prev => ({ ...prev, [selectedDeletionRequestId]: mergeAutoMatchTrack(prev[selectedDeletionRequestId] || [], selectedDeletionRequestId, res.matched_group, res.photo_ids) }));
        toast(`Mapped to ${res.matched_group} - ${res.photo_ids.length} photos.`, 'success');
      } catch (e) { toast(e.response?.data?.detail || e.response?.data?.error || e.message || 'Auto-populate failed', 'error'); }
      finally { setAutoPopulateSingleLoading(false); }
      return;
    }
    if (!selectedRequestId) return;
    setAutoPopulateSingleLoading(true);
    try {
      const res = await eventApi.autoPopulate(eid, selectedRequestId);
      if (!res.photo_ids?.length) { toast(res.matched_group ? `Matched ${res.matched_group} but no photos.` : 'No match above 0.7 threshold.'); return; }
      setTracksByRequest(prev => ({ ...prev, [selectedRequestId]: mergeAutoMatchTrack(prev[selectedRequestId] || [], selectedRequestId, res.matched_groups || res.matched_group, res.photo_ids) }));
      toast(`Mapped to ${res.matched_group} - ${res.photo_ids.length} photos.`, 'success');
    } catch (e) { toast(e.response?.data?.detail || e.response?.data?.error || e.message || 'Auto-populate failed', 'error'); }
    finally { setAutoPopulateSingleLoading(false); }
  }

  function beginRequestEdit(req) {
    setRequestEdit({
      requestId: req.request_id,
      email_address: req.email_address || '',
      phone_number: req.phone_number || '',
      notes: req.notes || '',
    });
  }

  async function saveRequestEdit() {
    const request = photoRequests.find(r => r.request_id === requestEdit.requestId);
    if (!request) return;
    setRequestEditSaving(true);
    try {
      const updated = await eventApi.updateMyRequestFields(eid, request.request_id, {
        name: request.name || '',
        email_address: requestEdit.email_address.trim(),
        phone_number: requestEdit.phone_number.trim(),
        notes: requestEdit.notes.trim(),
      });
      setPhotoRequests(prev => prev.map(item => item.request_id === request.request_id ? { ...item, ...updated } : item));
      setRequestEdit({ requestId: '', email_address: '', phone_number: '', notes: '' });
      toast('Request details updated.', 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to update request.', 'error');
    } finally {
      setRequestEditSaving(false);
    }
  }

  async function updateUnnamedFaceGroupFromRequest(request) {
    if (!request?.request_id) return;
    const cleanName = String(request.name || '').trim();
    if (!cleanName) {
      toast('Request name is empty.', 'warning');
      return;
    }
    setRequestNameSavingId(request.request_id);
    try {
      const match = await eventApi.autoPopulate(eid, request.request_id);
      const matchedGroup = String(match?.matched_group || '').trim();
      if (!matchedGroup) {
        toast('No matching face group found for this request.', 'info');
        return;
      }
      if ((groupNames[matchedGroup] || '').trim()) {
        toast(`${matchedGroup} is already named; existing names were left unchanged.`, 'info');
        return;
      }
      const res = await eventApi.saveGroupAssignment(eid, {
        group_id: matchedGroup,
        display_name: cleanName,
      });
      if (res.ok) {
        _applyGroupRename(matchedGroup, res.new_group_id, cleanName, res.groups);
        setTracksByRequest(prev => {
          const nextTracks = (prev[request.request_id] || []).map(track => (
            trackMatchedGroup(track) === matchedGroup
              ? { ...track, matchedGroup: res.new_group_id, label: track.source === 'auto' ? AUTO_MATCH_TRACK_LABEL : track.label }
              : track
          ));
          const next = { ...prev, [request.request_id]: nextTracks };
          tracksByRequestRef.current = { ...tracksByRequestRef.current, [request.request_id]: nextTracks };
          return next;
        });
        toast(`Named ${matchedGroup} as "${cleanName}".`, 'success');
      }
    } catch (e) {
      toast(e.response?.data?.detail || e.response?.data?.error || e.message || 'Failed to update face-group name.', 'error');
    } finally {
      setRequestNameSavingId('');
    }
  }

  async function autoPopulateAllRequests() {
    if (!photoRequests.length) {
      toast('No requests to auto populate.');
      return;
    }

    setAutoPopulateAllProgress({ active: true, completed: 0, total: photoRequests.length, failed: 0, current: '', failedRequests: [] });
    let matched = 0;
    let failed = 0;
    const failedRequests = [];

    for (let index = 0; index < photoRequests.length; index += 1) {
      const request = photoRequests[index];
      const rid = request.request_id;
      if (!rid) continue;
      setAutoPopulateAllProgress({ active: true, completed: index, total: photoRequests.length, failed, current: request.name || rid, failedRequests });

      try {
        const res = await eventApi.autoPopulate(eid, rid);
        if (res.photo_ids?.length) {
          const currentTracks = tracksByRequestRef.current[rid] || [];
          const nextTracks = mergeAutoMatchTrack(currentTracks, rid, res.matched_groups || res.matched_group, res.photo_ids);
          const payload = buildSavePayloadFromTracks(nextTracks);
          await eventApi.saveSelections(eid, rid, payload);
          setTracksByRequest(prev => ({ ...prev, [rid]: nextTracks }));
          tracksByRequestRef.current = { ...tracksByRequestRef.current, [rid]: nextTracks };
          lastSavedRef.current[rid] = JSON.stringify(nextTracks);
          matched += 1;
        }
      } catch (e) {
        failed += 1;
        failedRequests.push({
          name: request.name || rid,
          reason: e.response?.data?.error || e.message || 'Auto-populate failed.',
        });
      }

      setAutoPopulateAllProgress({ active: true, completed: index + 1, total: photoRequests.length, failed, current: request.name || rid, failedRequests });
    }

    setAutoPopulateAllProgress(prev => ({ ...prev, active: false, current: '' }));
    toast(`Auto populated and saved ${matched} of ${photoRequests.length} requests${failed ? `, ${failed} failed` : ''}.`, failed ? 'info' : 'success');
  }

  async function loadRequestSelections(requestId) {
    if (!requestId) return;
    try {
      const res = await eventApi.getPhotoRequest(eid, requestId);
      // selected_tracks being an array at all (even []) means it was explicitly
      // saved at least once, and is authoritative - including "the organiser
      // saved with everything removed". Only fall back to synthesising a track
      // from the legacy auto_mapped_photo_ids field when selected_tracks was
      // never saved in the first place (key genuinely absent), otherwise an
      // intentional deletion+save reappears on the next load/refresh.
      const rawTracks = res?.selected_tracks;
      const legacy = Array.isArray(res?.auto_mapped_photo_ids) ? res.auto_mapped_photo_ids : [];
      const hydrated = Array.isArray(rawTracks)
        ? rawTracks
        : legacy.length > 0 ? [{ id: `legacy-${requestId}`, label: 'Auto (previous)', source: 'auto', photoIds: legacy }] : [];
      setTracksByRequest(prev => ({ ...prev, [requestId]: hydrated }));
      tracksByRequestRef.current = { ...tracksByRequestRef.current, [requestId]: hydrated };
      lastSavedRef.current[requestId] = JSON.stringify(hydrated);
    } catch { /* ignore */ }
  }

  useEffect(() => { if (selectedRequestId) loadRequestSelections(selectedRequestId); }, [selectedRequestId, reloadKey]); // eslint-disable-line

  async function loadDeletionRequestSelections(requestId) {
    if (!requestId) return;
    try {
      const res = await eventApi.getDeletionRequest(eid, requestId);
      // See the matching comment in loadRequestSelections - same fix, same reason.
      const rawTracks = res?.selected_tracks;
      const legacy = Array.isArray(res?.auto_mapped_photo_ids) ? res.auto_mapped_photo_ids : [];
      const hydrated = Array.isArray(rawTracks)
        ? rawTracks
        : legacy.length > 0 ? [{ id: `legacy-${requestId}`, label: 'Auto (previous)', source: 'auto', photoIds: legacy }] : [];
      setTracksByDeletionRequest(prev => ({ ...prev, [requestId]: hydrated }));
      tracksByDeletionRequestRef.current = { ...tracksByDeletionRequestRef.current, [requestId]: hydrated };
      lastSavedDeletionRef.current[requestId] = JSON.stringify(hydrated);
    } catch { /* ignore */ }
  }

  useEffect(() => { if (selectedDeletionRequestId) loadDeletionRequestSelections(selectedDeletionRequestId); }, [selectedDeletionRequestId, reloadKey]); // eslint-disable-line

  async function loadCircleSelections(circleId) {
    if (!circleId) return;
    try {
      const res = await eventApi.getPhotoCircle(eid, circleId);
      const tracks = Array.isArray(res?.selected_tracks) ? res.selected_tracks : [];
      setTracksByCircle(prev => ({ ...prev, [circleId]: tracks }));
      tracksByCircleRef.current = { ...tracksByCircleRef.current, [circleId]: tracks };
      lastSavedCircleRef.current[circleId] = JSON.stringify(tracks);
    } catch { /* ignore */ }
  }

  useEffect(() => { if (selectedCircleId) loadCircleSelections(selectedCircleId); }, [selectedCircleId, reloadKey]); // eslint-disable-line

  useEffect(() => {
    const referenceRequestId = mapTab === 'deletion' ? selectedDeletionRequestId : selectedRequestId;
    if (!referenceRequestId) {
      setSelectedRequestPhotoUrl('');
      setSelectedRequestPhotoLoading(false);
      setSelectedRequestPhotoError(false);
      return undefined;
    }
    // Skip fetch for requests that have no selfie
    if (mapTab === 'requests') {
      const req = photoRequests.find(r => r.request_id === referenceRequestId);
      if (req && req.has_selfie === false) {
        setSelectedRequestPhotoUrl('');
        setSelectedRequestPhotoLoading(false);
        setSelectedRequestPhotoError(false);
        return undefined;
      }
    }
    // Deletion requests: use the presigned reference_photo_url returned by listDeletionRequests directly
    if (mapTab === 'deletion') {
      const req = deletionRequests.find(r => r.request_id === referenceRequestId);
      if (req?.reference_photo_url) {
        setSelectedRequestPhotoUrl(req.reference_photo_url);
        setSelectedRequestPhotoLoading(false);
        setSelectedRequestPhotoError(false);
      } else {
        setSelectedRequestPhotoUrl('');
        setSelectedRequestPhotoLoading(false);
        setSelectedRequestPhotoError(true);
      }
      return undefined;
    }
    let active = true;
    let objectUrl = '';
    setSelectedRequestPhotoUrl('');
    setSelectedRequestPhotoLoading(true);
    setSelectedRequestPhotoError(false);
    eventApi.getRequestPhotoBlobUrl(eid, referenceRequestId)
      .then(url => {
        objectUrl = url;
        if (active) {
          setSelectedRequestPhotoUrl(url);
          setSelectedRequestPhotoLoading(false);
          setSelectedRequestPhotoError(false);
        }
        else if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      })
      .catch(() => {
        if (active) {
          setSelectedRequestPhotoUrl('');
          setSelectedRequestPhotoLoading(false);
          setSelectedRequestPhotoError(true);
        }
      });
    return () => {
      active = false;
      if (objectUrl?.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
    };
  }, [eid, selectedRequestId, selectedDeletionRequestId, mapTab, deletionRequests, photoRequests]);

  useEffect(() => {
    if (mapTab !== 'requests' || !selectedRequestId) {
      setSelectedRequestSecondaryPhotos([]);
      return;
    }
    let active = true;
    eventApi.listRequestSecondaryPhotos(eid, selectedRequestId)
      .then(res => { if (active) setSelectedRequestSecondaryPhotos(res.photos || []); })
      .catch(() => { if (active) setSelectedRequestSecondaryPhotos([]); });
    return () => { active = false; };
  }, [eid, selectedRequestId, mapTab]);

  async function addSelectedRequestSecondaryPhoto(file) {
    const result = await eventApi.addRequestSecondaryPhoto(eid, selectedRequestId, file);
    setSelectedRequestSecondaryPhotos(prev => [...prev, { photo_key: result.photo_key, url: URL.createObjectURL(file) }]);
  }

  async function removeSelectedRequestSecondaryPhoto(photoKey) {
    await eventApi.deleteRequestSecondaryPhoto(eid, selectedRequestId, photoKey);
    setSelectedRequestSecondaryPhotos(prev => prev.filter(p => p.photo_key !== photoKey));
  }

  async function replaceSelectedRequestPhoto(file) {
    if (!file || !selectedRequestId) return;
    setReplacingRequestPhoto(true);
    try {
      await eventApi.updateRequestReferencePhoto(eid, selectedRequestId, file);
      setPhotoRequests(prev => prev.map(r => (r.request_id === selectedRequestId ? { ...r, has_selfie: true } : r)));
      toast('Request photo updated.', 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to update request photo.', 'error');
    } finally {
      setReplacingRequestPhoto(false);
    }
  }

  async function toggleSelectedRequestMatchAllPhotos(enabled) {
    if (!selectedRequestId) return;
    try {
      await eventApi.updateRequestMatchAllPhotos(eid, selectedRequestId, enabled);
      setPhotoRequests(prev => prev.map(r => (r.request_id === selectedRequestId ? { ...r, match_all_photos: enabled } : r)));
      toast(enabled ? 'This request will now receive every processed photo.' : 'Receive-all-photos turned off for this request.', 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to update setting.', 'error');
    }
  }

  async function toggleSelectedRequestAutoAddNewSubmissions(enabled) {
    if (!selectedRequestId) return;
    try {
      await eventApi.updateRequestAutoAddNewSubmissions(eid, selectedRequestId, enabled);
      setPhotoRequests(prev => prev.map(r => (r.request_id === selectedRequestId ? { ...r, auto_add_new_submissions: enabled } : r)));
      toast(enabled ? 'This request will now receive new submissions automatically.' : 'Auto-add new submissions turned off for this request.', 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to update setting.', 'error');
    }
  }

  function moveTrack(setTracks, targetId, trackId, direction) {
    setTracks(prev => {
      const tracks = prev[targetId] || [];
      const idx = tracks.findIndex(t => t.id === trackId);
      const swapIdx = idx + direction;
      if (idx === -1 || swapIdx < 0 || swapIdx >= tracks.length) return prev;
      const next = [...tracks];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return { ...prev, [targetId]: next };
    });
  }

  function removePhotoFromTrack(rid, tid, pid) {
    setTracksByRequest(prev => ({ ...prev, [rid]: (prev[rid] || []).map(t => t.id === tid ? { ...t, photoIds: (t.photoIds || []).filter(p => p !== pid) } : t).filter(t => (t.photoIds || []).length > 0) }));
  }

  function deleteTrack(rid, tid) {
    setTracksByRequest(prev => ({ ...prev, [rid]: (prev[rid] || []).filter(t => t.id !== tid) }));
  }

  // Explicit "this match is wrong" action for the "Auto match: xxx" indicator.
  // Removes the Smart Auto Match track and saves immediately (rather than
  // waiting on the 10s auto-save) so the backend's save_event_selections
  // route clears auto_mapped_* right away - both so the match can't reappear
  // via the legacy-hydration fallback on refresh, and so a future grouping
  // run's Smart Auto-Match step treats this request as unmatched again
  // instead of continuing to top up a rejected group.
  async function unlinkAutoMatch(requestId) {
    if (!requestId) return;
    const currentTracks = tracksByRequestRef.current[requestId] || [];
    const nextTracks = currentTracks.filter(t => t.source !== 'auto');
    if (nextTracks.length === currentTracks.length) return;
    setTracksByRequest(prev => ({ ...prev, [requestId]: nextTracks }));
    tracksByRequestRef.current = { ...tracksByRequestRef.current, [requestId]: nextTracks };
    try {
      const payload = buildSavePayloadFromTracks(nextTracks);
      await eventApi.saveSelections(eid, requestId, payload);
      lastSavedRef.current[requestId] = JSON.stringify(nextTracks);
      toast('Unlinked auto match.', 'success');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to unlink auto match.', 'error');
    }
  }

  function renameTrack(rid, tid, label) {
    setTracksByRequest(prev => ({
      ...prev,
      [rid]: (prev[rid] || []).map(t => t.id === tid ? { ...t, label } : t),
    }));
  }

  function removePhotoFromDeletionTrack(rid, tid, pid) {
    setTracksByDeletionRequest(prev => ({ ...prev, [rid]: (prev[rid] || []).map(t => t.id === tid ? { ...t, photoIds: (t.photoIds || []).filter(p => p !== pid) } : t).filter(t => (t.photoIds || []).length > 0) }));
  }

  function deleteDeletionTrack(rid, tid) {
    setTracksByDeletionRequest(prev => ({ ...prev, [rid]: (prev[rid] || []).filter(t => t.id !== tid) }));
  }

  function renameDeletionTrack(rid, tid, label) {
    setTracksByDeletionRequest(prev => ({
      ...prev,
      [rid]: (prev[rid] || []).map(t => t.id === tid ? { ...t, label } : t),
    }));
  }

  function removePhotoFromCircleTrack(cid, tid, pid) {
    setTracksByCircle(prev => ({ ...prev, [cid]: (prev[cid] || []).map(t => t.id === tid ? { ...t, photoIds: (t.photoIds || []).filter(p => p !== pid) } : t).filter(t => (t.photoIds || []).length > 0) }));
  }

  function deleteCircleTrack(cid, tid) {
    setTracksByCircle(prev => ({ ...prev, [cid]: (prev[cid] || []).filter(t => t.id !== tid) }));
  }

  function renameCircleTrack(cid, tid, label) {
    setTracksByCircle(prev => ({
      ...prev,
      [cid]: (prev[cid] || []).map(t => t.id === tid ? { ...t, label } : t),
    }));
  }

  function manualTrackSpec(requestId) {
    return {
      findIndex: tracks => tracks.findIndex(t => t.source === 'manual' && t.label === 'Manual'),
      create: () => ({ id: `manual-${requestId}`, label: 'Manual', source: 'manual', photoIds: [] }),
    };
  }

  function existingTrackSpec(trackId) {
    return {
      findIndex: tracks => tracks.findIndex(t => t.id === trackId),
      create: () => ({ id: trackId, label: 'Track', source: 'custom', photoIds: [] }),
    };
  }

  function namedTrackSpec(requestId, name) {
    const label = name.trim();
    return {
      findIndex: tracks => tracks.findIndex(t => (t.label || '').trim().toLowerCase() === label.toLowerCase()),
      create: () => ({ id: `custom-${requestId}-${Date.now()}`, label, source: 'custom', photoIds: [] }),
    };
  }

  // ── Share section (Add to Request / Add to Circle) helpers ─────────────────

  function kindMeta(kind) {
    if (kind === 'circle') {
      return {
        list: photoCircles,
        idKey: 'circle_id',
        nameOf: c => c.circle_name || 'Untitled circle',
        tracksMap: tracksByCircle,
        setTracks: setTracksByCircle,
        label: 'Circle',
        labelPlural: 'circles',
        loadSelections: loadCircleSelections,
      };
    }
    return {
      list: photoRequests,
      idKey: 'request_id',
      nameOf: r => r.name || r.email_address || 'Unnamed request',
      tracksMap: tracksByRequest,
      setTracks: setTracksByRequest,
      label: 'Request',
      labelPlural: 'requests',
      loadSelections: loadRequestSelections,
    };
  }

  function addPhotoIdsToTrack(kind, targetId, photoIds, trackSpec) {
    if (!targetId || !photoIds.length) return;
    const { setTracks } = kindMeta(kind);
    setTracks(prev => {
      const tracks = prev[targetId] || [];
      const idx = trackSpec.findIndex(tracks);
      const nextTracks = [...tracks];
      const trackIndex = idx >= 0 ? idx : nextTracks.push(trackSpec.create()) - 1;
      const track = nextTracks[trackIndex];
      const ids = new Set(track.photoIds || []);
      for (const pid of photoIds) ids.add(pid);
      nextTracks[trackIndex] = { ...track, photoIds: Array.from(ids) };
      return { ...prev, [targetId]: nextTracks };
    });
  }

  function trackSpecForName(targetId, name) {
    return name.trim().toLowerCase() === 'manual' ? manualTrackSpec(targetId) : namedTrackSpec(targetId, name);
  }

  function addPhotoIdsToAllTargets(kind, photoIds, trackName) {
    if (!photoIds.length) return;
    const { list, idKey, setTracks } = kindMeta(kind);
    setTracks(prev => {
      const next = { ...prev };
      for (const item of list) {
        const targetId = item[idKey];
        if (!targetId) continue;
        const trackSpec = trackSpecForName(targetId, trackName);
        const tracks = next[targetId] || [];
        const idx = trackSpec.findIndex(tracks);
        const nextTracks = [...tracks];
        const trackIndex = idx >= 0 ? idx : nextTracks.push(trackSpec.create()) - 1;
        const track = nextTracks[trackIndex];
        const ids = new Set(track.photoIds || []);
        for (const pid of photoIds) ids.add(pid);
        nextTracks[trackIndex] = { ...track, photoIds: Array.from(ids) };
        next[targetId] = nextTracks;
      }
      return next;
    });
  }

  function addPhotoIdsToClipboard(photoIds, label) {
    if (!photoIds.length) return;
    setClipboard(prev => [...prev, { id: `clip-${Date.now()}`, label, photoIds: [...photoIds], source: 'selection' }]);
    toast(`Added ${photoIds.length} photo${photoIds.length === 1 ? '' : 's'} to clipboard.`, 'success');
  }

  function clearSelectionIfThese(scope, isSelection = selectedThumbs.size > 0) {
    if (scope === 'these' && isSelection) setSelectedThumbs(new Set());
  }

  function shareClipboardLabel(photoIds = sharePhotoIds, photo = currentPhoto, isSelection = selectedThumbs.size > 0) {
    if (isSelection) return `Selection (${photoIds.length})`;
    const name = (photo?.path || '').split('/').pop() || (photo?.photo_id || '').slice(0, 8);
    return `Photo: ${name}`;
  }

  async function addPhotoIdsToAllTargetsPrompted(kind, photoIds, scope, isSelection = selectedThumbs.size > 0) {
    if (!photoIds.length) return;
    const name = await swalPrompt({ title: 'Add to track', label: 'Track name', placeholder: 'Manual', inputValue: 'Manual' });
    if (!name) return;
    addPhotoIdsToAllTargets(kind, photoIds, name);
    toast(`Added to "${name}" in all ${kindMeta(kind).labelPlural}.`, 'success');
    clearSelectionIfThese(scope, isSelection);
  }

  function openCrossPicker(kind, scope, photoIds = null) {
    setCrossPicker({ open: true, kind, scope, photoIds });
  }

  function closeCrossPicker() {
    setCrossPicker({ open: false, kind: null, scope: null, photoIds: null });
  }

  async function handleCrossPickerPick(targetId, opt) {
    const { kind, scope, photoIds: customPhotoIds } = crossPicker;
    const photoIds = customPhotoIds || (scope === 'all' ? allPhotoIds : sharePhotoIds);
    if (!photoIds.length) { closeCrossPicker(); return; }
    if (opt.newTrack) {
      const name = await swalPrompt({ title: 'New track', placeholder: 'Track name' });
      if (!name) return;
      addPhotoIdsToTrack(kind, targetId, photoIds, namedTrackSpec(targetId, name));
      toast(`Added to ${name}.`, 'success');
    } else {
      addPhotoIdsToTrack(kind, targetId, photoIds, existingTrackSpec(opt.track.id));
      toast(`Added to ${opt.track.label || 'track'}.`, 'success');
    }
    clearSelectionIfThese(scope);
    closeCrossPicker();
  }

  function handleSharePrimary(kind) {
    if (!sharePhotoIds.length) return;
    openCrossPicker(kind, 'these');
  }

  function buildShareMenuItems(kind, photoIds = sharePhotoIds, photo = currentPhoto, isSelection = selectedThumbs.size > 0) {
    const meta = kindMeta(kind);
    const items = [
      { id: 'track', label: 'Add to track', onClick: () => openCrossPicker(kind, 'these', photoIds) },
      { id: 'clipboard', label: 'Add to clipboard', onClick: () => { addPhotoIdsToClipboard(photoIds, shareClipboardLabel(photoIds, photo, isSelection)); clearSelectionIfThese('these', isSelection); } },
      { id: 'all-targets', label: `Add to all ${meta.labelPlural}`, onClick: () => addPhotoIdsToAllTargetsPrompted(kind, photoIds, 'these', isSelection) },
    ];

    if (filteredPhotos.length > 0) {
      items.push({ id: 'allphotos-track', label: 'Add all photos to track', onClick: () => openCrossPicker(kind, 'all') });
      items.push({ id: 'allphotos-all', label: `Add all photos to all ${meta.labelPlural}`, onClick: () => addPhotoIdsToAllTargetsPrompted(kind, allPhotoIds, 'all') });
    }
    return items;
  }

  function removePhotoIdsFromTracks(photoIds, scope = 'all') {
    const removeSet = new Set((photoIds || []).map(String));
    if (!removeSet.size) return;
    const strip = tracks => stripPhotoIdsFromTracks(tracks, removeSet);
    if (scope === 'all' || scope === 'requests') {
      setTracksByRequest(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, strip(tracks)])));
    }
    if (scope === 'all' || scope === 'deletion') {
      setTracksByDeletionRequest(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, strip(tracks)])));
    }
    if (scope === 'all' || scope === 'circles') {
      setTracksByCircle(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, strip(tracks)])));
    }
  }

  function stripPhotoIdsFromTracks(tracks, removeSet) {
    return (tracks || [])
      .map(track => ({ ...track, photoIds: (track.photoIds || []).filter(pid => !removeSet.has(String(pid))) }))
      .filter(track => (track.photoIds || []).length > 0);
  }

  function pruneTrackMap(trackMap, removeSet) {
    const next = {};
    const changedIds = [];
    for (const [id, tracks] of Object.entries(trackMap || {})) {
      const stripped = stripPhotoIdsFromTracks(tracks, removeSet);
      next[id] = stripped;
      if (JSON.stringify(stripped) !== JSON.stringify(tracks || [])) {
        changedIds.push(id);
      }
    }
    return { next, changedIds };
  }

  async function pruneAndSaveDeletedPhotoTracks(photoIds) {
    const removeSet = new Set((photoIds || []).map(String));
    if (!removeSet.size) return;

    const requestState = pruneTrackMap(tracksByRequestRef.current, removeSet);
    const deletionState = pruneTrackMap(tracksByDeletionRequestRef.current, removeSet);
    const circleState = pruneTrackMap(tracksByCircleRef.current, removeSet);

    if (requestState.changedIds.length) {
      tracksByRequestRef.current = requestState.next;
      setTracksByRequest(requestState.next);
    }
    if (deletionState.changedIds.length) {
      tracksByDeletionRequestRef.current = deletionState.next;
      setTracksByDeletionRequest(deletionState.next);
    }
    if (circleState.changedIds.length) {
      tracksByCircleRef.current = circleState.next;
      setTracksByCircle(circleState.next);
    }

    await Promise.all([
      ...requestState.changedIds.map(async (rid) => {
        await eventApi.saveSelections(eid, rid, buildSavePayloadFromTracks(requestState.next[rid] || []));
        lastSavedRef.current[rid] = JSON.stringify(requestState.next[rid] || []);
      }),
      ...deletionState.changedIds.map(async (rid) => {
        await eventApi.saveDeletionRequestSelections(eid, rid, buildSavePayloadFromTracks(deletionState.next[rid] || []));
        lastSavedDeletionRef.current[rid] = JSON.stringify(deletionState.next[rid] || []);
      }),
      ...circleState.changedIds.map(async (cid) => {
        await eventApi.saveCircleSelections(eid, cid, buildCircleSavePayloadFromTracks(circleState.next[cid] || []));
        lastSavedCircleRef.current[cid] = JSON.stringify(circleState.next[cid] || []);
      }),
    ]);
  }

  function removeCurrentPhotoFromAllTracks() {
    const ids = selectedThumbs.size ? Array.from(selectedThumbs) : currentPhoto?.photo_id ? [currentPhoto.photo_id] : [];
    if (!ids.length) return;
    removePhotoIdsFromTracks(ids);
    setSelectedThumbs(new Set());
    toast(`Removed ${ids.length} photo${ids.length === 1 ? '' : 's'} from all tracks.`, 'success');
  }

  function removeCurrentPhotoFromTrack(track, scope) {
    const ids = selectedThumbs.size ? Array.from(selectedThumbs) : currentPhoto?.photo_id ? [currentPhoto.photo_id] : [];
    if (!ids.length || !track?.id) return;
    const removeSet = new Set(ids.map(String));
    const stripOne = tracks => (tracks || [])
      .map(t => t.id === track.id ? { ...t, photoIds: (t.photoIds || []).filter(pid => !removeSet.has(String(pid))) } : t)
      .filter(t => (t.photoIds || []).length > 0);
    if (scope === 'requests') setTracksByRequest(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, stripOne(tracks)])));
    if (scope === 'deletion') setTracksByDeletionRequest(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, stripOne(tracks)])));
    if (scope === 'circles') setTracksByCircle(prev => Object.fromEntries(Object.entries(prev).map(([id, tracks]) => [id, stripOne(tracks)])));
    setSelectedThumbs(new Set());
    toast(`Removed from ${track.label || 'track'}.`, 'success');
  }

  async function deleteSelectedPhotos() {
    if (deletingPhotos) return;
    const selected = selectedThumbs.size
      ? Array.from(selectedThumbs)
      : currentPhoto?.photo_id
        ? [currentPhoto.photo_id]
        : [];
    if (!selected.length) return;
    const deletingSelection = selectedThumbs.size > 0;
    const ok = await swalConfirmDelete({
      title: deletingSelection
        ? `Delete ${selected.length} selected photo${selected.length === 1 ? '' : 's'}?`
        : 'Delete this photo?',
      text: 'This removes the gallery previews, thumbnails, face-group references, and search index entries for these photos.',
      confirmText: deletingSelection ? 'Delete selected' : 'Delete photo',
    });
    if (!ok) return;
    setDeletingPhotos(true);
    try {
      // Unprocessed (submission-backed) photos live in a different table with
      // their own delete route - split a mixed selection and issue both calls.
      const submissionIds = selected.filter(isUnprocessedPhotoId).map(pid => pid.slice(SUBMISSION_ID_PREFIX.length));
      const galleryIds = selected.filter(pid => !isUnprocessedPhotoId(pid));
      let deleted = [];
      let deletedCount = 0;
      if (galleryIds.length) {
        const result = await eventApi.deletePhotos(eid, galleryIds);
        deleted = deleted.concat(Array.isArray(result?.deleted_photo_ids) ? result.deleted_photo_ids.map(String) : galleryIds);
        deletedCount += result?.deleted_count ?? galleryIds.length;
        if (result?.groups && typeof result.groups === 'object') setGroups(result.groups);
      }
      if (submissionIds.length) {
        if (submissionIds.length === 1) await eventApi.deletePhotoSubmission(eid, submissionIds[0]);
        else await eventApi.bulkDeletePhotoSubmissions(eid, submissionIds);
        deleted = deleted.concat(submissionIds.map(id => `${SUBMISSION_ID_PREFIX}${id}`));
        deletedCount += submissionIds.length;
      }
      const deletedSet = new Set(deleted);
      setSelectedThumbs(new Set());
      setPhotos(prev => prev.filter(photo => !deletedSet.has(String(photo.photo_id))));
      setAiSearchPhotoIds(prev => prev.filter(pid => !deletedSet.has(String(pid))));
      setClipboard(prev => prev
        .map(item => ({ ...item, photoIds: (item.photoIds || []).filter(pid => !deletedSet.has(String(pid))) }))
        .filter(item => (item.photoIds || []).length > 0));
      await pruneAndSaveDeletedPhotoTracks(deleted);
      toast(`Deleted ${deletedCount} photo${deletedCount === 1 ? '' : 's'}.`, 'success');
    } catch (e) {
      toast(e.message || 'Failed to delete selected photos.', 'error');
    } finally {
      setDeletingPhotos(false);
    }
  }

  const sharePhotoIds = selectedThumbs.size
    ? Array.from(selectedThumbs)
    : currentPhoto?.photo_id
      ? [currentPhoto.photo_id]
      : [];
  const allPhotoIds = filteredPhotos.map(p => p.photo_id).filter(Boolean);

  const activePhotoIdsForTrackActions = selectedThumbs.size
    ? Array.from(selectedThumbs).map(String)
    : currentPhoto?.photo_id
      ? [String(currentPhoto.photo_id)]
      : [];
  const removeTrackMenuChildren = [
    ...requestTracks.filter(track => (track.photoIds || []).some(pid => activePhotoIdsForTrackActions.includes(String(pid))))
      .map((track, index) => ({ id: `remove-request-${track.id || index}`, label: `Request: ${track.label || `Track ${index + 1}`}`, onClick: () => removeCurrentPhotoFromTrack(track, 'requests') })),
    ...deletionRequestTracks.filter(track => (track.photoIds || []).some(pid => activePhotoIdsForTrackActions.includes(String(pid))))
      .map((track, index) => ({ id: `remove-deletion-${track.id || index}`, label: `Deletion: ${track.label || `Track ${index + 1}`}`, onClick: () => removeCurrentPhotoFromTrack(track, 'deletion') })),
    ...circleTracks.filter(track => (track.photoIds || []).some(pid => activePhotoIdsForTrackActions.includes(String(pid))))
      .map((track, index) => ({ id: `remove-circle-${track.id || index}`, label: `Circle: ${track.label || `Track ${index + 1}`}`, onClick: () => removeCurrentPhotoFromTrack(track, 'circles') })),
  ];
  const deletePhotoMenuItems = [
    { id: 'delete-photo', label: selectedThumbs.size > 0 ? `Delete selected (${selectedThumbs.size})` : 'Delete photo', onClick: deleteSelectedPhotos },
    ...(removeTrackMenuChildren.length ? [{ id: 'remove-tracks', label: 'Remove from Tracks', children: removeTrackMenuChildren }] : []),
    { id: 'remove-all-tracks', label: 'Remove from all tracks', onClick: removeCurrentPhotoFromAllTracks },
  ];

  // ── Selection auto-save every 10 s; group names every 60 s ─────────────────

  function buildSavePayload(rid) {
    const tracks = tracksByRequestRef.current[rid] || [];
    return buildSavePayloadFromTracks(tracks);
  }

  function buildDeletionSavePayload(rid) {
    const tracks = tracksByDeletionRequestRef.current[rid] || [];
    return buildSavePayloadFromTracks(tracks);
  }

  function buildSavePayloadFromTracks(tracks) {
    const urlSet = new Set();
    for (const t of tracks) for (const pid of t.photoIds || []) {
      const p = photoByIdRef.current.get(pid);
      const u = (p?.url || p?.path || '').toString().replace(/\\/g, '/').replace(/^\/+/, '');
      if (u) urlSet.add(u);
    }
    return { selected_urls: [...urlSet], tracks };
  }

  function buildCircleSavePayload(circleId) {
    const tracks = tracksByCircleRef.current[circleId] || [];
    return buildCircleSavePayloadFromTracks(tracks);
  }

  function buildCircleSavePayloadFromTracks(tracks) {
    const urlSet = new Set();
    for (const t of tracks) for (const pid of t.photoIds || []) {
      const p = photoByIdRef.current.get(pid);
      const u = (p?.url || p?.path || '').toString().replace(/\\/g, '/').replace(/^\/+/, '');
      if (u) urlSet.add(u);
    }
    return { selected_urls: [...urlSet], tracks };
  }

  // After each save, the server hands back its true post-commit track list
  // (see put_photo_request_payload et al. on the backend) - adopting it here
  // self-heals this tab's view against anything another device committed
  // moments earlier. Only adopted if nothing changed locally since the
  // payload was captured (checked via the same JSON-string comparison the
  // dirty-check uses); otherwise a local edit made while the save was still
  // in flight would be clobbered by the (now-stale) server response. Either
  // way, lastSavedRef is stamped with what was actually confirmed saved, so
  // the next cycle correctly re-saves anything that changed in the meantime.
  async function saveAllDirty(requestList) {
    const dirty = requestList.filter(r => {
      const rid = r.request_id;
      const current = JSON.stringify(tracksByRequestRef.current[rid] || []);
      return current !== (lastSavedRef.current[rid] ?? '[]');
    });
    if (!dirty.length) return;
    setAutoSaveStatus('saving');
    try {
      await Promise.all(dirty.map(async r => {
        const rid = r.request_id;
        const sentTracksJson = JSON.stringify(tracksByRequestRef.current[rid] || []);
        const payload = buildSavePayload(rid);
        const res = await eventApi.saveSelections(eid, rid, payload);
        const freshTracks = res?.selected_tracks ?? payload.tracks;
        if (JSON.stringify(tracksByRequestRef.current[rid] || []) === sentTracksJson) {
          setTracksByRequest(prev => ({ ...prev, [rid]: freshTracks }));
        }
        lastSavedRef.current[rid] = JSON.stringify(freshTracks);
      }));
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('idle');
    }
  }

  async function saveAllDirtyCircles(circleList) {
    const dirty = circleList.filter(c => {
      const cid = c.circle_id;
      const current = JSON.stringify(tracksByCircleRef.current[cid] || []);
      return current !== (lastSavedCircleRef.current[cid] ?? '[]');
    });
    if (!dirty.length) return;
    setAutoSaveStatus('saving');
    try {
      await Promise.all(dirty.map(async c => {
        const cid = c.circle_id;
        const sentTracksJson = JSON.stringify(tracksByCircleRef.current[cid] || []);
        const payload = buildCircleSavePayload(cid);
        const res = await eventApi.saveCircleSelections(eid, cid, payload);
        const freshTracks = res?.selected_tracks ?? payload.tracks;
        if (JSON.stringify(tracksByCircleRef.current[cid] || []) === sentTracksJson) {
          setTracksByCircle(prev => ({ ...prev, [cid]: freshTracks }));
        }
        lastSavedCircleRef.current[cid] = JSON.stringify(freshTracks);
      }));
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('idle');
    }
  }

  async function saveAllDirtyDeletionRequests(requestList) {
    const dirty = requestList.filter(r => {
      const rid = r.request_id;
      const current = JSON.stringify(tracksByDeletionRequestRef.current[rid] || []);
      return current !== (lastSavedDeletionRef.current[rid] ?? '[]');
    });
    if (!dirty.length) return;
    setAutoSaveStatus('saving');
    try {
      await Promise.all(dirty.map(async r => {
        const rid = r.request_id;
        const sentTracksJson = JSON.stringify(tracksByDeletionRequestRef.current[rid] || []);
        const payload = buildDeletionSavePayload(rid);
        const res = await eventApi.saveDeletionRequestSelections(eid, rid, payload);
        const freshTracks = res?.selected_tracks ?? payload.tracks;
        if (JSON.stringify(tracksByDeletionRequestRef.current[rid] || []) === sentTracksJson) {
          setTracksByDeletionRequest(prev => ({ ...prev, [rid]: freshTracks }));
        }
        lastSavedDeletionRef.current[rid] = JSON.stringify(freshTracks);
      }));
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('idle');
    }
  }

  useEffect(() => {
    if (!photoRequests.length) return;
    const timer = setInterval(() => saveAllDirty(photoRequests), 10_000);
    return () => clearInterval(timer);
  }, [photoRequests, eid]); // eslint-disable-line

  useEffect(() => {
    if (!photoCircles.length) return;
    const timer = setInterval(() => saveAllDirtyCircles(photoCircles), 10_000);
    return () => clearInterval(timer);
  }, [photoCircles, eid]); // eslint-disable-line

  useEffect(() => {
    if (!deletionRequests.length) return;
    const timer = setInterval(() => saveAllDirtyDeletionRequests(deletionRequests), 10_000);
    return () => clearInterval(timer);
  }, [deletionRequests, eid]); // eslint-disable-line

  useEffect(() => {
    if (autoSaveStatus !== 'saved') return;
    const t = setTimeout(() => setAutoSaveStatus('idle'), 2500);
    return () => clearTimeout(t);
  }, [autoSaveStatus]);

  // Manual save - also stamps lastSavedRef so auto-save doesn't re-fire.
  async function saveSelections() {
    if (mapTab === 'deletion') {
      if (!selectedDeletionRequestId) return;
      try {
        const rid = selectedDeletionRequestId;
        const sentTracksJson = JSON.stringify(tracksByDeletionRequestRef.current[rid] || []);
        const payload = buildDeletionSavePayload(rid);
        const res = await eventApi.saveDeletionRequestSelections(eid, rid, payload);
        const freshTracks = res?.selected_tracks ?? payload.tracks;
        if (JSON.stringify(tracksByDeletionRequestRef.current[rid] || []) === sentTracksJson) {
          setTracksByDeletionRequest(prev => ({ ...prev, [rid]: freshTracks }));
        }
        lastSavedDeletionRef.current[rid] = JSON.stringify(freshTracks);
        toast(`Saved ${payload.selected_urls.length} photos.`, 'success');
      } catch (e) { toast(e.message || 'Failed to save', 'error'); }
      return;
    }
    if (mapTab === 'circles') {
      if (!selectedCircleId) return;
      try {
        const cid = selectedCircleId;
        const sentTracksJson = JSON.stringify(tracksByCircleRef.current[cid] || []);
        const payload = buildCircleSavePayload(cid);
        const res = await eventApi.saveCircleSelections(eid, cid, payload);
        const freshTracks = res?.selected_tracks ?? payload.tracks;
        if (JSON.stringify(tracksByCircleRef.current[cid] || []) === sentTracksJson) {
          setTracksByCircle(prev => ({ ...prev, [cid]: freshTracks }));
        }
        lastSavedCircleRef.current[cid] = JSON.stringify(freshTracks);
        toast(`Saved ${payload.selected_urls.length} photos.`, 'success');
      } catch (e) { toast(e.message || 'Failed to save', 'error'); }
      return;
    }
    if (!selectedRequestId) return;
    try {
      const rid = selectedRequestId;
      const sentTracksJson = JSON.stringify(tracksByRequestRef.current[rid] || []);
      const payload = buildSavePayload(rid);
      const res = await eventApi.saveSelections(eid, rid, payload);
      const freshTracks = res?.selected_tracks ?? payload.tracks;
      if (JSON.stringify(tracksByRequestRef.current[rid] || []) === sentTracksJson) {
        setTracksByRequest(prev => ({ ...prev, [rid]: freshTracks }));
      }
      lastSavedRef.current[rid] = JSON.stringify(freshTracks);
      toast(`Saved ${payload.selected_urls.length} photos.`, 'success');
    } catch (e) { toast(e.message || 'Failed to save', 'error'); }
  }

  async function deleteSelectedRequest() {
    if (!selectedRequestId || !selectedRequest) return;
    const label = selectedRequest.name || selectedRequest.email_address || selectedRequest.request_id;
    const ok = await swalConfirmDelete({
      title: `Delete request for ${label}?`,
      text: 'Shared photos from this request will be removed from their Moments feed.',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await eventApi.deletePhotoRequest(eid, selectedRequestId);
      setPhotoRequests(prev => prev.filter(r => r.request_id !== selectedRequestId));
      setTracksByRequest(prev => {
        const next = { ...prev };
        delete next[selectedRequestId];
        return next;
      });
      delete lastSavedRef.current[selectedRequestId];
      setSelectedRequestId('');
      setRequestDialogOpen(false);
      toast('Photo request deleted.', 'success');
    } catch (e) {
      toast(e.message || 'Failed to delete request.', 'error');
    }
  }

  async function getOrGenerateShareUrl(requestId) {
    if (shareUrls[requestId]) return shareUrls[requestId];
    try {
      const res = await eventApi.generateShareLink(eid, requestId);
      const url = res.share_url || '';
      setShareUrls(prev => ({ ...prev, [requestId]: url }));
      return url;
    } catch (e) {
      toast(e.message || 'Failed to generate share link.', 'error');
      return '';
    }
  }

  async function handleCopyShareLink(requestId) {
    const url = await getOrGenerateShareUrl(requestId);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast('Share link copied to clipboard.', 'success');
    } catch {
      toast('Could not copy. Link: ' + url, 'info');
    }
  }

  async function handleSendEmail(requestId) {
    setSendingEmailId(requestId);
    try {
      const res = await eventApi.sendPhotosEmail(eid, requestId);
      const url = res.share_url || '';
      setShareUrls(prev => ({ ...prev, [requestId]: url }));
      toast(`Email sent to ${res.sent_to || 'recipient'}.`, 'success');
    } catch (e) {
      toast(e.message || 'Failed to send email.', 'error');
    } finally {
      setSendingEmailId('');
    }
  }

  function applyFilterOnDeletionTracks() {
    const ids = new Set();
    for (const track of deletionRequestTracks) {
      for (const pid of track.photoIds || []) ids.add(String(pid));
    }
    if (!ids.size) {
      toast('No photos in the selected deletion request tracks.', 'info');
      return;
    }
    setTrackPhotoFilterIds(ids);
    setCurrentIndex(0);
    setSelectedThumbs(new Set());
    toast(`Filtered gallery to ${ids.size} tracked photo${ids.size === 1 ? '' : 's'}.`, 'success');
    if (isMobileTabs) {
      setDeletionDialogOpen(false);
      viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function closeRegroupModal() {
    setRegroupModal({ open: false, photo: null, faceIndex: null, face: null });
  }

  const jumpToGroupSample = useCallback((g) => {
    const sample = sampleByGroup[g];
    if (!sample) return;
    const i = filteredPhotos.findIndex(p => p.path === sample.photo.path);
    if (i >= 0) setCurrentIndex(i);
    if (isMobileTabs) {
      setFacesSheetOpen(false);
      viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [sampleByGroup, filteredPhotos, isMobileTabs]);

  function onImageLoad(ev) {
    const img = ev.currentTarget;
    if (!img.naturalWidth) return;
    const cW = img.clientWidth, cH = img.clientHeight, ia = img.naturalWidth / img.naturalHeight, ca = cW / cH;
    let rW = cW, rH = cH;
    if (ca > ia) { rH = cH; rW = rH * ia; } else { rW = cW; rH = rW / ia; }
    setImgTransform({ scaleX: rW / img.naturalWidth, scaleY: rH / img.naturalHeight, offsetX: (cW - rW) / 2, offsetY: (cH - rH) / 2 });
  }

  const half = Math.floor(thumbWindow / 2);
  let thumbStart = Math.max(0, currentIndex - half);
  let thumbEnd = Math.min(filteredPhotos.length, thumbStart + thumbWindow);
  if (thumbEnd - thumbStart < thumbWindow) thumbStart = Math.max(0, thumbEnd - thumbWindow);
  const visibleThumbs = filteredPhotos.slice(thumbStart, thumbEnd);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /><Typography mt={1} color="text.secondary">Loading gallery…</Typography></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  const noPhotosYet = !photos.length;
  const showAddToTrackActions = mapTab === 'requests' || mapTab === 'circles';


  const groupPanelExpanded = isMobileTabs || groupPanelOpen;
  const groupControlsPanel = (
    <aside className={`panel-section${isMobileTabs ? ' panel-section--standalone' : ''}`}>
      <div className="group-panel-header">
        <h2 style={{ margin: 0 }}>Group Controls</h2>
        {!isMobileTabs && (
          <button type="button" className="group-panel-toggle" onClick={() => setGroupPanelOpen(p => !p)} aria-label={groupPanelOpen ? 'Collapse group controls' : 'Expand group controls'}>
            {groupPanelOpen ? '▾' : '▸'}
          </button>
        )}
        {isMobileTabs && (
          <IconButton size="small" onClick={() => setFacesSheetOpen(false)} aria-label="Close face groups">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {groupPanelExpanded && (
        <>
          <Stack direction="row" spacing={1} mb={1} mt={1}>
            <span style={{ color: '#5f6368', fontSize: '0.9rem' }}>Photos: <strong>{filteredPhotos.length}</strong></span>
            <span style={{ color: '#5f6368', fontSize: '0.9rem' }}>Groups: <strong>{Object.keys(groups).length}</strong></span>
          </Stack>
          <GroupSearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by group or name…" />
          <div className="group-list">
            <details className="group-expander" open={showAssigned}>
              <summary onClick={e => e.preventDefault()}><button type="button" className="expander-toggle" onClick={() => setShowAssigned(p => !p)}>{showAssigned ? '▾' : '▸'} Assigned ({assignedGroups.length})</button></summary>
              {showAssigned && assignedGroups.length === 0 && <div className="group-empty">None match search.</div>}
              {showAssigned && assignedGroups.map(({ group, count }) => (
                <OrgGroupListItem
                  key={group}
                  eid={eid}
                  group={group}
                  count={count}
                  value={groupNames[group] || ''}
                  selected={selectedGroups.has(group)}
                  thumbSrc={groupThumbSrc(group)}
                  memoryFaces={memoryFaces}
                  memoryFaceThumbUrls={memoryFaceThumbUrls}
                  groupNames={groupNames}
                  groupThumbSrc={groupThumbSrc}
                  ensureGroupThumbnails={ensureGroupThumbnails}
                  showAddToTrackActions={showAddToTrackActions}
                  isOrganiser={event.is_organiser}
                  onToggleSelect={handleToggleGroupFiltered}
                  onJumpToSample={jumpToGroupSample}
                  onAddToRequest={addGroupToRequest}
                  onAddToAllRequests={addGroupToAll}
                  onAddToClipboard={addGroupToClipboard}
                  onAddToCircle={addGroupToCircle}
                  onAddToAllCircles={addGroupToAllCircles}
                  onBlurGroup={setBlurGroupJob}
                  onSelectMemoryFace={selectMemoryFaceForGroup}
                  onCreateNew={createMemoryFaceForGroup}
                  onClear={clearGroupAssignment}
                  onOpenOptions={refreshMemoryFaces}
                />
              ))}
            </details>
            <details className="group-expander" open={showUnassigned}>
              <summary onClick={e => e.preventDefault()}><button type="button" className="expander-toggle" onClick={() => setShowUnassigned(p => !p)}>{showUnassigned ? '▾' : '▸'} Unassigned ({unassignedGroups.length})</button></summary>
              {showUnassigned && unassignedGroups.length === 0 && <div className="group-empty">None match search.</div>}
              {showUnassigned && unassignedGroups.map(({ group, count }) => (
                <OrgGroupListItem
                  key={group}
                  eid={eid}
                  group={group}
                  count={count}
                  value={groupNames[group] || ''}
                  selected={selectedGroups.has(group)}
                  thumbSrc={groupThumbSrc(group)}
                  memoryFaces={memoryFaces}
                  memoryFaceThumbUrls={memoryFaceThumbUrls}
                  groupNames={groupNames}
                  groupThumbSrc={groupThumbSrc}
                  ensureGroupThumbnails={ensureGroupThumbnails}
                  showAddToTrackActions={showAddToTrackActions}
                  isOrganiser={event.is_organiser}
                  onToggleSelect={handleToggleGroupFiltered}
                  onJumpToSample={jumpToGroupSample}
                  onAddToRequest={addGroupToRequest}
                  onAddToAllRequests={addGroupToAll}
                  onAddToClipboard={addGroupToClipboard}
                  onAddToCircle={addGroupToCircle}
                  onAddToAllCircles={addGroupToAllCircles}
                  onBlurGroup={setBlurGroupJob}
                  onSelectMemoryFace={selectMemoryFaceForGroup}
                  onCreateNew={createMemoryFaceForGroup}
                  onClear={clearGroupAssignment}
                  onOpenOptions={refreshMemoryFaces}
                />
              ))}
            </details>
          </div>
          {!isMobileTabs && (
            <label className="match-mode-toggle">
              <input type="checkbox" checked={matchAll} onChange={e => setMatchAll(e.target.checked)} />
              <span className="match-mode-ui" aria-hidden="true" />
              Match all selected groups
            </label>
          )}
          <div className={`panel-actions${isMobileTabs ? ' panel-actions--floating' : ''}`}>
            {isMobileTabs && (
              <label className="match-mode-toggle match-mode-toggle--compact">
                <input
                  type="checkbox"
                  checked={matchAll}
                  onChange={e => {
                    setMatchAll(e.target.checked);
                    setFacesSheetOpen(false);
                    viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
                <span className="match-mode-ui" aria-hidden="true" />
                Match all selected groups
              </label>
            )}
            <button type="button" className="save-btn" onClick={autoPopulateFromMemory} disabled={autoPopulating}>
              {autoPopulating && <CircularProgress size={14} color="inherit" />}
              {autoPopulating ? 'Matching...' : 'Auto-populate from Memory'}
            </button>
            {(selectedGroups.size > 0 || trackPhotoFilterIds) && (
              <button type="button" className="clear-btn" onClick={() => { setSelectedGroups(new Set()); setTrackPhotoFilterIds(null); setMatchAll(false); }}>Clear Filters</button>
            )}
          </div>
          {event.is_organiser && (
            <button
              type="button"
              className="link-btn"
              onClick={rebuildGroupEmbeddings}
              disabled={rebuildingEmbeddings}
              title="Recomputes face-group matching embeddings from the current photos. Not needed after every delete - only run this if auto-matching starts feeling off."
              style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#5f6368', textDecoration: 'underline', cursor: rebuildingEmbeddings ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {rebuildingEmbeddings && <CircularProgress size={12} color="inherit" />}
              {rebuildingEmbeddings ? 'Rebuilding face embeddings…' : 'Rebuild face embeddings'}
            </button>
          )}
        </>
      )}
    </aside>
  );

  return (
    <>
      <UcDownloadProgressBar active={ucDownloading} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>

      {dragActive && (
        <Box
          sx={{
            position: 'fixed', inset: 0, zIndex: 2000,
            bgcolor: 'rgba(25, 118, 210, 0.15)',
            border: '4px dashed', borderColor: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Paper elevation={4} sx={{ px: 4, py: 3, textAlign: 'center' }}>
            <FileUploadOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>Drop photos to submit</Typography>
            <Typography variant="body2" color="text.secondary">They'll be added to Photo Submissions for review</Typography>
          </Paper>
        </Box>
      )}

      {showPhotosTab && notReadyMobile && (
        <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <PhotoLibraryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Grouping not started yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a grouping job from the Event tab. Photos and faces will appear here once it's ready.
          </Typography>
        </Box>
      )}

      {showPhotosTab && !notReadyMobile && noPhotosYet && (
        <Alert severity="info" sx={{ mb: 2 }}>No photos found in the gallery metadata.</Alert>
      )}

      {showPhotosTab && !notReadyMobile && !noPhotosYet && (
      <div className="viewer-container">
        <section className="slider-section" ref={viewerSectionRef}>
          <div className="slider-wrapper">
            <div
              className={`main-photo-wrapper${previewSwipeActive ? ' preview-swiping' : ''}`}
              onTouchStart={handlePreviewTouchStart}
              onTouchMove={handlePreviewTouchMove}
              onTouchEnd={handlePreviewTouchEnd}
              onTouchCancel={handlePreviewTouchEnd}
            >
              {currentPhoto ? (
                <>
                  <img key={currentPhoto.path} className="main-photo"
                    src={showOriginal ? (originalBlobUrl || previewSrc(currentPhoto.photo_id)) : previewSrc(currentPhoto.photo_id)}
                    alt={currentPhoto.path} onLoad={onImageLoad} onClick={enterFullscreen} />
                  <button type="button" className="preview-swipe-btn preview-swipe-btn-left" onClick={() => nav(-1)} aria-label="Previous photo">
                    &#8249;
                  </button>
                  <button type="button" className="preview-swipe-btn preview-swipe-btn-right" onClick={() => nav(1)} aria-label="Next photo">
                    &#8250;
                  </button>
                  {showFaceBoxes && (
                    <div className="main-overlay">
                      {(currentPhoto.faces || []).map((face, idx) => (
                        <div key={`${currentPhoto.path}-${idx}`}
                          className="face-overlay face-overlay-clickable"
                          style={{ left: `${imgTransform.offsetX + face.bbox[0] * imgTransform.scaleX}px`, top: `${imgTransform.offsetY + face.bbox[1] * imgTransform.scaleY}px`, width: `${face.size[0] * imgTransform.scaleX}px`, height: `${face.size[1] * imgTransform.scaleY}px` }}
                          onClick={(e) => { e.stopPropagation(); setRegroupModal({ open: true, photo: currentPhoto, faceIndex: idx, face }); }}
                          title="Click to reassign this face to a different group">
                          <div className="face-label">{displayName(face.group || 'Unknown', groupNames)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="status">No photos match the selected groups.</div>}
            </div>
            <div className="slider-nav">
              <button type="button" className="nav-btn" onClick={() => nav(-1)}>←</button>
              <PhotoJumpInput className="photo-counter" current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
              <button type="button" className="nav-btn" onClick={() => nav(1)}>→</button>
              {currentPhoto && <button type="button" className="nav-btn fullscreen-btn" onClick={enterFullscreen} title="Fullscreen view" aria-label="Enter fullscreen">⛶</button>}
            </div>
            {(selectedGroups.size > 0 || trackPhotoFilterIds) && (
              <div className="active-filter-bar">
                <button
                  type="button"
                  className="clear-btn"
                  onClick={() => { setSelectedGroups(new Set()); setTrackPhotoFilterIds(null); setMatchAll(false); }}
                >
                  {filteredPhotos.length}/{photos.length} selected - Clear filters
                </button>
              </div>
            )}
            {currentPhoto && (
              <div className="viewer-actions">
                {isUnprocessedPhotoId(currentPhoto?.photo_id) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
                    <UnprocessedPhotoBadge />
                    <Typography variant="caption" color="text.secondary">Not yet processed</Typography>
                  </Box>
                )}
                <SplitButton
                  label="Download original"
                  onPrimary={() => {
                    if (!currentPhoto?.photo_id) return;
                    // Unprocessed (submission-backed) photos have no gallery
                    // "original" route - use the same presigned URL already
                    // resolved for the preview image itself.
                    const url = isUnprocessedPhotoId(currentPhoto.photo_id)
                      ? previewSrc(currentPhoto.photo_id)
                      : eventApi.originalUrl(eid, currentPhoto.photo_id);
                    downloadUrlAsBlob(url, `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`).catch((err) => toast(err.message || 'Download failed.', 'error'));
                  }}
                  menuItems={[
                    { id: 'uc-original', label: 'Download uncompressed original', disabled: !currentPhoto?.uc_org_url || ucDownloading || !!(currentPhoto?.blurred_group_ids?.length), onClick: () => currentPhoto?.photo_id && downloadUcOriginalPhoto(currentPhoto.photo_id) },
                  ]}
                />
                {!isUnprocessedPhotoId(currentPhoto?.photo_id) && (
                  <button type="button" className={`clear-btn viewer-action-btn${showFaceBoxes ? ' active' : ''}`} onClick={() => { const v = !showFaceBoxes; setShowFaceBoxes(v); setPref('showFaceBoxes', v); }}>
                    {showFaceBoxes ? 'Hide Facebox' : 'Show Facebox'}
                  </button>
                )}
                {!isUnprocessedPhotoId(currentPhoto?.photo_id) && (
                  <button type="button" className="clear-btn viewer-action-btn" onClick={() => setPhotoMetadataDialog({ open: true, photo: currentPhoto })}>
                    Tags & Notes
                  </button>
                )}
                {event.is_organiser && currentPhoto?.blurred_group_ids?.length > 0 && (
                  <button
                    type="button"
                    className="clear-btn viewer-action-btn revert-blur-btn"
                    disabled={revertingBlur}
                    onClick={async () => {
                      if (!currentPhoto?.photo_id) return;
                      setRevertingBlur(true);
                      try {
                        await eventApi.revertBlur(eid, currentPhoto.photo_id);
                        toast('Photo reverted to original.', 'success');
                        setReloadKey(k => k + 1);
                      } catch (e) {
                        toast(e.response?.data?.error || e.message || 'Revert failed', 'error');
                      } finally {
                        setRevertingBlur(false);
                      }
                    }}
                  >
                    {revertingBlur ? 'Reverting…' : 'Revert to original'}
                  </button>
                )}
                {cast.available && (
                  <button type="button" className={`clear-btn viewer-action-btn${cast.connected ? ' cast-active' : ''}`} onClick={cast.toggleCast} title="Cast to a TV on the same wifi">
                    {cast.connected ? <CastConnectedIcon fontSize="small" /> : <CastIcon fontSize="small" />}
                    {cast.connected ? 'Casting' : 'Cast to device'}
                  </button>
                )}
                <SplitButton
                  label={deletingPhotos ? 'Deleting...' : selectedThumbs.size > 0 ? `Delete selected (${selectedThumbs.size})` : 'Delete photo'}
                  onPrimary={deleteSelectedPhotos}
                  menuItems={deletePhotoMenuItems}
                  disabled={deletingPhotos}
                />
              </div>
            )}
            {currentPhoto && (photoRequests.length > 0 || photoCircles.length > 0) && (
              <>
                <div className="share-divider"><span>Share</span></div>
                <div className="viewer-actions share-actions">
                  {photoRequests.length > 0 && (
                    <SplitButton label="Add to Request" onPrimary={() => handleSharePrimary('request')} menuItems={buildShareMenuItems('request')} />
                  )}
                  {photoCircles.length > 0 && (
                    <SplitButton label="Add to Circle" onPrimary={() => handleSharePrimary('circle')} menuItems={buildShareMenuItems('circle')} />
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {!isMobileTabs && groupControlsPanel}
      </div>
      )}

      {showPhotosTab && !notReadyMobile && !noPhotosYet && (
      <>
      <section className="gallery-ai-search" aria-label="Search event photos">
        <GalleryAiSearchField
          active={aiSearchActive}
          loading={aiSearchLoading}
          onSubmit={submitAiSearch}
          onClear={clearAiSearch}
          searchModeLabel={aiSearchModeLabel}
          selectedSearchMode={selectedAiSearchMode}
          onToggleSearchMode={toggleAiSearchMode}
          onInvalidateCache={handleInvalidateCache}
          invalidatingCache={invalidatingCache}
        />
        <div className="gallery-ai-search-meta">
          {aiSearchError ? (
            <span className="gallery-ai-search-error">{aiSearchError}</span>
          ) : aiSearchActive && aiSearchQuery ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <span>Showing {filteredPhotos.length} ranked match{filteredPhotos.length === 1 ? '' : 'es'} for <strong>{aiSearchQuery}</strong></span>
                {aiSearchModeLabel && <Chip label={aiSearchModeLabel} size="small" color="primary" variant="outlined" />}
              </Stack>
              {aiSearchModeNote && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                  {aiSearchModeNote}
                </Typography>
              )}
            </Box>
          ) : (
            <span>{photos.length} photos available</span>
          )}
          {event.is_organiser && (
            <FormControlLabel
              sx={{ ml: 1 }}
              control={<Checkbox size="small" checked={includeUnprocessed} onChange={(e) => setIncludeUnprocessed(e.target.checked)} />}
              label={<Typography variant="caption">Include unprocessed photos</Typography>}
            />
          )}
        </div>
      </section>

      <section className="thumb-section" ref={thumbRef} style={{ height: thumbHeight, position: 'relative', overflowY: 'auto', overflowX: 'hidden', alignItems: 'flex-start' }}>
        {thumbStart > 0 && <button type="button" className="thumb-jump" style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} onClick={() => setCurrentIndex(Math.max(0, thumbStart - thumbWindow))}>+{thumbStart} earlier</button>}
        <div className="thumb-container" style={{ minHeight: '100%', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: '4px', paddingLeft: thumbStart > 0 ? '60px' : 0, paddingRight: thumbEnd < filteredPhotos.length ? '60px' : 0, overflow: 'visible' }}>
          {visibleThumbs.map((photo, idx) => {
            const pi = thumbStart + idx;
            const isSelected = selectedThumbs.has(photo.photo_id);
            const src = presignedUrlsRef.current.thumbnails[photo.photo_id];
            return (
              <button type="button" key={`${photo.photo_id || photo.path}-${pi}`} className={`thumbnail ${pi === currentIndex ? 'active' : ''} ${isSelected ? 'selected' : ''}`} style={{ position: 'relative' }} onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  setSelectedThumbs(prev => {
                    const n = new Set(prev);
                    if (n.has(photo.photo_id)) n.delete(photo.photo_id);
                    else n.add(photo.photo_id);
                    return n;
                  });
                } else {
                  setCurrentIndex(pi);
                  setSelectedThumbs(new Set()); // clear selection on normal click
                  setThumbPreviewOpen(true);
                  viewerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }} onContextMenu={(e) => {
                e.preventDefault();
                setThumbContextMenu({ x: e.clientX, y: e.clientY, photo });
              }} title={photo.path}>
                {src ? <img src={src} alt={photo.path} loading="lazy" decoding="async" /> : <div className="thumb-skeleton" />}
                {photo.unprocessed && (
                  <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                    <UnprocessedPhotoBadge />
                  </Box>
                )}
              </button>
            );
          })}
        </div>
        {thumbEnd < filteredPhotos.length && <button type="button" className="thumb-jump" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} onClick={() => setCurrentIndex(thumbEnd)}>{filteredPhotos.length - thumbEnd}+ more</button>}
        {thumbContextMenu && (() => {
          const ctxPhoto = thumbContextMenu.photo;
          const isSelection = selectedThumbs.size > 0 && selectedThumbs.has(ctxPhoto.photo_id);
          const photoIds = isSelection ? Array.from(selectedThumbs) : (ctxPhoto.photo_id ? [ctxPhoto.photo_id] : []);
          const ctxItems = [];
          if (photoRequests.length > 0) {
            ctxItems.push({ id: 'ctx-request', label: 'Add to Request', children: buildShareMenuItems('request', photoIds, ctxPhoto, isSelection) });
          }
          if (photoCircles.length > 0) {
            ctxItems.push({ id: 'ctx-circle', label: 'Add to Circle', children: buildShareMenuItems('circle', photoIds, ctxPhoto, isSelection) });
          }
          return (
            <ThumbnailContextMenu
              anchor={thumbContextMenu}
              items={ctxItems}
              onClose={() => setThumbContextMenu(null)}
            />
          );
        })()}
      </section>
      <div className="resize-handle" style={{ height: '12px', background: '#ccc', cursor: 'ns-resize', userSelect: 'none', touchAction: 'none' }}
        onMouseDown={(e) => {
          const startY = e.clientY;
          const startHeight = thumbHeight;
          let rafId = null;
          const onMouseMove = (ev) => {
            const next = Math.max(60, startHeight + ev.clientY - startY);
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => setThumbHeight(next));
          };
          const onMouseUp = () => { if (rafId) cancelAnimationFrame(rafId); document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          const startY = e.touches[0].clientY;
          const startHeight = thumbHeight;
          let rafId = null;
          const onTouchMove = (ev) => { ev.preventDefault(); const next = Math.max(60, startHeight + ev.touches[0].clientY - startY); if (rafId) cancelAnimationFrame(rafId); rafId = requestAnimationFrame(() => setThumbHeight(next)); };
          const onTouchEnd = () => { if (rafId) cancelAnimationFrame(rafId); document.removeEventListener('touchmove', onTouchMove); document.removeEventListener('touchend', onTouchEnd); };
          document.addEventListener('touchmove', onTouchMove, { passive: false });
          document.addEventListener('touchend', onTouchEnd);
        }}
      />
      {isMobileTabs && (
        <button type="button" className="faces-trigger-btn" onClick={() => setFacesSheetOpen(true)}>
          <FaceRetouchingNaturalIcon fontSize="small" />
          <span className="faces-trigger-count">{Object.keys(groups).length}</span>
        </button>
      )}
      </>
      )}

      {isMobileTabs && !notReadyMobile && (
        <Dialog
          open={facesSheetOpen}
          onClose={() => setFacesSheetOpen(false)}
          className="request-detail-dialog faces-sheet-dialog"
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          <Box className="request-detail-dialog-body">
            {groupControlsPanel}
          </Box>
        </Dialog>
      )}

      {showRequestsTab && (
      <>
      <section className="map-photos-section">
        <div className="map-section-top">
          <div className="map-tabs-row">
            <div className="map-tabs">
              <button type="button" className={`map-tab-btn ${mapTab === 'requests' ? 'active' : ''}`} onClick={() => setMapTab('requests')}>Requests</button>
              <button type="button" className={`map-tab-btn ${mapTab === 'circles' ? 'active' : ''}`} onClick={() => setMapTab('circles')}>Circles</button>
              <button type="button" className={`map-tab-btn ${mapTab === 'deletion' ? 'active' : ''}`} onClick={() => setMapTab('deletion')}>Deletion Requests</button>
              <button type="button" className={`map-tab-btn ${mapTab === 'submissions' ? 'active' : ''}`} onClick={() => setMapTab('submissions')}>Photo Submissions</button>
            </div>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" className="map-tabs-refresh-btn" onClick={refreshTabData} disabled={refreshingTab} aria-label="Refresh">
                  {refreshingTab ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
          {mapTab === 'deletion' && (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={applyFilterOnDeletionTracks}
              disabled={!deletionRequestTracks.length}
              sx={{ textTransform: 'none' }}
            >
              Apply Filter on selected tracks
            </Button>
          )}
          {mapTab === 'requests' && (
            <Button
              type="button"
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={autoPopulateAllRequests}
              disabled={autoPopulateAllProgress.active || photoRequests.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {autoPopulateAllProgress.active ? 'Auto populating...' : 'Auto populate all'}
            </Button>
          )}
        </div>
        {autoPopulateAllProgress.total > 0 && (
          <div className="auto-populate-all-progress">
            <LinearProgress variant="determinate" value={(autoPopulateAllProgress.completed / autoPopulateAllProgress.total) * 100} />
            <span>
              {autoPopulateAllProgress.completed} of {autoPopulateAllProgress.total} requests processed
              {autoPopulateAllProgress.current ? ` - ${autoPopulateAllProgress.current}` : ''}
              {autoPopulateAllProgress.failed ? (
                <Tooltip
                  title={
                    <span>
                      {autoPopulateAllProgress.failedRequests.map((f, i) => (
                        <span key={i} style={{ display: 'block' }}>{f.name}{f.reason ? `: ${f.reason}` : ''}</span>
                      ))}
                    </span>
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={4000}
                >
                  <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                    {`, ${autoPopulateAllProgress.failed} failed`}
                  </span>
                </Tooltip>
              ) : ''}
            </span>
          </div>
        )}
        {mapTab === 'submissions' ? (
          <PhotoSubmissionsPanel
            event={event}
            isMobile={isMobileTabs}
            onReload={() => setReloadKey(k => k + 1)}
            onGroupingComplete={onGroupingComplete}
            toast={toast}
            refreshKey={submissionsRefreshKey}
            pendingDroppedFiles={pendingDroppedFiles}
            clearPendingDroppedFiles={() => setPendingDroppedFiles([])}
            onAddSubmissionsToTrack={(kind, submissionIds) => {
              const ids = (submissionIds || []).filter(Boolean).map(id => `sub:${id}`);
              if (ids.length) openCrossPicker(kind, 'these', ids);
            }}
            onSubmissionsDeleted={(submissionIds) => {
              // Immediately fixes any request/circle/deletion-request track
              // this session already has loaded that referenced the deleted
              // submission(s) - the backend's own orphan-sweep (best-effort,
              // possibly skipped for very large events) already fixes what's
              // stored in S3; this closes the gap where THIS tab's in-memory
              // state doesn't know that happened yet, which would otherwise
              // keep showing a broken thumbnail until a full reload. Also
              // marks those tracks dirty, so the existing 10s autosave picks
              // up and persists the cleanup on its own - no extra save call needed.
              const ids = (submissionIds || []).filter(Boolean).map(id => `sub:${id}`);
              if (ids.length) removePhotoIdsFromTracks(ids, 'all');
            }}
          />
        ) : mapTab === 'deletion' ? (
          <div className="map-requests-layout">
            <aside className="request-menu">
              {deletionRequests.length === 0 ? <div className="request-empty">No deletion requests yet.</div> : deletionRequests.map(r => (
                <button key={r.request_id} type="button" className={`request-menu-item ${selectedDeletionRequestId === r.request_id ? 'active' : ''}`} onClick={() => { setSelectedDeletionRequestId(r.request_id); setDeletionDialogOpen(true); }}>{r.name || 'Unknown'}</button>
              ))}
              {event.is_organiser && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setAddRequestMode('deletion'); setAddRequestOpen(true); }}
                  sx={{ mt: 'auto', textTransform: 'none', borderColor: '#efcfdb', color: '#C73866', '&:hover': { borderColor: '#C73866', bgcolor: '#fff4f8' } }}
                >
                  + Add deletion request
                </Button>
              )}
            </aside>
            <MobileDetailDialog
              isMobile={isMobileTabs}
              open={deletionDialogOpen}
              onClose={() => setDeletionDialogOpen(false)}
              title={selectedDeletionRequest?.name || 'Deletion request'}
            >
            <div className="request-details">
              {selectedDeletionRequest ? (
                <>
                  {!isMobileTabs && <h3>{selectedDeletionRequest.name}</h3>}
                  <div className="request-detail-layout">
                    <div className="request-photo-preview-wrap">
                      {selectedRequestPhotoLoading && (
                        <div className="request-photo-loader">
                          <CircularProgress size={26} />
                        </div>
                      )}
                      {selectedRequestPhotoError && !selectedRequestPhotoLoading && <span className="request-photo-error">Photo unavailable</span>}
                      {selectedRequestPhotoUrl && !selectedRequestPhotoLoading && !selectedRequestPhotoError && (
                        <img src={selectedRequestPhotoUrl} alt="Reference" className="request-photo-preview" loading="lazy" />
                      )}
                    </div>
                    <div className="request-detail-grid">
                      <div><strong>Email:</strong> {selectedDeletionRequest.email_address}</div>
                      <div><strong>Status:</strong> {selectedDeletionRequest.status || 'pending'}</div>
                      <div><strong>Request ID:</strong> {selectedDeletionRequest.request_id}</div>
                      <div><strong>Submitted:</strong> {selectedDeletionRequest.created_at ? new Date(selectedDeletionRequest.created_at).toLocaleString() : 'Unknown'}</div>
                      {selectedDeletionRequest.notes && <div className="request-detail-wide"><strong>Notes:</strong> {selectedDeletionRequest.notes}</div>}
                      <div className="request-detail-wide"><strong>Notice:</strong> CircleMoments assists with face matching only. The organiser is responsible for actioning this do-not-share request.</div>
                    </div>
                  </div>
                  <div className="request-actions">
                    <Button type="button" variant="contained" startIcon={autoPopulateSingleLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />} className="action-btn-ai action-btn-wide" onClick={autoPopulate} disabled={autoPopulateAllProgress.active || autoPopulateSingleLoading} sx={{ textTransform: 'none' }}>{autoPopulateSingleLoading ? 'Matching…' : 'Autopopulate'}</Button>
                    <Button type="button" variant="outlined" startIcon={<ManageSearchIcon />} className="action-btn-accent action-btn-wide" onClick={applyFilterOnDeletionTracks} disabled={!deletionRequestTracks.length} sx={{ textTransform: 'none' }}>Apply Filter on selected tracks</Button>
                    <Button type="button" variant="outlined" startIcon={<CheckCircleIcon />} className="action-btn-accent" onClick={saveSelections} sx={{ textTransform: 'none' }}>Save now</Button>
                    <span className={`autosave-pill ${autoSaveStatus}`}>
                      {autoSaveStatus === 'saving' && 'Saving...'}
                      {autoSaveStatus === 'saved' && 'All saved'}
                      {autoSaveStatus === 'idle' && 'Auto-saves every 10 s'}
                    </span>
                  </div>
                  {deletionRequestTracks.length > 0 && (
                    <div className="selection-tracks">
                      {deletionRequestTracks.map((track, i) => (
                        <div key={track.id} className="selection-track">
                          <div className="selection-track-header">
                            <div className="track-title-group">
                              <div className="track-reorder-btns">
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByDeletionRequest, selectedDeletionRequestId, track.id, -1)} disabled={i === 0} title="Move up">▲</button>
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByDeletionRequest, selectedDeletionRequestId, track.id, 1)} disabled={i === deletionRequestTracks.length - 1} title="Move down">▼</button>
                              </div>
                              <EditableTrackLabel
                                label={track.label}
                                count={(track.photoIds || []).length}
                                onRename={(label) => renameDeletionTrack(selectedDeletionRequestId, track.id, label)}
                              />
                            </div>
                            <div className="track-actions">
                              <button type="button" className="icon-btn" onClick={() => copyTrackToClipboard(track)} title="Copy to clipboard">⎘</button>
                              <button type="button" className="icon-btn" onClick={() => deleteDeletionTrack(selectedDeletionRequestId, track.id)} title="Delete track">✖</button>
                            </div>
                          </div>
                          <div className="suggested-photos-strip">
                            {(track.photoIds || []).map(pid => (
                              <div key={`${track.id}-${pid}`} className="suggested-photo-wrap">
                                <button type="button" className="suggested-photo-item" onClick={() => openTrackPhotoPreview(pid, track)}>
                                  {thumbSrc(pid) && <img src={thumbSrc(pid)} alt={pid} loading="lazy" />}
                                </button>
                                <button type="button" className="remove-photo-btn" onClick={() => removePhotoFromDeletionTrack(selectedDeletionRequestId, track.id, pid)}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="request-empty">Select a deletion request.</div>}
            </div>
            </MobileDetailDialog>
          </div>
        ) : mapTab === 'requests' ? (
          <div className="map-requests-layout">
            <aside className="request-menu">
              {/* Filter bar */}
              <Stack direction="row" spacing={0.5} className="request-filter-bar" sx={{ p: 0.5, flexWrap: 'wrap', gap: '4px' }}>
                {[
                  { key: 'all', label: `All (${photoRequests.length})` },
                  { key: 'with_photo', label: 'With selfie' },
                  { key: 'accepted', label: 'Invited' },
                ].map(opt => (
                  <Button
                    key={opt.key}
                    size="small"
                    variant={requestsFilter === opt.key ? 'contained' : 'outlined'}
                    onClick={() => setRequestsFilter(opt.key)}
                    sx={{ textTransform: 'none', fontSize: 11, py: 0.25, px: 0.75, minWidth: 0, flexShrink: 0 }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Stack>
              {filteredRequests.length === 0 ? (
                <div className="request-empty">
                  {photoRequests.length === 0 ? 'No requests yet.' : 'No requests match this filter.'}
                </div>
              ) : filteredRequests.map(r => (
                <button
                  key={r.request_id}
                  type="button"
                  className={`request-menu-item ${selectedRequestId === r.request_id ? 'active' : ''}`}
                  onClick={() => { setSelectedRequestId(r.request_id); setRequestDialogOpen(true); }}
                  title={r.has_selfie === false ? 'No selfie yet' : ''}
                >
                  {r.has_selfie === false && <span style={{ fontSize: 10, marginRight: 4, opacity: 0.6 }}>◌</span>}
                  {r.name || 'Unknown'}
                  {r.on_behalf_of_relationship && <span title={`Submitted on behalf of ${r.subject_name || 'someone else'} (${r.on_behalf_of_relationship === 'minor' ? 'minor' : 'another adult'})`} style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>👪</span>}
                </button>
              ))}
              {event.is_organiser && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setAddRequestMode('photos'); setAddRequestOpen(true); }}
                  sx={{ mt: 'auto', textTransform: 'none', borderColor: '#efcfdb', color: '#C73866', '&:hover': { borderColor: '#C73866', bgcolor: '#fff4f8' } }}
                >
                  + Add request
                </Button>
              )}
            </aside>
            <MobileDetailDialog
              isMobile={isMobileTabs}
              open={requestDialogOpen}
              onClose={() => setRequestDialogOpen(false)}
              title={selectedRequest?.name || 'Request'}
              chips={selectedRequest && (
                <>
                  {selectedRequest.has_selfie === false && (
                    <Chip label="No selfie" size="small" color="warning" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                  )}
                  {selectedRequest.invitation_id && (
                    <Chip label="Invited" size="small" color="info" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                  )}
                  {selectedRequest.on_behalf_of_relationship && (
                    <Chip label={selectedRequest.on_behalf_of_relationship === 'minor' ? 'On behalf of a minor' : 'On behalf of another adult'} size="small" color="secondary" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                  )}
                </>
              )}
            >
            <div className="request-details">
              {selectedRequest ? (
                <>
                  {!isMobileTabs && (
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {selectedRequest.name}
                      {selectedRequest.has_selfie === false && (
                        <Chip label="No selfie" size="small" color="warning" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                      )}
                      {selectedRequest.invitation_id && (
                        <Chip label="Invited" size="small" color="info" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                      )}
                      {selectedRequest.on_behalf_of_relationship && (
                        <Chip label={selectedRequest.on_behalf_of_relationship === 'minor' ? 'On behalf of a minor' : 'On behalf of another adult'} size="small" color="secondary" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                      )}
                    </h3>
                  )}
                  <div className="request-detail-layout">
                    <div className="request-photo-col">
                      <div className="request-photo-preview-wrap">
                        {selectedRequest.has_selfie === false ? (
                          <div className="request-photo-preview-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 8, minHeight: 80 }}>
                            <Typography variant="caption" color="text.secondary">No selfie uploaded yet</Typography>
                          </div>
                        ) : selectedRequestPhotoLoading && (
                          <div className="request-photo-loader">
                            <CircularProgress size={26} />
                          </div>
                        )}
                        {selectedRequest.has_selfie !== false && selectedRequestPhotoError && !selectedRequestPhotoLoading && <span className="request-photo-error">Photo unavailable</span>}
                        {selectedRequest.has_selfie !== false && selectedRequestPhotoUrl && !selectedRequestPhotoLoading && !selectedRequestPhotoError && (
                          <img src={selectedRequestPhotoUrl} alt="Reference" className="request-photo-preview" loading="lazy" />
                        )}
                      </div>
                      <Button
                        component="label"
                        size="small"
                        variant="text"
                        startIcon={replacingRequestPhoto ? <CircularProgress size={12} color="inherit" /> : <EditIcon sx={{ fontSize: 14 }} />}
                        disabled={replacingRequestPhoto}
                        sx={{ textTransform: 'none', fontSize: 11, alignSelf: 'flex-start', p: 0, minWidth: 0, mt: 0.5 }}
                      >
                        {replacingRequestPhoto ? 'Uploading…' : selectedRequest.has_selfie === false ? 'Add photo' : 'Replace photo'}
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) replaceSelectedRequestPhoto(f); }}
                        />
                      </Button>
                    </div>
                    <div className="request-detail-right">
                      <ReferencePhotoRow
                        photos={selectedRequestSecondaryPhotos}
                        onAdd={addSelectedRequestSecondaryPhoto}
                        onRemove={removeSelectedRequestSecondaryPhoto}
                        label="Additional reference photos"
                      />
                      <FormControlLabel
                        sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(selectedRequest.match_all_photos)}
                            onChange={(e) => toggleSelectedRequestMatchAllPhotos(e.target.checked)}
                            sx={{ pt: 0 }}
                          />
                        }
                        label={<Typography variant="body2">Receive every processed photo (VIP), regardless of face matching</Typography>}
                      />
                      <Tooltip title={event.auto_share_new_submissions ? 'Already covered by the event-wide auto-share setting.' : 'New submissions get added to this request automatically, without processing.'}>
                        <span>
                          <FormControlLabel
                            sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(selectedRequest.auto_add_new_submissions)}
                                disabled={Boolean(event.auto_share_new_submissions)}
                                onChange={(e) => toggleSelectedRequestAutoAddNewSubmissions(e.target.checked)}
                                sx={{ pt: 0 }}
                              />
                            }
                            label={<Typography variant="body2">Auto-add new submissions, without processing</Typography>}
                          />
                        </span>
                      </Tooltip>
                      <div className="request-detail-grid">
                        <div className="request-detail-wide"><strong>Requester:</strong> <span className={`requester-chip ${selectedRequesterIdentity.className}`}><span>{selectedRequesterIdentity.main}</span>{selectedRequesterIdentity.sub && <small>{selectedRequesterIdentity.sub}</small>}</span></div>
                        <div><strong>Joined via:</strong> {
                          selectedRequest.is_organiser_request ? 'Organiser' :
                          selectedRequest.invitation_id ? `Invitation${selectedRequest.invitation_campaign_name ? ` - ${selectedRequest.invitation_campaign_name}` : ` #${selectedRequest.invitation_id}`}` :
                          selectedRequest.customer_id ? 'QR / Join page (CM user)' :
                          'QR / Join page'
                        }</div>
                        <div><strong>Invite code:</strong> {selectedRequest.invite_code}</div>
                        <div><strong>Email:</strong> {selectedRequest.email_address}</div>
                        {selectedRequest.phone_number && <div><strong>Phone:</strong> {selectedRequest.country_code || ''} {selectedRequest.phone_number}</div>}
                        {selectedRequest.whatsapp_number && <div><strong>WhatsApp:</strong> {selectedRequest.whatsapp_country_code || ''} {selectedRequest.whatsapp_number}</div>}
                        <div><strong>Request ID:</strong> {selectedRequest.request_id}</div>
                        {selectedRequest.on_behalf_of_relationship && (
                          <div className="request-detail-wide"><strong>Submitted on behalf of{selectedRequest.on_behalf_of_relationship === 'minor' ? ' (minor)' : ' (another adult)'}:</strong> {selectedRequest.subject_name || 'Not specified'}</div>
                        )}
                        {selectedRequest.notes && <div className="request-detail-wide"><strong>Notes:</strong> {selectedRequest.notes}</div>}
                        {selectedRequestMatchedGroups.length > 0 && (
                          <div>
                            <strong>Auto match:</strong>{' '}
                            <small>{selectedRequestMatchedGroups.map(matchedGroupDisplay).join(', ')}</small>{' '}
                            <IconButton
                              size="small"
                              onClick={() => unlinkAutoMatch(selectedRequestId)}
                              title="Unlink auto match (removes it and stops future auto-matching for this request)"
                              sx={{ p: 0.25, color: 'text.secondary', verticalAlign: 'middle' }}
                            >
                              <LinkOffIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="request-actions">
                    <Button type="button" variant="contained" startIcon={autoPopulateSingleLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />} className="action-btn-ai action-btn-wide" onClick={autoPopulate} disabled={autoPopulateAllProgress.active || autoPopulateSingleLoading} sx={{ textTransform: 'none' }}>{autoPopulateSingleLoading ? 'Matching…' : 'Auto populate'}</Button>
                    {selectedRequestMatchedGroups.length > 0 && (
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<ManageSearchIcon />}
                        className="action-btn-accent action-btn-wide"
                        onClick={() => updateUnnamedFaceGroupFromRequest(selectedRequest)}
                        disabled={requestNameSavingId === selectedRequest.request_id}
                        sx={{ textTransform: 'none' }}
                      >
                        {requestNameSavingId === selectedRequest.request_id ? 'Updating names...' : 'Update unnamed facegroups'}
                      </Button>
                    )}
                    <Button type="button" variant="outlined" startIcon={<CheckCircleIcon />} className="action-btn-accent" onClick={saveSelections} sx={{ textTransform: 'none' }}>Save now</Button>
                    <Button type="button" variant="outlined" color="error" startIcon={<DeleteIcon />} className="action-btn-danger" onClick={deleteSelectedRequest} sx={{ textTransform: 'none' }}>Delete request</Button>
                    <span className={`autosave-pill ${autoSaveStatus}`}>
                      {autoSaveStatus === 'saving' && 'Saving…'}
                      {autoSaveStatus === 'saved' && 'All saved'}
                      {autoSaveStatus === 'idle' && 'Auto-saves every 10 s'}
                    </span>
                  </div>

                  {requestTracks.length > 0 && (
                    <div className="selection-tracks">
                      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                        {[
                          { key: 'all', label: 'All' },
                          { key: 'processed', label: 'Processed' },
                          { key: 'unprocessed', label: 'Unprocessed' },
                        ].map(opt => (
                          <Button
                            key={opt.key}
                            size="small"
                            variant={requestTrackFilter === opt.key ? 'contained' : 'outlined'}
                            onClick={() => setRequestTrackFilter(opt.key)}
                            sx={{ textTransform: 'none', fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </Stack>
                      {requestTracks.map((track, i) => (
                        <div key={track.id} className="selection-track">
                          <div className="selection-track-header">
                            <div className="track-title-group">
                              <div className="track-reorder-btns">
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByRequest, selectedRequestId, track.id, -1)} disabled={i === 0} title="Move up">▲</button>
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByRequest, selectedRequestId, track.id, 1)} disabled={i === requestTracks.length - 1} title="Move down">▼</button>
                              </div>
                              <EditableTrackLabel
                                label={track.label}
                                count={(track.photoIds || []).length}
                                onRename={(label) => renameTrack(selectedRequestId, track.id, label)}
                              />
                            </div>
                            <div className="track-actions">
                              <button type="button" className="icon-btn" onClick={() => copyTrackToClipboard(track)} title="Copy to clipboard">⎘</button>
                              <button type="button" className="icon-btn" onClick={() => deleteTrack(selectedRequestId, track.id)} title="Delete track">✖</button>
                            </div>
                          </div>
                          <div className="suggested-photos-strip">
                            {filterTrackPhotoIds(track.photoIds, requestTrackFilter).map(pid => (
                              <div key={`${track.id}-${pid}`} className="suggested-photo-wrap">
                                <button type="button" className="suggested-photo-item" onClick={() => openTrackPhotoPreview(pid, track)}>
                                  {thumbSrc(pid) && <img src={thumbSrc(pid)} alt={pid} loading="lazy" />}
                                </button>
                                {isUnprocessedPhotoId(pid) && (
                                  <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                                    <UnprocessedPhotoBadge />
                                  </Box>
                                )}
                                <button type="button" className="remove-photo-btn" onClick={() => removePhotoFromTrack(selectedRequestId, track.id, pid)}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Share section ─────────────────────────────────────── */}
                  <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fff4f8' }}>
                    <Typography variant="caption" fontWeight={700} color="#C73866" sx={{ display: 'block', mb: 1 }}>
                      Share Photos with Recipient
                    </Typography>
                    {selectedRequest.is_organiser_request && (
                      <Box sx={{ mb: 1.5 }}>
                        {requestEdit.requestId === selectedRequest.request_id ? (
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              label="Email address"
                              value={requestEdit.email_address}
                              onChange={e => setRequestEdit(prev => ({ ...prev, email_address: e.target.value }))}
                              fullWidth
                            />
                            <TextField
                              size="small"
                              label="Phone"
                              value={requestEdit.phone_number}
                              onChange={e => setRequestEdit(prev => ({ ...prev, phone_number: e.target.value }))}
                              fullWidth
                            />
                            <TextField
                              size="small"
                              label="Notes"
                              value={requestEdit.notes}
                              onChange={e => setRequestEdit(prev => ({ ...prev, notes: e.target.value }))}
                              fullWidth
                              multiline
                              minRows={2}
                              inputProps={{ maxLength: 2000 }}
                            />
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="contained" onClick={saveRequestEdit} disabled={requestEditSaving} sx={{ textTransform: 'none' }}>
                                {requestEditSaving ? 'Saving...' : 'Save details'}
                              </Button>
                              <Button size="small" onClick={() => setRequestEdit({ requestId: '', email_address: '', phone_number: '', notes: '' })} disabled={requestEditSaving} sx={{ textTransform: 'none' }}>
                                Cancel
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => beginRequestEdit(selectedRequest)} sx={{ textTransform: 'none' }}>
                            Edit recipient details
                          </Button>
                        )}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Share via:
                    </Typography>
                    <Stack direction="row" spacing={1} className="share-method-row">
                      {emailIntegrationEnabled ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={sendingEmailId === selectedRequest.request_id ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                          onClick={() => handleSendEmail(selectedRequest.request_id)}
                          disabled={!!sendingEmailId || !selectedRequest.email_address}
                          fullWidth
                          className="share-method-btn"
                          sx={{ textTransform: 'none', bgcolor: '#C73866', '&:hover': { bgcolor: '#a02d52' } }}
                        >
                          {sendingEmailId === selectedRequest.request_id ? 'Sending…' : 'Email'}
                        </Button>
                      ) : (
                        <Tooltip title="Enable Email integration in Profile → Integrations to send emails">
                          <span className="share-method-btn">
                            <Button size="small" variant="outlined" startIcon={<SendIcon />} disabled fullWidth sx={{ textTransform: 'none' }}>
                              Email
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="WhatsApp sharing - coming soon">
                        <span className="share-method-btn">
                          <Button size="small" variant="outlined" disabled fullWidth sx={{ textTransform: 'none' }}>
                            WhatsApp
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => handleCopyShareLink(selectedRequest.request_id)}
                        fullWidth
                        className="share-method-btn"
                        sx={{ textTransform: 'none' }}
                      >
                        Copy link
                      </Button>
                    </Stack>
                    {shareUrls[selectedRequest.request_id] && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', flex: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {shareUrls[selectedRequest.request_id]}
                        </Typography>
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => navigator.clipboard.writeText(shareUrls[selectedRequest.request_id])}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {!emailIntegrationEnabled && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Enable Email in <strong>Profile → Integrations</strong> to send photos by email.
                      </Typography>
                    )}
                  </Box>
                </>
              ) : <div className="request-empty">Select a request.</div>}
            </div>
            </MobileDetailDialog>
          </div>
        ) : (
          <div className="map-requests-layout">
            <aside className="request-menu">
              {photoCircles.length === 0 ? <div className="request-empty">No circles yet.</div> : photoCircles.map(c => (
                <button key={c.circle_id} type="button" className={`request-menu-item ${selectedCircleId === c.circle_id ? 'active' : ''}`} onClick={() => { setSelectedCircleId(c.circle_id); setCircleDialogOpen(true); }}>
                  {c.circle_name || 'Untitled circle'}
                </button>
              ))}
            </aside>
            <MobileDetailDialog
              isMobile={isMobileTabs}
              open={circleDialogOpen}
              onClose={() => setCircleDialogOpen(false)}
              title={selectedCircle?.circle_name || 'Circle'}
            >
            <div className="request-details">
              {selectedCircle ? (
                <>
                  {!isMobileTabs && <h3>{selectedCircle.circle_name}</h3>}
                  <div className="request-detail-layout">
                    <div className="circle-color-preview-wrap">
                      <span className="circle-color-preview" style={{ background: selectedCircle.color || '#4ECDC4' }} />
                    </div>
                    <div className="request-detail-grid">
                      <div><strong>Members:</strong> {selectedCircle.member_count || 0}</div>
                      <div><strong>Color:</strong> {selectedCircle.color || '#4ECDC4'}</div>
                      <div><strong>Circle ID:</strong> {selectedCircle.circle_id}</div>
                      <div><strong>Shared:</strong> {selectedCircle.share_updated_at ? new Date(selectedCircle.share_updated_at).toLocaleString() : 'Not shared yet'}</div>
                      <div className="request-detail-wide"><strong>Description:</strong> {selectedCircle.description || 'No description'}</div>
                    </div>
                  </div>
                  <div className="request-actions">
                    <Button type="button" variant="outlined" startIcon={<CheckCircleIcon />} className="action-btn-accent" onClick={saveSelections} sx={{ textTransform: 'none' }}>Save now</Button>
                    <span className={`autosave-pill ${autoSaveStatus}`}>
                      {autoSaveStatus === 'saving' && 'Saving...'}
                      {autoSaveStatus === 'saved' && 'All saved'}
                      {autoSaveStatus === 'idle' && 'Auto-saves every 10 s'}
                    </span>
                  </div>
                  {circleTracks.length > 0 && (
                    <div className="selection-tracks">
                      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                        {[
                          { key: 'all', label: 'All' },
                          { key: 'processed', label: 'Processed' },
                          { key: 'unprocessed', label: 'Unprocessed' },
                        ].map(opt => (
                          <Button
                            key={opt.key}
                            size="small"
                            variant={circleTrackFilter === opt.key ? 'contained' : 'outlined'}
                            onClick={() => setCircleTrackFilter(opt.key)}
                            sx={{ textTransform: 'none', fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </Stack>
                      {circleTracks.map((track, i) => (
                        <div key={track.id} className="selection-track">
                          <div className="selection-track-header">
                            <div className="track-title-group">
                              <div className="track-reorder-btns">
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByCircle, selectedCircleId, track.id, -1)} disabled={i === 0} title="Move up">▲</button>
                                <button type="button" className="icon-btn-tiny" onClick={() => moveTrack(setTracksByCircle, selectedCircleId, track.id, 1)} disabled={i === circleTracks.length - 1} title="Move down">▼</button>
                              </div>
                              <EditableTrackLabel
                                label={track.label}
                                count={(track.photoIds || []).length}
                                onRename={(label) => renameCircleTrack(selectedCircleId, track.id, label)}
                              />
                            </div>
                            <div className="track-actions">
                              <button type="button" className="icon-btn" onClick={() => copyTrackToClipboard(track)} title="Copy to clipboard">⎘</button>
                              <button type="button" className="icon-btn" onClick={() => deleteCircleTrack(selectedCircleId, track.id)} title="Delete track">✖</button>
                            </div>
                          </div>
                          <div className="suggested-photos-strip">
                            {filterTrackPhotoIds(track.photoIds, circleTrackFilter).map(pid => (
                              <div key={`${track.id}-${pid}`} className="suggested-photo-wrap">
                                <button type="button" className="suggested-photo-item" onClick={() => openTrackPhotoPreview(pid, track)}>
                                  {thumbSrc(pid) && <img src={thumbSrc(pid)} alt={pid} loading="lazy" />}
                                </button>
                                {isUnprocessedPhotoId(pid) && (
                                  <Box sx={{ position: 'absolute', bottom: 2, left: 2 }}>
                                    <UnprocessedPhotoBadge />
                                  </Box>
                                )}
                                <button type="button" className="remove-photo-btn" onClick={() => removePhotoFromCircleTrack(selectedCircleId, track.id, pid)}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="request-empty">Select a circle.</div>}
            </div>
            </MobileDetailDialog>
          </div>
        )}
      </section>

      {mapTab !== 'submissions' && clipboard.length > 0 && (!isMobileTabs || (
        mapTab === 'circles' ? circleDialogOpen : mapTab === 'deletion' ? deletionDialogOpen : requestDialogOpen
      )) && (
        <ClipboardPanel
          clipboard={clipboard}
          selectedTargetId={mapTab === 'circles' ? selectedCircleId : mapTab === 'deletion' ? selectedDeletionRequestId : selectedRequestId}
          targets={mapTab === 'circles' ? photoCircles : mapTab === 'deletion' ? deletionRequests : photoRequests}
          targetLabel={mapTab === 'circles' ? 'circle' : mapTab === 'deletion' ? 'deletion request' : 'request'}
          onPasteTo={pasteClipboardItem}
          onPasteToAll={pasteClipboardItemToAll}
          onDelete={removeFromClipboard}
        />
      )}
      </>
      )}

      <AddToOtherTargetDialog
        open={crossPicker.open}
        meta={crossPicker.kind ? kindMeta(crossPicker.kind) : null}
        onClose={closeCrossPicker}
        onPickTrack={handleCrossPickerPick}
      />

      <PhotoMetadataDialog
        open={photoMetadataDialog.open}
        photo={photoMetadataDialog.photo}
        eventId={eid}
        onClose={() => setPhotoMetadataDialog({ open: false, photo: null })}
        onSave={savePhotoMetadata}
      />

      <Dialog open={Boolean(trackPreviewPhoto)} onClose={() => setTrackPreviewPhoto(null)} maxWidth="lg" fullWidth>
        {trackPreviewPhoto && (
          <>
            <DialogContent sx={{ p: 0, bgcolor: '#111' }}>
              <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <Box
                  component="img"
                  src={previewSrc(trackPreviewPhoto.photoId)}
                  alt=""
                  onLoad={onTrackPreviewImageLoad}
                  sx={{ width: '100%', maxHeight: '82vh', objectFit: 'contain', display: 'block' }}
                />
                <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                  {(photoById.get(String(trackPreviewPhoto.photoId))?.faces || [])
                    .filter(face => {
                      const matchedGroup = trackMatchedGroup(trackPreviewPhoto.track);
                      return matchedGroup && String(face.group || 'Unknown') === matchedGroup;
                    })
                    .map((face, idx) => (
                      <div
                        key={`${trackPreviewPhoto.photoId}-${idx}`}
                        className="face-overlay"
                        style={{
                          left: `${trackPreviewTransform.offsetX + face.bbox[0] * trackPreviewTransform.scaleX}px`,
                          top: `${trackPreviewTransform.offsetY + face.bbox[1] * trackPreviewTransform.scaleY}px`,
                          width: `${face.size[0] * trackPreviewTransform.scaleX}px`,
                          height: `${face.size[1] * trackPreviewTransform.scaleY}px`,
                        }}
                      >
                        <div className="face-label">{displayName(face.group || 'Unknown', groupNames)}</div>
                      </div>
                    ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
              <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>{trackPreviewPhoto.track?.label || 'Track photo'}</Typography>
                  <Typography variant="caption" color="text.secondary">{trackPreviewPhoto.photoId}</Typography>
                </Box>
                {isUnprocessedPhotoId(trackPreviewPhoto.photoId) && <UnprocessedPhotoBadge />}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Download original">
                  <IconButton
                    aria-label="Download original"
                    onClick={() => {
                      // Unprocessed (submission-backed) photos have no gallery
                      // "original" endpoint - fall back to the same presigned
                      // URL already resolved for the preview itself.
                      const url = isUnprocessedPhotoId(trackPreviewPhoto.photoId)
                        ? previewSrc(trackPreviewPhoto.photoId)
                        : eventApi.originalUrl(eid, trackPreviewPhoto.photoId);
                      downloadUrlAsBlob(url, `circlemoments-${eid}-${trackPreviewPhoto.photoId}.jpg`).catch((err) => toast(err.message || 'Download failed.', 'error'));
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Button variant="contained" onClick={() => setTrackPreviewPhoto(null)} sx={{ textTransform: 'none' }}>
                  Close
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      <FaceRegroupModal
        open={regroupModal.open}
        onClose={closeRegroupModal}
        photo={regroupModal.photo}
        faceIndex={regroupModal.faceIndex}
        face={regroupModal.face}
        eventId={eid}
        groups={groups}
        groupNames={groupNames}
        groupThumbSrc={groupThumbSrc}
        ensureGroupThumbnails={ensureGroupThumbnails}
        memoryFaceThumbUrls={memoryFaceThumbUrls}
        photos={photos}
        previewSrc={previewSrc}
        onRegrouped={handleRegrouped}
        onBlurFace={event.is_organiser ? ({ photo, face }) => setBlurPreview({ open: true, photo, face }) : undefined}
      />

      <BlurFacePreviewModal
        open={blurPreview.open}
        onClose={() => setBlurPreview({ open: false, photo: null, face: null })}
        photo={blurPreview.photo}
        face={blurPreview.face}
        eventId={eid}
        onAccepted={() => {
          toast('Face blurred successfully.', 'success');
          setReloadKey(k => k + 1);
        }}
      />

      <BlurGroupProgressModal
        open={blurGroupJob.open}
        eventId={eid}
        group={blurGroupJob.group}
        mode={blurGroupJob.mode}
        onClose={() => setBlurGroupJob({ open: false, group: null, mode: null })}
        onDone={() => {
          toast(
            blurGroupJob.mode === 'blur' ? 'Faces blurred across all photos.' : 'Blur reverted across all photos.',
            'success',
          );
          setReloadKey(k => k + 1);
        }}
      />

      <PhotoRequestFormDialog
        open={addRequestOpen}
        onClose={() => setAddRequestOpen(false)}
        eventId={eid}
        inviteCode={event.invite_code}
        mode={addRequestMode}
        onSuccess={(count) => {
          setAddRequestOpen(false);
          const noun = addRequestMode === 'deletion' ? 'deletion request' : 'request';
          toast(`${count > 1 ? `${count} ${noun}s` : noun[0].toUpperCase() + noun.slice(1)} added successfully.`, 'success');
          setReloadKey(k => k + 1);
        }}
      />

      <Dialog open={thumbPreviewOpen && Boolean(currentPhoto)} onClose={() => { setThumbPreviewOpen(false); setPreviewInfoOpen(false); }} maxWidth="lg" fullWidth>
        {currentPhoto && (
          <>
            <DialogContent sx={{ p: 0, bgcolor: '#111', position: 'relative' }}>
              <IconButton
                size={isMobilePreview ? 'small' : 'medium'}
                onClick={() => { setThumbPreviewOpen(false); setPreviewInfoOpen(false); }}
                aria-label="Close preview"
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
              >
                <CloseIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
              </IconButton>
              <Tooltip title={previewInfoOpen ? 'Hide photo info' : 'Photo info'}>
                <IconButton
                  size={isMobilePreview ? 'small' : 'medium'}
                  onClick={() => setPreviewInfoOpen((v) => !v)}
                  aria-label="Toggle photo info"
                  sx={{
                    position: 'absolute', top: 8, left: 8, zIndex: 1, color: 'common.white',
                    bgcolor: previewInfoOpen ? 'primary.main' : 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: previewInfoOpen ? 'primary.dark' : 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <InfoOutlinedIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
              <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={previewSrc(currentPhoto.photo_id)}
                  alt={currentPhoto.path}
                  sx={{ width: '100%', maxHeight: '82vh', objectFit: 'contain', display: 'block' }}
                />
                {filteredPhotos.length > 1 && (
                  <>
                    <IconButton
                      size={isMobilePreview ? 'small' : 'medium'}
                      onClick={() => nav(-1)}
                      aria-label="Previous photo"
                      sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateBeforeIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                    </IconButton>
                    <IconButton
                      size={isMobilePreview ? 'small' : 'medium'}
                      onClick={() => nav(1)}
                      aria-label="Next photo"
                      sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                      <NavigateNextIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                    </IconButton>
                    <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', px: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white', fontSize: 12 }}>
                      <PhotoJumpInput current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <Collapse in={previewInfoOpen}>
              <Box sx={{ bgcolor: '#111', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <PhotoInfoPanel photo={currentPhoto} dark />
              </Box>
            </Collapse>
            <DialogActions
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between', px: 2, py: 1.5, gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{currentPhoto.path}</Typography>
                {currentPhoto.submitted_by_name && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                    onClick={currentPhoto.submitted_by_username ? () => navigate(`/profile/${currentPhoto.submitted_by_username}`) : undefined}
                    sx={{
                      cursor: currentPhoto.submitted_by_username ? 'pointer' : 'default',
                      '&:hover .submitted-by-name': currentPhoto.submitted_by_username ? { textDecoration: 'underline' } : undefined,
                    }}
                  >
                    <Avatar src={currentPhoto.submitted_by_avatar || undefined} sx={{ width: 16, height: 16, fontSize: 10 }}>
                      {currentPhoto.submitted_by_name.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Typography variant="caption" noWrap className="submitted-by-name" sx={{ color: 'grey.400' }}>
                      Submitted by {currentPhoto.submitted_by_name}
                    </Typography>
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                <Tooltip title="Share photo">
                  <IconButton
                    size={isMobilePreview ? 'small' : 'medium'}
                    onClick={() => sharePhoto(eventApi.originalUrl(eid, currentPhoto.photo_id), `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`, {
                      title: event.event_name,
                      text: `Photo from ${event.event_name || 'this event'} on Circle Moments`,
                    }).catch((err) => toast(err.message || 'Share failed.', 'error'))}
                  >
                    <IosShareIcon fontSize={isMobilePreview ? 'small' : 'medium'} />
                  </IconButton>
                </Tooltip>
                <SplitButton
                  label="Download original"
                  menuPlacement="up"
                  onPrimary={() => downloadUrlAsBlob(eventApi.originalUrl(eid, currentPhoto.photo_id), `circlemoments-${eid}-${currentPhoto.photo_id}.jpg`).catch((err) => toast(err.message || 'Download failed.', 'error'))}
                  menuItems={[
                    { id: 'uc-original', label: 'Download uncompressed original', disabled: !currentPhoto.uc_org_url || ucDownloading || !!(currentPhoto.blurred_group_ids?.length), onClick: () => downloadUcOriginalPhoto(currentPhoto.photo_id) },
                  ]}
                />
                {isMobilePreview ? (
                  <>
                    <Tooltip title="More options">
                      <IconButton size="small" onClick={(e) => setPreviewMoreAnchor(e.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu anchorEl={previewMoreAnchor} open={!!previewMoreAnchor} onClose={() => setPreviewMoreAnchor(null)}>
                      <MenuItem onClick={() => { setPreviewMoreAnchor(null); setThumbPreviewOpen(false); enterFullscreen(); }}>
                        <FullscreenIcon fontSize="small" style={{ marginRight: 8 }} /> Fullscreen view
                      </MenuItem>
                      <MenuItem onClick={() => { setPreviewMoreAnchor(null); toggleShowcasePhoto(currentPhoto.photo_id); }}>
                        <AddAPhotoIcon fontSize="small" style={{ marginRight: 8 }} />
                        {showcasedPhotoIds.has(currentPhoto.photo_id) ? 'Remove from profile' : 'Add to Profile View'}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Tooltip title="Fullscreen view">
                      <IconButton aria-label="Fullscreen view" onClick={() => { setThumbPreviewOpen(false); enterFullscreen(); }}>
                        <FullscreenIcon />
                      </IconButton>
                    </Tooltip>
                    <AddToProfileButton
                      size="small"
                      added={showcasedPhotoIds.has(currentPhoto.photo_id)}
                      loading={showcaseTogglingId === currentPhoto.photo_id}
                      onToggle={() => toggleShowcasePhoto(currentPhoto.photo_id)}
                    />
                    <Button variant="outlined" onClick={() => setThumbPreviewOpen(false)} sx={{ textTransform: 'none' }}>
                      Close
                    </Button>
                  </>
                )}
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {isFullscreen && currentPhoto && (
        <div
          className={`fs-overlay${fsZoom.isZoomed ? ' fs-zoomed' : ''}`}
          onTouchStart={handleFsTouchStart}
          onTouchMove={handleFsTouchMove}
          onTouchEnd={handleFsTouchEnd}
          onTouchCancel={handleFsTouchEnd}
        >
          <button type="button" className="fs-close-btn" onClick={exitFullscreen} aria-label="Exit fullscreen">✕</button>
          <button
            type="button"
            className="fs-uc-btn"
            onClick={() => downloadUcOriginalPhoto(currentPhoto.photo_id)}
            disabled={!currentPhoto.uc_org_url || ucDownloading || !!(currentPhoto.blurred_group_ids?.length)}
            title={currentPhoto.uc_org_url ? 'Download uncompressed original' : 'Uncompressed original not available'}
            aria-label="Download uncompressed original"
          >
            <DownloadIcon fontSize="small" />
          </button>
          <div className="fs-photo-area" ref={fsZoom.containerRef}>
            <img
              key={currentPhoto.path + '-fs'}
              className={`main-photo${photoTransition ? ` photo-anim-${photoTransition}` : ''}`}
              src={showOriginal ? (originalBlobUrl || previewSrc(currentPhoto.photo_id)) : previewSrc(currentPhoto.photo_id)}
              alt={currentPhoto.path}
              draggable={false}
              style={{
                transform: `translate(${fsZoom.transform.x}px, ${fsZoom.transform.y}px) scale(${fsZoom.transform.scale})`,
                transition: fsZoom.isGesturing ? 'none' : 'transform 0.2s ease-out',
              }}
            />
            {!fsZoom.isZoomed && (
              <>
                <button type="button" className={`preview-swipe-btn preview-swipe-btn-left fs-nav-arrow${fsNavHighlight ? ' fs-nav-highlight' : ''}`} onClick={() => navWithTransition(-1)} aria-label="Previous photo">&#8249;</button>
                <button type="button" className={`preview-swipe-btn preview-swipe-btn-right fs-nav-arrow${fsNavHighlight ? ' fs-nav-highlight' : ''}`} onClick={() => navWithTransition(1)} aria-label="Next photo">&#8250;</button>
              </>
            )}
          </div>
          {!fsZoom.isZoomed && (
            <>
              <PhotoJumpInput className="fs-counter" current={currentIndex + 1} total={filteredPhotos.length} onJump={goToIndex} />
              <button type="button" className={`fs-auto-btn${autoTransition ? ' active' : ''}`} onClick={() => setAutoTransition(p => !p)} title="Auto-transition">
                <span className="fs-auto-icon">{autoTransition ? '⏸' : '▶'}</span>
                <span className="fs-auto-label">Auto-transition</span>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

// Thrown by waitForGroupingCompletion (and the upload-link page's own
// triggerGroupingWithRetry) when a caller explicitly cancels via `signal` -
// distinguished from a real failure/timeout so callers can skip showing an
// error message for a user-initiated cancel.
export class GroupingCancelledError extends Error {
  constructor() {
    super('Grouping wait cancelled.');
    this.name = 'GroupingCancelledError';
  }
}

// Polls /grouping-status until the job reaches a terminal state. Used by the local-folder
// auto-sync engine so a detected batch is only considered "delivered" (cleared from the
// pending queue, its files permanently marked seen) once grouping has actually finished -
// not merely once the upload itself finished. Throws on 'failed' so the caller's retry
// logic (useLocalAutoSync keeps failed batches queued) kicks in; resolves on 'complete'.
// `signal` (an optional AbortSignal), if aborted, throws GroupingCancelledError at the
// next checkpoint instead of continuing to poll - used by the upload-link page's own
// cancel-grouping button.
export async function waitForGroupingCompletion(eventId, opts = {}) {
  const {
    hooks = {}, onStatusChange, jobType = 'initial', pollIntervalMs = 8000, timeoutMs = 45 * 60 * 1000,
    // Defaults to the organiser JWT-authenticated status route; the upload-link page
    // passes a closure over eventApi.getGroupingStatusViaLink(eventId, token) instead,
    // since a link visitor may have no JWT (or the wrong one) of their own.
    statusFetcher = () => eventApi.getGroupingStatus(eventId),
    signal,
  } = opts;
  const emitProgress = hooks.setProgress || (() => {});
  const emitStatusComment = hooks.setStatusComment || (() => {});
  const emitLog = hooks.setLog || (() => {});
  const startedAt = Date.now();
  console.log(`[Upload] waitForGroupingCompletion: event=${eventId} jobType=${jobType} pollIntervalMs=${pollIntervalMs}`);
  let pollCount = 0;
  for (;;) {
    if (signal?.aborted) {
      console.log(`[Upload] waitForGroupingCompletion: event=${eventId} aborted after ${pollCount} poll(s)`);
      throw new GroupingCancelledError();
    }
    if (Date.now() - startedAt > timeoutMs) {
      console.error(`[Upload] waitForGroupingCompletion: event=${eventId} TIMED OUT after ${pollCount} poll(s)`);
      throw new Error('Timed out waiting for grouping to complete.');
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    if (signal?.aborted) throw new GroupingCancelledError();
    let res;
    try {
      res = await statusFetcher();
      pollCount += 1;
    } catch (err) {
      console.warn(`[Upload] waitForGroupingCompletion: event=${eventId} poll #${pollCount + 1} failed (transient, retrying):`, err?.message || err);
      continue; // transient network error - keep polling rather than failing the batch
    }
    console.log(`[Upload] waitForGroupingCompletion: event=${eventId} poll #${pollCount} status=${res.status} progress=${res.progress ? JSON.stringify(res.progress) : `${res.processed || 0}/${res.total || 0}`}`);
    emitLog(res.log || '');
    emitProgress(res.progress || {
      processed: res.processed || 0,
      total: res.total || 0,
      percent: res.percent || 0,
      current_image: res.current_image || '',
      phase: res.status || 'processing',
    });
    emitStatusComment(res.comment || (res.status === 'processing' ? 'Grouping photos…' : res.status === 'starting' ? 'Grouping server is starting…' : 'Finalising…'));
    onStatusChange?.({ grouping_status: res.status, grouping_job_type: res.grouping_job_type || jobType });
    if (res.status === 'complete') {
      console.log(`[Upload] waitForGroupingCompletion: event=${eventId} COMPLETE after ${pollCount} poll(s)`);
      return res;
    }
    if (res.status === 'failed') {
      console.error(`[Upload] waitForGroupingCompletion: event=${eventId} FAILED after ${pollCount} poll(s): ${res.comment || 'Grouping failed.'}`);
      throw new Error(res.comment || 'Grouping failed.');
    }
    // A status of 'none' means something (a Cancel button, cancelGrouping
    // called from a completely different tab/session, etc.) reset the job
    // server-side while this loop's own signal/timeout never fired - without
    // this check it fell through to "keep polling" indefinitely, which is
    // exactly the stuck "Preparing..." state with no way out that a real
    // device auto-sync run got stuck in.
    if (res.status === 'none') {
      console.warn(`[Upload] waitForGroupingCompletion: event=${eventId} status reset to 'none' (cancelled elsewhere?) after ${pollCount} poll(s)`);
      throw new GroupingCancelledError();
    }
    // 'starting' / 'processing' / 'uploading' - keep polling.
  }
}

// Uploads `files` into the photo-submissions pool (eventphotosubmissions),
// UPLOAD_BATCH_SIZE at a time, never triggering grouping. A single unchunked
// call for a large selection would either hard-fail outright (the backend's
// /photo-submissions/upload-urls route caps at 500 files per call) or, even
// under that cap, hash/read EXIF for the whole batch in one unchunked
// round-trip - this mirrors runOrganiserUploadBatches' own batching cadence
// instead. Used by every web/PWA "Upload" entry point (AddPhotosSection,
// SmartSortSection, organiser auto-sync upload-only batches, and
// PhotoSubmissionsPanel's own direct-upload button) so none of them can
// regress back to a single giant request.
async function uploadPhotoSubmissionsInBatches(eventId, files, opts = {}) {
  const { onProgress } = opts;
  const batchSize = UPLOAD_BATCH_SIZE;
  const allIds = [];
  let processed = 0;
  const total = files.length;
  for (let i = 0; i < files.length; i += batchSize) {
    const batchEntries = files.slice(i, i + batchSize);
    const form = new FormData();
    for (const entry of batchEntries) {
      // Accepts three shapes: a raw File (AddPhotosSection/SmartSortSection's
      // own acceptedImages/selectedServerImages), a {file, relativePath}
      // wrapper, or a lazy {relativePath, resolveFile} descriptor - the
      // auto-sync engine (useLocalAutoSync.js) deliberately never hands over
      // real Files up front (resolving a whole large batch eagerly caused
      // unbounded app storage growth on-device), only a resolveFile() to call
      // right before this file is actually needed - see
      // runOrganiserUploadBatches' own identical per-file resolution.
      let file = entry?.file;
      let relativePath = entry?.relativePath || file?.webkitRelativePath || file?.name;
      if (!file && entry?.resolveFile) {
        try {
          file = await entry.resolveFile();
          relativePath = relativePath || file?.name;
        } catch {
          // Source file moved/deleted since it was detected - drop it
          // silently, matching useLocalAutoSync's own "already marked seen" handling.
          processed += 1;
          continue;
        }
      }
      if (!file) {
        // Plain File passed directly (not wrapped in {file, relativePath}).
        file = entry;
        relativePath = relativePath || entry?.webkitRelativePath || entry?.name;
      }
      form.append('files', file, relativePath);
      processed += 1;
    }
    const result = await eventApi.uploadPhotoSubmissions(eventId, form);
    const ids = Array.isArray(result?.submission_ids) ? result.submission_ids : [];
    allIds.push(...ids);
    onProgress?.({ uploaded: Math.min(processed, total), total });
  }
  return allIds;
}

// Module-level so it's callable from SmartSortSection's initial-grouping flow,
// AddPhotosSection's manual "Add & Regroup" button/local-folder auto-sync, and (via
// resumeUploadJob.js/SyncWorkerPage.js) the native background-upload resume route -
// one compress/batch/upload engine for both `mode`s ('initial'|'add'), since they hit
// the exact same backend endpoints (getOriginalUploadUrls -> S3 PUT ->
// groupingUploadComplete) parameterized by `mode`, not two different transports (the
// add-mode "multipart POST" that used to live here was eventApi.uploadAddPhotosBatch
// unpacking a FormData just to call these same presigned-URL functions underneath -
// pure incidental duplication, now removed). `payload` is normalized by every caller
// into { source_directory, source_mode, limit, model, credit_hold_id,
// organizer_terms_ack, policy_version } regardless of mode. `hooks` redirects
// progress/log/status updates (used by the local-folder auto-sync engine and, on
// native, the headless upload-worker route); `hooks.onFileCompressed`/`onFileUploaded`
// are additive, no-op-by-default staging callbacks for native background uploads (see
// the native background-upload plan) - they never affect browser/PWA behavior.
export async function runOrganiserUploadBatches(event, filesToUpload, mode, payload, opts = {}) {
  const {
    hooks = {},
    uploadCancelledRef = { current: false },
    uploadAbortRef = { current: null },
    driveFileIdByPathRef = { current: null },
    // Set only when this call is resuming a job an earlier (interrupted) call
    // already started - see resumeUploadJob.js, which reads these back out of
    // the job manifest's `uploadSession`. `filesToUpload` on a resume is only
    // the still-pending remainder, never the full original batch.
    resumeUploadId = '',
    resumeStagingS3Prefix = '',
    alreadyUploadedCount = 0,
  } = opts;
  const emitLog = hooks.setLog || (() => {});
  const emitProgress = hooks.setProgress || (() => {});
  const emitStatusComment = hooks.setStatusComment || (() => {});
  const onFileCompressed = hooks.onFileCompressed || (() => {});
  const onFileUploaded = hooks.onFileUploaded || (() => {});
  // Fires once the upload session's identity is known/updated so a later
  // resume of this same job (see resumeUploadJob.js) can reuse it instead of
  // starting an unrelated new session the server has no memory of - see
  // stagingProvider.setUploadSession's own doc comment for the failure this
  // prevents. No-op by default (browser/PWA never resumes).
  const onUploadSessionEstablished = hooks.onUploadSessionEstablished || (() => {});
  const isResume = Boolean(resumeUploadId);
  const baseUploaded = Math.max(0, alreadyUploadedCount);
  const batchSize = UPLOAD_BATCH_SIZE;
  const totalBatches = Math.ceil(filesToUpload.length / batchSize);
  let uploaded = 0;
  let skipped = 0;
  uploadCancelledRef.current = false;
  console.log(`[Upload] runOrganiserUploadBatches: event=${event.event_id} mode=${mode} files=${filesToUpload.length} totalBatches=${totalBatches} isResume=${isResume} alreadyUploaded=${baseUploaded}`);

  emitLog('');
  emitStatusComment('Compressing photos in browser...');
  emitProgress({ processed: 0, total: filesToUpload.length, percent: 0, current_image: '', phase: 'compressing' });

  const uploadId = resumeUploadId || `${event.event_id}-${mode}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  let stagingS3Prefix = resumeStagingS3Prefix || '';
  onUploadSessionEstablished({ uploadId, stagingS3Prefix });
  let lastUploadHeartbeat = 0;
  const sendUploadHeartbeat = async (extra = {}) => {
    const now = Date.now();
    if (!extra.force && now - lastUploadHeartbeat < UPLOAD_HEARTBEAT_INTERVAL_MS) return;
    lastUploadHeartbeat = now;
    await eventApi.groupingUploadProgress(event.event_id, {
      mode,
      upload_id: uploadId,
      total_files: baseUploaded + filesToUpload.length,
      uploaded_count: baseUploaded + uploaded,
      skipped_count: skipped,
      staging_s3_prefix: stagingS3Prefix,
      ...extra,
    });
  };

  // Pre-warm immediately for uploads with <= UPLOAD_PREWARM_IMMEDIATE_THRESHOLD images
  // (not enough left for the 2-batches-out trigger below); larger uploads fire inside
  // the loop once 2 batches remain.
  if (filesToUpload.length <= UPLOAD_PREWARM_IMMEDIATE_THRESHOLD) {
    eventApi.preWarmGroupingServer(event.event_id, mode).catch(() => {});
  }

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
    const batch = filesToUpload.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize);
    console.log(`[Upload] runOrganiserUploadBatches: event=${event.event_id} batch #${batchIndex + 1}/${totalBatches} compressing ${batch.length} file(s)`);
    if (uploadCancelledRef.current) throw new Error('Upload cancelled.');

    const compressedBatch = [];
    const ucMetadataBatch = [];
    for (let i = 0; i < batch.length; i += 1) {
      if (uploadCancelledRef.current) throw new Error('Upload cancelled.');
      const fileEntry = batch[i];
      // Local-folder auto-sync hands over lazy `{relativePath, resolveFile}`
      // descriptors instead of already-resolved Files (see useLocalAutoSync's
      // resolveAndSubmit) so a large batch's originals are read from SAF and
      // compressed one at a time, not all copied into app cache up front -
      // confirmed on-device that eagerly resolving a 100+ file batch before any
      // compression started caused large, unbounded app storage growth.
      let file = fileEntry.file;
      if (!file && fileEntry.resolveFile) {
        try {
          file = await fileEntry.resolveFile();
        } catch {
          // Source file moved/deleted since it was detected - drop it silently,
          // matching useLocalAutoSync's own "already marked seen" handling.
          continue;
        }
      }
      file = file || fileEntry;
      emitStatusComment(`Compressing ${file.name}...`);
      // Hash + read capture date/EXIF from the RAW bytes before compression
      // touches them - compression is lossy/irreversible, so this is the
      // last point the original's true identity (and its EXIF block) is
      // available. Collected into ucMetadataBatch below once relativePath
      // (the compressed copy's final name) is known, and posted to
      // /uc-file-metadata alongside this batch's upload-URL request.
      const ucMd5Hash = await computeUcMd5Hash(file);
      const { capturedAt, exifTags } = await extractCaptureMetadata(file);
      let compressed = await compressEventImage(file);
      if (uploadCancelledRef.current) throw new Error('Upload cancelled.');
      const relPath = fileEntry.relativePath || file.webkitRelativePath || file.name;
      const parts = relPath.split('/');
      if (parts.length > 1 && payload.source_mode !== 'cloud-google') parts.shift();
      parts[parts.length - 1] = uniqueCompressedImageName(file.name);
      // Sanitize to exactly match what /original-upload-urls will echo back as
      // relative_path (see media.py's _safe_upload_path) - otherwise any special
      // character in the original filename (spaces, parens, unicode) desyncs the
      // client's upload-URL lookup key from the server's response key, and the
      // file silently never gets uploaded (no error, just uploaded_count stuck at 0).
      const relativePath = sanitizeUploadRelativePath(parts.join('/'), file.name);
      // Re-key any Drive fileId ref from the raw picked/enumerated name
      // (relPath, e.g. "Copy of _BS36338.JPG" - what driveFileIdByPathRef is
      // originally populated with, before this file is even compressed) to
      // the FINAL sanitized+suffixed name this photo is actually stored
      // under (e.g. "Copy_of__BS36338_velv.jpg"). Keying the durable
      // drive_original_refs manifest by the raw Drive name instead (the old
      // behaviour) both required a fragile stem/sanitize-matching heuristic
      // server-side to bridge it back to the stored photo later, AND could
      // silently collide/overwrite if two different Drive files (different
      // folders, same organiser) happened to share a filename - the
      // sanitized+suffixed name is guaranteed unique per upload, so this is
      // a correctness fix, not just a rename.
      if (driveFileIdByPathRef.current && Object.prototype.hasOwnProperty.call(driveFileIdByPathRef.current, relPath)) {
        const driveId = driveFileIdByPathRef.current[relPath];
        delete driveFileIdByPathRef.current[relPath];
        driveFileIdByPathRef.current[relativePath] = driveId;
      }
      ucMetadataBatch.push({
        relative_path: relativePath, uc_filename: file.name, uc_md5_hash: ucMd5Hash,
        captured_at: capturedAt || null, exif_tags: exifTags || null,
      });
      compressedBatch.push({ file: compressed, relative_path: relativePath, original_name: file.name });
      // 3rd arg (pre-compression original path) lets stagingProvider.stageFile
      // find and update a pre-populated placeholder entry (see createJob's
      // `entries`) instead of creating a disconnected duplicate.
      onFileCompressed(relativePath, compressed, fileEntry.relativePath);
      compressed = null;
      emitProgress({
        processed: Math.min(filesToUpload.length, batchIndex * batchSize + i + 1),
        total: filesToUpload.length,
        percent: Math.round((Math.min(filesToUpload.length, batchIndex * batchSize + i + 1) / filesToUpload.length) * 100),
        current_image: file.name,
        phase: 'compressing',
      });
      await sendUploadHeartbeat();
    }

    if (uploadCancelledRef.current) throw new Error('Upload cancelled.');
    emitStatusComment(`Requesting upload URLs for batch ${batchIndex + 1} of ${totalBatches}...`);
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    const urlPayload = {
      mode,
      upload_id: uploadId,
      // Never wipe the staging prefix on a resumed run's own "first" batch -
      // batchIndex restarts at 0 for every call, but a resume's batch 0 is
      // really continuing an already-in-progress upload_id, and reset_staging
      // purges the ENTIRE staging prefix for this organiser/event (see
      // media.py's event_original_upload_urls), which would delete every file
      // the interrupted original run already uploaded.
      reset_staging: batchIndex === 0 && !isResume,
      total_files: baseUploaded + filesToUpload.length,
      files: compressedBatch.map(item => ({
        name: item.original_name,
        relative_path: item.relative_path,
        size: item.file.size,
        content_type: item.file.type || 'image/jpeg',
      })),
    };
    // Fire-and-forget alongside the upload-URL request itself, same as
    // setDriveOriginalRefs below - best-effort bookkeeping that must never
    // block or fail the actual upload.
    if (ucMetadataBatch.length) eventApi.setUcFileMetadata(event.event_id, ucMetadataBatch).catch(() => {});
    const urlRes = await eventApi.getOriginalUploadUrls(event.event_id, urlPayload);
    if (urlRes.prefix) {
      stagingS3Prefix = urlRes.prefix;
      onUploadSessionEstablished({ uploadId, stagingS3Prefix });
    }
    const uploadsByPath = new Map((urlRes.uploads || []).map(item => [item.relative_path, item]));
    skipped += (urlRes.rejected || []).length;

    emitStatusComment(`Uploading batch ${batchIndex + 1} of ${totalBatches}...`);
    const putResults = await Promise.all(compressedBatch.map(async (item) => {
      const upload = uploadsByPath.get(item.relative_path);
      if (!upload) {
        item.file = null;
        return false;
      }
      try {
        const putRes = await fetch(upload.url, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: item.file,
          signal: controller.signal,
        });
        if (putRes.ok) onFileUploaded(item.relative_path);
        return putRes.ok;
      } finally {
        item.file = null;
      }
    }));
    const batchUploaded = putResults.filter(Boolean).length;
    uploaded += batchUploaded;
    skipped += putResults.length - batchUploaded;
    uploadAbortRef.current = null;
    console.log(`[Upload] runOrganiserUploadBatches: event=${event.event_id} batch #${batchIndex + 1}/${totalBatches} done, ${batchUploaded}/${putResults.length} uploaded this batch, ${uploaded}/${filesToUpload.length} total`);
    await sendUploadHeartbeat({ force: true });
    emitProgress({
      processed: uploaded + skipped,
      total: filesToUpload.length,
      percent: Math.round(((uploaded + skipped) / filesToUpload.length) * 100),
      current_image: '',
      phase: 'uploading',
    });
    // Pre-warm: start the container 2 batches (≈100 images) before the end so it has
    // more time to warm up concurrently. For uploads <= UPLOAD_PREWARM_IMMEDIATE_THRESHOLD
    // images the block above already fired before the loop started.
    if (totalBatches > 2 && batchIndex === totalBatches - 3) {
      eventApi.preWarmGroupingServer(event.event_id, mode).catch(() => {});
    }
  }
  if (uploadCancelledRef.current) throw new Error('Upload cancelled.');
  if (baseUploaded + uploaded <= 0) {
    console.error(`[Upload] runOrganiserUploadBatches: event=${event.event_id} no photos uploaded (baseUploaded=${baseUploaded} uploaded=${uploaded})`);
    throw new Error('No photos were uploaded. Check that compressed JPEGs are under 6 MB.');
  }
  console.log(`[Upload] runOrganiserUploadBatches: event=${event.event_id} upload phase complete, ${baseUploaded + uploaded} uploaded, ${skipped} skipped - calling grouping-upload-complete`);
  emitStatusComment(mode === 'add' ? 'Re-grouping uploaded photos…' : 'Finalising upload...');
  // Stash Drive fileId refs now, alongside upload-complete - NOT once grouping
  // finishes. Grouping runs entirely server-side and the organiser may close
  // the browser the instant uploads are done; the refs are durably stored and
  // applied server-side once metadata.json exists (see grouping-status).
  if ((payload.source_mode || 'local') === 'cloud-google' && driveFileIdByPathRef.current) {
    const refs = Object.entries(driveFileIdByPathRef.current)
      .map(([relative_path, drive_file_id]) => ({ relative_path, drive_file_id }));
    driveFileIdByPathRef.current = null;
    if (refs.length) eventApi.setDriveOriginalRefs(event.event_id, refs).catch(() => {});
  }
  const result = await eventApi.groupingUploadComplete(event.event_id, {
    mode,
    upload_id: uploadId,
    source_directory: payload.source_directory || '',
    source_mode: payload.source_mode || 'local',
    limit: payload.limit,
    model: payload.model,
    credit_hold_id: payload.credit_hold_id,
    // Add mode historically reported `uploaded_count` as the requested total
    // rather than the real accumulated count (the pre-merge runAddPhotosUpload/
    // eventApi.uploadAddPhotosBatch always sent total_files here, never the
    // actual per-run uploaded tally) - preserved as-is rather than "fixed" here,
    // since it's plausibly read server-side for credit/grouping reconciliation
    // and this merge is meant to be behavior-preserving on the success path.
    uploaded_count: mode === 'add' ? (baseUploaded + filesToUpload.length) : (baseUploaded + uploaded),
    skipped_count: skipped,
    staging_s3_prefix: stagingS3Prefix,
    organizer_terms_ack: payload.organizer_terms_ack || false,
    policy_version: payload.policy_version || '',
  });
  emitProgress({ processed: 0, total: 0, percent: 0, current_image: '', phase: 'processing' });
  return result;
}

// ── Add More Photos controls (embedded in Smart Sort when grouping exists) ──
// `autoSync` (optional) is the single useLocalAutoSync instance owned by the parent
// SmartSortSection - passed down so this component's Step 1 area can arm/display/
// drive the same auto-sync engine instead of instantiating its own.
function AddPhotosSection({ event, onStatusChange, onGalleryRefresh, driveConnected, driveEmail, accountFaceGroupingModel, accountFaceGroupingModelLoaded, autoSync = null, embedded = false, autoSyncUploadedSubmissionIds = [], onAutoSyncSubmissionIdsConsumed = null, autoSyncAlsoGroup = false, onAutoSyncAlsoGroupChange = null }) {
  const [serverFiles, setServerFiles] = useState([]);
  const [sourceDir, setSourceDir] = useState('');
  const [sourceMode, setSourceMode] = useState('local');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [photoLimit, setPhotoLimit] = useState('');
  const [starting, setStarting] = useState(false);
  const [rebuildingSearch, setRebuildingSearch] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0, percent: 0, current_image: '', phase: 'starting' });
  const [statusComment, setStatusComment] = useState('');
  const [log, setLog] = useState('');
  const [localProcessing, setLocalProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const fileInputRef = useRef(null);
  const directoryInputRef = useRef(null);
  const pollingRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const uploadCancelledRef = useRef(false);
  // relativePath -> Drive fileId, captured at picker time so the brokered
  // "uncompressed original" download can resolve back to the source file in
  // the organiser's own Drive once grouping has finished.
  const driveFileIdByPathRef = useRef(null);
  // Metadata-only Drive selection awaiting a server-side import - see
  // SmartSortSection's driveImportPendingRef for the full rationale.
  const driveImportPendingRef = useRef(null);
  // See SmartSortSection's matching refs for the full rationale - set only
  // when serverFiles came from manualFolder.pickFolder() (a real SAF folder)
  // or nativeFilePicker.pickFiles() (individually-picked SAF documents) on
  // native, respectively; reset by every other selection path. Mutually
  // exclusive with each other.
  const manualFolderTreeUriRef = useRef(null);
  const manualFolderDocsRef = useRef(null);
  const lastStatusPollRef = useRef(0);
  const statusPollInFlightRef = useRef(false);
  // "Match Originals from Drive" dialog state - null when closed. See
  // handleMatchDriveOriginals's own doc comment for the full flow.
  // phase: 'checksums' | 'matching' | 'review' | 'saving'
  const [matchDialog, setMatchDialog] = useState(null);
  const matchCancelRef = useRef(false);
  // Native-only: observes whatever CmUploadWorker (WorkManager) is already
  // doing for this event's add-photos job - never resumes or re-runs
  // anything (native never stopped running it); purely reads state back for
  // display. See SmartSortSection's matching effect/doc comment.
  const [resumeJob, setResumeJob] = useState(null);
  const nativeAddJob = useNativeUploadObserver(event.event_id, ['add-photos']);
  // Same idea, for the native "upload only" jobs (manual Upload button here,
  // or SmartSortSection's auto-sync engine - both use the 'organiser-photo-
  // submit' flowKind) - without this, that job ran fully headlessly with no
  // progress bar at all once a gallery exists, since nativeAddJob above only
  // ever watches 'add-photos'. See the id-reconciliation effect further down
  // (near sessionSubmissionIds) for how its results get back into "Start
  // grouping (N)" once it finishes - mirrors SmartSortSection's identical fix.
  const nativePhotoSubmitJob = useNativeUploadObserver(event.event_id, ['organiser-photo-submit']);
  useEffect(() => { setResumeJob(nativeAddJob || nativePhotoSubmitJob || null); }, [nativeAddJob, nativePhotoSubmitJob]);
  const nudgedAddStatusRef = useRef(false);
  const priorNativeAddJobRef = useRef(null);
  useEffect(() => {
    if (nativeAddJob && !nudgedAddStatusRef.current && (event.grouping_status || 'none') !== 'starting' && (event.grouping_status || 'none') !== 'processing') {
      nudgedAddStatusRef.current = true;
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'add' });
    }
    // Once a previously-observed native add job disappears (CmUploadWorker
    // deleted it on terminal success), refresh the gallery - mirrors the old
    // resume path's `.then(() => onGalleryRefresh?.())`.
    if (!nativeAddJob && priorNativeAddJobRef.current) {
      onGalleryRefresh?.();
      nudgedAddStatusRef.current = false;
    }
    priorNativeAddJobRef.current = nativeAddJob;
  }, [nativeAddJob, event.grouping_status, onStatusChange, onGalleryRefresh]);

  // ── Decoupled "upload without grouping" (requirement: 3-step upload) ──────
  // Accumulates submission_ids uploaded in THIS mount only (never persisted) -
  // "Start grouping" processes exactly this set, not the event's whole
  // unprocessed backlog. Reset naturally on navigation/remount.
  const [sessionSubmissionIds, setSessionSubmissionIds] = useState(() => loadSessionSubmissionIds(event.event_id));
  useEffect(() => {
    saveSessionSubmissionIds(event.event_id, sessionSubmissionIds);
  }, [event.event_id, sessionSubmissionIds]);
  // Reconciliation: a persisted id can go stale if it was already grouped
  // through some other path (or through a bug where this component's own
  // completion-handling never got a chance to clear it - see the
  // grouping_job_type mismatch this fixed) - without this check, a stale
  // "already grouped" id would sit in localStorage forever, surviving every
  // refresh, and clicking "Start grouping" would silently re-submit an
  // already-processed photo. Runs on mount, and again every time
  // event.grouping_status transitions into 'complete' - grouping kicked off
  // from the Photo Submissions tab's own "Process all unprocessed"/"Add to
  // gallery" buttons finishes through a totally separate poller that this
  // component never sees directly, so mount-only would leave the stale count
  // showing until the next full page refresh (see EventGalleryViewer's
  // onGroupingComplete, which is what makes event.grouping_status catch up
  // for that case). The boolean-dependency trick means this only re-fires on
  // the false->true/true->false edge, not on every intermediate
  // starting/processing tick.
  useEffect(() => {
    if (!sessionSubmissionIds.length) return undefined;
    let cancelled = false;
    eventApi.listPhotoSubmissions(event.event_id).then((res) => {
      if (cancelled) return;
      const allSubs = (res?.submitters || []).flatMap(s => s.submissions || []);
      const stillUngrouped = new Set(allSubs.filter(s => !s.grouped).map(s => s.submission_id));
      setSessionSubmissionIds(prev => prev.filter(id => stillUngrouped.has(id)));
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id, event.grouping_status === 'complete']);
  // Auto-sync batches uploaded without grouping are detected by
  // SmartSortSection (the auto-sync engine lives there) and handed down here
  // so they land in the same "Start grouping" pool as the manual button's own
  // uploads, instead of a second, separate accumulator/button.
  useEffect(() => {
    if (autoSyncUploadedSubmissionIds.length) {
      setSessionSubmissionIds(prev => [...prev, ...autoSyncUploadedSubmissionIds]);
      onAutoSyncSubmissionIdsConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSyncUploadedSubmissionIds]);
  const [uploadOnlyBusy, setUploadOnlyBusy] = useState(false);
  const [uploadOnlyProgress, setUploadOnlyProgress] = useState({ uploaded: 0, total: 0 });
  const [groupingIngestStatus, setGroupingIngestStatus] = useState('idle'); // idle | processing | complete | failed
  // Synchronous guard (set before the first await, unlike groupingIngestStatus
  // which only flips to 'processing' once the network call resolves) - closes
  // the window where a fast double-click on "Start grouping" could fire the
  // ingestPhotoSubmissions call twice before the button's disabled state catches up.
  const [startingGrouping, setStartingGrouping] = useState(false);
  // Snapshot of the submission_ids handed to ingestPhotoSubmissions for the
  // grouping run currently in flight - null when no such run is outstanding.
  // Resolved by the EXISTING pollAddStatus loop below, not a second poller of
  // our own. A separate poll loop here used to run alongside pollAddStatus
  // (both watching the exact same grouping-status endpoint, both calling
  // onStatusChange independently) - two uncoordinated pollers racing on the
  // same event.grouping_status could report completion out of order, and
  // since that same status also gates whether the Upload/Start-grouping UI
  // is shown, the UI could reappear (with the just-grouped ids not yet
  // cleared from sessionSubmissionIds) and let the organiser resubmit the
  // exact same photos for grouping a second time. Folding into the one
  // existing poller removes the race entirely.
  const pendingIngestSubmissionIdsRef = useRef(null);
  // Native upload-only jobs (whether started by this component's own manual
  // Upload button, or by SmartSortSection's auto-sync engine - both use the
  // 'organiser-photo-submit' flowKind) run headlessly in CmUploadWorker and
  // don't report their resulting submission_ids back to JS directly. Keep a
  // rolling snapshot of the organiser's own ungrouped submission_ids, updated
  // on mount and after every observed job completion, so whichever trigger
  // started a job, the diff against this snapshot reliably discovers exactly
  // what that job just created.
  const lastKnownOrganiserUngroupedIdsRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    eventApi.listPhotoSubmissions(event.event_id).then((res) => {
      if (cancelled) return;
      lastKnownOrganiserUngroupedIdsRef.current = new Set(
        (res?.submitters || []).filter(s => s.is_organiser).flatMap(s => s.submissions.filter(x => !x.grouped).map(x => x.submission_id))
      );
    }).catch(() => { lastKnownOrganiserUngroupedIdsRef.current = new Set(); });
    return () => { cancelled = true; };
  }, [event.event_id]);
  const priorNativePhotoSubmitJobRef = useRef(null);
  useEffect(() => {
    if (!nativePhotoSubmitJob && priorNativePhotoSubmitJobRef.current) {
      const before = lastKnownOrganiserUngroupedIdsRef.current || new Set();
      eventApi.listPhotoSubmissions(event.event_id).then((res) => {
        const currentIds = (res?.submitters || [])
          .filter(s => s.is_organiser)
          .flatMap(s => s.submissions.filter(x => !x.grouped).map(x => x.submission_id));
        lastKnownOrganiserUngroupedIdsRef.current = new Set(currentIds);
        const newIds = currentIds.filter(id => !before.has(id));
        if (newIds.length) {
          setSessionSubmissionIds(prev => [...prev, ...newIds]);
          setSnack({ open: true, msg: `${newIds.length} photo${newIds.length === 1 ? '' : 's'} uploaded (see Photo Submissions tab).`, sev: 'success' });
        }
      }).catch(() => {});
    }
    priorNativePhotoSubmitJobRef.current = nativePhotoSubmitJob;
  }, [nativePhotoSubmitJob, event.event_id]);


  // Upload only - lands photos in the submissions pool (eventphotosubmissions),
  // never triggers grouping. Mirrors PhotoSubmissionsPanel.handleOrganiserFilesUpload's
  // web path; native hands off to CmUploadWorker's 'organiser-photo-submit'
  // flowKind (canGroup:false), which runs the same upload without grouping.
  const handleUploadOnly = async () => {
    if (!canIncrementallyGroup) {
      setSnack({ open: true, msg: `Switch your account face model back to ${faceModelLabel(eventFaceModel)} to add photos to this event.`, sev: 'warning' });
      return;
    }
    if (!acceptedImages.length) { setSnack({ open: true, msg: 'Choose a folder first.', sev: 'warning' }); return; }
    if (acceptedImages.length > MAX_GROUPING_REQUEST_PHOTOS) { setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' }); return; }
    const consentOk = await confirmFaceGroupingConsent();
    if (!consentOk) {
      setSnack({ open: true, msg: 'Upload was cancelled because consent was not granted.', sev: 'info' });
      return;
    }

    // Drive-sourced selection: no photo bytes are held on this device -
    // schedule a server-side import (mode='upload_only') and poll it instead
    // of the local-file compress/PUT loop below. See drive_import_service.py.
    if (sourceMode === 'cloud-google' && driveImportPendingRef.current?.length) {
      const driveFiles = driveImportPendingRef.current;
      setUploadOnlyBusy(true);
      setUploadOnlyProgress({ uploaded: 0, total: driveFiles.length });
      try {
        const { job_id: jobId } = await eventApi.scheduleDriveImport(event.event_id, {
          mode: 'upload_only',
          drive_files: driveFiles.map((d) => ({ drive_file_id: d.driveFileId, name: d.name })),
          source_directory: sourceDir || 'Google Drive',
        });
        for (;;) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const job = await eventApi.getDriveImportStatus(event.event_id, jobId);
          setUploadOnlyProgress({ uploaded: job.done_count || 0, total: job.total_count || driveFiles.length });
          if (job.status === 'complete') {
            const ids = job.submission_ids || [];
            setSessionSubmissionIds(prev => [...prev, ...ids]);
            setSnack({ open: true, msg: `${ids.length} photo${ids.length === 1 ? '' : 's'} imported from Drive (see Photo Submissions tab).`, sev: 'success' });
            break;
          }
          if (job.status === 'failed' || job.status === 'cancelled') {
            throw new Error(job.error || 'Drive import failed.');
          }
        }
        driveImportPendingRef.current = null;
        setServerFiles([]);
        setSourceDir('');
      } catch (err) {
        setSnack({ open: true, msg: err.message || 'Drive import failed', sev: 'error' });
      } finally {
        setUploadOnlyBusy(false);
        setUploadOnlyProgress({ uploaded: 0, total: 0 });
      }
      return;
    }

    setUploadOnlyBusy(true);
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(event.event_id, 'photo-submit');
    const filesToUpload = acceptedImages.map((f) => ({ file: f, relativePath: (f.webkitRelativePath || f.name) }));
    try {
      if (isNative) {
        await nativeUploadService.startUploadJob({
          jobId, flowKind: 'organiser-photo-submit', eventId: event.event_id,
          flowMeta: { canGroup: false },
          totalFiles: filesToUpload.length,
          entries: filesToUpload,
          resumeSource: manualFolderTreeUriRef.current
            ? { kind: 'saf', treeUri: manualFolderTreeUriRef.current }
            : manualFolderDocsRef.current
              ? { kind: 'saf-docs', docs: manualFolderDocsRef.current }
              : null,
        });
        setSnack({ open: true, msg: `Uploading ${filesToUpload.length} photo${filesToUpload.length === 1 ? '' : 's'} in the background - check back in Photo Submissions once it's done.`, sev: 'info' });
      } else {
        setUploadOnlyProgress({ uploaded: 0, total: acceptedImages.length });
        const ids = await uploadPhotoSubmissionsInBatches(event.event_id, acceptedImages, {
          onProgress: (p) => setUploadOnlyProgress(p),
        });
        setSessionSubmissionIds(prev => [...prev, ...ids]);
        setSnack({ open: true, msg: `${ids.length} photo${ids.length === 1 ? '' : 's'} selected and uploaded (see Photo Submissions tab).`, sev: 'success' });
      }
      setServerFiles([]);
      setSourceDir('');
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Upload failed', sev: 'error' });
    } finally {
      setUploadOnlyBusy(false);
      setUploadOnlyProgress({ uploaded: 0, total: 0 });
    }
  };

  // Start grouping - processes exactly this session's accumulated
  // submission_ids (never the event's whole unprocessed backlog). Same
  // ingestPhotoSubmissions call PhotoSubmissionsPanel's own "Add unprocessed
  // to gallery" button already uses, just scoped to sessionSubmissionIds.
  const handleStartGrouping = async () => {
    if (!sessionSubmissionIds.length || startingGrouping || groupingIngestStatus === 'processing') return;
    setStartingGrouping(true);
    const mode = hasExistingGallery ? 'add' : 'initial';
    // Snapshot now - acceptedImages/sessionSubmissionIds can keep growing
    // (another upload can complete) while this run is in flight, and only
    // these specific ids should ever be cleared once pollAddStatus below
    // sees this run finish, never the whole list.
    const submittedIds = sessionSubmissionIds;
    try {
      const result = await eventApi.ingestPhotoSubmissions(event.event_id, submittedIds, mode);
      // Use the server-CONFIRMED mode (it can coerce a stale guess - see
      // ingestPhotoSubmissions' own comment), not our own hasExistingGallery
      // guess, for onStatusChange - a mismatch here previously left
      // pollAddStatus's activation (gated on grouping_job_type === 'add')
      // never triggering, so a completed run's ids never got cleared.
      const actualMode = result?.actual_mode === 'add' ? 'add' : (result?.actual_mode === 'initial' ? 'initial' : mode);
      pendingIngestSubmissionIdsRef.current = submittedIds;
      // Belt-and-suspenders: activate this component's own polling directly
      // rather than relying solely on the event.grouping_job_type prop
      // round-trip matching 'add' exactly.
      setLocalProcessing(true);
      setGroupingIngestStatus('processing');
      setSnack({ open: true, msg: `Grouping ${submittedIds.length} photo${submittedIds.length === 1 ? '' : 's'}…`, sev: 'info' });
      onStatusChange({ grouping_status: 'starting', grouping_job_type: actualMode });
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Failed to start grouping', sev: 'error' });
    } finally {
      setStartingGrouping(false);
    }
  };

  // Restore in-progress state whenever the event's own reported status shows
  // an add-photos job actively running that this component isn't already
  // locally tracking - re-attach the polling loop automatically. Reacts to
  // every change of grouping_status/grouping_job_type (not just the very
  // first mount): a job can be started by a completely different session now
  // (e.g. an upload-link visitor, not just this organiser's own browser tab),
  // so the *first* `event` snapshot this component happens to render with is
  // no longer a reliable one-shot check - if it mounts even slightly before
  // the page's real, fresh event data arrives, a mount-only effect would use
  // a stale snapshot and never notice the real in-progress job once the
  // fresh data actually lands in props. Re-running on every prop change closes
  // that gap regardless of timing.
  useEffect(() => {
    const jobType = event.grouping_job_type || 'initial';
    const st = event.grouping_status || 'none';
    if (jobType === 'add' && (st === 'starting' || st === 'processing' || st === 'uploading')) {
      setLocalProcessing(true);
      setStatusComment(st === 'starting' ? 'Grouping server is starting…' : 'Processing added photos...');
    }
  }, [event.grouping_job_type, event.grouping_status]);

  const acceptedImages = useMemo(() => {
    const images = serverFiles.filter(isCompressibleEventImage);
    const limitValue = photoLimit === '' ? 0 : Number(photoLimit);
    return limitValue > 0 ? images.slice(0, limitValue) : images;
  }, [photoLimit, serverFiles]);
  const eventFaceModel = normaliseFaceModel(event.grouping_model);
  const accountFaceModel = normaliseFaceModel(accountFaceGroupingModel);
  // Until the account's real face model has loaded, assume compatible rather than
  // guessing DEFAULT_FACE_GROUPING_MODEL - guessing caused a flash of the mismatch
  // warning (and disabled Add Photos buttons) on every load for non-default accounts.
  const canIncrementallyGroup = !accountFaceGroupingModelLoaded || eventFaceModel === accountFaceModel;
  const hasExistingGallery = Boolean(event.s3_output_prefix);

  const manualFolder = useManualFolderPersistence({
    eventId: event.event_id,
    onFilesReady: (files, folderName, treeUri) => {
      setServerFiles(files);
      setSourceDir(folderName);
      setSourceMode('local');
      manualFolderTreeUriRef.current = treeUri || null;
      manualFolderDocsRef.current = null;
    },
  });

  const handleLocalFolderChange = (e) => {
    const files = Array.from(e.target.files || []);
    const acceptedCount = files.filter(isCompressibleEventImage).length;
    if (acceptedCount > MAX_GROUPING_REQUEST_PHOTOS) {
      setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' });
    }
    setServerFiles(files);
    setSourceMode('local');
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    e.target.value = '';
    const firstFile = files[0];
    const nativePath = firstFile?.path || firstFile?.mozFullPath || '';
    if (nativePath) {
      const normalized = nativePath.replace(/\\/g, '/');
      const relative = (firstFile.webkitRelativePath || firstFile.name || '').replace(/\\/g, '/');
      const removeParts = relative.split('/').filter(Boolean).length > 1 ? relative.split('/').filter(Boolean).length : 1;
      setSourceDir(normalized.split('/').slice(0, -removeParts).join('/'));
    } else {
      const rel = firstFile?.webkitRelativePath || firstFile?.name || '';
      setSourceDir(firstFile?.webkitRelativePath ? rel.split('/')[0] : files.length > 1 ? 'Selected photos' : rel);
    }
  };

  const handleLocalFilesSelect = async () => {
    if (!canIncrementallyGroup) return;
    setSourceMode('local');
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    // Native: bypasses <input type=file> entirely - see SmartSortSection's
    // matching handleLocalFilesSelect for the full rationale.
    if (nativeFilePicker.supported()) {
      const picked = await nativeFilePicker.pickFiles();
      if (!picked.length) return; // user cancelled the OS picker
      manualFolderDocsRef.current = picked.map((p) => ({ relativePath: p.name, uri: p.uri }));
      setServerFiles(picked.map((p) => ({ name: p.name, webkitRelativePath: p.name, size: p.size, lastModified: Date.now(), type: '' })));
      setSourceDir(picked.length > 1 ? 'Selected photos' : picked[0].name);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleLocalFolderSelect = async () => {
    if (!canIncrementallyGroup) return;
    setSourceMode('local');
    if (!manualFolder.supported) {
      manualFolderTreeUriRef.current = null;
      manualFolderDocsRef.current = null;
      directoryInputRef.current?.click();
      return;
    }
    const result = await manualFolder.pickFolder();
    if (result) {
      setServerFiles(result.files);
      setSourceDir(result.folderName);
      manualFolderTreeUriRef.current = result.treeUri || null;
      manualFolderDocsRef.current = null;
    }
  };

  // Add-mode twin of SmartSortSection's runDriveDownloadAndUpload - schedules
  // a server-side Drive import (mode='group_now', job_type='add') and
  // returns immediately. See drive_import_service.py.
  const runDriveDownloadAndUploadAdd = async (photoFiles) => {
    const creditHold = await eventApi.initiateGrouping(event.event_id, 'add');
    eventApi.preWarmGroupingServer(event.event_id, 'add').catch(() => {});
    try {
      setLocalProcessing(true);
      await eventApi.scheduleDriveImport(event.event_id, {
        mode: 'group_now',
        job_type: 'add',
        drive_files: photoFiles.map((d) => ({ drive_file_id: d.id, name: d.name })),
        source_directory: 'Google Drive',
        credit_hold_id: creditHold.credit_hold_id,
      });
      onStatusChange({ grouping_status: 'starting', grouping_job_type: 'add' });
      setSnack({ open: true, msg: `Importing ${photoFiles.length} photos from Drive in the background - you can close the app.`, sev: 'info' });
    } catch (err) {
      setLocalProcessing(false);
      setSnack({ open: true, msg: err.message || 'Drive import failed.', sev: 'error' });
    }
  };

  const handleDriveSelect = async () => {
    if (!canIncrementallyGroup) return;
    manualFolderTreeUriRef.current = null;
    manualFolderDocsRef.current = null;
    setSnack({ open: true, msg: 'Opening Google Drive picker…', sev: 'info' });
    try {
      const token = await getDriveAccessToken(driveEmail);
      const pickedDocs = await openDrivePicker(token);
      if (!pickedDocs.length) { setSnack({ open: true, msg: 'No files selected.', sev: 'info' }); return; }
      if (pickedDocs.some(d => d.mimeType === DRIVE_FOLDER_MIME)) {
        setSnack({ open: true, msg: 'Reading folder contents…', sev: 'info' });
      }
      const docs = await expandDrivePickerDocs(pickedDocs, token);
      if (!docs.length) { setSnack({ open: true, msg: 'No image files found in the selected folder.', sev: 'warning' }); return; }
      const photoMimes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/bmp', 'image/tiff']);
      const photoFiles = docs.filter(d => photoMimes.has(d.mimeType));
      if (!photoFiles.length) { setSnack({ open: true, msg: 'No image files selected.', sev: 'warning' }); return; }
      if (photoFiles.length > MAX_GROUPING_REQUEST_PHOTOS) { setSnack({ open: true, msg: GROUPING_LIMIT_MESSAGE, sev: 'warning' }); return; }

      if (Capacitor.isNativePlatform() && photoFiles.length <= DRIVE_BACKGROUND_MAX_FILES) {
        await runDriveDownloadAndUploadAdd(photoFiles);
        return;
      }

      // Metadata-only selection - no photo bytes are ever downloaded to this
      // device. Clicking "Upload" (handleUploadOnly below) schedules the
      // actual server-side import (drive_import_service.py).
      driveImportPendingRef.current = photoFiles.map((d) => ({ driveFileId: d.id, name: d.name }));
      setServerFiles(photoFiles.map((d) => ({ name: d.name, webkitRelativePath: d.name, size: 0, lastModified: Date.now(), type: d.mimeType || '' })));
      setSourceDir(photoFiles.length > 1 ? 'Google Drive' : photoFiles[0].name);
      setSourceMode('cloud-google');
      setSnack({ open: true, msg: `${photoFiles.length} Drive photos selected.`, sev: 'success' });
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Drive access failed.', sev: 'error' });
    }
  };

  // "Match Originals from Drive" - reconnects already-uploaded gallery photos
  // to their full-resolution Drive counterparts by content/filename, without
  // re-uploading anything. Deliberately reuses getDriveAccessToken/
  // openDrivePicker (the SAME fresh, interactive picker-session token every
  // other Drive action here uses) rather than the stored offline refresh
  // token the uncompressed-original relay uses later - drive.file's
  // folder-enumerated-children access does not reliably survive to a
  // separately-obtained token (confirmed on-device: this is exactly why the
  // relay itself needed drive_original_refs written correctly at upload
  // time), so this stays entirely within one interactive session, same as
  // the working "initial import" flow already does.
  //
  // Three phases, driven by matchDialog.phase: 'checksums' (reading each
  // selected file's md5 from Drive, cancellable) -> 'matching' (one API
  // call, classifies candidates into both/hash_only/filename_only) ->
  // 'review' (organiser ticks/unticks candidates, all pre-checked) ->
  // 'saving' (persists only the confirmed subset via the existing
  // setDriveOriginalRefs, then reloads the gallery so previously-greyed-out
  // "download uncompressed original" buttons light up).
  const handleMatchDriveOriginals = async () => {
    try {
      const token = await getDriveAccessToken(driveEmail);
      const pickedDocs = await openDrivePicker(token);
      if (!pickedDocs.length) return;

      matchCancelRef.current = false;
      setMatchDialog({ phase: 'checksums', progress: { completed: 0, total: 0 }, results: null, checked: {} });

      const checksummed = await resolveDriveChecksumsWithProgress(
        pickedDocs, token,
        (progress) => setMatchDialog((d) => (d ? { ...d, progress } : d)),
        () => matchCancelRef.current,
      );
      if (matchCancelRef.current) { setMatchDialog(null); return; }
      if (!checksummed.length) {
        setMatchDialog(null);
        setSnack({ open: true, msg: 'No matchable image files found.', sev: 'warning' });
        return;
      }

      setMatchDialog((d) => (d ? { ...d, phase: 'matching' } : d));
      const entries = checksummed.map((f) => ({ drive_file_id: f.id, name: f.name, md5_checksum: f.md5Checksum }));
      const results = await eventApi.matchDriveOriginals(event.event_id, entries);
      if (matchCancelRef.current) { setMatchDialog(null); return; }

      const totalCandidates = (results.both?.length || 0) + (results.hash_only?.length || 0) + (results.filename_only?.length || 0);
      if (!totalCandidates) {
        setMatchDialog(null);
        setSnack({ open: true, msg: `No matches found among ${checksummed.length} Drive photo${checksummed.length === 1 ? '' : 's'} checked.`, sev: 'info' });
        return;
      }
      const checked = {};
      ['both', 'hash_only', 'filename_only'].forEach((cat) => {
        (results[cat] || []).forEach((m) => { checked[`${cat}:${m.relative_path}`] = true; });
      });
      setMatchDialog({ phase: 'review', progress: null, results, checked });
    } catch (err) {
      setMatchDialog(null);
      setSnack({ open: true, msg: err.message || 'Matching Drive originals failed.', sev: 'error' });
    }
  };

  const handleCancelMatchDialog = () => {
    matchCancelRef.current = true;
    setMatchDialog(null);
  };

  const toggleMatchChecked = (key) => {
    setMatchDialog((d) => (d ? { ...d, checked: { ...d.checked, [key]: !d.checked[key] } } : d));
  };

  const handleSaveDriveMatches = async () => {
    if (!matchDialog?.results) return;
    const refs = [];
    ['both', 'hash_only', 'filename_only'].forEach((cat) => {
      (matchDialog.results[cat] || []).forEach((m) => {
        if (matchDialog.checked[`${cat}:${m.relative_path}`]) {
          refs.push({ relative_path: m.relative_path, drive_file_id: m.drive_file_id });
        }
      });
    });
    if (!refs.length) { setMatchDialog(null); return; }
    setMatchDialog((d) => (d ? { ...d, phase: 'saving' } : d));
    try {
      await eventApi.setDriveOriginalRefs(event.event_id, refs);
      setMatchDialog(null);
      setSnack({ open: true, msg: `Linked ${refs.length} photo${refs.length === 1 ? '' : 's'} to their Drive originals.`, sev: 'success' });
      // Un-greys the "download uncompressed original" affordance for every
      // photo just linked - it reads uc_org_url resolved server-side from
      // the event_drive_original_refs table, which only just changed.
      onGalleryRefresh?.();
    } catch (err) {
      setMatchDialog((d) => (d ? { ...d, phase: 'review' } : d));
      setSnack({ open: true, msg: err.message || 'Saving matches failed.', sev: 'error' });
    }
  };


  const pollAddStatus = useCallback(async () => {
    const now = Date.now();
    if (statusPollInFlightRef.current || now - lastStatusPollRef.current < GROUPING_STATUS_POLL_INTERVAL_MS) {
      return;
    }
    statusPollInFlightRef.current = true;
    lastStatusPollRef.current = now;
    try {
      const res = await eventApi.getGroupingStatus(event.event_id);
      setLog(res.log || '');
      setProgress(res.progress || {
        processed: res.processed || 0,
        total: res.total || 0,
        percent: res.percent || 0,
        current_image: res.current_image || '',
        phase: res.status || 'processing',
      });
      setStatusComment(res.comment || (res.status === 'uploading' ? 'Finalising...' : res.status === 'starting' ? 'Grouping server is starting…' : 'Processing added photos...'));
      onStatusChange({ grouping_status: res.status, grouping_job_type: res.grouping_job_type || 'add' });
      if (res.status !== 'processing' && res.status !== 'uploading' && res.status !== 'starting') {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setLocalProcessing(false);
        setStarting(false);
        if (res.status === 'complete') {
          setSnack({ open: true, msg: 'Added photos grouped successfully.', sev: 'success' });
          onGalleryRefresh?.();
        } else if (res.status === 'failed') {
          setSnack({ open: true, msg: 'Add photos processing failed.', sev: 'error' });
        }
        // Resolve any "Start grouping" run this component itself kicked off
        // (see handleStartGrouping) - this is the single poller both that
        // flow and the older upload-and-group-immediately flow share, so
        // there's no second, uncoordinated poll loop racing this one.
        if (pendingIngestSubmissionIdsRef.current) {
          const mine = new Set(pendingIngestSubmissionIdsRef.current);
          pendingIngestSubmissionIdsRef.current = null;
          setGroupingIngestStatus(res.status === 'complete' ? 'complete' : 'failed');
          if (res.status === 'complete') {
            setSessionSubmissionIds(prev => prev.filter(id => !mine.has(id)));
          }
        }
      }
    } catch {
      // Keep polling; transient status failures should not hide progress.
    } finally {
      statusPollInFlightRef.current = false;
    }
  }, [event.event_id, onGalleryRefresh, onStatusChange]);

  useEffect(() => {
    if (!localProcessing) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      return undefined;
    }
    pollAddStatus();
    pollingRef.current = setInterval(pollAddStatus, GROUPING_STATUS_POLL_INTERVAL_MS);
    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [localProcessing, pollAddStatus]);

  const handleRebuildSearch = async () => {
    setRebuildingSearch(true);
    try {
      await eventApi.rebuildSearchIndex(event.event_id);
      setSnack({ open: true, msg: 'Search index rebuild started. This runs in the background.', sev: 'info' });
    } catch (err) {
      setSnack({ open: true, msg: err.message || 'Failed to rebuild search index', sev: 'error' });
    } finally {
      setRebuildingSearch(false);
    }
  };

  // When auto-sync triggers a batch while this component is the one on screen, the
  // upload runs through runOrganiserUploadBatches with hooks pointed at autoSync's own
  // progress/statusComment/log state (see SmartSortSection's handleAutoSyncBatch) -
  // this component's own `starting`/`progress`/etc. are never touched by that path,
  // so the progress view below reads from whichever source is actually active.
  const autoSyncBusy = autoSync?.status === 'submitting';
  // A native job (CmUploadWorker/WorkManager) runs with no visible hooks at
  // all - see useNativeUploadObserver's doc comment - so neither autoSync's
  // state nor this component's own starting-driven state ever gets real
  // numbers written to it while native is doing the work. Without this, the
  // progress block fell back to a static "Preparing…" for the entire run
  // even though the native notification (fed by CmUploadWorker's own
  // UploadNotifications calls) was counting up the whole time - confirmed on
  // a real device: notification progressed normally while the in-app screen
  // looked completely frozen. useNativeUploadObserver's own polling already
  // refreshes resumeJob every ~2.5s, so its numbers are always reasonably
  // fresh. `resumeJob` here means "native is currently running a job for this
  // event" - not necessarily one resumed after the app was closed; it's
  // equally true for a job started moments ago by this very click, so the
  // text must be phase-based (mirrors SmartSortSection's own displayStatusComment)
  // rather than unconditionally saying "Resuming".
  const resuming = Boolean(resumeJob) && !autoSyncBusy;
  const nativeUploadPhase = resumeJob?.phase;
  const displayStatusComment = autoSyncBusy
    ? autoSync.statusComment
    : resuming
      ? ({ compressing: 'Compressing photos…', uploading: 'Uploading photos…', processing: 'Finalising upload…' }[nativeUploadPhase] || 'Starting…')
      : statusComment;
  const displayProgress = autoSyncBusy
    ? autoSync.progress
    : resuming
      ? {
        processed: resumeJob.completedFiles,
        total: resumeJob.totalFiles,
        percent: resumeJob.totalFiles > 0 ? Math.round((resumeJob.completedFiles / resumeJob.totalFiles) * 100) : 0,
        current_image: '',
        phase: nativeUploadPhase,
      }
      : progress;
  const displayLog = autoSyncBusy ? autoSync.log : log;
  const progressPercent = Number.isFinite(Number(displayProgress.percent)) ? Number(displayProgress.percent) : 0;

  const content = (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1.5} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center">
        <AddPhotoAlternateIcon color="primary" />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Add More Photos</Typography>
          <Typography variant="caption" color="text.secondary">
            Upload additional photos to extend the existing grouping
          </Typography>
        </Box>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={rebuildingSearch ? <CircularProgress size={14} /> : <SearchIcon />}
          onClick={handleRebuildSearch}
          disabled={starting || localProcessing || rebuildingSearch}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Rebuild Search
        </Button>
      </Stack>

      {!canIncrementallyGroup && !starting && !localProcessing && !autoSyncBusy && !resuming && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This event was grouped with {faceModelLabel(eventFaceModel)}. Your account default is {faceModelLabel(accountFaceModel)}, so Add More Photos is disabled until you switch back.
        </Alert>
      )}

      {starting || localProcessing || autoSyncBusy || resuming ? (
        <Stack spacing={1.25}>
          <Typography variant="body2" fontWeight={700}>{displayStatusComment}</Typography>
          <Typography variant="caption" color="text.secondary">
            {displayProgress.total > 0 ? `${displayProgress.processed} of ${displayProgress.total} photos` : 'Preparing…'}
          </Typography>
          <LinearProgress variant={displayProgress.total > 0 ? 'determinate' : 'indeterminate'} value={Math.max(0, Math.min(progressPercent, 100))} sx={{ height: 6, borderRadius: 999 }} />
          {displayProgress.total > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.max(0, Math.min(progressPercent, 100))}% complete
            </Typography>
          )}
          {(starting || (event.grouping_status || 'none') === 'starting') ? (
            shouldShowUploadWarning() ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{UPLOAD_IN_PROGRESS_WARNING}</Alert>
            ) : shouldShowBackgroundableNotice() ? (
              <Alert severity="info" sx={{ py: 0.5 }}>{UPLOAD_BACKGROUNDABLE_NOTICE}</Alert>
            ) : null
          ) : (event.grouping_status || 'none') === 'processing' ? (
            <Alert severity="success" sx={{ py: 0.5 }}>{GROUPING_IN_PROGRESS_NOTICE}</Alert>
          ) : null}
          {/* Processing log hidden from frontend display (backend logging unaffected)
          {displayLog && (
            <Box component="details" sx={{ mt: 0.5 }}>
              <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary', fontSize: '0.875rem' }}>
                Processing log
              </Box>
              <Box sx={{ bgcolor: '#111', color: '#cfc', borderRadius: 1, p: 1.5, mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {displayLog}
              </Box>
            </Box>
          )}
          */}
          {/* Always available, including while autoSyncBusy - this progress block
              can be showing a stuck/orphaned "starting" state with no local upload
              loop actually running anymore (e.g. after the app was closed mid-batch
              and reopened), and there was previously no way out of it at all once
              that happened. See cancelInFlightBatch's doc comment for why the
              auto-sync engine also needs to be told about this, not just the local
              component state. */}
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={async () => {
              uploadCancelledRef.current = true;
              uploadAbortRef.current?.abort();
              // This same block renders for a native job too (via `resuming`) -
              // without this, cancelGrouping only resets the server's status
              // and CmUploadWorker keeps running until it happens to notice
              // (its own ~8s grouping-status poll seeing status='none') and
              // clean itself up - confirmed on-device: the screen stayed on
              // "Resuming…" for a stretch after clicking Cancel, then briefly
              // flashed a stale "starting" state before finally clearing.
              if (Capacitor.isNativePlatform() && resumeJob) {
                await nativeUploadService.cancelJob({ jobId: resumeJob.jobId }).catch(() => {});
                setResumeJob(null);
              }
              try {
                await eventApi.cancelGrouping(event.event_id);
              } catch {
                // Local abort still wins for the UI; backend cleanup may already be complete.
              }
              autoSync?.cancelInFlightBatch?.();
              setStarting(false);
              setLocalProcessing(false);
              // Revert parent status to 'complete' - add-photos cancel never resets to 'none'.
              onStatusChange({ grouping_status: 'complete', grouping_job_type: 'initial' });
            }}
          >
            Cancel
          </Button>
        </Stack>
      ) : (
        <>
          <input ref={fileInputRef} type="file" multiple
            accept=".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif"
            onChange={handleLocalFolderChange} style={{ display: 'none' }} />
          <input ref={directoryInputRef} type="file" multiple webkitdirectory="true" directory=""
            accept=".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif"
            onChange={handleLocalFolderChange} style={{ display: 'none' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 1 &middot; Select photos
              </Typography>
              {autoSync?.armed ? (
                <>
                  <AutoSyncStatusCard autoSync={autoSync} />
                  {onAutoSyncAlsoGroupChange && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={autoSyncAlsoGroup ? 'group' : 'upload'}
                      onChange={(e, value) => { if (value) onAutoSyncAlsoGroupChange(value === 'group'); }}
                      sx={{ mt: 0.75 }}
                    >
                      <ToggleButton value="upload" sx={{ px: 1.5, py: 0.25, lineHeight: 1.2 }}>
                        <Typography variant="caption">Upload only</Typography>
                      </ToggleButton>
                      <ToggleButton value="group" sx={{ px: 1.5, py: 0.25, lineHeight: 1.2 }}>
                        <Typography variant="caption">Upload + Group</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </>
              ) : (
                <>
                  <ChooseFolderDropdown
                    driveConnected={Boolean(driveConnected)}
                    onLocalFilesSelect={handleLocalFilesSelect}
                    onLocalFolderSelect={handleLocalFolderSelect}
                    onDriveSelect={handleDriveSelect}
                    onAutoSyncSelect={autoSync ? async () => {
                      const consentOk = await confirmFaceGroupingConsent();
                      if (!consentOk) return;
                      autoSync.selectFolder(promptAutoSyncIncludeExisting, promptStopOtherAutoSync);
                    } : undefined}
                    autoSyncSupported={Boolean(autoSync?.supported)}
                    disabled={starting || localProcessing || !canIncrementallyGroup || Boolean(autoSync?.scanning) || manualFolder.scanning}
                  />
                  {autoSync && <AutoSyncScanProgress autoSync={autoSync} />}
                  {/* Manual (non-auto-sync) "Choose local folder" scan - see the matching
                      comment in SmartSortSection's Step 1 area above. */}
                  <AutoSyncScanProgress autoSync={manualFolder} />
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                    {sourceMode === 'cloud-google'
                      ? <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png" alt="Drive" sx={{ width: 14, height: 14, flexShrink: 0 }} />
                      : <FolderOpenIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />}
                    <Typography variant="caption" color={acceptedImages.length ? 'success.main' : 'text.secondary'}>
                      {acceptedImages.length
                        ? `${acceptedImages.length} ${sourceMode === 'cloud-google' ? 'Drive' : 'local'} photos ready${sourceDir && sourceDir !== 'Selected photos' ? ` from "${sourceDir}"` : ''}.`
                        : 'No photos selected yet.'}
                    </Typography>
                  </Stack>
                  {driveConnected && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Box component="img" src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_32dp.png" alt="" sx={{ width: 14, height: 14 }} />}
                      onClick={handleMatchDriveOriginals}
                      disabled={starting || localProcessing}
                      sx={{ textTransform: 'none', mt: 0.5, alignSelf: 'flex-start', px: 0.5 }}
                    >
                      Match Originals from Drive
                    </Button>
                  )}
                  {manualFolder.reconnect && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
                      <Stack spacing={0.75} alignItems="flex-start">
                        <span>Continue with "{manualFolder.reconnect.folderName}"?</span>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={manualFolder.reconnectNow}>Reconnect</Button>
                          <Button
                            size="small"
                            sx={{ opacity: 0.7 }}
                            onClick={async () => {
                              const ok = await swalConfirmDelete({
                                title: 'Reset this folder?',
                                text: "This forgets the picked folder for this event - you'll need to choose it (or a different one) again.",
                                confirmText: 'Reset',
                              });
                              if (ok) await manualFolder.reset();
                            }}
                          >
                            Reset
                          </Button>
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                </>
              )}
            </Box>

            <ArrowForwardIcon sx={{ color: 'grey.300', mt: 4, display: { xs: 'none', sm: 'block' } }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 2 &middot; Upload
              </Typography>
              <Button
                variant="contained"
                startIcon={uploadOnlyBusy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleUploadOnly}
                disabled={uploadOnlyBusy || localProcessing || !acceptedImages.length || !canIncrementallyGroup || Boolean(resumeJob)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {uploadOnlyBusy
                  ? (uploadOnlyProgress.total > 0 ? `Uploading ${uploadOnlyProgress.uploaded}/${uploadOnlyProgress.total}…` : 'Uploading…')
                  : 'Upload'}
              </Button>
              {!Capacitor.isNativePlatform() && acceptedImages.length > LARGE_WEB_UPLOAD_WARNING_THRESHOLD && !uploadOnlyBusy && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  {acceptedImages.length} photos is a big upload for a browser tab - keep this tab open until it finishes. The mobile app can do this in the background instead.
                </Typography>
              )}
              {sessionSubmissionIds.length > 0 && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                  {sessionSubmissionIds.length} photo{sessionSubmissionIds.length === 1 ? '' : 's'} selected and uploaded (see Photo Submissions tab).
                </Typography>
              )}
            </Box>

            <ArrowForwardIcon sx={{ color: 'grey.300', mt: 4, display: { xs: 'none', sm: 'block' } }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                STEP 3 &middot; Start grouping
              </Typography>
              <Tooltip title={!sessionSubmissionIds.length ? 'Upload photos first' : `Group the ${sessionSubmissionIds.length} photo(s) uploaded in this window - not any other pending submissions`}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={(startingGrouping || groupingIngestStatus === 'processing') ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleStartGrouping}
                    disabled={!sessionSubmissionIds.length || startingGrouping || groupingIngestStatus === 'processing' || localProcessing || Boolean(resumeJob)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {startingGrouping ? 'Starting…' : groupingIngestStatus === 'processing' ? `Grouping… ${progress.percent || 0}%` : `Start grouping${sessionSubmissionIds.length ? ` (${sessionSubmissionIds.length})` : ''}`}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Stack>
          <ResumeUploadBanner
            job={resumeJob}
            sx={{ mt: 1, py: 0.5 }}
            onCancel={async () => {
              // A resumed job has no live component to hold uploadCancelledRef/
              // uploadAbortRef for (see resumeUploadJob.js's own doc comment on
              // why) - cancelJob is the only thing that can actually stop it,
              // same as the progress block's Cancel button above stops a
              // still-locally-running one.
              await nativeUploadService.cancelJob({ jobId: resumeJob.jobId }).catch(() => {});
              try {
                await eventApi.cancelGrouping(event.event_id);
              } catch {
                // Local state still clears below; backend cleanup may already be complete.
              }
              autoSync?.cancelInFlightBatch?.();
              setResumeJob(null);
              onStatusChange({ grouping_status: 'complete', grouping_job_type: 'initial' });
            }}
          />

          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => setAdvancedOpen(v => !v)}
              sx={{ textTransform: 'none', color: 'text.secondary', px: 0, minWidth: 0 }}
            >
              Advanced{advancedOpen ? ' ▴' : ' ▾'}
            </Button>
            <Collapse in={advancedOpen}>
              <TextField
                label="Photo limit"
                value={photoLimit}
                onChange={e => setPhotoLimit(e.target.value.replace(/[^\d]/g, ''))}
                size="small"
                placeholder="Default"
                helperText="Only process the first N photos in the selected folder (leave blank to process all)."
                sx={{ width: { xs: '100%', sm: 200 }, mt: 1 }}
                inputProps={{ inputMode: 'numeric', min: 0 }}
              />
              <AutoMatchToggle event={event} onStatusChange={onStatusChange} />
              <Box sx={{ mt: 1 }}>
                <Tooltip title="This can be changed in Account Settings">
                  <Chip label={`Face model: ${faceModelLabel(eventFaceModel)}`} size="small" variant="outlined" />
                </Tooltip>
              </Box>
            </Collapse>
          </Box>
        </>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>

      <MatchDriveOriginalsDialog
        eventId={event.event_id}
        matchDialog={matchDialog}
        onCancel={handleCancelMatchDialog}
        onToggleChecked={toggleMatchChecked}
        onSave={handleSaveDriveMatches}
      />
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
      {content}
    </Paper>
  );
}

// ── "Match Originals from Drive" review dialog ─────────────────────────────────
const MATCH_CATEGORY_LABELS = {
  both: 'Filename & hash match',
  hash_only: 'Hash match only (different filename in Drive)',
  filename_only: 'Filename match only (content differs from what was uploaded)',
};

function MatchDriveOriginalsDialog({ eventId, matchDialog, onCancel, onToggleChecked, onSave }) {
  if (!matchDialog) return null;
  const { phase, progress, results, checked } = matchDialog;
  const totalChecked = results
    ? ['both', 'hash_only', 'filename_only'].reduce(
      (sum, cat) => sum + (results[cat] || []).filter((m) => checked[`${cat}:${m.relative_path}`]).length, 0,
    )
    : 0;

  return (
    <Dialog open onClose={phase === 'review' ? onCancel : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>Match Originals from Drive</DialogTitle>
      <DialogContent dividers>
        {(phase === 'checksums' || phase === 'matching') && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {phase === 'checksums'
                ? `Reading file checksums from Drive… ${progress.total ? `${progress.completed} / ${progress.total}` : ''}`
                : 'Matching against your gallery…'}
            </Typography>
            <LinearProgress
              variant={phase === 'checksums' && progress.total ? 'determinate' : 'indeterminate'}
              value={phase === 'checksums' && progress.total ? (progress.completed / progress.total) * 100 : undefined}
            />
          </Box>
        )}

        {phase === 'saving' && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>Saving matches…</Typography>
            <LinearProgress />
          </Box>
        )}

        {phase === 'review' && results && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Review the matches below - all are pre-selected. Untick anything that doesn't look right, then save.
            </Typography>
            {['both', 'hash_only', 'filename_only'].map((cat) => {
              const items = results[cat] || [];
              if (!items.length) return null;
              return (
                <Accordion key={cat} defaultExpanded disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{MATCH_CATEGORY_LABELS[cat]} ({items.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {items.map((m) => {
                        const key = `${cat}:${m.relative_path}`;
                        return (
                          <Box key={key} sx={{ width: 96, textAlign: 'center' }}>
                            <Stack direction="row" justifyContent="center">
                              <Checkbox
                                size="small"
                                checked={Boolean(checked[key])}
                                onChange={() => onToggleChecked(key)}
                                sx={{ p: 0.25 }}
                              />
                            </Stack>
                            <Box
                              component="img"
                              src={eventApi.thumbUrl(eventId, m.photo_id)}
                              alt=""
                              loading="lazy"
                              sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, display: 'block', mx: 'auto', bgcolor: 'grey.100' }}
                            />
                            <Typography variant="caption" noWrap title={m.uc_filename} sx={{ display: 'block', mt: 0.25 }}>
                              {m.uc_filename}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={phase === 'saving'} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {phase === 'review' && (
          <Button variant="contained" onClick={onSave} disabled={!totalChecked} sx={{ textTransform: 'none' }}>
            Save Match{totalChecked ? ` (${totalChecked})` : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Mobile sticky section tabs (segmented control) ─────────────────────────────
const MOBILE_TAB_ITEMS = [
  { key: 'event', label: 'Event', icon: EventNoteIcon },
  { key: 'photos', label: 'Photos', icon: PhotoLibraryIcon },
  { key: 'requests', label: 'Share', icon: AssignmentIcon },
];

const MOBILE_TAB_ITEMS_ATTENDEE = [
  { key: 'event', label: 'Event', icon: EventNoteIcon },
  { key: 'photos', label: 'Photos', icon: PhotoLibraryIcon },
  { key: 'requests', label: 'Submissions', icon: AddPhotoAlternateIcon },
];

function MobileSectionTabs({ activeTab, onChange, onBack, items = MOBILE_TAB_ITEMS, photoCount = null, photoCountPulse = false }) {
  return (
    <div className="mobile-tab-bar">
      <button
        type="button"
        className="mobile-tab-back-btn"
        onClick={onBack}
        aria-label="Back to events"
      >
        <ArrowBackIcon fontSize="small" />
      </button>
      <div className="mobile-tab-grid" role="tablist" aria-label="Event sections" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
        {items.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            className={`mobile-tab-btn${activeTab === key ? ' active' : ''}${key === 'photos' && photoCountPulse ? ' pulse' : ''}`}
            onClick={() => onChange(key)}
          >
            <Icon className="mobile-tab-icon" />
            <span>{key === 'photos' && photoCount !== null ? `${label} (${photoCount})` : label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main EventPage component ───────────────────────────────────────────────────
export default function EventPage() {
  const { event_id } = useParams();
  const navigate = useNavigate();
  const { user, isOrgContext, orgRole, activeOrg } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupingStatus, setGroupingStatus] = useState('none');
  const [attendeePhotosReloadKey, setAttendeePhotosReloadKey] = useState(0);
  const [galleryReloadKey, setGalleryReloadKey] = useState(0);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveEmail, setDriveEmail] = useState('');
  // Starts unset (not DEFAULT_FACE_GROUPING_MODEL) so `accountFaceGroupingModelLoaded`
  // can distinguish "we don't know the account's model yet" from "it's adaface" - guessing
  // adaface here caused a flash of the buffalo_l mismatch warning on every page load for
  // buffalo_l accounts, until getProfile() resolved and corrected it back to hidden.
  const [accountFaceGroupingModel, setAccountFaceGroupingModel] = useState(null);
  const [accountFaceGroupingModelLoaded, setAccountFaceGroupingModelLoaded] = useState(false);
  const [emailIntegrationEnabled, setEmailIntegrationEnabled] = useState(false);
  const [presentUsers, setPresentUsers] = useState([]);
  const [mobileTab, setMobileTab] = useState('event');
  const [photoCount, setPhotoCount] = useState(null);
  const [photoCountPulse, setPhotoCountPulse] = useState(false);
  const [submitPhotosBusy, setSubmitPhotosBusy] = useState(false);
  const [eventShareFlash, setEventShareFlash] = useState(false);
  const prevPhotoCountRef = useRef(null);

  // Standalone "Share Event" trigger for the organiser's gallery toolbar -
  // shares the same invite link as EventHeader's own share icon (see
  // shareEventInvite), just surfaced somewhere an organiser browsing photos
  // will actually see it rather than only in the invite-code card above.
  const handleShareEvent = async () => {
    try {
      if (!(await shareEventInvite(event))) return;
      setEventShareFlash(true);
      setTimeout(() => setEventShareFlash(false), 2000);
    } catch {
      // User cancelled the native share sheet.
    }
  };

  // Fetches (or auto-provisions) the event's default upload link and
  // navigates there - lets an attendee submit photos the same way an
  // anonymous share-page visitor can, without the organiser needing to have
  // set anything up first. See _get_or_create_default_upload_link.
  async function handleSubmitPhotos() {
    setSubmitPhotosBusy(true);
    try {
      const link = await eventApi.getDefaultUploadLink(event.event_id);
      navigate(`/upload/${link.token}`);
    } catch (err) {
      window.alert(err.message || 'Failed to open the submissions page.');
    } finally {
      setSubmitPhotosBusy(false);
    }
  }
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Pulse the Photos tab when the filtered photo count changes while the user
  // is on another tab (e.g. ticking a face checkbox on the People tab), so
  // they notice that something changed elsewhere.
  useEffect(() => {
    if (photoCount === null) return;
    const prev = prevPhotoCountRef.current;
    prevPhotoCountRef.current = photoCount;
    if (prev === null || prev === photoCount || mobileTab === 'photos') return;
    setPhotoCountPulse(true);
    const timer = setTimeout(() => setPhotoCountPulse(false), 900);
    return () => clearTimeout(timer);
  }, [photoCount, mobileTab]);

  useEffect(() => {
    if (!event_id) return;
    setLoading(true);
    eventApi.getEvent(Number(event_id))
      .then(ev => { setEvent(ev); setGroupingStatus(ev.grouping_status || 'none'); })
      .catch(() => setError('Event not found or you do not have access.'))
      .finally(() => setLoading(false));
  }, [event_id]);

  useEffect(() => {
    profileApi.getDriveStatus()
      .then(r => { setDriveConnected(Boolean(r.connected)); setDriveEmail(r.drive_email || ''); })
      .catch(() => {});
    profileApi.getProfile()
      .then(profile => {
        setAccountFaceGroupingModel(normaliseFaceModel(profile?.face_grouping_model));
        setEmailIntegrationEnabled(Boolean(profile?.email_integration_enabled));
      })
      .catch(() => {})
      .finally(() => setAccountFaceGroupingModelLoaded(true));
  }, []);

  // ── Org presence tracking (60s poll, admin/organiser only) ──────────────────
  useEffect(() => {
    if (!event_id || !isOrgContext || !['admin', 'organiser'].includes(orgRole)) return;
    const tick = async () => {
      await api.pingPresence(Number(event_id));
      try {
        const data = await api.getPresence(Number(event_id));
        setPresentUsers(data.present || []);
      } catch { /* silent */ }
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [event_id, isOrgContext, orgRole]);

  const refreshEventDetails = useCallback(async () => {
    if (!event_id) return null;
    const updated = await eventApi.getEvent(Number(event_id));
    setEvent(updated);
    setGroupingStatus(updated.grouping_status || 'none');
    return updated;
  }, [event_id]);

  const handleStatusChange = useCallback((statusOrPatch) => {
    const patch = typeof statusOrPatch === 'string' ? { grouping_status: statusOrPatch } : statusOrPatch;
    const newStatus = patch.grouping_status;
    setGroupingStatus(newStatus);
    setEvent(prev => prev ? { ...prev, ...patch } : prev);
    if (newStatus === 'complete') {
      refreshEventDetails().then(() => setGalleryReloadKey(k => k + 1)).catch(() => {});
    } else if (newStatus === 'failed') {
      refreshEventDetails().catch(() => {});
    }
  }, [refreshEventDetails]);

  const refreshGallery = useCallback(() => {
    setGalleryReloadKey(k => k + 1);
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
        <Container maxWidth="xl">
          <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={400} />
        </Container>
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box sx={{ py: 4 }}>
        <Container maxWidth="md">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2, textTransform: 'none' }}>Events</Button>
          <Alert severity="error">{error || 'Event not found.'}</Alert>
          <StrandedAutoSyncStopButton eventId={event_id} />
        </Container>
      </Box>
    );
  }

  // Hide the gallery when the user explicitly requests a re-run (status reset to 'none').
  // Any non-'none' status (processing, complete, failed) keeps the gallery visible.
  const isReady = groupingStatus !== 'none' && (groupingStatus === 'complete' || Boolean(event.s3_output_prefix));
  const showMobileTabs = isMobile;
  const mobileTabItems = (event.is_organiser ? MOBILE_TAB_ITEMS : MOBILE_TAB_ITEMS_ATTENDEE)
    .filter(item => item.key !== 'requests' || event.is_organiser || event.role !== 'circle_viewer');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
      <Container maxWidth="xl" sx={{ pt: 2 }}>
        {/* Back button */}
        {showMobileTabs ? null : isMobile ? (
          <IconButton
            onClick={() => navigate('/dashboard')}
            aria-label="Back to events"
            size="small"
            sx={{ mb: 0.5, ml: -1, color: 'text.secondary' }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 1.5, textTransform: 'none' }}
          >
            Events
          </Button>
        )}

        {/* ── Org presence banner ── */}
        {isOrgContext && presentUsers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
            {presentUsers.map(u => u.customer_name).join(', ')} from <strong>{activeOrg?.org_name}</strong>{' '}
            {presentUsers.length === 1 ? 'is' : 'are'} also viewing this event - face name assignments may be in stale state.
          </Alert>
        )}

        {/* ── Mobile sticky section tabs ── */}
        {showMobileTabs && (
          <MobileSectionTabs activeTab={mobileTab} onChange={setMobileTab} onBack={() => navigate('/dashboard')} items={mobileTabItems} photoCount={photoCount} photoCountPulse={photoCountPulse} />
        )}

        {(!showMobileTabs || mobileTab === 'event') && (
          <>
            <EventHeader
              event={{ ...event, grouping_status: groupingStatus }}
              onUpdated={(updated) => setEvent(updated)}
            />
            <EventAgendaAndToolsPanel
              event={{ ...event, grouping_status: groupingStatus }}
              onSaved={(updated) => {
                setEvent(updated);
                setGroupingStatus(updated.grouping_status || 'none');
              }}
            />
            {!event.is_organiser && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={submitPhotosBusy ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
                  onClick={handleSubmitPhotos}
                  disabled={submitPhotosBusy}
                  sx={{ textTransform: 'none' }}
                >
                  Submit Photos
                </Button>
              </Box>
            )}
            {showMobileTabs && !event.is_organiser && photoCount > 0 && (
              <Box sx={{ py: 5, px: 2, textAlign: 'center' }}>
                <PhotoLibraryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Your photos are ready
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Head over to the Photos tab to view them.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ── Organiser view ── */}
        {event.is_organiser && (
          <>
            {(!showMobileTabs || mobileTab === 'event') && (
              <SmartSortSection
                event={{ ...event, grouping_status: groupingStatus }}
                onStatusChange={handleStatusChange}
                onGalleryRefresh={() => {
                  refreshGallery();
                  setAttendeePhotosReloadKey(k => k + 1);
                }}
                driveConnected={driveConnected}
                driveEmail={driveEmail}
                accountFaceGroupingModel={accountFaceGroupingModel}
                accountFaceGroupingModelLoaded={accountFaceGroupingModelLoaded}
              />
            )}
            {!showMobileTabs && isReady && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="h6" fontWeight={700}>Photo Gallery & Assignment</Typography>
                <Tooltip title={eventShareFlash ? 'Link shared!' : 'Share event'}>
                  <IconButton size="small" onClick={handleShareEvent}>
                    <IosShareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            <Box className="page" sx={{ display: showMobileTabs && mobileTab === 'event' ? 'none' : 'block' }}>
              <EventGalleryViewer
                key={galleryReloadKey}
                event={event}
                isReady={isReady}
                emailIntegrationEnabled={emailIntegrationEnabled}
                mobileTab={showMobileTabs ? mobileTab : null}
                onPhotoCountChange={showMobileTabs ? setPhotoCount : undefined}
                onOpenSubmissionsTab={showMobileTabs ? () => setMobileTab('requests') : undefined}
                onGroupingComplete={() => refreshEventDetails().catch(() => {})}
              />
            </Box>
          </>
        )}

        {/* ── Attendee view ── */}
        {!event.is_organiser && event.role !== 'circle_viewer' && (!showMobileTabs || mobileTab === 'requests') && (
          <>
            <MyRequestSection event={event} onSubmissionsChanged={() => setAttendeePhotosReloadKey(k => k + 1)} />
            <AttendeePhotoUploadSection event={event} />
          </>
        )}
        {!event.is_organiser && (
          <AttendeeGalleryViewer
            event={event}
            accountFaceGroupingModel={accountFaceGroupingModel}
            mobileTab={showMobileTabs ? mobileTab : null}
            onPhotoCountChange={showMobileTabs ? setPhotoCount : undefined}
          />
        )}

        {(!showMobileTabs || mobileTab === 'event') && (
          <EventFeedbackSection
            event={event}
            isOrgContext={isOrgContext}
            orgRole={orgRole}
            userName={user?.customer_name || ''}
          />
        )}
      </Container>
    </Box>
  );
}
